// PrepareAndSend — the full-screen e-signature document-prep experience
// (Prepare & Send build phase 1; replaces SendForSignatureModal).
//
// CONSUMER: internal-ui.
//
// The advisor sees the document's pages rendered in-browser with a
// field overlay: click a field type, click the page to place it; drag
// to move, corner-drag to resize, arrow keys to nudge, one click to
// repeat initials on every page. Every field is color-coded to its
// signer. Fields travel to the server in PDF points (top-left origin —
// shared/esign/field-types.ts is the single statement of that space)
// and the pages render at scale 1, so screen pixels and PDF points are
// the SAME number here — the conversion helpers exist for a future
// zoom control, but at 1:1 nothing can drift.
//
// Doctrine notes: signers stay restricted to people already linked to
// the record (same as the modal this replaces — server re-validates);
// the send button names the recipients ("Send to Bob & Sue"), never
// "Submit"; and nothing is sent until that explicit click.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ChevronDown, ChevronUp, GripVertical } from "lucide-react";

import type { EsignFieldType } from "../../../../shared/esign/field-types";
import {
  kindCarriesItsOwnCompany,
  type EsignSendableKind,
} from "../../../../shared/esign/sendable-kinds";
import {
  api,
  apiV11,
  ApiError,
  type EsignDraftDocument,
  type EsignSendResponse,
  type RecordChecklistKind,
  type RecordChecklistResponse,
  type ServiceRecordPerson,
} from "../../api";
import { colors } from "../../design/tokens";
import { isChunkLoadError } from "../../lib/lazy-with-reload";
import { Button, ErrorBanner, LoadingBlock } from "../../ui";
import { useToast } from "../Toast";
import { FinishSplitButton, type FinishAction } from "./FinishSplitButton";
import { PrepareToolbar } from "./PrepareToolbar";
import { armedHint, armedToolId, pageNeedsText, type ToolId } from "./toolbar-helpers";
import {
  FIELD_TYPE_LABEL,
  FIELD_TYPE_SHORT_LABEL,
  NUDGE_PT,
  clampToPage,
  computePackageBlockers,
  describePages,
  fieldsInRect,
  isFieldOffPage,
  moveInList,
  otherDocumentBlockerNote,
  placeAtPoint,
  FIXED_TEXT_SIZE,
  FIXED_TEXT_LINE_PITCH_PT,
  strikeRect,
  repeatOnEveryPage,
  requiredTriState,
  signerColor,
  THIRD_SEAT_COLOR,
  FOURTH_SEAT_COLOR,
  PREPARER_COLOR,
  slotsFromSignerHints,
  distributeSigningBlocks,
  type MappingSlot,
  type PrepField,
  lastRadioGroupFor,
  guestSignerKey,
  guestSignerIndex,
  isGuestSignerKey,
  remapAfterGuestRemoval,
  type PrepStamp,
  isMappedRecordValue,
  sequentialSigningOrder,
  defaultOfferSigningOrder,
  signersInOrder,
} from "./prepare-helpers";
import { onlyWhatYouKnow } from "@shared/esign/only-what-you-know";
import {
  isMultilineField,
  multilineBaselineYs,
  multilineFontSize,
  multilineHeightFor,
  multilineLineCount,
  MULTILINE_BASELINE_LIFT_PT,
  MULTILINE_PADDING_PT,
  MULTILINE_RULE_OVERHANG_PT,
  snapHeightToLines,
  withOpenBoxWrap,
} from "@shared/esign/multiline-text";
import { MULTILINE_SCREEN_FONT_FAMILY } from "./multiline-measure";
import { layoutStyledMultiline, layoutStyledMultilineInBox, textStyleCss } from "./text-style-helpers";
import {
  pitchAfterSizeChange,
  parseTextStyle,
  TEXT_INDENT_STEP_PT,
  TEXT_STYLE_COLORS,
  TEXT_STYLE_SIZES,
  MAX_TEXT_INDENT_PT,
  type EsignTextStyle,
} from "@shared/esign/text-style";
import {
  OFFER_KIND_HEADING,
  OFFER_KIND_LABEL,
  OFFER_KIND_ORDER,
  ghostCensus,
  ghostOffer,
  isGhost,
  offerTag,
  partitionForSend,
  sumCensus,
  type GhostOffer,
  type OfferKind,
} from "@shared/esign/ghost-offers";
import {
  documentDotState,
  fitToWidthScale,
  neighbourKey,
  packageWalk,
  packageWalkKey,
  placePopover,
  pxToPt,
  seatForKey,
  splitWalkKey,
  stepZoom,
} from "@shared/esign/prep-workspace";
import {
  isReceivedDocument,
  RECEIVED_DOCUMENT_EXPLANATION,
} from "@shared/esign/document-origin";
import {
  unconfirmedPagesMessage,
  unconfirmedReceivedPages,
} from "@shared/esign/page-confirmation";
import {
  castRoles,
  castSeat,
  castingBlockers,
  addSeatToCasting,
  castingRecord,
  extraSeatsOnOffer,
  leaveSeatOff,
  seatsTheSenderStamps,
  swapFirstTwo,
  type CastPerson,
  type CastSeat,
  type Casting,
  fitCastingToForms,
  firmSeatJoinsSend,
} from "@shared/esign/role-casting";
import {
  firstSeatOf,
  matchCaptionToRole,
  rolesFor,
  rolesForForm,
  seatAppliesToRecord,
  secondSeatOf,
  type RoleSide,
  type RoleTone,
} from "@shared/esign/signing-roles";
import { copyTextToClipboard } from "./copy-to-clipboard";
import { MergeFieldPicker, type MergeFieldOption } from "./MergeFieldPicker";
import { ForwardRecipientsPicker, type ForwardRecipient } from "./ForwardRecipientsPicker";
import { InPersonPanel } from "./InPersonPanel";
import { PageThumbs } from "./PageThumbs";
import { PreviewAsSigner } from "./PreviewAsSigner";
import {
  ESIGN_ANY_DOCUMENT_TYPE,
  ESIGN_DEFAULT_EXPIRY,
  ESIGN_DEFAULT_REMINDER,
  ESIGN_DEFAULT_REMINDER_MAX,
  ESIGN_REMEMBERED_VIDEOS,
  ESIGN_WORDING_DEFAULTS,
  rememberVideo,
} from "../../settings/user-settings-registry";
import { useUserSetting, useUserSettingsLoaded } from "../../settings/user-settings-context";
import { SIGNING_INVITATION_FALLBACK_BODY } from "@shared/esign/signing-invitation";
import {
  PdfRenderTimeout,
  renderPdfPages,
  type RenderedPdf,
  type RenderedPdfPage,
} from "./render-pdf-pages";
import { documentFingerprint } from "../../../../shared/esign/document-fingerprint";
import { AAR_FORM_SIGNATURES } from "../../../../shared/esign/aar-form-signatures";
import { realDocumentType } from "../../../../shared/esign/placeholder-document-types";
import { isProviderStamp } from "../../../../shared/esign/field-suggestions";
import {
  dropSigningMarksOnSignedBlocks,
  insideRegions,
  officeUseRegions,
  snapSigningMarksToRules,
} from "../../../../shared/esign/vector-blanks";
import {
  placeOnDocument,
  recognizeForms,
  revisionForDisplay,
  type RecognizedForm,
} from "../../../../shared/esign/form-recognition";
// The ceiling on numbered mapping slots lives in shared, alongside the
// code that fills them in, so the screen and the route can never
// disagree about how many signers a saved map may hold.
import { MAX_MAPPING_SLOTS, seatsAPackageSigns } from "../../../../shared/esign/layout-apply";
import {
  DEFAULT_PRINTED_FONT_PT,
  BOLDSIGN_CHECKBOX_ON,
  PREPARER_SIGNER_KEY,
  isPreparerField,
  printsByStamp,
} from "../../../../shared/esign/field-types";
import {
  fieldsBlockingSend,
  isUnselectedSignerField,
  sendBlockReason,
} from "../../../../shared/esign/send-readiness";
import { CLIENT_COMPANY_LABELS } from "../connected/ExperienceStrip";
import { anchorForBox } from "../../../../shared/esign/layout-anchors";
import { mergeTokenLabel } from "../../../../shared/microsoft-graph/merge-fields";
import {
  unfilledBindings,
  bindingPersonSignerKey,
  isEmptySeatBox,
  isPersonScopedToken,
  isSignerScopedToken,
  wantsDefaultSeat,
  SEND_DATE_PLUS_6_TOKEN,
  sixMonthsAfterTypedStart,
} from "../../../../shared/esign/field-merge-bindings";
import {
  fitSuggestionsToPages,
  mergeSuggestions,
  selectSpansForSuggester,
  suggestFromText,
  suggestFromWidgets,
  type SuggestedField,
  type SuggestionSource,
} from "../../../../shared/esign/field-suggestions";
import {
  blankKey,
  detectBlanks,
  foldStackedProposals,
  isRun,
  dominantTypeSize,
  seatOnRuledRun,
  dropProposalsOnWrittenBlanks,
  isAlreadyWrittenOn,
  isSigningBlockField,
  suggestFromBlanks,
  unmappedBlanks,
  type DetectedBlank,
} from "@shared/esign/vector-blanks";
import type { LearnedCaption } from "@shared/esign/label-bindings";
import { resolveEsignSenderIdentity } from "@shared/esign/sender-identity";
import { useSession } from "../../session";
import { NotConnectedNotice } from "../NotConnectedNotice";
import {
  describeCadence,
  ESIGN_UI_MAX_REMINDERS,
  reminderMinutes,
  type ReminderUnit,
} from "./reminder-cadence";
import { defaultSigningSubject, documentDisplayName } from "@shared/esign/signing-email-extras";
import { ReviewAndSend, type ReviewSection, type ReviewVideo } from "./ReviewAndSend";
import { readDraftState, worthKeeping, type DraftDocumentWork, type EsignDraftStateV1 } from "./package-draft";
import { lockedSignature } from "./correction-helpers";
import { SignersAndOrderStep } from "./SignersAndOrderStep";
import { describeSigningOrder } from "@shared/esign/signing-order";

export type { PrepStamp } from "./prepare-helpers";

/** The date printed beside a stamped signature. Long form, matching the
 *  DateSigned stamps the provider renders for real signers, so the two
 *  do not read as different kinds of date on the same page. */
function formatStampDate(d: Date): string {
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

/** "Saved by Brian Baker on Sep 14 · used 6 times since" — the answer to
 *  Joey Ellis's 2026-09-18 question: the "you've sent this before"
 *  banner recognized a document with no way to say WHOSE map it was
 *  applying, so the question "is this Stew guessing, or is this what
 *  the team set up, and who" was unanswerable from the screen. Every
 *  piece is optional and the line degrades honestly rather than
 *  guessing: a map with no creator (an import, or a user row since
 *  removed) says only when and how often; one with neither says only
 *  the usage count, which is still real information. */
function layoutProvenanceLine(info: AppliedLayoutInfo): string | null {
  const parts: string[] = [];
  if (info.createdByName && info.createdAt) {
    const when = new Date(info.createdAt);
    const dateText = Number.isNaN(when.getTime())
      ? null
      : when.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    parts.push(dateText ? `Saved by ${info.createdByName} on ${dateText}` : `Saved by ${info.createdByName}`);
  } else if (info.createdByName) {
    parts.push(`Saved by ${info.createdByName}`);
  } else if (info.createdAt) {
    const when = new Date(info.createdAt);
    if (!Number.isNaN(when.getTime())) {
      parts.push(
        `Saved ${when.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`,
      );
    }
  }
  // timesApplied is the count BEFORE this send — the layout's own
  // "times_applied" column is only incremented once this send actually
  // goes through, so this reads as history, not as counting a use that
  // hasn't happened yet.
  if (info.timesApplied > 0) {
    parts.push(`used ${info.timesApplied} ${info.timesApplied === 1 ? "time" : "times"} before this`);
  }
  return parts.length > 0 ? parts.join(" · ") : null;
}

const STAMP_DEFAULT_WIDTH_PT = 140;
const STAMP_MIN_HEIGHT_PT = 24;
const STAMP_MAX_HEIGHT_PT = 90;

/** One contract term the document and the record disagree about, as the
 *  server reports it. Mirrors ContractTermDivergence in
 *  shared/realty/contract-sync.ts — the server is the only side that can
 *  see this, so the shape arrives rather than being derived here. */
export interface RecordSyncTerm {
  field: string;
  label: string;
  recordValue: string;
  documentValue: string;
  /** False when the typed text could not be read as this kind of term
   *  ("10%" in a dollar box). Listed so the advisor knows the two are
   *  out of step; never written, because nobody can say what it meant. */
  writable: boolean;
}

/** How long after the person's last click or keystroke autosave waits
 *  (PR 8e): long enough that a burst of typing is one save, short enough
 *  that closing the laptop loses almost nothing. */
const AUTOSAVE_PAUSE_MS = 2500;

/** A document's status in the package rail, in the drawing's words
 *  (PR 9b): what is placed, what came from the record, what is left. */
export function railStatus(placed: number, todo: number, fromRecord: number): string {
  if (placed === 0) return todo > 0 ? `${todo} to decide` : "Nothing to sign";
  if (todo > 0) return `${placed} ${placed === 1 ? "field" : "fields"} · ${todo} to decide`;
  return fromRecord > 0
    ? `All fields placed · ${fromRecord} filled from the record`
    : `All fields placed · ${placed} ${placed === 1 ? "field" : "fields"}`;
}

/** Where a record field's answer comes from, in the words of the
 *  drawing's "Auto-filled from this record" list (PR 9b). */
export function mergeSourceLabel(token: string): string {
  const ns = token.split(".")[0] ?? "";
  if (ns === "contact") return "Contacts";
  if (ns === "household") return "Household";
  if (ns === "service_record") return token.includes("property") ? "Property" : "The policy, loan or account";
  if (ns === "transaction") return "Contract card";
  if (ns === "opportunity" || ns === "offer_default") return "This opportunity";
  if (ns === "advisor") return "Your profile";
  if (ns === "firm") return "Firm profile";
  if (ns === "other_agent") return "The other side's agent";
  if (ns === "county") return "County";
  return "Other record fields";
}

/** The form's own name for "Update the {name} template": no extension,
 *  no dashes. */
export function templateNameFor(filename: string): string {
  return (
    filename
      .replace(/\.[a-z0-9]{2,5}$/i, "")
      // A browser's duplicate-download marker and a version note are the
      // file's history, not the form's name (Nate, 2026-10-13: "Addendum
      // (AAR) (version 3) (3)").
      .replace(/(\s*\((?:\d+|v(?:ersion)?\s*\d+)\))+\s*$/i, "")
      .replace(/[-_]+/g, " ")
      .replace(/\s+/g, " ")
      .trim() || "form"
  );
}

/** The template button's words. A long file name in a 12px uppercase
 *  button was cut off mid-word ("UPDATE THE ADDENDUM (AAR) (VERSION 3)
 *  (3) …"), so past a button's worth of name it says "this form" and the
 *  name moves to the tooltip. */
export function templateButtonLabel(filename: string): string {
  const name = templateNameFor(filename);
  return name.length <= 24 ? `Update the ${name} template` : "Update this form's template";
}

/** How many of a document's fields fill in from the record. */
function recordFilledCount(fields: readonly { mergeToken?: string | null }[] | undefined): number {
  return (fields ?? []).filter((f) => Boolean(f.mergeToken)).length;
}

/** What the top bar says about the draft (PR 8e). */
type AutosaveState =
  | { kind: "idle" }
  | { kind: "saving" }
  | { kind: "saved"; at: Date }
  /** Someone saved this draft elsewhere; autosave stopped. */
  | { kind: "blocked" }
  | { kind: "failed" };

/** "Autosaved 2:14 PM" and its siblings; null says nothing. */
export function autosaveLabel(state: AutosaveState): string | null {
  switch (state.kind) {
    case "saving":
      return "Saving…";
    case "saved":
      return `Autosaved ${state.at.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
    case "blocked":
      return "Not autosaved: this draft was changed somewhere else";
    case "failed":
      return "Couldn't autosave. Trying again when you next change something";
    default:
      return null;
  }
}

/** The package a correction is for (PR 10b). */
export interface CorrectionTarget {
  envelopeId: string;
  /** The package's name, for the banner. */
  title: string;
  /** Why, as typed in the Correct & resend window. */
  reason: string;
  /** The signer keys of people who have signed: their boxes are locked. */
  signedKeys: readonly string[];
  signedNames: readonly string[];
}

export interface PrepareAndSendProps {
  open: boolean;
  onClose: () => void;
  /** The top bar's eyebrow when this screen is the package wizard's later
   *  steps (Documents tab PR 8a). Absent keeps "Prepare & send". */
  eyebrow?: string | null;
  /** A saved draft to open as it was left (PR 8c). Its documents arrive
   *  through the ordinary documents props; this carries the rest. */
  draft?: { id: string; versionNumber: number; state: unknown } | null;
  /** "Fix what hasn't been signed yet" (Documents tab PR 10b): this screen
   *  is correcting a package that is out, opened from its saved work
   *  (passed as `draft`, which is then never saved over). The boxes of
   *  people who signed, the sender's own boxes and the stamped lines are
   *  in the file already and stay as they are; Send becomes "Send the
   *  correction". */
  correction?: CorrectionTarget | null;
  /** After a correction has been accepted by the signing service. */
  onCorrected?: () => void;
  /** After "Save as draft" (new or saved over), so the tab's draft line
   *  can refresh. The screen stays open. */
  onDraftSaved?: () => void;
  /** Open on "Who signs, and in what order" (step 2 of the package
   *  wizard, PR 8d) before the field screen. Set for a package from the
   *  wizard or a draft; absent opens straight on the fields, as before. */
  startOnSigners?: boolean;
  /** Which record this document is being sent FROM. Processing records
   *  were the only sender until 2026-08-23; opportunities and service
   *  records send through the same screen and the same API. */
  recordKind: EsignSendableKind;
  recordId: string;
  /** Which company this document is for. Only meaningful for a
   *  STANDALONE (household) send: a household can be served by all five
   *  companies, so nothing can be read off the record — and the company
   *  decides which saved layouts are offered and whether the Realty
   *  commission-disclosure merge fields exist. Every other kind carries
   *  its own company and the server ignores this. */
  companySlug?: string | null;
  documentId: string;
  documentFilename: string;
  /** The document's own type, when it already has one. Null means the
   *  prep screen asks (Grant, 2026-08-23) — an untyped signed document
   *  can never match an automation's document-type filter. */
  documentType?: string | null;
  /** "received" when the OTHER SIDE wrote this document. The
   *  one-document form of PrepareDocument.origin, same shape as
   *  documentType above, so a single-document caller can say it without
   *  building a package of one. Absent means ours. */
  documentOrigin?: string | null;
  /** THE REST OF THE PACKAGE, when this send is more than one document
   *  (Nate, 2026-08-31: a contract, its addenda and a prequal letter,
   *  signed together as one).
   *
   *  The FIRST entry is the primary document and must match the
   *  documentId/documentFilename/documentType props above — those stay
   *  required so every existing caller works untouched, and so a
   *  one-document send needs to know nothing about packages. Omit this
   *  entirely for one document; the screen then looks and behaves
   *  exactly as it always has, with no document rail at all. */
  documents?: readonly PrepareDocument[];
  candidates: ServiceRecordPerson[];
  /** THE TEAM MEMBER, on a Stewardship Productions personnel send
   *  (2026-09-28: onboarding and contractor paperwork). A team member
   *  is not a contact, so they cannot come from `candidates`; they
   *  arrive already on the send as a named signer (the same lane a
   *  co-op agent uses) and fill the Team Member seat. Absent on every
   *  other send, which then behaves exactly as before. */
  personnelSigner?: { name: string; email: string } | null;
  /** Fires after a successful send, carrying the envelope it produced.
   *  The argument is new (2026-09-03, so an offer can hold the package it
   *  sent); every caller that ignores it behaves exactly as before. */
  onSent?: (result: { envelope_id: string; realtyOfferAttached?: boolean }) => void;
  /** The Realty offer this package is for (PR 8e): a draft saved here
   *  remembers it, and the send files the package under it on the
   *  server, whichever screen finishes it. */
  realtyOfferId?: string | null;
  /** MAPPING MODE (2026-09-01): teach a BLANK FORM's field map with no
   *  household, no contact, no deal — Grant, relaying Nate: "he needs to
   *  be able to create a contract without having to do all this mapping
   *  and work."
   *
   *  When present, this screen opens against the template's own bytes
   *  instead of a record's document, signers are numbered slots the
   *  mapper types (never real people — nobody here is ever emailed),
   *  and the only action offered is "Update the {form} template" (its
   *  name since PR 9b; it was "Save map for this form"). Every
   *  other prop (recordKind, recordId, documentId, candidates) is still
   *  required by the type and is IGNORED — pass harmless placeholders
   *  (see RealtyFormsLibraryPage's caller) rather than threading a
   *  second, optional-everything prop shape through a screen the real
   *  send path also depends on.
   *
   *  `documentId` doubles as the template id on every network call this
   *  mode makes, so the caller passes the template's id there too. */
  mapping?: {
    templateId: string;
    formName: string;
    /** 'listing' | 'purchase' | 'both' from the form's own catalog row,
     *  where it has one. It decides WHICH seats the mapping slots offer
     *  on a company with sides: a listing contract is mapped in Sellers,
     *  a purchase contract in Buyers. Null or 'both' takes the
     *  company's default list. */
    appliesToSide?: string | null;
  };
}

/** One document in the package, as the caller assembled it. */
export interface PrepareDocument {
  documentId: string;
  filename: string;
  documentType?: string | null;
  /** Known up front for a document copied from a saved template — its
   *  layout can then be found without re-deriving the fingerprint. Left
   *  out for an uploaded file, whose fingerprint this screen computes
   *  when it renders it. */
  contentFingerprint?: string | null;
  /** "received" when the OTHER SIDE wrote this document — a listing
   *  agent's counteroffer. Its boxes came off the suggestion ladder
   *  rather than off a map a person made, so it earns a page-by-page
   *  look before it can be sent. Absent means ours, which is every
   *  document the OS produced. See shared/esign/page-confirmation.ts. */
  origin?: string | null;
  /** An ATTACHMENT (PR 8c): a file from the record that goes out to be
   *  read, never signed. The screen offers no suggestions on it (reading
   *  a 40-page inspection report for blanks would cost money for nothing)
   *  and the rail says what it is. */
  attachment?: boolean;
}

/** What this screen remembers about a document while the sender is
 *  working on a DIFFERENT one.
 *
 *  Switching documents must never cost someone the fields they just
 *  placed, so the active document's working state is parked here on the
 *  way out and restored on the way back. Rendered pages are kept too:
 *  re-fetching and re-rasterising a 40-page contract every time someone
 *  flicks between it and its addendum would make the rail feel broken. */

/** Who saved the map this document's proposals came from, and how much
 *  it has been leaned on — what turns "you've sent this before" into an
 *  answerable question (Joey Ellis, 2026-09-18: the banner said the
 *  file was recognized with no way to tell whose map it was applying,
 *  Stew's reading or a teammate's saved work). Null fields are honest
 *  gaps, not zeros — a map with no creator is one an import wrote or
 *  whose author has since left, and the panel says so rather than
 *  guessing a name. */
/** WHICH FORM THE OS THINKS THIS IS (Grant, 2026-10-13: Nate's
 *  contracts came out wrong every time because none of them was ever
 *  recognized, and the screen never said so). Stated on the panel in
 *  plain words, because "these boxes are the team's reviewed map" and
 *  "these boxes are guesses" deserve very different trust.
 *
 *   * `applied`        — recognized, and the reviewed map was moved
 *                        onto this copy's pages.
 *   * `other_revision` — recognized, but this copy prints a different
 *                        revision than the map was drawn on, so the map
 *                        was NOT applied.
 *   * `unknown`        — a Realty document the OS has no reviewed map
 *                        for; every box is a suggestion. */
interface FormRecognitionNote {
  kind: "applied" | "other_revision" | "unknown";
  /** "Counter Offer (June 2021)", one per form. */
  forms: string[];
  /** other_revision only: what this copy prints vs what the map is. */
  documentRevision?: string | null;
  mapRevision?: string | null;
  /** applied only: pages whose position could not be measured against
   *  this copy, so the map's own positions were used. */
  unmeasuredPages?: number;
}

function formLabel(f: RecognizedForm, revision: string | null): string {
  const rev = revisionForDisplay(revision);
  return rev ? `${f.displayName} (${rev})` : f.displayName;
}

interface AppliedLayoutInfo {
  createdByName: string | null;
  createdAt: string | null;
  timesApplied: number;
}

interface ParkedDocumentState {
  fields: PrepField[];
  stamps: PrepStamp[];
  pages: RenderedPdfPage[] | null;
  rendered: RenderedPdf | null;
  fingerprint: string | null;
  /** True once the suggestion ladder has run for this document, so
   *  coming back to it doesn't re-propose fields the sender already
   *  accepted or deleted. */
  suggested: boolean;
  appliedLayout: string | null;
  /** Who saved the applied map, and how established it is — see
   *  `appliedLayoutInfo`'s own comment. Carried per document like the
   *  rest of this snapshot. */
  appliedLayoutInfo: AppliedLayoutInfo | null;
  /** Carried per document, so switching between the forms in a package
   *  does not lose the warning on the one that needs it. */
  layoutPositionsUnchecked: boolean;
  /** Which reviewed form this document was recognized as, or that it
   *  wasn't — see FormRecognitionNote. */
  formRecognition: FormRecognitionNote | null;
  suggestNote: string | null;
  /** How many suggestions were skipped because the page already had
   *  writing on them — see writtenOnCount. */
  writtenOnCount: number;
  /** The blanks the form itself draws — carried per document (2026-09-29)
   *  so the "still unmapped" list never describes the wrong document,
   *  and so a document read in the background has them ready. */
  detectedBlanks: DetectedBlank[];
}

/** WHERE THE SUGGESTION LADDER WRITES (2026-09-29, build PR 3).
 *
 *  The ladder used to write straight into the active document's React
 *  state, so it could only ever run for the document on screen — and a
 *  package's other documents were read only when somebody clicked onto
 *  them. Package-wide Accept all and Tab across documents need every
 *  document read up front, so the ladder now writes through a sink:
 *  the active document's sink is the React state it always wrote to,
 *  and a background read's sink is a draft that gets parked when the
 *  read finishes. One ladder, two destinations; the ladder itself does
 *  not know which. */
interface SuggestSink {
  documentId: string;
  getFingerprint(): string | null;
  setFingerprint(v: string | null): void;
  setSuggesting(v: boolean): void;
  setSuggestNote(v: string | null): void;
  setWrittenOnCount(n: number): void;
  setAppliedLayout(v: string | null): void;
  setAppliedLayoutInfo(v: AppliedLayoutInfo | null): void;
  setLayoutPositionsUnchecked(v: boolean): void;
  setFormRecognition(v: FormRecognitionNote | null): void;
  setDetectedBlanks(v: DetectedBlank[]): void;
  setStamps(update: (prev: PrepStamp[]) => PrepStamp[]): void;
  setFields(update: (prev: PrepField[]) => PrepField[]): void;
}

/** A fresh, empty parked state for a document whose pages have been
 *  drawn but whose ladder has not run yet. */
function emptyParkedState(rendered: RenderedPdf): ParkedDocumentState {
  return {
    fields: [],
    stamps: [],
    pages: rendered.pages,
    rendered,
    fingerprint: null,
    suggested: true,
    appliedLayout: null,
    appliedLayoutInfo: null,
    layoutPositionsUnchecked: false,
    formRecognition: null,
    suggestNote: null,
    writtenOnCount: 0,
    detectedBlanks: [],
  };
}

// Stamps only — a signature stamp is never part of a multi-selection,
// so it keeps the original single-key drag shape.
type DragState =
  | { kind: "move"; key: string; startX: number; startY: number; origX: number; origY: number }
  | { kind: "resize"; key: string; startX: number; startY: number; origW: number; origH: number };

/** Fields, separately from stamps (Joey, 2026-09-18: multi-select).
 *  "move" carries every key being dragged together plus each one's OWN
 *  starting position — dragging one box in a multi-selection moves the
 *  whole group by the same delta, not just the box under the pointer.
 *  "resize" stays single-field: the resize handle only ever renders
 *  when exactly one field is selected. */
type FieldDragState =
  | {
      kind: "move";
      keys: string[];
      startX: number;
      startY: number;
      origins: Map<string, { x: number; y: number }>;
    }
  | { kind: "resize"; key: string; startX: number; startY: number; origW: number; origH: number };

/** Resolve a suggestion's role hint ("Buyer", "Borrower") to someone
 *  actually on this record.
 *
 *  Deliberately conservative — it matches a hint against a person's own
 *  name or the role the record gives them, and returns null on anything
 *  ambiguous. A field assigned to the WRONG signer is worse than one
 *  assigned to nobody in particular: the advisor scans for unassigned
 *  fields, but a confidently wrong assignment reads as decided. */
/** What a proposed field says when you hover it — where it came from,
 *  in words, and how sure that rung is. Provenance is honest: "the
 *  document declared this" and "Stew read it that way" are different
 *  claims and must not read alike. */
export function proposalTitle(p: {
  source: SuggestionSource;
  confidence: number;
  reason: string;
}): string {
  const from =
    p.source === "form_field"
      ? "From the document's own form fields"
      : p.source === "text_layer"
        ? "Found on a line in the document"
        : p.source === "saved_layout"
          ? "From your saved layout"
          : "Suggested by Stew";
  const unsure = p.confidence < 0.6 ? " · worth a check" : "";
  return `${p.reason} — ${from}${unsure}. Click to adjust, or accept it as-is.`;
}

/** ONE ROW IN THE SIGNER PANEL, whatever kind of signer it is.
 *
 *  The panel deals in three kinds and used to pretend they were all
 *  contacts — mapping slots already masqueraded as `ServiceRecordPerson`
 *  with a fake `contact_id`, which needed a paragraph of comment to
 *  explain. Adding a signer who genuinely is not a contact (Grant,
 *  2026-09-03) made that pretence untenable, so the panel now says what
 *  each row is:
 *
 *    'person' — someone on this record. `contactId` is real.
 *    'guest'  — a name and an email typed by the sender: a co-op agent,
 *               a seller, a title officer. NO contact, NO household,
 *               nothing written to our database beyond one row on the
 *               envelope's signer list.
 *    'slot'   — a numbered stand-in in mapping mode. Never emailed,
 *               never sent anything; it exists so a saved layout can be
 *               taught positionally with no real people involved.
 *
 *  `key` is what a placed field is filed under (EsignPlacedField.
 *  signerKey). For a person it IS the contact id, which is why every
 *  already-saved layout and every already-open prep screen keeps
 *  working unchanged. */
interface PanelSigner {
  key: string;
  kind: "person" | "guest" | "slot";
  /** Real only for 'person'. Null is what makes a guest a guest. */
  contactId: string | null;
  displayName: string;
  /** Null for a slot (nothing is ever sent to one). */
  email: string | null;
  isPrimary: boolean;
}

/** The colour a seat's person wears, by the seat's tone: the record's
 *  first person blue, the second amber, the advisor green, a broker
 *  navy — so Buyer 1 is the same colour on every send whatever position
 *  they hold in the list. Neutral (name-only) seats place no fields. */
const TONE_COLOR: Record<RoleTone, ReturnType<typeof signerColor> | null> = {
  first: signerColor(0),
  second: signerColor(1),
  third: THIRD_SEAT_COLOR,
  fourth: FOURTH_SEAT_COLOR,
  owner: signerColor(2),
  broker: signerColor(3),
  neutral: null,
};

/** What the signers route returns for casting, plus the company it
 *  read off the record (null on a standalone send until chosen). */
interface RolesContext {
  company: string | null;
  /** The record's people as the route lists them — the same list the
   *  send restricts to. Used to cast when the caller opened this
   *  screen before its own candidates had loaded. */
  people: { contactId: string; displayName: string; email: string | null; isPrimary: boolean }[];
  owner: { userId: string; displayName: string } | null;
  parties: {
    key: string;
    partyRole: string;
    displayName: string;
    email: string | null;
    contactId: string | null;
  }[];
  otherPartyName: string | null;
  /** The firm's standing holder of a seat (Realty's managing broker). */
  firmRoles: { roleKey: string; userId: string; displayName: string; email: string | null }[];
  side: RoleSide;
}

export function matchSignerHint(
  hint: string | null,
  people: readonly { key: string; displayName: string }[],
): string | null {
  if (!hint) return null;
  const needle = hint.trim().toLowerCase();
  if (!needle) return null;
  const hits = people.filter((p) => {
    const name = p.displayName.toLowerCase();
    return name.includes(needle) || needle.includes(name.split(" ")[0] ?? "\u0000");
  });
  // Exactly one match, or nothing. Two people matching "buyer" is
  // precisely the case where guessing does damage.
  return hits.length === 1 ? hits[0]!.key : null;
}

// "What fills this box?" (2026-09-02) — the four answers a blank on a
// contract actually has: a signer's mark (signature or initials), a
// date, or a fact the record already knows. These lead the palette,
// same size, same row. Everything else ESIGN_FIELD_TYPES knows about
// (a plain unbound text box, checkboxes, pick-one/choose-one, a date
// the SIGNER picks, a file upload) is real functionality, not "what
// fills this box" in the same sense — kept one click away under
// "Other field types" rather than competing for the same first glance.
const FILL_CHOICE_TYPES = ["signature", "initials", "date_signed"] as const;

/** Heights used when a person fills a detected blank in one click. The
 *  same numbers shared/esign/vector-blanks.ts seats a PROPOSED field at,
 *  so a blank filled by hand and one the OS proposed are
 *  indistinguishable on the page. */
/** A multi-line box's text on the page, one line per printed rule
 *  (Grant, 2026-09-29: "it needs to start on the first line and the lines
 *  and text size formatting needs to perfectly match the lines").
 *
 *  Each line is positioned ABSOLUTELY from the geometry in
 *  shared/esign/multiline-text.ts — the same numbers the stamper prints
 *  from — rather than left to the browser's line boxes, so line k lands
 *  on rule k whatever the font's ascent happens to be. The screen renders
 *  pages at scale 1 (px == pt), which is what makes that arithmetic hold.
 *  Measured and drawn in Helvetica/Arial, the face the stamper embeds, so
 *  the wrap the sender sees is the wrap that prints. */
function MultilineFieldText({
  field,
  text,
  placeholder,
}: {
  field: PrepField & { linePitch: number };
  text: string | null;
  placeholder: string;
}) {
  const indent = (field.firstLineIndent ?? 0) + (field.textStyle?.indent ?? 0);
  if (!text || !text.trim()) {
    // Nothing typed yet: the label sits on the first line, where the
    // text will, rather than floating in the middle of the box.
    return (
      <span className="esign-prep-field-lines">
      <span
        className="esign-prep-field-line"
        data-placeholder="true"
        style={{
          left: MULTILINE_PADDING_PT + indent,
          top: Math.max(0, field.linePitch - MULTILINE_RULE_OVERHANG_PT - MULTILINE_BASELINE_LIFT_PT - 10),
        }}
      >
        {placeholder}
      </span>
      </span>
    );
  }
  // Box-relative: these spans are children of the box, which already
  // sits at the field's page position.
  const laid = layoutStyledMultilineInBox(field, text);
  // Clipped HERE rather than on the box, so the resize handle and the ×
  // that sit on the box's corner are not cut off with the text.
  return (
    <span className="esign-prep-field-lines">
      {laid.lines.map((line, k) =>
        line.text ? (
          <span
            key={k}
            className="esign-prep-field-line"
            style={{
              left: line.x,
              // The baseline sits 0.85em below the top of a line box set
              // at line-height 1 in Helvetica/Arial (ascent 0.905, less
              // half the overflow of its 1.117em content area).
              top: line.baselineY - laid.fontSize * 0.85,
              fontSize: laid.fontSize,
              fontFamily: MULTILINE_SCREEN_FONT_FAMILY,
              ...textStyleCss(field.textStyle),
            }}
          >
            {line.text}
          </span>
        ) : null,
      )}
      {laid.overflow > 0 ? (
        <span className="esign-prep-field-overflow">
          {laid.overflow} {laid.overflow === 1 ? "line" : "lines"} over
        </span>
      ) : null}
    </span>
  );
}

/** The formatting row for a sender's own text box: bold, italic, underline,
 *  color, size and indent. Everything is one style on the whole box (this is
 *  a form, not a word processor), saved with the template and printed by
 *  the stamper in the same face. Rules and bounds: shared/esign/text-style.ts.
 *
 *  `onChange` takes a function from the current field to the next, so a
 *  second click while the first is still rendering never overwrites it. */
function TextStyleControls({
  field,
  onChange,
}: {
  field: PrepField;
  onChange: (next: (f: PrepField) => PrepField) => void;
}) {
  const style = field.textStyle ?? {};
  const multiline = isMultilineField(field);
  const edit = (change: (cur: EsignTextStyle, f: PrepField) => { style: EsignTextStyle; patch?: Partial<PrepField> }) =>
    onChange((f) => {
      const { style: nextStyle, patch } = change({ ...(f.textStyle ?? {}) }, f);
      const { textStyle: _dropped, ...rest } = f;
      const parsed = parseTextStyle(nextStyle);
      return { ...rest, ...(patch ?? {}), ...(parsed ? { textStyle: parsed } : {}) };
    });
  const toggle = (key: "bold" | "italic" | "underline") =>
    edit((cur) => {
      if (cur[key]) delete cur[key];
      else cur[key] = true;
      return { style: cur };
    });
  const setSize = (value: string) =>
    edit((cur, f) => {
      const oldSize = cur.size ?? f.fontSize ?? DEFAULT_PRINTED_FONT_PT;
      if (!value) {
        delete cur.size;
        return { style: cur };
      }
      const size = Number(value);
      cur.size = size;
      // Bigger type needs more room between lines: the pitch follows the
      // size, and the box keeps the same number of lines.
      if (isMultilineField(f)) {
        const pitch = pitchAfterSizeChange(f.linePitch, oldSize, size);
        return {
          style: cur,
          patch: { linePitch: pitch, height: multilineHeightFor(multilineLineCount(f.height, f.linePitch), pitch) },
        };
      }
      return { style: cur };
    });
  const setIndent = (delta: number) =>
    edit((cur) => {
      const next = Math.max(0, Math.min(MAX_TEXT_INDENT_PT, (cur.indent ?? 0) + delta));
      if (next > 0) cur.indent = next;
      else delete cur.indent;
      return { style: cur };
    });
  return (
    <div className="esign-prep-format" role="group" aria-label="Text formatting">
      <span className="esign-prep-format-label">Text style</span>
      <div className="esign-prep-format-row">
        <button
          type="button"
          className="esign-prep-format-btn"
          aria-pressed={style.bold === true}
          aria-label="Bold"
          title="Bold"
          onClick={() => toggle("bold")}
          style={{ fontWeight: 800 }}
        >
          B
        </button>
        <button
          type="button"
          className="esign-prep-format-btn"
          aria-pressed={style.italic === true}
          aria-label="Italic"
          title="Italic"
          onClick={() => toggle("italic")}
          style={{ fontStyle: "italic" }}
        >
          I
        </button>
        <button
          type="button"
          className="esign-prep-format-btn"
          aria-pressed={style.underline === true}
          aria-label="Underline"
          title="Underline"
          onClick={() => toggle("underline")}
          style={{ textDecoration: "underline" }}
        >
          U
        </button>
        {multiline ? (
          <>
            <button
              type="button"
              className="esign-prep-format-btn"
              aria-label="Indent less"
              title="Indent less"
              disabled={!style.indent}
              onClick={() => setIndent(-TEXT_INDENT_STEP_PT)}
            >
              ⇤
            </button>
            <button
              type="button"
              className="esign-prep-format-btn"
              aria-label="Indent more"
              title="Indent more"
              disabled={(style.indent ?? 0) >= MAX_TEXT_INDENT_PT}
              onClick={() => setIndent(TEXT_INDENT_STEP_PT)}
            >
              ⇥
            </button>
          </>
        ) : null}
      </div>
      <div className="esign-prep-format-row">
        {multiline ? (
          <label className="esign-prep-format-size">
            Size
            <select value={style.size ?? ""} onChange={(e) => setSize(e.target.value)}>
              <option value="">Match the form</option>
              {TEXT_STYLE_SIZES.map((n) => (
                <option key={n} value={n}>
                  {n} pt
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <span className="esign-prep-format-colors" role="radiogroup" aria-label="Text color">
          {TEXT_STYLE_COLORS.map((c) => {
            const on = (style.color ?? "#000000") === c.hex;
            return (
              <button
                key={c.hex}
                type="button"
                role="radio"
                aria-checked={on}
                aria-label={c.label}
                title={c.label}
                className="esign-prep-format-swatch"
                data-on={on ? "true" : undefined}
                style={{ background: c.hex }}
                onClick={() =>
                  edit((cur) => {
                    // Black is the default, so choosing it clears the color.
                    if (c.hex === "#000000") delete cur.color;
                    else cur.color = c.hex;
                    return { style: cur };
                  })
                }
              />
            );
          })}
        </span>
      </div>
    </div>
  );
}

/** TYPE IN THE BOX ITSELF (Grant, 2026-10-14: "the easiest thing would
 *  be to just click and type in the actual multiline box"). A selected
 *  text box becomes its own editor, in the type it will print in, so the
 *  page is where the words go; the popover beside it only offers the
 *  hand-off. A multi-line box keeps its lines on the form's rules: the
 *  line height is the rules' pitch and the first line is seated on the
 *  first rule, the same arithmetic MultilineFieldText draws with. */
function FieldTextEditor({
  field,
  placeholder,
  onChange,
}: {
  field: PrepField;
  placeholder: string;
  onChange: (value: string) => void;
}) {
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  if (isMultilineField(field)) {
    const pitch = field.linePitch;
    const fontSize = multilineFontSize(field.textStyle?.size ?? field.fontSize ?? DEFAULT_PRINTED_FONT_PT, pitch);
    const firstBaseline = multilineBaselineYs({ y: 0, height: field.height, linePitch: pitch })[0] ?? pitch;
    // Where a line box of this height puts its baseline (Arial: half
    // the leading plus the ascent, ~0.35em below the line's middle).
    const paddingTop = Math.max(0, firstBaseline - (pitch / 2 + 0.3465 * fontSize));
    return (
      <textarea
        className="esign-prep-field-editor"
        data-multiline="true"
        aria-label="Type the text for this box"
        autoFocus
        value={field.value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onPointerDown={stop}
        onClick={stop}
        spellCheck
        style={{
          fontSize,
          lineHeight: `${pitch}px`,
          paddingTop,
          paddingLeft: MULTILINE_PADDING_PT + (field.textStyle?.indent ?? 0),
          paddingRight: MULTILINE_PADDING_PT,
          textIndent: field.firstLineIndent ?? 0,
          fontFamily: MULTILINE_SCREEN_FONT_FAMILY,
          ...textStyleCss(field.textStyle),
        }}
      />
    );
  }
  const fontSize = Math.min(field.fontSize ?? DEFAULT_PRINTED_FONT_PT, Math.max(6, field.height * 0.75));
  return (
    <input
      className="esign-prep-field-editor"
      aria-label="Type the text for this box"
      autoFocus
      value={field.value ?? ""}
      placeholder={placeholder}
      maxLength={500}
      onChange={(e) => onChange(e.target.value)}
      onPointerDown={stop}
      onClick={stop}
      style={{
        fontSize,
        fontFamily: MULTILINE_SCREEN_FONT_FAMILY,
        ...textStyleCss(field.textStyle),
        // A tall one-line box (one over no printed lines the OS could
        // read) starts at the top, where the eye starts, rather than
        // floating the cursor in the middle of the box (2026-10-15).
        ...(isTallSingleLineBox(field) ? { height: Math.ceil(fontSize * 1.5) + 4 } : {}),
      }}
    />
  );
}

/** A text box much taller than one line of its type that is not a
 *  multi-line box: drawn tall over a page with no rules under it. Its
 *  text sits at the top instead of the middle. */
function isTallSingleLineBox(field: PrepField): boolean {
  if (field.fieldType !== "text" || isMultilineField(field)) return false;
  return field.height > (field.fontSize ?? DEFAULT_PRINTED_FONT_PT) * 3;
}

const FILLED_HEIGHT: Record<EsignFieldType, number> = {
  signature: 28,
  initials: 28,
  date_signed: 24,
  text: 24,
  checkbox: 16,
  dropdown: 24,
  radio: 16,
  editable_date: 24,
  attachment: 28,
};

function isTypingTarget(el: EventTarget | null): boolean {
  return (
    el instanceof HTMLElement &&
    (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable)
  );
}

// ── Folding panel sections (Nate, 2026-09-02) ────────────────────────
//
// "The window to select parties and add signature blocks to the
// document is so tiny it is unusable. I want that window size much
// larger, in fact it should take up the majority of the right hand tool
// bar. Make it and the other information there be collapsible depending
// on what is needed to be seen."
//
// He is describing a real split, not a preference. The panel's bottom
// half was pinned open and held everything a send needs but nobody
// edits on a normal send — reminders, deadline, signing order, the
// note, who gets a copy afterwards — while the two things a sender is
// actually working in (who signs, and the field buttons) shared
// whatever scrolling sliver was left. Folding the settled parts to one
// line each hands that space back.
//
// What NEVER folds, deliberately: the signers, the field palette, the
// blockers that explain why Send is disabled, the package review, and
// Send itself. A safety affordance that a saved preference can hide is
// exactly what the Cleanup System's fixed-chrome rule forbids.
const FOLD_KEY_PREFIX = "esign-prep-fold:";

/** Remembered per browser, not per account: which sections someone
 *  keeps folded is high-churn view state — the same category CLAUDE.md's
 *  localStorage rule puts folder expand/collapse in — and it costs
 *  nothing to re-open a section on a second machine. Every access is
 *  guarded: a browser with site data blocked throws on read, and this
 *  screen must still render. */
function readFold(id: string, fallback: boolean): boolean {
  try {
    const raw = localStorage.getItem(`${FOLD_KEY_PREFIX}${id}`);
    if (raw === "open") return true;
    if (raw === "closed") return false;
  } catch {
    // Storage unavailable — the default is a perfectly good answer.
  }
  return fallback;
}

function FoldingSection({
  id,
  title,
  summary,
  defaultOpen = false,
  children,
}: {
  /** Stable id for the remembered open/closed state. */
  id: string;
  title: string;
  /** One line of what is set right now, read WITHOUT opening it — a
   *  folded section that hides its own state would trade a cramped
   *  panel for a blind one. */
  summary?: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(() => readFold(id, defaultOpen));
  const toggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(`${FOLD_KEY_PREFIX}${id}`, next ? "open" : "closed");
      } catch {
        // Not remembering it is a smaller failure than not opening it.
      }
      return next;
    });
  }, [id]);

  return (
    <div className="esign-prep-fold" data-fold-id={id} data-open={open ? "true" : undefined}>
      <button
        type="button"
        className="esign-prep-fold-head"
        onClick={toggle}
        aria-expanded={open}
      >
        <ChevronDown
          className="esign-prep-fold-chevron"
          size={14}
          aria-hidden="true"
        />
        <span className="esign-prep-fold-title">{title}</span>
        {!open && summary ? <span className="esign-prep-fold-summary">{summary}</span> : null}
      </button>
      {open ? <div className="esign-prep-fold-body">{children}</div> : null}
    </div>
  );
}

/** The "Whose?" picker's one non-seat choice — see its onChange. */
const EVERYONE_ONE_LINE = "__everyone_one_line__";

export function PrepareAndSend({
  open,
  onClose,
  recordKind,
  recordId,
  companySlug = null,
  documentId: primaryDocumentId,
  documentFilename: primaryFilename,
  documentType: primaryDocumentType = null,
  documentOrigin: primaryDocumentOrigin = null,
  documents: packageProp,
  candidates,
  personnelSigner = null,
  onSent,
  realtyOfferId = null,
  mapping,
  eyebrow = null,
  draft = null,
  correction = null,
  onCorrected,
  onDraftSaved,
  startOnSigners = false,
}: PrepareAndSendProps) {
  const toast = useToast();

  // ── The package, and which document is on screen ──────────────────
  //
  // A one-document send builds a one-entry package from the props it
  // has always taken, so everything below this point works on a list
  // and there is no "is this a package?" branch anywhere in the screen.
  // With one entry the rail is not rendered at all.
  const propDocs = useMemo<PrepareDocument[]>(
    () =>
      // A placeholder type ("email_attachment") is where the file came
      // from, not what it is, so the screen treats it as untyped and
      // asks — see shared/esign/placeholder-document-types.ts.
      packageProp && packageProp.length > 0
        ? packageProp.map((d) => ({ ...d, documentType: realDocumentType(d.documentType) }))
        : [
            {
              documentId: primaryDocumentId,
              filename: primaryFilename,
              documentType: realDocumentType(primaryDocumentType),
              origin: primaryDocumentOrigin,
            },
          ],
    [packageProp, primaryDocumentId, primaryFilename, primaryDocumentType, primaryDocumentOrigin],
  );
  // ── The sender's own order (Nate, 2026-09-02) ──────────────────────
  //
  // "Sometimes it is necessary to change the order of the forms for
  // signing. I want the ability to drag the files on the left to change
  // their order prior to sending."
  //
  // The rail's order IS the envelope's order — payloadDocuments below
  // maps over this list, and the server staples them in the order it is
  // handed. So this is not a display preference; it decides what the
  // client reads first.
  //
  // Held as document IDS rather than as a reordered copy of the list,
  // so the caller stays the source of truth for MEMBERSHIP while the
  // sender owns the ORDER. Null until they move something — an
  // untouched package is byte-for-byte the list the caller passed.
  const [docOrder, setDocOrder] = useState<string[] | null>(null);
  const packageDocs = useMemo<PrepareDocument[]>(() => {
    if (!docOrder) return propDocs;
    const remaining = new Map(propDocs.map((d) => [d.documentId, d]));
    const ordered: PrepareDocument[] = [];
    for (const id of docOrder) {
      const doc = remaining.get(id);
      if (doc) {
        ordered.push(doc);
        remaining.delete(id);
      }
    }
    // A document the caller added since the last drag keeps its place at
    // the end rather than disappearing. The order is a preference laid
    // over the list; it can never subtract from it.
    for (const d of propDocs) if (remaining.has(d.documentId)) ordered.push(d);
    return ordered;
  }, [propDocs, docOrder]);
  const [activeIndex, setActiveIndex] = useState(0);
  // ── The review pass on a document THEY wrote ──────────────────────
  //
  // Which pages of which received documents the sender has looked at and
  // said yes to. Held here rather than stored, on purpose: a
  // confirmation is a person saying "the boxes on this page are right",
  // and moving a box afterwards makes that sentence untrue about the
  // document in front of them. Keeping it beside the field placements it
  // is about means it cannot go stale — close the screen and you
  // re-place and re-confirm together. See page-confirmation.ts.
  const [confirmedPagesByDoc, setConfirmedPagesByDoc] = useState<Record<string, number[]>>({});
  /** Set when the server comes back saying the document and the record
   *  disagree about a contract term. Holds the terms and which delivery
   *  the interrupted send was using, so answering resumes exactly that
   *  send rather than a fresh one. */
  const [syncPrompt, setSyncPrompt] = useState<{
    deliver: "email" | "in_person";
    terms: RecordSyncTerm[];
  } | null>(null);
  const activeDoc = packageDocs[Math.min(activeIndex, packageDocs.length - 1)]!;
  const documentId = activeDoc.documentId;
  const documentFilename = activeDoc.filename;
  const documentType = activeDoc.documentType ?? null;
  // "What kind of document is this?" — asked once per document, only
  // when that document arrives untyped. Keyed by document id
  // (2026-09-09) rather than one shared value — a package sends several
  // untyped documents at once (a mortgage compliance package: Purchase
  // Contract, Loan Estimate, Initial Loan Application), and a single
  // flat value would let picking one document's type silently overwrite
  // whatever had been picked for another while it wasn't on screen.
  const [chosenTypeByDoc, setChosenTypeByDoc] = useState<Record<string, string>>({});
  // The ACTIVE document's own answer to the question above.
  const chosenType = chosenTypeByDoc[documentId] ?? "";
  const setChosenType = useCallback(
    (value: string) => setChosenTypeByDoc((prev) => ({ ...prev, [documentId]: value })),
    [documentId],
  );
  const isPackage = packageDocs.length > 1;
  /** Every document's parked working state, keyed by document id. The
   *  ACTIVE document's state lives in ordinary React state (below) and
   *  is written back here when the sender moves away from it. */
  const parkedRef = useRef<Map<string, ParkedDocumentState>>(new Map());
  // ── A DRAFT BEING OPENED (Documents tab PR 8c) ────────────────────
  //
  // A draft's documents arrive through the ordinary props; its saved work
  // is handed to each document the moment it is read, in place of the
  // suggestion pass (see runSuggestions), so the page comes back exactly
  // as it was left and nothing is re-read or re-charged. Set during
  // render, keyed by draft id, so it is in place before the load effect
  // asks for the first document.
  const draftState = useMemo(() => (draft ? readDraftState(draft.state) : null), [draft]);
  const draftWorkRef = useRef<Map<string, DraftDocumentWork>>(new Map());
  const draftCastingRef = useRef<Casting | null>(null);
  const appliedDraftIdRef = useRef<string | null>(null);
  if (open && draft && draftState && appliedDraftIdRef.current !== draft.id) {
    appliedDraftIdRef.current = draft.id;
    draftWorkRef.current = new Map(Object.entries(draftState.work));
    draftCastingRef.current = draftState.casting;
  }
  // ── Correction mode (PR 10b) ──────────────────────────────────────
  // What may not change: the boxes of anyone who has signed, and what is
  // already printed into the file (the sender's own boxes, a multi-line
  // value the OS prints, the stamped signature). They can't be grabbed,
  // and Send compares them with how the package went out before anything
  // is asked of the signing service.
  const correctionLockedKeys = useMemo(() => new Set(correction?.signedKeys ?? []), [correction]);
  const isLockedInCorrection = (f: PrepField): boolean =>
    correction !== null && (correctionLockedKeys.has(f.signerKey) || printsByStamp(f));
  /** The locked part of the package as it went out, per document, read
   *  from the saved work the correction opened on. */
  const correctionBaseline = useMemo(() => {
    if (!correction || !draftState) return null;
    const out = new Map<string, string>();
    for (const [docId, work] of Object.entries(draftState.work)) {
      out.set(docId, lockedSignature(work.fields, work.stamps, correctionLockedKeys));
    }
    return out;
  }, [correction, draftState, correctionLockedKeys]);
  /** The draft this screen saves over: the one it was opened from, or the
   *  one its first "Save as draft" created. */
  const [savedDraft, setSavedDraft] = useState<{ id: string; versionNumber: number } | null>(null);
  const [savingDraft, setSavingDraft] = useState(false);
  // ── Autosave (PR 8e) ──────────────────────────────────────────────
  // The draft is saved a moment after the person stops working, once
  // they have done something; opening a screen alone saves nothing.
  const [autosave, setAutosave] = useState<AutosaveState>({ kind: "idle" });
  const autosaveRef = useRef(autosave);
  autosaveRef.current = autosave;
  const autosaveTimerRef = useRef<number | undefined>(undefined);
  const userTouchedRef = useRef(false);
  const lastSavedSignatureRef = useRef<string | null>(null);
  const draftSaveInFlightRef = useRef(false);
  const draftSavePromiseRef = useRef<Promise<boolean> | null>(null);
  /** The draft the last successful write produced, for the save on close
   *  that waits on a write still in flight. */
  const lastWrittenDraftRef = useRef<{ id: string; versionNumber: number } | null>(null);
  const savedThisSessionRef = useRef(false);
  useEffect(() => () => window.clearTimeout(autosaveTimerRef.current), []);
  /** The draft writes go to. A ref rather than the state above because
   *  autosave and the save on close run outside a render; the state is
   *  only what the buttons read. */
  const savedDraftRef = useRef<{ id: string; versionNumber: number } | null>(null);
  useEffect(() => {
    if (!open) return;
    const opened = draft ? { id: draft.id, versionNumber: draft.versionNumber } : null;
    savedDraftRef.current = opened;
    setSavedDraft(opened);
  }, [open, draft]);
  /** Documents that ride along unsigned; read by the suggestion pass. */
  const attachmentIdsRef = useRef<Set<string>>(new Set());
  attachmentIdsRef.current = new Set(packageDocs.filter((d) => d.attachment).map((d) => d.documentId));
  /** Every document in the package, by id, for the apply call: the
   *  Addenda Incorporated boxes are ticked from what is in the envelope
   *  (session 4, 2026-11-04), so the screen shows them before the send.
   *  A ref, not a dep, for the same reason the signer list is one below:
   *  the apply pass runs off a callback made before the package settled. */
  const packageDocIdsRef = useRef<string[]>([]);
  packageDocIdsRef.current = packageDocs.map((d) => d.documentId);
  // ── THE PACKAGE IS ONE JOB (2026-09-29, build PR 3) ─────────────────
  //
  // Every document in a package is read when the package opens — the
  // active one first, exactly as before, then the rest one at a time in
  // the background, each parked with its ghosts ready. That is what
  // lets Accept all, Tab and the rail's dots span the whole envelope,
  // and it is why the "you haven't opened this document" stop is gone
  // (Grant's decision 2): there is no document nobody has read.
  //
  // `parkedRef` is a ref, and a ref changing does not re-render. Until
  // now it only changed on a document switch, which changed `documentId`
  // too. A background read changes it with nothing else moving, so this
  // counter is bumped alongside every write to the map and sits in the
  // deps of every memo that reads it.
  const [parkedVersion, setParkedVersion] = useState(0);
  const bumpParked = useCallback(() => setParkedVersion((v) => v + 1), []);
  /** Background reads in flight, by document id. The load effect for a
   *  document being read joins its promise rather than fetching the
   *  same file twice, and the reader never overwrites a document that
   *  became active while it was being read. */
  const inFlightRef = useRef<Map<string, Promise<ParkedDocumentState>>>(new Map());
  /** Which document the background reader is on, for the rail. */
  const [readingDocId, setReadingDocId] = useState<string | null>(null);
  /** Documents the background reader could not get or draw, with the
   *  reason. Opening one retries through the normal load path, which
   *  shows the full error and its "Try again". */
  const [readFailures, setReadFailures] = useState<Record<string, string>>({});
  const readFailuresRef = useRef(readFailures);
  readFailuresRef.current = readFailures;
  /** The active document's id, readable from inside async work that
   *  outlives the render it started in. */
  const activeDocIdRef = useRef<string>(documentId);
  activeDocIdRef.current = documentId;
  /** Bumped when the screen closes, so a background read that finishes
   *  after that writes nothing into the next package's map. */
  const openGenRef = useRef(0);
  /** A blank to select once its document is on screen — how Tab lands
   *  on the next document's first blank. */
  const [jumpKey, setJumpKey] = useState<string | null>(null);
  /** STEW READS THE OTHER DOCUMENTS (2026-09-30, build PR 4). Once per
   *  open, after every document is read: the blanks still open across
   *  the package are sent with the text of the package's other
   *  uploaded documents, and what comes back lands on each blank as a
   *  `reading` — offered with the document's name, never sure. */
  const packageReadRef = useRef(false);
  const [packageReadNote, setPackageReadNote] = useState<string | null>(null);
  // ── Mapping mode's signers: numbered slots, never real people ──────
  //
  // A saved layout only ever stores ORDINAL positions (see layout-
  // apply.ts's header), so a map taught against "Signer 1" / "Signer 2"
  // is exactly the shape a real send would have taught. Renaming a slot
  // to the word the FORM itself uses ("Owner", "Buyer") is worth doing:
  // matchSignerHint below matches Stew's signerHint against these very
  // labels, so a slot named "Owner" gets the owner's fields assigned
  // automatically, same as a real person named that would.
  const [mappingSlots, setMappingSlots] = useState<MappingSlot[]>(() =>
    mapping
      ? [
          { id: "slot-0", label: "Signer 1" },
          { id: "slot-1", label: "Signer 2" },
        ]
      : [],
  );
  /** True the moment the advisor touches the slots themselves — adds,
   *  renames or removes one. Once that happens, a suggestion re-run must
   *  never overwrite their own naming with a fresh guess from the
   *  document; the role-derived rename below only fires while the
   *  slots are still whatever they started as. */
  const slotsCustomizedRef = useRef(false);
  const candidatesFromSlots = useCallback(
    (slots: MappingSlot[]): PanelSigner[] =>
      slots.map((s, i) => ({
        key: s.id,
        kind: "slot" as const,
        contactId: null,
        isPrimary: i === 0,
        displayName: s.label || `Signer ${i + 1}`,
        email: null,
      })),
    [],
  );
  const mappingCandidates = useMemo<PanelSigner[]>(
    () => candidatesFromSlots(mappingSlots),
    [mappingSlots, candidatesFromSlots],
  );
  // ── Signers who are NOT our contacts (Grant, 2026-09-03) ──────────
  //
  // "A signer who is not our client (a co-op agent, a seller, a title
  // officer) is added by name and email only and never becomes a
  // contact or household in our database."
  //
  // They live HERE, in this screen's own state, and nowhere else until
  // the send writes them onto esign_envelope_signers with an empty
  // contact link. Nothing creates a contact; nothing creates a
  // household. Close the screen without sending and they are simply
  // gone, which is the correct lifetime for a person we have decided
  // not to keep.
  //
  // The key is positional (`guest:<n>`) and matches what the server
  // derives for the same entry, so the fields placed for a guest reach
  // the right signer without either side inventing an id the other
  // can't reproduce.
  // A personnel send opens with its team member already on it (see the
  // personnelSigner prop): the one person the document is for.
  const [guests, setGuests] = useState<{ name: string; email: string }[]>(() =>
    personnelSigner && !mapping ? [{ name: personnelSigner.name, email: personnelSigner.email }] : [],
  );
  const guestSigners = useMemo<PanelSigner[]>(
    () =>
      guests.map((g, i) => ({
        key: guestSignerKey(i),
        kind: "guest" as const,
        contactId: null,
        isPrimary: false,
        displayName: g.name,
        email: g.email,
      })),
    [guests],
  );
  // ── The people already on this record ────────────────────────────
  //
  // The counterparty on a Realty transaction — the co-op agent, the
  // title officer, the escrow officer — is usually ALREADY recorded on
  // the record's parties roster, with a name and an email. Offering
  // them here turns Nate's most common "add a signer" from typing into
  // one click, and it is the same roster (the same endpoint) the
  // completion-routing picker further down this panel already loads.
  //
  // They are added as GUESTS, never as contact-backed signers, even
  // when the party row happens to carry a contact id: a party who is a
  // contact but is not one of THIS record's people would be refused by
  // the send route's record-membership rule, and the point of a guest
  // signer is that no such membership is needed.
  //
  // Fail-soft: no roster means no chips, and typing still works.
  const [parties, setParties] = useState<
    { name: string; email: string; roleLabel: string }[] | null
  >(null);
  useEffect(() => {
    if (!open || mapping || parties !== null) return;
    if (recordKind !== "transaction" && recordKind !== "opportunity") {
      setParties([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res =
          recordKind === "transaction"
            ? await api.listTransactionParties(recordId)
            : await api.listOpportunityParties(recordId);
        if (cancelled) return;
        setParties(
          res.parties
            // No email means nothing to send to, so there is nothing to
            // offer — the chip would open the form half-filled, which
            // is worse than not offering it.
            .filter((pt): pt is typeof pt & { email: string } => Boolean(pt.email))
            .map((pt) => ({ name: pt.name, email: pt.email, roleLabel: pt.role_label })),
        );
      } catch {
        if (!cancelled) setParties([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, mapping, parties, recordKind, recordId]);

  // The inline add-a-signer form. Deliberately closed until asked for:
  // most sends are to the record's own people, and two empty boxes
  // sitting under every signer list would be clutter on the common
  // path.
  const [addingGuest, setAddingGuest] = useState(false);
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestError, setGuestError] = useState<string | null>(null);
  // The household's other adults, added by the server for a Realty
  // offer (offer program session 6). Filled when the casting context
  // arrives; the seat list and the signer list both read the SAME union
  // below, so the wizard and the editor cannot disagree about who is
  // there.
  const [householdPeople, setHouseholdPeople] = useState<ServiceRecordPerson[]>([]);
  const seatCandidates = useMemo(
    () => [
      ...candidates,
      ...householdPeople.filter((h) => !candidates.some((c) => c.contact_id === h.contact_id)),
    ],
    [candidates, householdPeople],
  );
  const recordSigners = useMemo<PanelSigner[]>(
    () =>
      seatCandidates
        .filter((p) => p.email)
        .map((p) => ({
          key: p.contact_id,
          kind: "person" as const,
          contactId: p.contact_id,
          isPrimary: p.is_primary,
          displayName: p.display_name,
          email: p.email,
        })),
    [seatCandidates],
  );
  const eligible = useMemo(
    () => (mapping ? mappingCandidates : [...recordSigners, ...guestSigners]),
    [mapping, mappingCandidates, recordSigners, guestSigners],
  );
  /** Parties on this record who aren't already on the send. Matched by
   *  email, which is the identity the provider itself keys on, so a
   *  party who IS one of the record's people never appears twice. */
  const partySuggestions = useMemo(() => {
    const taken = new Set(
      eligible.map((p) => (p.email ?? "").toLowerCase()).filter((e) => e.length > 0),
    );
    const seen = new Set<string>();
    return (parties ?? []).filter((pt) => {
      const key = pt.email.toLowerCase();
      if (taken.has(key) || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [parties, eligible]);
  // `selected` (declared below) is the one Set every downstream
  // computation reads as "who is this send/map for" — syncing it here
  // rather than threading a second Set through the file keeps that true
  // in mapping mode too.
  useEffect(() => {
    if (mapping) setSelected(new Set(mappingSlots.map((s) => s.id)));
  }, [mapping, mappingSlots]);

  function addMappingSlot() {
    slotsCustomizedRef.current = true;
    setMappingSlots((prev) => {
      if (prev.length >= MAX_MAPPING_SLOTS) return prev;
      return [...prev, { id: crypto.randomUUID(), label: `Signer ${prev.length + 1}` }];
    });
  }
  function renameMappingSlot(id: string, label: string) {
    slotsCustomizedRef.current = true;
    setMappingSlots((prev) => prev.map((s) => (s.id === id ? { ...s, label } : s)));
  }
  /** Point a mapping slot at a catalog seat, or back at a free word.
   *  Picking a seat takes its label too, so the slot on screen reads
   *  the same as the seat a send will cast. */
  function setSlotSeat(id: string, roleKey: string | null) {
    slotsCustomizedRef.current = true;
    const role = mappingCatalog.find((r) => r.key === roleKey);
    setMappingSlots((prev) =>
      prev.map((s) =>
        s.id === id ? { ...s, roleKey: role?.key ?? null, label: role?.label ?? s.label } : s,
      ),
    );
  }
  /** Add a slot already named for a catalog seat and start placing for it,
   *  straight from the toolbar's Placing-for menu. */
  function addSeatSlot(roleKey: string) {
    const role = mappingCatalog.find((r) => r.key === roleKey);
    if (!role) return;
    slotsCustomizedRef.current = true;
    const id = crypto.randomUUID();
    setMappingSlots((prev) =>
      prev.length >= MAX_MAPPING_SLOTS || prev.some((s) => s.roleKey === role.key)
        ? prev
        : [...prev, { id, label: role.label, roleKey: role.key }],
    );
    setFillNow(false);
    setActiveSignerId(id);
  }
  function removeMappingSlot(id: string) {
    slotsCustomizedRef.current = true;
    setMappingSlots((prev) => (prev.length <= 1 ? prev : prev.filter((s) => s.id !== id)));
    // A field pointing at a slot that no longer exists is an orphan —
    // same rule the "signs / gets a copy" switch already follows above.
    setFields((prev) => prev.filter((f) => f.signerKey !== id));
  }
  const [selected, setSelected] = useState<Set<string>>(
    () =>
      new Set([
        ...eligible.filter((p) => p.isPrimary).map((p) => p.key),
        // The personnel send's team member is guest 0 and is ticked.
        ...(personnelSigner && !mapping ? [guestSignerKey(0)] : []),
      ]),
  );
  const [activeSignerId, setActiveSignerId] = useState<string | null>(null);
  /** The same value, readable from inside the suggestion pass.
   *
   *  That pass runs off a useCallback created before the effects that
   *  pick a default signer have run, so reading this through the closure
   *  can hand it `null` — and a proposal with no owner is DROPPED. It is
   *  why Baker's locked boxes disappeared from the mapping screen while
   *  the same boxes merely landed on the wrong person on the send
   *  screen: same missing owner, different value of this variable at the
   *  moment the pass happened to run (2026-09-15). */
  const activeSignerIdRef = useRef<string | null>(activeSignerId);
  activeSignerIdRef.current = activeSignerId;
  /** Everyone on this send, IN PANEL ORDER — which is the order the
   *  sender reads top to bottom, the signing order when one is
   *  enforced, and the ordinal a saved layout is taught in and applied
   *  from. Deliberately not `[...selected]`: a Set iterates in
   *  insertion order, so unchecking and re-checking a signer would
   *  quietly move them to the end and shift a saved layout's fields
   *  onto the wrong person. */
  const selectedSignerKeys = useMemo(
    () => eligible.filter((p) => selected.has(p.key)).map((p) => p.key),
    [eligible, selected],
  );
  /** The same list, readable from inside the suggestion pass.
   *
   *  A saved layout addresses its fields by ORDINAL, and the pass turns
   *  each ordinal back into a signer by indexing this list. It runs off
   *  a useCallback that deliberately does not re-create on every change
   *  of selection — re-running it would re-propose the whole document —
   *  so reading the array through the closure handed it whatever the
   *  selection was at MOUNT: one signer, before the effects that fill it
   *  in have run. Every remembered field belonging to signer two and
   *  beyond then resolved to `undefined` and was dropped on the floor.
   *  That is half of Baker's vanishing boxes (2026-09-14); the other
   *  half was the server truncating to the slots it was sent. */
  const selectedSignerKeysRef = useRef<string[]>(selectedSignerKeys);
  selectedSignerKeysRef.current = selectedSignerKeys;
  /** The contact behind a signer key, or null for a guest — somebody
   *  who deliberately isn't one of our contacts. */
  const contactIdForSignerKey = useCallback(
    (key: string): string | null => eligible.find((p) => p.key === key)?.contactId ?? null,
    [eligible],
  );
  /** Every selected signer we hold a contact record for. The preview
   *  needs one set of contact values per person, so a Borrower box and a
   *  Co-Borrower box can show two different names. */
  const selectedSignerContactIds = useMemo(
    () =>
      selectedSignerKeys
        .map((k) => contactIdForSignerKey(k))
        .filter((v): v is string => v !== null),
    [selectedSignerKeys, contactIdForSignerKey],
  );

  const [placingType, setPlacingType] = useState<EsignFieldType | null>(null);
  /** Armed for the preparer's own box rather than a signer's (Baker,
   *  2026-09-14). Kept separate from placingType because it changes WHO
   *  the next click places for, not WHAT it places. */
  const [placingLocked, setPlacingLocked] = useState(false);
  // Toolbar program PR 4a: the armed locked box draws a line, not text.
  // Only meaningful with placingLocked; cleared with it (see the effect).
  const [placingStrike, setPlacingStrike] = useState(false);
  useEffect(() => {
    if (!placingLocked && placingStrike) setPlacingStrike(false);
  }, [placingLocked, placingStrike]);
  // "What fills this box?" (2026-09-02) — merging OS data into a blank
  // used to mean placing a plain Text box and only THEN discovering,
  // down in the properties panel, that it could be bound to a record
  // field. Choosing "Record field" up front arms placement of a text
  // field ALREADY bound: one motion instead of two, and the record
  // catalog is right there next to Signature / Initials / Date signed
  // rather than a secondary step nobody stumbles onto.
  const [pendingMergeToken, setPendingMergeToken] = useState<string | null>(null);
  const [recordFieldPickerOpen, setRecordFieldPickerOpen] = useState(false);
  const [fields, setFields] = useState<PrepField[]>([]);

  // ── THE PAGE IS THE WORKSPACE (2026-09-29) ──────────────────────────
  // Zoom, the folded document rail and the collapsible panel. All three
  // are how THIS device shows the page, so they live in the browser and
  // never on the account (the localStorage rule's device-dependent
  // case). "fit" means "as wide as the document area allows", recomputed
  // whenever that area changes size.
  const [zoom, setZoom] = useState<number | "fit">(() => {
    try {
      const raw = localStorage.getItem("esign-prep-zoom");
      if (raw === "fit" || raw === null) return "fit";
      const n = Number(raw);
      return Number.isFinite(n) && n > 0 ? n : "fit";
    } catch {
      return "fit";
    }
  });
  const [docAreaWidth, setDocAreaWidth] = useState(0);
  const docAreaRef = useRef<HTMLDivElement | null>(null);
  const [railExpanded, setRailExpanded] = useState(() => {
    try {
      return localStorage.getItem("esign-prep-rail") === "open";
    } catch {
      return false;
    }
  });
  const [panelCollapsed, setPanelCollapsed] = useState(() => {
    try {
      return localStorage.getItem("esign-prep-panel") === "closed";
    } catch {
      return false;
    }
  });
  function chooseZoom(next: number | "fit") {
    setZoom(next);
    try {
      localStorage.setItem("esign-prep-zoom", String(next));
    } catch {
      /* a private window; the choice lives for this open only */
    }
  }
  function toggleRail() {
    setRailExpanded((v) => {
      try {
        localStorage.setItem("esign-prep-rail", v ? "closed" : "open");
      } catch {
        /* see chooseZoom */
      }
      return !v;
    });
  }
  // "Fill now (you)" in the Placing for menu: Text and Checkbox place the
  // sender's own locked boxes. Reset when a signer is picked.
  const [fillNow, setFillNow] = useState(false);
  function togglePanel() {
    setPanelCollapsed((v) => {
      try {
        localStorage.setItem("esign-prep-panel", v ? "open" : "closed");
      } catch {
        /* see chooseZoom */
      }
      return !v;
    });
  }
  // Multi-select (Joey, 2026-09-18: unchecking "Required" on a whole
  // disclosure form meant doing it box by box). `selectedKeys` is the
  // one source of truth everywhere on this screen; `setSelectedKey`
  // below is kept as a same-named wrapper so every EXISTING call site
  // ("click this field", "click empty space to clear") needs no change
  // at all — it still means "select exactly this one, or none." Shift/
  // Cmd-click (toggleFieldSelection) and dragging a selection box
  // (handleOverlayPointerDown, below) are the two NEW ways to build a
  // selection of more than one.
  const [selectedKeys, setSelectedKeysState] = useState<Set<string>>(new Set());
  const setSelectedKey = useCallback((key: string | null) => {
    setSelectedKeysState(key ? new Set([key]) : new Set());
  }, []);
  const toggleFieldSelection = useCallback((key: string) => {
    setSelectedKeysState((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  const [message, setMessage] = useState("");
  // Completion routing (Nate, 2026-08-26): who receives the EXECUTED
  // copy once everyone signs. Recorded with the send; delivered by the
  // completion webhook after the sealed copy files.
  const [forwardTo, setForwardTo] = useState<ForwardRecipient[]>([]);
  // The envelope's clock (2026-08-24). Reminders default ON, because the
  // gap this closes is that nothing chased an unsigned document at all —
  // an opt-in would have left most sends exactly as silent as before,
  // and "the system does its own job" is the Minimum Viable Typing rule.
  // Every-3-days-up-to-3 is a nudge, not a dunning loop, and the footer
  // states it in plain words so it is never a surprise.
  //
  // Where the dials START is the user's own choice as of 2026-08-26
  // (E-sign → Settings → "Sending defaults"); the registry baselines
  // are the exact defaults that were hardcoded here before, so a user
  // who never opens that page sees no change. Read once at mount (lazy
  // useState): the settings blob loads at app boot, long before this
  // screen opens, and a mid-prep settings edit should not yank dials
  // the sender may have already adjusted for THIS document.
  const [defaultReminder] = useUserSetting(ESIGN_DEFAULT_REMINDER);
  const [defaultReminderMax] = useUserSetting(ESIGN_DEFAULT_REMINDER_MAX);
  const [defaultExpiry] = useUserSetting(ESIGN_DEFAULT_EXPIRY);
  // Typed, not picked (Nate, 2026-09-29: "let him type it"): a number and
  // a unit, as short as one hour, and how many times; 0 times = never.
  // The account default (days, or "off") seeds it.
  const [remindAmount, setRemindAmount] = useState<string>(() =>
    defaultReminder === "off" ? "2" : String(Number(defaultReminder)),
  );
  const [remindUnit, setRemindUnit] = useState<ReminderUnit>("days");
  const [remindMaxText, setRemindMaxText] = useState<string>(() =>
    defaultReminder === "off" ? "0" : String(Number(defaultReminderMax)),
  );
  const remindEveryMinutes = reminderMinutes(remindAmount, remindUnit, remindMaxText);
  const remindMax = Math.min(ESIGN_UI_MAX_REMINDERS, Math.max(0, Math.floor(Number(remindMaxText) || 0)));
  // Expiry defaults OFF (baseline). Most documents do not become invalid
  // on a date, and a wrong deadline kills a contract that was merely slow.
  const [expiryDays, setExpiryDays] = useState<number | null>(() =>
    defaultExpiry === "none" ? null : Number(defaultExpiry),
  );
  // Signing order and copy-only recipients (2026-08-24). Both off /
  // empty by default: most documents genuinely don't care who signs
  // first, and everyone selected is a signer unless said otherwise.
  const [enforceOrder, setEnforceOrder] = useState(false);
  const [copyOnly, setCopyOnly] = useState<Set<string>>(new Set());
  // Step 2, "Signers & order" (PR 8d): whether that screen is showing,
  // and each signer's number when a signing order is set. People sharing
  // a number sign together (shared/esign/signing-order.ts). A signer with
  // no number here signs in list order, which is what the field screen's
  // own "one at a time" box has always meant.
  const [signersStep, setSignersStep] = useState(false);
  const [orderByKey, setOrderByKey] = useState<Record<string, number>>({});
  useEffect(() => {
    if (open) setSignersStep(startOnSigners && !mapping);
  }, [open, startOnSigners, mapping]);
  // Review & send (Documents tab PR 8b, Nate's "Before it goes"). The
  // window is only a view: the subject and video live here, beside the
  // note, so "Back to editing" loses nothing. A null subject means "the
  // default", recomputed as the documents and address change.
  const [reviewOpen, setReviewOpen] = useState(false);
  const [subjectDraft, setSubjectDraft] = useState<string | null>(null);
  const [signingVideo, setSigningVideo] = useState<ReviewVideo | null>(null);
  // ── The email's wording (PR 8f) ────────────────────────────────────
  // This send's own lines (null: the library's), the library's lines for
  // the preview, and the sender's two "remember" choices.
  const [emailBody, setEmailBody] = useState<string | null>(null);
  const [standardBody, setStandardBody] = useState<string | null>(null);
  const [saveWordingDefault, setSaveWordingDefault] = useState(false);
  const [rememberVideoOn, setRememberVideoOn] = useState(false);
  const [wordingDefaults, setWordingDefaults] = useUserSetting(ESIGN_WORDING_DEFAULTS);
  const [rememberedVideos, setRememberedVideos] = useUserSetting(ESIGN_REMEMBERED_VIDEOS);
  const settingsLoaded = useUserSettingsLoaded();
  const prefilledWordingRef = useRef(false);
  const [pages, setPages] = useState<RenderedPdfPage[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** True when the load TIMED OUT rather than failed — the one case
   *  where retrying in this same tab is known not to help. */
  const [loadTimedOut, setLoadTimedOut] = useState(false);
  // Bumped by "Try again" — a load that failed or timed out must be
  // retryable without closing and reopening the whole screen.
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [sending, setSending] = useState(false);
  /** Read by autosave's timer, which can fire between a send starting
   *  and the render that follows. */
  const sendingRef = useRef(false);
  /** "Save map" is its own in-flight state: it must never look like a
   *  send is happening, because the whole point is that nothing goes
   *  out. */
  const [savingLayout, setSavingLayout] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<EsignSendResponse | null>(null);
  // null = unknown (check in flight / failed) — the footnote only claims
  // "sent from your Outlook" once that's actually true; a tester hit the
  // promise with no Outlook connected (2026-08-22).
  const [outlookConnected, setOutlookConnected] = useState<boolean | null>(null);
  // Almost everyone sends this from their own Outlook. The one named
  // exception (sender-identity.ts) routes THEIR signing email through
  // the Insurance company inbox instead — a client email leaving as her
  // personally is exactly what got reported as a problem. The footnote
  // below must say the true thing to whoever is looking at this screen,
  // not the default thing.
  const { user } = useSession();
  const senderIdentity = resolveEsignSenderIdentity(user?.email ?? null);
  // undefined = not checked yet; null = checked, none saved.
  const [mySignature, setMySignature] = useState<
    { imagePngBase64: string; naturalWidth: number; naturalHeight: number } | null | undefined
  >(undefined);
  // The record's own requirements for "what kind of document is this?"
  // — shared across every document in the package (undefined = not
  // loaded yet); chosenTypeByDoc (above, keyed per document) holds the
  // sender's actual answers.
  const [typeOptions, setTypeOptions] = useState<
    Array<{ id: string; name: string; purpose: string | null }> | undefined
  >(undefined);

  // ── Which company, on the standalone lanes ────────────────────────
  //
  // A household and a contact belong to no single company, so nothing
  // can be read off the record — and the company decides which saved
  // layouts are offered and whether Realty's commission-disclosure
  // merge fields exist. The caller supplies it when it already knows
  // (the E-Sign page asks up front); when it doesn't, this screen asks,
  // because this is the moment a human is looking at the document.
  //
  // Someone who works for one company is never asked: the single
  // option is selected silently.
  const needsCompany = !kindCarriesItsOwnCompany(recordKind) && !companySlug;
  const [companyOptions, setCompanyOptions] = useState<
    { slug: string; label: string }[] | null
  >(null);
  const [chosenCompany, setChosenCompany] = useState<string>("");
  const effectiveCompany = companySlug ?? (chosenCompany || null);
  // Everything else a draft kept (PR 8c), put back once per opened draft.
  // Its fields go back through runSuggestions as each document is read;
  // its casting through the cast effect below, which otherwise casts anew.
  const restoredDraftIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open || !draft || !draftState || restoredDraftIdRef.current === draft.id) return;
    restoredDraftIdRef.current = draft.id;
    const st = draftState;
    // Saved from step 1: the documents are the draft, and the screen
    // opens on them the way it opens on any new package.
    if (st.startedOnly) return;
    if (st.companySlug) setChosenCompany(st.companySlug);
    setGuests(st.guests);
    setSelected(new Set(st.selected));
    setCopyOnly(new Set(st.copyOnly));
    setEnforceOrder(st.enforceOrder);
    setOrderByKey(st.orderByKey);
    setRemindAmount(st.remind.amount);
    setRemindUnit(st.remind.unit);
    setRemindMaxText(st.remind.times);
    setExpiryDays(st.expiryDays);
    setMessage(st.message);
    setSubjectDraft(st.subject);
    setSigningVideo(st.video);
    setEmailBody(st.emailBody);
    setForwardTo(st.forwardTo);
    setChosenTypeByDoc(st.chosenTypeByDoc);
    setConfirmedPagesByDoc(st.confirmedPagesByDoc);
  }, [open, draft, draftState]);
  // The cast effect runs once per company and side for as long as this
  // screen stays mounted. When it has already run (the screen was open on
  // this record before), the draft's casting is put back here instead.
  useEffect(() => {
    if (!open || !draftCastingRef.current || castedForRef.current === null) return;
    setCasting(draftCastingRef.current);
    draftCastingRef.current = null;
  });
  /** The company this map is being taught for, in the words the team
   *  uses. The mapping banner named one company for everybody until
   *  2026-09-14 — Baker mapped a Mortgage disclosure and the screen told
   *  him it was for Realty, which is a small thing that makes a screen
   *  hard to trust while you are already unsure it saved. Falls back to
   *  naming no company rather than to a guess. */
  const mappingCompanyLabel = effectiveCompany
    ? (CLIENT_COMPANY_LABELS[effectiveCompany] ?? null)
    : null;
  // Field suggestions (Grant, 2026-08-22 — "AI reads the documents and
  // suggests fields"). `suggesting` covers the whole ladder, including
  // Stew's round trip; `suggestNote` carries the plain-English line
  // shown when a rung couldn't run (a scan, no budget) — never an error
  // wall, because manual placement works regardless.
  // The document's content fingerprint, computed once from the text
  // pdf.js already extracted. Used to FIND a saved layout on open and
  // to TEACH one on send. Null for a scan, which does neither.
  const fingerprintRef = useRef<string | null>(null);
  // The rendered document, kept so the suggestion ladder can run AGAIN
  // when the standalone lanes learn their company (see the effect below
  // runSuggestions) without re-fetching or re-rendering anything.
  const renderedRef = useRef<RenderedPdf | null>(null);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestNote, setSuggestNote] = useState<string | null>(null);
  // How many places the ladder found but left alone because the page
  // already has an answer written there (Nate, 2026-09-25: a lender's
  // finished pre-approval came back with 100 suggested boxes). Said out
  // loud in the panel, so an empty-looking suggestion list on a finished
  // form reads as deliberate rather than as the OS missing things.
  const [writtenOnCount, setWrittenOnCount] = useState(0);

  // Every blank the FORM ITSELF draws — the ruled lines and tick boxes
  // an AAR contract marks its fields with (shared/esign/vector-blanks.ts).
  //
  // Kept even for the ones that became proposals, because the honest
  // question an advisor has is "is there anything left on this form?",
  // and answering it needs the whole population, not the leftovers of
  // one rung.
  const [detectedBlanks, setDetectedBlanks] = useState<DetectedBlank[]>([]);
  /** The blank whose "what goes here?" menu is open, if any. */
  const [fillingBlank, setFillingBlank] = useState<DetectedBlank | null>(null);
  /** Whether that menu is showing the record-field list. Its own flag,
   *  not the palette's: opening the picker from a blank must not arm the
   *  click-the-page placement mode the palette's picker arms. */
  const [blankPickerOpen, setBlankPickerOpen] = useState(false);
  const blankRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  /** What this company has been taught its captions mean — arrives with
   *  the merge fields (see the ensureMergeOptions fetch below). A ref
   *  because the suggestion pass reads it inside a callback and must see
   *  the value that is loaded NOW, not the one from its last render. */
  const learnedCaptionsRef = useRef<Map<string, LearnedCaption>>(new Map());
  // The saved layout these proposals came from, when they did. Drives
  // the panel copy: "we remembered this" and "we read it" are different
  // claims and must not read alike.
  const [appliedLayout, setAppliedLayout] = useState<string | null>(null);
  /** Who saved that map, and how many sends it has served — see
   *  AppliedLayoutInfo. Null whenever appliedLayout is null; set
   *  alongside it, never independently. */
  const [appliedLayoutInfo, setAppliedLayoutInfo] = useState<AppliedLayoutInfo | null>(null);
  /** Whether the applied map's boxes were MEASURED off the real page or
   *  reconstructed from the standard form.
   *
   *  Seven Realty forms carry the flag and five are forms an agent sends:
   *  the counter offer, the H.O.A. addendum, the well and septic addenda,
   *  and the Lead-Based Paint Disclosure. Until 2026-09-18 the only place
   *  that said so was the forms library page, which the agent drafting a
   *  contract never opens. A box on a contract is a promise about where
   *  it lands, and the moment to mention it is the moment before it
   *  goes. */
  const [layoutPositionsUnchecked, setLayoutPositionsUnchecked] = useState(false);
  const [formRecognition, setFormRecognition] = useState<FormRecognitionNote | null>(null);
  // Merge fields (Grant, 2026-08-22) — what a text field can be bound
  // to, WITH this record's live values. Loaded ONCE for the record and
  // shared by the picker and the page overlay, so a bound field can show
  // what it will actually print. Lazy: an advisor who never binds
  // anything never pays for the request.
  const [mergeOptions, setMergeOptions] = useState<MergeFieldOption[] | null>(null);
  const [mergeLoadError, setMergeLoadError] = useState<string | null>(null);
  // Which (record, company, first-signer) combination the merge values
  // were fetched FOR — not a boolean. Contact tokens resolve against the
  // first selected signer, so changing who signs first must re-fetch:
  // the reviewed values have to be the values the send will print
  // (2026-08-30 audit, finding C7).
  const mergeLoadRef = useRef<string | null>(null);
  /** The in-flight (or settled) fetch for the key above, so the
   *  suggestion pass can await the same one the picker uses. */
  const mergeLoadPromiseRef = useRef<Promise<MergeFieldOption[]> | null>(null);
  /** The latest options, readable without making runSuggestions depend
   *  on them — same ref pattern as renderedRef / parkedRef. */
  const mergeOptionsRef = useRef<MergeFieldOption[] | null>(null);
  /** Contact-token values per signer contact id (Baker, 2026-09-14). A
   *  contact token prints the person whose box it is, so the page needs
   *  one of these per signer — a single map is what showed Tirsa's name
   *  in Brian's box on screen, before the send was ever pressed. */
  const [mergeValuesByContact, setMergeValuesByContact] = useState<
    Record<string, Record<string, string | null>>
  >({});
  // Which field's picker is open, by key. One at a time.
  const [pickerForKey, setPickerForKey] = useState<string | null>(null);
  const [placingStamp, setPlacingStamp] = useState(false);
  const [stamps, setStamps] = useState<PrepStamp[]>([]);
  const [selectedStampKey, setSelectedStampKey] = useState<string | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const fieldDragRef = useRef<FieldDragState | null>(null);
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  // The page a thumbnail last jumped to (PR 9b), outlined in the rail.
  const [thumbPage, setThumbPage] = useState(1);
  const jumpToPage = useCallback((pageNumber: number) => {
    setThumbPage(pageNumber);
    pageRefs.current.get(pageNumber)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);
  useEffect(() => setThumbPage(1), [documentId]);
  // ── Marquee selection (Joey, 2026-09-18) ────────────────────────
  // "Drag a box around a group of fields" — the ref carries the live
  // rectangle so pointerup can read its FINAL value without a stale
  // closure over React state; the state copy is for rendering the
  // rectangle itself while the drag is in progress.
  const marqueeDragRef = useRef<{
    pageNumber: number;
    originX: number;
    originY: number;
    additive: boolean;
    rect: { x: number; y: number; width: number; height: number } | null;
  } | null>(null);
  const [marqueeRect, setMarqueeRect] = useState<{
    pageNumber: number;
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);

  // ── Signing roles (2026-09-15) ──────────────────────────────────
  //
  // Nate, 2026-09-08: "The OS already knows who the roles are in a
  // transaction for real estate, but the e-sign portion does not take
  // those roles from the Processing or Opportunity record." A document
  // now opens already CAST: every seat the company's catalog names
  // (Buyer 1 / Buyer 2 / Buyer's Agent on a Realty offer; Borrower /
  // Co-borrower / Loan Officer on a Mortgage disclosure; Grantor /
  // Trustee on a Planning case) is filled from the record's own
  // people, its owner, its Parties list and its Contract card, with
  // three honest outcomes (sure / ask / nobody) and never a silent
  // guess. Built for all five companies; the catalog decides the seats.
  //
  // The seat list drives the SAME `selected` set and the same guest
  // list everything below already reads, so a cast person is an
  // ordinary signer to every other part of this screen: the fields,
  // the colours, the saved layouts, the send. A seat that STAMPS (the
  // advisor's own signature) is not a signer row — the stamps tool
  // below places it, and nothing is emailed to the advisor. A NAME-ONLY
  // seat (the seller on a buy-side copy, the escrow officer) is never a
  // signer either; it is recorded on the envelope's casting so the OS
  // can say who was named, and its lines print rather than sign.
  //
  // Fail-soft: if the context can't be loaded there is no roles panel,
  // and the plain signer list below works exactly as it always has.
  const [castCtx, setCastCtx] = useState<RolesContext | "unavailable" | null>(null);
  /** The seats the package's forms have signer boxes for, or null when any
   *  form's map can't say (2026-10-27; fitCastingToForms). Loaded with the
   *  casting context so the cast below never runs without it. */
  const formSeatsRef = useRef<ReadonlySet<string> | null>(null);
  useEffect(() => {
    if (!open || mapping || castCtx !== null) return;
    let cancelled = false;
    void (async () => {
      try {
        const [res, seats] = await Promise.all([
          apiV11.listEsignRecordSigners(recordKind, recordId, effectiveCompany),
          // Fail-soft: no answer means the record is cast as before.
          apiV11
            .getEsignFormSeats(
              recordKind,
              recordId,
              packageDocs.map((d) => d.documentId),
              effectiveCompany,
            )
            .catch(() => null),
        ]);
        if (cancelled) return;
        formSeatsRef.current = seats ? seatsAPackageSigns(seats.documents) : null;
        setHouseholdPeople(res.signers.filter((p) => p.from_household));
        setCastCtx({
          company: res.company_slug,
          people: res.signers.map((p) => ({
            contactId: p.contact_id,
            displayName: p.display_name,
            email: p.email,
            isPrimary: p.is_primary,
          })),
          ...res.casting,
          // A personnel send's team member fills the Team Member seat the
          // way a Parties row fills an escrow seat: offered as a party,
          // cast onto the guest already on the send (matched by email).
          parties: personnelSigner
            ? [
                ...res.casting.parties,
                {
                  key: "personnel:team_member",
                  partyRole: "team_member",
                  displayName: personnelSigner.name,
                  email: personnelSigner.email,
                  contactId: null,
                },
              ]
            : res.casting.parties,
        });
      } catch {
        if (!cancelled) setCastCtx("unavailable");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, mapping, castCtx, recordKind, recordId, effectiveCompany, personnelSigner]);
  const rolesCtx = castCtx && castCtx !== "unavailable" ? castCtx : null;
  /** The same context, readable from inside the suggestion pass, which
   *  runs off a callback made before the casting arrived. */
  const rolesCtxRef = useRef(rolesCtx);
  rolesCtxRef.current = rolesCtx;
  /** THE SEATS THIS FORM IS MAPPED IN. Mapping mode has no record and
   *  no casting — it has a company and, on Realty, a side the catalog
   *  row already knows. Offering that company's real seats is what lets
   *  a blank form be taught once in Buyers and Sellers rather than in
   *  Signer 1 and Signer 2. */
  const mappingCatalog = useMemo(() => {
    if (!mapping) return [];
    const side: RoleSide =
      mapping.appliesToSide === "listing"
        ? "sell"
        : mapping.appliesToSide === "purchase"
          ? "buy"
          : "na";
    // Every seat the company has, because a form's blocks are not
    // limited to the side it is filed under — a seller's form can carry
    // the buyer's acknowledgement. The declared side still decides
    // which seats the slots START on, just below.
    return rolesForForm(companySlug, side);
  }, [mapping, companySlug]);
  // A standalone send learns its company from the picker above, so the
  // seat list follows that choice rather than the (null) record company.
  const rolesCompany = rolesCtx ? (rolesCtx.company ?? effectiveCompany) : null;
  const roles = useMemo(
    () => (rolesCtx ? rolesFor(rolesCompany, rolesCtx.side) : []),
    [rolesCtx, rolesCompany],
  );
  /** The seats, readable from inside the suggestion pass. */
  const rolesRef = useRef(roles);
  rolesRef.current = roles;
  /** The same list, readable from inside the suggestion pass, which
   *  runs off a callback created before it settles. */
  /** The same list, readable from inside the suggestion pass. */
  const mappingCatalogRef = useRef(mappingCatalog);
  mappingCatalogRef.current = mappingCatalog;
  // THE SLOTS OPEN AS THIS COMPANY'S FIRST TWO SIGNING SEATS.
  //
  // A purchase contract opens on Buyer 1 and Buyer 2, a listing on
  // Seller 1 and Seller 2, a mortgage form on Borrower and Co-borrower
  // — which is what the form's own signature lines say, so the mapper
  // is choosing between right answers rather than inventing names.
  //
  // Runs ONCE, and only while the slots are still untouched: a saved
  // map's own seats arrive later and win (see the adoption block in the
  // suggestion pass), and so does anything the mapper picks.
  const seededSlotsRef = useRef(false);
  useEffect(() => {
    if (!mapping || seededSlotsRef.current || mappingCatalog.length === 0) return;
    if (slotsCustomizedRef.current) return;
    // Seeded from the form's OWN side: a listing contract opens on
    // Seller 1 and Seller 2 even though the picker offers every seat.
    const side: RoleSide =
      mapping.appliesToSide === "listing"
        ? "sell"
        : mapping.appliesToSide === "purchase"
          ? "buy"
          : "na";
    const signing = rolesFor(companySlug, side).filter((r) => r.kind === "signs").slice(0, 2);
    if (signing.length === 0) return;
    seededSlotsRef.current = true;
    setMappingSlots((prev) =>
      prev.some((sl) => sl.roleKey)
        ? prev
        : signing.map((r, i) => ({ id: prev[i]?.id ?? `slot-${i}`, label: r.label, roleKey: r.key })),
    );
  }, [mapping, mappingCatalog]);
  const [casting, setCasting] = useState<Casting | null>(null);
  const castingRef = useRef<Casting | null>(casting);
  castingRef.current = casting;
  /** The people a seat can be filled from: everyone on the record, with
   *  or without an email. A seat naming a person with no email on file
   *  says so, rather than pretending nobody is there. */
  const castPeople = useMemo(() => {
    const own = seatCandidates.map((p) => ({
      contactId: p.contact_id,
      displayName: p.display_name,
      email: p.email,
      isPrimary: p.is_primary,
    }));
    return own.length > 0 || !rolesCtx ? own : rolesCtx.people;
  }, [seatCandidates, rolesCtx]);
  /** The signer key a seat's person is filed under, or null when the
   *  seat sends nothing (empty, name-only, or the advisor's own stamp). */
  const seatSignerKey = useCallback(
    (s: CastSeat): string | null => {
      if (!s.person || s.role.kind === "name_only" || s.role.kind === "stamp") return null;
      if (s.person.kind === "person") return s.person.contactId;
      if (s.person.kind === "party") {
        if (isGuestSignerKey(s.person.key)) return s.person.key;
        const email = s.person.email?.toLowerCase();
        return email ? (eligible.find((p) => p.email?.toLowerCase() === email)?.key ?? null) : null;
      }
      return null;
    },
    [eligible],
  );
  const seatSignerKeyRef = useRef(seatSignerKey);
  seatSignerKeyRef.current = seatSignerKey;
  // Cast ONCE per company-and-side, when the context arrives. After
  // that every change is an explicit click (pick / leave off / swap),
  // never a re-cast that could undo what someone just chose.
  const castedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!rolesCtx || mapping || roles.length === 0) return;
    const stamp = `${rolesCompany ?? ""}:${rolesCtx.side}`;
    if (castedForRef.current === stamp) return;
    castedForRef.current = stamp;
    // A draft brings its own casting back, exactly as it was left: who
    // was picked for a seat that asked, who was left off. Its signers and
    // guests are restored with the rest of the draft, so nothing is added.
    if (draftCastingRef.current) {
      setCasting(draftCastingRef.current);
      draftCastingRef.current = null;
      return;
    }
    // Buyer / Seller 3 and 4 only join the send when the record has that
    // many people (seatAppliesToRecord) — every two-buyer send stays as
    // it was.
    // THE FORMS DECIDE WHO SIGNS (Grant, 2026-10-27): a seat the package's
    // forms have no signer line for is left off, so the Commission
    // Disbursement Authorization (the managing broker's line only) no
    // longer opens with the sellers on it. Unknown forms change nothing.
    const formSeats = formSeatsRef.current;
    const next = fitCastingToForms(
      castRoles(roles.filter((r) => seatAppliesToRecord(r, castPeople.length)), {
        people: castPeople,
        parties: rolesCtx.parties,
        owner: rolesCtx.owner,
        otherPartyName: rolesCtx.otherPartyName,
        firmRoles: rolesCtx.firmRoles,
      }),
      formSeats,
    );
    setCasting(next);
    // Put the cast people on the send: the record's own people by
    // contact id, a party as a guest (name and email, no contact
    // created — the same rule the "Also on this record" chips follow).
    const newGuests: { name: string; email: string }[] = [];
    const keys: string[] = [];
    for (const seat of next) {
      const person = seat.person;
      if (seat.role.kind !== "signs" || !person || !person.email) continue;
      // The firm's standing seat holder (the managing broker) is offered,
      // never put on the send for you: a signer with no fields blocks the
      // send, and most forms carry no broker line. Step 2 adds them in
      // one click. When the forms' maps say they DO have a broker line
      // (2026-10-27), the broker joins on their own.
      if (seat.role.fillRule.kind === "firm_role" && !firmSeatJoinsSend(seat.role.key, formSeats)) continue;
      const email = person.email.toLowerCase();
      if (person.kind === "person" && person.contactId) {
        keys.push(person.contactId);
      } else if (person.kind === "party") {
        const existing = [...recordSigners, ...guestSigners].find(
          (p) => p.email?.toLowerCase() === email,
        );
        if (existing) keys.push(existing.key);
        else if (!newGuests.some((g) => g.email.toLowerCase() === email)) {
          keys.push(guestSignerKey(guests.length + newGuests.length));
          newGuests.push({ name: person.displayName, email: person.email });
        }
      }
    }
    if (newGuests.length > 0) setGuests((prev) => [...prev, ...newGuests]);
    if (keys.length > 0) {
      setSelected((prev) => {
        const n = new Set(prev);
        keys.forEach((k) => n.add(k));
        return n;
      });
    }
  }, [rolesCtx, mapping, roles, rolesCompany, castPeople, recordSigners, guestSigners, guests.length]);
  // ── ONLY WHAT YOU KNOW (2026-09-15) ─────────────────────────────
  //
  // A reviewed contract has hundreds of blanks and about fourteen
  // questions. The fill rules recorded on each blank are what tell them
  // apart, so this is the short list: what the sender must answer, the
  // firm's standard answers already filled in, and nothing the OS can
  // work out for itself.
  //
  // THE ACTIVE DOCUMENT ONLY, deliberately for now. A package's other
  // documents keep their fields in a ref rather than in React state, so
  // a list spanning all of them would need that lifted first — a real
  // change to a screen this size, and not one to make on the way past.
  // The panel says which document it is talking about when there is
  // more than one, rather than implying it covered them all.
  const askable = useMemo(
    () =>
      fields
        .filter((f) => f.fillRule)
        .map((f) => ({
          key: f.key,
          documentId: activeDoc.documentId,
          pageNumber: f.pageNumber,
          fieldType: f.fieldType,
          y: f.y,
          fillRule: f.fillRule ?? null,
          caption: f.caption ?? null,
          value: f.value ?? null,
          // What decides whether a `record` blank's promise is kept.
          // Without it the list counted every one as filled, including
          // the 203 that carry no binding and print nothing (2026-09-18).
          mergeToken: f.mergeToken ?? null,
        })),
    [fields, activeDoc.documentId],
  );
  const shortForm = useMemo(
    () => onlyWhatYouKnow(askable, new Map([[activeDoc.documentId, activeDoc.filename]])),
    [askable, activeDoc.documentId, activeDoc.filename],
  );
  /** Which questions belong to a box spanning several lines, so the
   *  answer box has several lines too. */
  const multilineQuestionKeys = useMemo(
    () => new Set(fields.filter((f) => isMultilineField(f)).map((f) => f.key)),
    [fields],
  );
  /** Which questions the sender has typed into. The firm's standard
   *  answers arrive already filled, and the screen has to keep saying
   *  which of the two they are looking at: a proposal it made, or their
   *  own word. Touching one settles it, so the row stops reading as a
   *  default from that moment on. */
  const [answeredByHand, setAnsweredByHand] = useState<ReadonlySet<string>>(() => new Set());
  /** Answering writes straight onto the field, so the page and the list
   *  can never disagree about what will print. */
  function answerQuestion(fieldKey: string, answer: string) {
    setAnsweredByHand((prev) => (prev.has(fieldKey) ? prev : new Set(prev).add(fieldKey)));
    // Typing is one of the three things that make a ghost real
    // (ghost-offers.ts), so an answered blank leaves the ghost state
    // here — before 2026-09-29 it stayed a proposal, which under the
    // new rule would have meant "answered, and then not sent".
    setFields((prev) =>
      prev.map((f) =>
        f.key === fieldKey ? taken({ ...f, value: answer === "" ? null : answer }) : f,
      ),
    );
  }

  /** THE CASTING, in the shape the layout code needs: which signer key
   *  holds each seat, plus the seat's label for the message a sender
   *  reads when a seat is empty. Sent with every suggest, every save and
   *  every send, so a map taught in seats lands in seats. Empty until
   *  the record is cast, which keeps the positional path. */
  /** The seats the person at this keyboard fills with their own hand.
   *
   *  Decided HERE and nowhere else, because this is the only place that
   *  knows both who is signed in and who the casting put in each seat.
   *  The rule itself is in shared and tested there — the broker's
   *  initials and a second agent's signature are stamp seats too, and
   *  they belong to somebody who is not in this browser. */
  const myStampSeats = useMemo(
    () => seatsTheSenderStamps(casting ?? [], user?.id ?? null, Boolean(mySignature)),
    [casting, user?.id, mySignature],
  );
  const seatPayload = useMemo(
    () =>
      (casting ?? [])
        .filter((seat) => seat.role.kind !== "name_only")
        .map((seat) => ({
          roleKey: seat.role.key,
          signerKey: seatSignerKey(seat),
          label: seat.role.label,
          ...(myStampSeats.has(seat.role.key) ? { stampsAsSender: true } : {}),
        })),
    [casting, seatSignerKey, myStampSeats],
  );
  const seatPayloadRef = useRef(seatPayload);
  seatPayloadRef.current = seatPayload;
  /** WHO IS IN EACH SEAT, for the preview (Baker, 2026-09-16).
   *
   *  The same map the server builds at send time, so a locked box
   *  bound to a person's field shows the name it will actually print.
   *  Seats nobody fills are absent, which is what makes such a box
   *  read as empty on screen rather than resolving to a stranger. */
  const seatSignerKeyByRole = useMemo(() => {
    const m = new Map<string, string>();
    for (const seat of casting ?? []) {
      const k = seatSignerKey(seat);
      if (k) m.set(seat.role.key, k);
    }
    return m;
  }, [casting, seatSignerKey]);
  const fieldCountByRole = useMemo(() => {
    const m = new Map<string, number>();
    for (const seat of casting ?? []) {
      const k = seatSignerKey(seat);
      // Real fields only (2026-10-13): a dashed suggestion is not this
      // seat's until somebody accepts it, and counting them is how
      // Daniel's row read "30 fields" for 13.
      m.set(seat.role.key, k ? fields.filter((f) => f.signerKey === k && !isGhost(f)).length : 0);
    }
    return m;
  }, [casting, fields, seatSignerKey]);
  const roleBlockers = useMemo(
    () => (casting ? castingBlockers(casting, fieldCountByRole) : []),
    [casting, fieldCountByRole],
  );
  /** Which seat's picker is open, if any. */
  const [pickingRole, setPickingRole] = useState<string | null>(null);
  const [typedName, setTypedName] = useState("");
  /** Set when "someone else" was chosen FOR a seat: the guest form's
   *  next successful add fills that seat. */
  const guestForRoleRef = useRef<string | null>(null);
  /** Make the send agree with a seat change: the newcomer is selected
   *  and active; whoever they replaced is unselected — and their placed
   *  fields dropped, visibly, as unchecking them would — unless another
   *  seat still holds them. */
  function syncSelectionForSeat(before: CastSeat | null, after: CastSeat | null, whole: Casting) {
    const oldKey = before ? seatSignerKey(before) : null;
    const newKey = after ? seatSignerKey(after) : null;
    if (oldKey && oldKey !== newKey) {
      const stillHeld = whole.some(
        (seat) => seat.role.key !== after?.role.key && seatSignerKey(seat) === oldKey,
      );
      if (!stillHeld) {
        setSelected((prev) => {
          const n = new Set(prev);
          n.delete(oldKey);
          return n;
        });
        setFields((fs) => fs.filter((f) => f.signerKey !== oldKey));
      }
    }
    if (newKey) {
      setSelected((prev) => new Set(prev).add(newKey));
      setActiveSignerId(newKey);
    }
  }
  /** Put someone in a seat (the picker). A party who isn't on the send
   *  yet becomes a guest first, so their key exists before the seat
   *  points at it. */
  /** The casting with an empty seat for `roleKey` when it is one of the
   *  extra buyer / seller seats on offer and not on the send yet (Nate,
   *  2026-11-03). Every other casting comes back unchanged. */
  function withExtraSeat(base: Casting, roleKey: string): Casting {
    const extra = extraSeatsOnOffer(roles, base).find((r) => r.key === roleKey);
    return extra ? addSeatToCasting(base, extra, roles) : base;
  }
  function fillSeat(roleKey: string, person: CastPerson) {
    if (!casting) return;
    let cast = person;
    if (person.kind === "party" && person.email && !isGuestSignerKey(person.key)) {
      const email = person.email.toLowerCase();
      const existing = eligible.find((p) => p.email?.toLowerCase() === email);
      if (!existing) {
        const key = guestSignerKey(guests.length);
        setGuests((prev) => [...prev, { name: person.displayName, email: person.email ?? "" }]);
        cast = { ...person, key };
      }
    }
    const before = casting.find((seat) => seat.role.key === roleKey) ?? null;
    const next = castSeat(withExtraSeat(casting, roleKey), roleKey, cast);
    setCasting(next);
    syncSelectionForSeat(before, next.find((seat) => seat.role.key === roleKey) ?? null, next);
    setPickingRole(null);
    setTypedName("");
  }
  /** Step 2's Role dropdown (Nate, 2026-10-15: "I cannot change the
   *  role from signer to buyer 2"). Moves a signer already on the send
   *  into an EMPTY seat, or out of every seat ("Signer (no seat)").
   *
   *  DECISION: only empty seats are offered, never one somebody holds.
   *  Taking an occupied seat would push its person off the send and
   *  drop their placed fields (fillSeat's sync), a destructive side
   *  effect a dropdown should not hide. Trading two people's seats stays
   *  the field screen's "Swap". The person stays on the send either way:
   *  their seat changes, not whether they sign. */
  function seatSigner(signerKey: string, roleKey: string | null) {
    if (!casting) return;
    const signer = eligible.find((p) => p.key === signerKey);
    if (!signer) return;
    let next = casting;
    for (const seat of casting) {
      if (seat.role.key !== roleKey && seatSignerKey(seat) === signerKey) {
        next = castSeat(next, seat.role.key, null);
      }
    }
    // Buyer / Seller 3 or 4 (Nate, 2026-11-03) is not on the casting until
    // someone is put in it. It was offered against the casting the
    // dropdown drew from, so that is the one asked.
    if (roleKey) {
      const extra = extraSeatsOnOffer(roles, casting).find((r) => r.key === roleKey);
      if (extra) next = addSeatToCasting(next, extra, roles);
      const target = next.find((seat) => seat.role.key === roleKey);
      if (!target || target.person || target.role.kind !== "signs") return;
      const person: CastPerson =
        signer.kind === "person" && signer.contactId
          ? { key: signer.contactId, kind: "person", contactId: signer.contactId, displayName: signer.displayName, email: signer.email }
          : { key: signer.key, kind: "party", contactId: null, displayName: signer.displayName, email: signer.email };
      next = castSeat(next, roleKey, person);
    }
    setCasting(next);
  }
  /** "Leave them off this time." A deliberate click on a seat. */
  function leaveOff(roleKey: string) {
    if (!casting) return;
    const before = casting.find((seat) => seat.role.key === roleKey) ?? null;
    const next = leaveSeatOff(casting, roleKey);
    setCasting(next);
    syncSelectionForSeat(before, next.find((seat) => seat.role.key === roleKey) ?? null, next);
    setPickingRole(null);
  }
  /** Buyer 1 ↔ Buyer 2 (Borrower ↔ Co-borrower). The people trade
   *  seats AND the fields placed for each seat trade with them, so a
   *  first-signature line stays a first-signature line. */
  function swapSeats() {
    if (!casting) return;
    const rs = casting.map((seat) => seat.role);
    const a = firstSeatOf(rs), b = secondSeatOf(rs);
    const sa = a ? casting.find((seat) => seat.role.key === a.key) : null;
    const sb = b ? casting.find((seat) => seat.role.key === b.key) : null;
    const ka = sa ? seatSignerKey(sa) : null;
    const kb = sb ? seatSignerKey(sb) : null;
    setCasting(swapFirstTwo(casting));
    if (ka && kb) {
      setFields((fs) =>
        fs.map((f) =>
          f.signerKey === ka ? { ...f, signerKey: kb } : f.signerKey === kb ? { ...f, signerKey: ka } : f,
        ),
      );
    }
  }
  const canSwapSeats = useMemo(() => {
    if (!casting) return false;
    const rs = casting.map((seat) => seat.role);
    const a = firstSeatOf(rs), b = secondSeatOf(rs);
    return Boolean(
      a && b && casting.find((seat) => seat.role.key === a.key)?.person && casting.find((seat) => seat.role.key === b.key)?.person,
    );
  }, [casting]);

  // Signer colors are stable by position in the ELIGIBLE list, so a
  // signer keeps their color whether or not they're currently checked.
  const colorByContact = useMemo(() => {
    const map = new Map<string, ReturnType<typeof signerColor>>();
    eligible.forEach((p, i) => map.set(p.key, signerColor(i)));
    // A cast seat's person wears the SEAT's colour (see TONE_COLOR), so
    // the second buyer is amber whether they were listed first or third.
    for (const seat of casting ?? []) {
      const k = seatSignerKey(seat);
      const c = TONE_COLOR[seat.role.tone];
      if (k && c) map.set(k, c);
    }
    // Outside the rotation on purpose — a locked box belongs to nobody
    // on the signer list and must never borrow a person's colour.
    map.set(PREPARER_SIGNER_KEY, PREPARER_COLOR);
    return map;
  }, [eligible, casting, seatSignerKey]);

  // The document's type in words. Falls back to the stored id when the
  // type isn't one of THIS record's options — an off-list type is still
  // a real filed type, and showing the id beats showing nothing.
  const typeLabel = useMemo(() => {
    if (!documentType) return null;
    // Never a raw id on screen: a type the catalog doesn't name reads
    // as no type yet, not as "email_attachment".
    return typeOptions?.find((t) => t.id === documentType)?.name ?? "No type yet";
  }, [documentType, typeOptions]);

/** A suggestion as this screen handles it: the shared shape, plus the
 *  two things only a SAVED LAYOUT carries — which signer by position,
 *  and what was typed into the field last time. */
type SuggestedLike = SuggestedField & {
  signerIndex?: number | undefined;
  /** Who this proposal belongs to, when the rung that produced it knows
   *  — a saved layout does. Absent from every other rung, which is why
   *  the ordinal fallback below still exists. */
  signerKey?: string | undefined;
  value?: string | null | undefined;
  required?: boolean | undefined;
  mergeToken?: string | null | undefined;
  options?: string[] | null | undefined;
  groupName?: string | null | undefined;
  /** How this blank fills, when the rung that produced it knows — a
   *  saved map does, because the import recorded it. */
  fillRule?: string | null | undefined;
  /** The seat a LOCKED box reads its person from, when the map knows
   *  it — see PrepField.roleKey. */
  roleKey?: string | null | undefined;
  /** From the reviewed map of a form this copy was recognized as, and
   *  already moved onto this copy's pages (2026-10-13). */
  fromRecognizedForm?: boolean | undefined;
  /** An offer term for this box — see PrepField.term. */
  term?: PrepField["term"] | undefined;
};

/** How many text spans the semantic pass is given. Matches the server's
 *  own ceiling (MAX_SUGGESTER_SPANS in routes/esign.ts) — the selection
 *  is applied here so the payload carries the spans most likely to sit
 *  beside a blank, from every page, rather than the first 600 in
 *  reading order. */
const SUGGESTER_SPAN_BUDGET = 600;

/** Adjusting a proposal IS accepting it — with changes. The moment the
 *  advisor moves, resizes, retypes or reassigns a suggested field, it
 *  stops being a suggestion and becomes theirs, so it renders solid and
 *  "Clear suggestions" can no longer take it away. */
/** The keys the "only what you know" list counts as the sender's (ask +
 *  gap) for ANY document's fields — the active document's list is built
 *  from the same call; this is what lets a parked document's ghosts be
 *  counted and walked the same way (2026-09-29). */
function askedKeysIn(fields: readonly PrepField[], doc: PrepareDocument): Set<string> {
  const askable = fields
    .filter((f) => f.fillRule)
    .map((f) => ({
      key: f.key,
      documentId: doc.documentId,
      pageNumber: f.pageNumber,
      fieldType: f.fieldType,
      y: f.y,
      fillRule: f.fillRule ?? null,
      caption: f.caption ?? null,
      value: f.value ?? null,
      mergeToken: f.mergeToken ?? null,
    }));
  const form = onlyWhatYouKnow(askable, new Map([[doc.documentId, doc.filename]]));
  const out = new Set<string>();
  for (const g of form.groups) {
    for (const q of g.questions) if (q.kind === "ask" || q.kind === "gap") out.add(q.key);
  }
  return out;
}

function taken<T extends PrepField>(f: T): T {
  if (!f.proposal) return f;
  // The proposal is kept as the field's ORIGIN (2026-09-29), so Backspace
  // on an accepted field puts it back to the ghost it was rather than
  // deleting it — the mirror of accepting. Never sent: the payload lists
  // what it sends key by key.
  const { proposal: origin, ...rest } = f;
  // A blank whose only offer is what another document says: making it
  // real takes that value, the way accepting any offer prints its
  // value (2026-09-30). A value the sender typed stays theirs.
  // An offer term (the SCA percentage, an HOA box) replaces what the map
  // carried, unless the sender has typed something else over it.
  const value =
    f.term && (f.value ?? "") === (f.term.replaces ?? "")
      ? f.term.value
      : (f.value ?? "").trim()
        ? f.value
        : (f.reading?.value ?? f.value ?? null);
  return { ...rest, origin, value } as T;
}

  const proposedCount = fields.filter((f) => f.proposal).length;

  /** THE REAL FIELDS — what the send carries and every blocker reads.
   *  A ghost (a field still carrying its `proposal` marker) is drawn,
   *  offered, and never sent, so it can never block, never count as a
   *  signer's field, and never be "off the page" in a way that matters.
   *  See shared/esign/ghost-offers.ts for the rule. */
  // A person-printing box in a seat nobody holds is not part of this send
  // either (Grant, 2026-11-04): hidden, not counted, not a blocker.
  const realFields = useMemo(
    () => fields.filter((f) => !isGhost(f) && !isEmptySeatBox(f, seatSignerKeyByRole)),
    [fields, seatSignerKeyByRole],
  );

  /** Accepting a ghost only drops its `proposal` marker — the field
   *  itself is untouched, so an accepted offer and a hand-placed field
   *  are the same thing from here on. Accepts every ghost the predicate
   *  says yes to; the callers below decide which those are. */
  const acceptGhosts = useCallback(
    (shouldAccept: (f: PrepField) => boolean) => {
      const accept = (f: PrepField): PrepField => {
        if (!f.proposal || !shouldAccept(f)) return f;
        return taken(f);
      };
      setFields((prev) => prev.map(accept));
      // PACKAGE-WIDE (2026-09-29, Grant's decision 2): the parked
      // documents take the same predicate. The active document's own
      // parked copy is stale by design and skipped; its live state
      // above is the truth.
      let changed = false;
      for (const [id, st] of parkedRef.current) {
        if (id === documentId) continue;
        if (!st.fields.some((f) => f.proposal && shouldAccept(f))) continue;
        parkedRef.current.set(id, { ...st, fields: st.fields.map(accept) });
        changed = true;
      }
      if (changed) bumpParked();
    },
    [documentId, bumpParked],
  );

  /** The mirror of accepting: a real field goes back to being a ghost
   *  (Backspace on a value, Undo on a group). Only fields the ladder
   *  placed can go back — a hand-placed box has no proposal to return
   *  to, so it is deleted instead, as before. */
  const unacceptTo = useCallback(
    (keys: ReadonlySet<string>, source: SuggestionSource) => {
      const unaccept = (f: PrepField): PrepField => {
        if (!keys.has(f.key) || f.proposal) return f;
        const { origin, ...rest } = f;
        return {
          ...rest,
          proposal: origin ?? { source, confidence: 0.98, reason: "Put back as a suggestion" },
          // A value that came off another document leaves with the
          // accept, so the ghost is offered as the guess it was rather
          // than as a typed value.
          ...(f.reading && f.value === f.reading.value ? { value: null } : {}),
          // An offer term goes back to offering itself over the map's own
          // value, the same way.
          ...(f.term && f.value === f.term.value ? { value: f.term.replaces } : {}),
        };
      };
      setFields((prev) => prev.map(unaccept));
      // Package-wide, the mirror of acceptGhosts above.
      let changed = false;
      for (const [id, st] of parkedRef.current) {
        if (id === documentId) continue;
        if (!st.fields.some((f) => !f.proposal && keys.has(f.key))) continue;
        parkedRef.current.set(id, { ...st, fields: st.fields.map(unaccept) });
        changed = true;
      }
      if (changed) bumpParked();
    },
    [documentId, bumpParked],
  );

  // The full selection, and the single-field shorthand every existing
  // per-field editor below is built around — null the moment more than
  // one is selected, so that editor cleanly steps aside for the
  // multi-select panel instead of showing one field's properties as if
  // they applied to the whole group.
  const selectedFields = useMemo(
    () => fields.filter((f) => selectedKeys.has(f.key)),
    [fields, selectedKeys],
  );
  const selectedField = selectedFields.length === 1 ? selectedFields[0] : null;

  // Clicking a field on the page opens its editor at the BOTTOM of the
  // right-hand panel, below the fold — so on a long panel, selecting a
  // pick-one looked like nothing happened until you thought to scroll
  // (staging round 5). The editor now comes to the advisor. Keyed on
  // MEMBERSHIP of the selection, not the fields it reads: scrolling on
  // every keystroke while they type choices into that same editor would
  // be its own bug, and a Set's object identity changes on every
  // render even when its contents don't, so keying on `selectedKeys`
  // itself would do exactly that.
  const selectedEditorRef = useRef<HTMLDivElement | null>(null);
  const selectionSignature = useMemo(
    () => [...selectedKeys].sort().join("\u0000"),
    [selectedKeys],
  );
  useEffect(() => {
    if (selectedKeys.size === 0) return;
    const el = selectedEditorRef.current;
    if (!el) return;
    const reduced =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "nearest" });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on selection MEMBERSHIP (selectionSignature), not the fields/selectedKeys objects it reads; see comment above
  }, [selectionSignature]);

  // The moment a field STOPS being a suggestion (Accept all, or any
  // adjustment — both drop the proposal marker), its owner must really
  // be on the send. Round 5's trap: the AI proposed fields for a
  // household member who wasn't checked as a signer, "Accept all" was
  // the primary action, and the send then failed with a 400 that named
  // nobody. Accepting a person's field now checks them as a signer —
  // visibly (checkbox flips, toast says so) and reversibly. The ref
  // makes this fire only when a contact NEWLY gains an accepted field,
  // so deliberately unchecking someone later is respected, not fought
  // (that case gets a named error at send time instead).
  const prevAcceptedOwnersRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const owners = new Set(fields.filter((f) => !f.proposal).map((f) => f.signerKey));
    const prev = prevAcceptedOwnersRef.current;
    prevAcceptedOwnersRef.current = owners;
    const newcomers = [...owners].filter(
      (id) => !prev.has(id) && !selected.has(id) && eligible.some((p) => p.key === id),
    );
    if (newcomers.length === 0) return;
    setSelected((prevSel) => {
      const next = new Set(prevSel);
      for (const id of newcomers) next.add(id);
      return next;
    });
    const names = newcomers.map(
      (id) => eligible.find((p) => p.key === id)?.displayName ?? "A household member",
    );
    toast.success(
      names.length === 1
        ? `${names[0]} was added as a signer — an accepted field is theirs.`
        : `${names.join(" and ")} were added as signers — accepted fields are theirs.`,
    );
  }, [fields, selected, eligible, toast]);

  // The person contact.* tokens preview as — the first SELECTED signer,
  // matching the send path's recipient (signers[0]). Falls back to null
  // (the server then uses the record's default) before anyone is picked.
  const firstSignerContactId = useMemo(
    // A GUEST cannot key this: contact.* tokens resolve against a
    // contact, and a co-op agent has none. Falling through to the next
    // selected signer who IS a contact keeps the preview honest —
    // showing the buyer's name for {{contact.full_name}} — rather than
    // previewing blanks because the title officer happened to be
    // listed first.
    () => eligible.find((p) => selected.has(p.key) && p.contactId)?.contactId ?? null,
    [eligible, selected],
  );

  /** Fetch the record's mergeable fields for the current first signer.
   *  Safe to call on every render path that might need them — the ref
   *  keys the fetch, so repeats for the same signer are free and a
   *  signer change re-fetches. */
  const ensureMergeOptions = useCallback((): Promise<MergeFieldOption[]> => {
    // No signer to key the cache on in mapping mode — there is no
    // "first signer", only slots, and the values are SAMPLES anyway, so
    // one fetch per template covers every slot.
    const key = mapping
      ? `template:${mapping.templateId}`
      : // The signer SET is part of the key: adding a co-borrower has to
        // fetch their values, and a cache keyed only on the first signer
        // would go on showing one name in both boxes.
        `${recordKind}:${recordId}:${effectiveCompany ?? ""}:${firstSignerContactId ?? ""}:${realtyOfferId ?? ""}:` +
        [...selectedSignerContactIds].sort().join(",");
    const inFlight = mergeLoadPromiseRef.current;
    if (mergeLoadRef.current === key && inFlight) return inFlight;
    mergeLoadRef.current = key;
    // The PROMISE is kept, not just the flag: the suggestion pass awaits
    // this so the label dictionary can check a token against what this
    // record really offers. Returning early with nothing in flight would
    // let suggestions run against an empty set and bind nothing.
    const promise = (
      mapping
        ? apiV11.listTemplateMergeFields(mapping.templateId)
        : apiV11.listEsignMergeFields(
            recordKind,
            recordId,
            effectiveCompany,
            firstSignerContactId,
            selectedSignerContactIds,
            realtyOfferId,
          )
    )
      .then((res) => {
        mergeOptionsRef.current = res.fields;
        setMergeOptions(res.fields);
        // Absent in mapping mode: a template's signers are numbered
        // slots, not people, so there is nobody to look values up for
        // and the picker's samples are the right answer there.
        const perContact: Record<string, Record<string, string | null>> =
          "values_by_contact" in res ? ((res as { values_by_contact?: Record<string, Record<string, string | null>> }).values_by_contact ?? {}) : {};
        setMergeValuesByContact(perContact);
        // The company's learned caption vocabulary rides along on this
        // response so it is in hand before the document is read.
        learnedCaptionsRef.current = new Map(
          (res.learned_captions ?? []).map((c) => [
            c.caption_key,
            {
              token: c.token,
              label: c.label,
              fieldType: (c.field_type as LearnedCaption["fieldType"]) ?? null,
            },
          ]),
        );
        return res.fields;
      })
      .catch((err) => {
        mergeOptionsRef.current = [];
        setMergeOptions([]);
        setMergeLoadError(
          err instanceof ApiError
            ? err.message
            : "Couldn't read this record's fields — type the value in instead.",
        );
        // An empty list, never a rejection: a record whose fields can't
        // be read still gets its signature lines placed, unbound.
        return [];
      });
    mergeLoadPromiseRef.current = promise;
    return promise;
  }, [
    mapping,
    recordKind,
    recordId,
    effectiveCompany,
    firstSignerContactId,
    selectedSignerContactIds,
    realtyOfferId,
  ]);

  // A saved layout can bring bindings back with it. Load the values so
  // those fields show what they will print, without anyone opening the
  // picker first — and RE-load when the first selected signer changes,
  // so a bound field never keeps showing the previous person's value.
  const hasBoundField = fields.some((f) => f.mergeToken);
  useEffect(() => {
    if (hasBoundField) void ensureMergeOptions();
  }, [hasBoundField, ensureMergeOptions]);

  // ALSO loaded up front, as soon as the screen opens with a signer
  // chosen (2026-09-01). The label dictionary can only bind a blank to a
  // token it can confirm this record offers, so the options have to be
  // in hand BEFORE the suggestion pass runs — waiting for a bound field
  // to exist first is circular, and the practical effect was that
  // nothing ever auto-bound. One fetch either way; it just happens
  // sooner.
  useEffect(() => {
    if (open && firstSignerContactId) void ensureMergeOptions();
  }, [open, firstSignerContactId, ensureMergeOptions]);

  /** token → what it is and what it holds on THIS record. */
  const mergeByToken = useMemo(() => {
    const map = new Map<string, MergeFieldOption>();
    for (const o of mergeOptions ?? []) map.set(o.token, o);
    return map;
  }, [mergeOptions]);

  /** What a bound field will actually print, for the page overlay and
   *  the chip. Falls back to the token while the values are loading —
   *  never to silence, which would look like an empty field.
   *
   *  `signerKey` matters for a `contact.*` token and for nothing else:
   *  that family reads the person whose box this is, so a Borrower and a
   *  Co-Borrower name box show two different names, while the loan
   *  number shows one. Omitting it keeps the old shared answer, which is
   *  right for the picker (it is describing a token, not a box). */
  function boundPreview(
    token: string,
    signerKey?: string,
    /** The seat a LOCKED box reads its person from (Baker,
     *  2026-09-16). Such a box belongs to the preparer, so `signerKey`
     *  above is the preparer's sentinel and names nobody; the seat is
     *  how it finds its person. */
    roleKey?: string | null,
  ): { label: string; value: string | null } {
    const o = mergeByToken.get(token);
    // The end date follows a start date the sender typed over (Nate,
    // 2026-10-21), the same rule the send applies, so the screen never
    // previews an expiry the send will not print.
    if (token === SEND_DATE_PLUS_6_TOKEN) {
      const followed = sixMonthsAfterTypedStart(fields);
      if (followed) return { label: o?.label ?? mergeTokenLabel(token) ?? token, value: followed };
    }
    // The catalog's own words when this record doesn't offer the token,
    // so a box linked to something it can't fill still reads "Legal full
    // name" rather than a raw code.
    const shared = { label: o?.label ?? mergeTokenLabel(token) ?? token, value: o?.value ?? null };
    if (!isPersonScopedToken(token)) return shared;
    // MAPPING A BLANK FORM (Baker, 2026-10-03). The signers are numbered
    // slots, not people, so there is nobody to look a name up for, and
    // the template's merge-fields route already hands back each token's
    // SAMPLE as its value. That sample is the honest preview: asking
    // "whose contact record?" of a slot can only answer "nobody", which
    // raised a red "nothing to print" over a form that will fill fine
    // on every real send.
    if (mapping) return shared;
    // Asked exactly the way the server asks it at send time, so the
    // screen cannot promise a name the send then refuses to print.
    const personKey = bindingPersonSignerKey(
      { fieldType: "text", pageNumber: 1, ...(signerKey ? { signerKey } : {}), roleKey: roleKey ?? null },
      seatSignerKeyByRole,
    );
    if (!personKey) return signerKey ? { label: shared.label, value: null } : shared;
    // The name this package is being sent under, which is exactly what
    // the send prints into a `signer.name` box (resolvePackageBindings).
    if (isSignerScopedToken(token)) {
      const name = eligible.find((p) => p.key === personKey)?.displayName?.trim() ?? "";
      return { label: shared.label, value: name || null };
    }
    const contactId = contactIdForSignerKey(personKey);
    // No entry means a signer who isn't one of our contacts. Shown as
    // having nothing rather than borrowing the shared value, because
    // the send refuses that field for exactly the same reason.
    if (!contactId) return { label: shared.label, value: null };
    const perSigner = mergeValuesByContact[contactId];
    if (!perSigner || !(token in perSigner)) return shared;
    return { label: shared.label, value: perSigner[token] ?? null };
  }

  /** What a bound box shows when the record has nothing for it yet.
   *
   *  NEVER the token's label (Grant, 2026-11-04: "Legal full name"
   *  printed where a name belonged). A label names a kind of value; it
   *  is not a value, and on a page that otherwise reads like the
   *  finished contract it looked like one. Empty is the honest preview,
   *  and the side panel still says why (emptyBindingReason). The one
   *  exception is mapping a blank form, where the label IS the point:
   *  the sender is choosing which record field a box reads. */
  function unresolvedBindingText(token: string, signerKey?: string, roleKey?: string | null): string {
    return mapping ? boundPreview(token, signerKey, roleKey).label : "";
  }

  /** WHY a bound box has nothing to print, in words the advisor can act
   *  on (Nate, 2026-09-25). "Nothing on this record yet" beside a
   *  buyer's name sent him looking for a name that WAS on the record:
   *  the box had no seat, or the person had a first name and no legal
   *  last name, and the legal-name field refuses half a name on
   *  purpose. Saying which is the difference between a ten-second fix
   *  and a support question. */
  function emptyBindingReason(field: PrepField): string {
    const token = field.mergeToken ?? "";
    // A field linked to something this document's company can't fill at
    // all: a client's contact field on a Productions form, whose signers
    // are team members (Baker, 2026-10-03). Say so, and name the field
    // that will fill, rather than sending someone to fill a record that
    // does not exist.
    if (mergeOptions !== null && token && !mergeByToken.has(token)) {
      return isPersonScopedToken(token) && mergeByToken.has("signer.name")
        ? "this form can't fill that. Click Change and pick \u201cSigner's name\u201d"
        : "this form can't fill that. Click Change to pick another field";
    }
    if (!isPersonScopedToken(token)) return "nothing on this record yet";
    const personKey = bindingPersonSignerKey(
      {
        fieldType: "text",
        pageNumber: 1,
        ...(field.signerKey ? { signerKey: field.signerKey } : {}),
        roleKey: field.roleKey ?? null,
      },
      seatSignerKeyByRole,
    );
    if (!personKey) {
      return field.roleKey
        ? "nobody is in that seat on this send yet"
        : "pick whose this is below";
    }
    if (isSignerScopedToken(token)) return "this signer has no name on the send yet";
    const contactId = contactIdForSignerKey(personKey);
    // Points at the field that WILL fill rather than at typing: a value
    // typed here prints on this send only, and the same box comes back
    // empty for the next person.
    if (!contactId) {
      return "this signer isn't one of our contacts. Switch to \u201cSigner's name\u201d to print the name they're being sent this under, or type it below";
    }
    const values = mergeValuesByContact[contactId];
    if (token === "contact.legal_full_name" && values) {
      const first = values["contact.legal_first_name"];
      const last = values["contact.legal_last_name"];
      if (!first && !last) return "no legal name on this person's contact record";
      if (!last) return `${first} has no legal last name on their contact record`;
      if (!first) return "no legal first name on this person's contact record";
    }
    return "nothing on this person's contact record yet";
  }

  /** "What fills this box?" as the panel's own first line, for a field
   *  that already exists (placed or proposed) — the same question the
   *  palette leads with at creation, asked again the moment the
   *  advisor looks at a field rather than only when they're placing
   *  one. */
  function fillSummaryLabel(field: PrepField): string {
    if (field.strike) return "Strike through · printed as a line";
    if (field.fieldType === "text") {
      const locked = isPreparerField(field) ? "Locked · " : "";
      return field.mergeToken
        ? `${locked}Record field: ${boundPreview(field.mergeToken, field.signerKey, field.roleKey).label}`
        : `${locked}${isPreparerField(field) ? "You type this" : "Free text"}`;
    }
    return FIELD_TYPE_LABEL[field.fieldType];
  }

  /** "3 Signature · 2 Text · 1 Checkbox" — what a multi-selection is
   *  made of, at a glance, so bulk-editing "Required" doesn't happen
   *  blind to what's actually selected. */
  function multiSelectSummaryLabel(items: PrepField[]): string {
    const counts = new Map<string, number>();
    for (const f of items) {
      const label = FIELD_TYPE_LABEL[f.fieldType];
      counts.set(label, (counts.get(label) ?? 0) + 1);
    }
    return [...counts.entries()].map(([label, n]) => `${n} ${label}`).join(" · ");
  }

  // Default the active signer to the first selected one.
  useEffect(() => {
    if (!activeSignerId || !selected.has(activeSignerId)) {
      setActiveSignerId([...selected][0] ?? null);
    }
  }, [selected, activeSignerId]);

  // Know whether the advisor's Outlook is actually connected, so the
  // footer tells the truth about how the links will reach signers.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api
      .getMicrosoftGraphStatus()
      .then((res) => {
        if (!cancelled) setOutlookConnected(res.connection.connected);
      })
      .catch(() => {
        if (!cancelled) setOutlookConnected(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Whether the sender has a saved signature to stamp with (Preferences).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    apiV11
      .getMyEsignSignature()
      .then((res) => {
        if (!cancelled) setMySignature(res.signature ?? null);
      })
      .catch(() => {
        if (!cancelled) setMySignature(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // The type options, fetched only when we'd actually ask — a document
  // that already knows what it is doesn't need the list.
  useEffect(() => {
    // Mapping mode never asks — the template already has whatever type
    // it was uploaded with, shown read-only via the documentType prop.
    if (!open || mapping) return;
    let cancelled = false;
    apiV11
      .listEsignDocumentTypes(recordKind, recordId, effectiveCompany)
      .then((res) => {
        if (!cancelled) setTypeOptions(res.types);
      })
      .catch(() => {
        // A failed lookup must never block a send: the question is
        // optional, so silence here degrades to "we didn't ask".
        if (!cancelled) setTypeOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, mapping, recordKind, recordId, effectiveCompany]);

  useEffect(() => {
    if (!open || !needsCompany) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.listCompanies();
        if (cancelled) return;
        const mine = res.companies
          // Productions employs the team but delivers no client
          // service, so it never appears on a client-facing choice.
          .filter((co) => !co.is_support_entity)
          .map((co) => ({ slug: co.slug, label: co.display_name }));
        setCompanyOptions(mine);
        if (mine.length === 1) setChosenCompany(mine[0]!.slug);
      } catch {
        if (!cancelled) setCompanyOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, needsCompany]);

  // Load + render the ACTIVE document when the screen opens, and again
  // whenever the sender moves to a different document in the package.
  //
  // A document already worked on comes back from the parked map instead
  // of being re-fetched: someone flicking between a 40-page contract
  // and its addendum must not wait for a re-render each way, and — the
  // part that matters — must not have the suggestion ladder re-propose
  // fields they already accepted or deleted.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    /** Put a parked document on screen. Also the landing for a
     *  background read that finishes while this document is active. */
    const restore = (parked: ParkedDocumentState) => {
      setPages(parked.pages);
      renderedRef.current = parked.rendered;
      fingerprintRef.current = parked.fingerprint;
      setFields(parked.fields);
      setStamps(parked.stamps);
      setAppliedLayout(parked.appliedLayout);
      setAppliedLayoutInfo(parked.appliedLayoutInfo);
      setLayoutPositionsUnchecked(parked.layoutPositionsUnchecked);
      setFormRecognition(parked.formRecognition);
      setSuggestNote(parked.suggestNote);
      setWrittenOnCount(parked.writtenOnCount);
      setDetectedBlanks(parked.detectedBlanks);
      setLoadError(null);
      setLoadTimedOut(false);
    };
    const parked = parkedRef.current.get(documentId);
    if (parked && parked.pages) {
      restore(parked);
      return;
    }
    setPages(null);
    setLoadError(null);
    setLoadTimedOut(false);
    setFields([]);
    setStamps([]);
    setDetectedBlanks([]);
    setSelectedKey(null);
    setSelectedStampKey(null);
    fingerprintRef.current = null;
    renderedRef.current = null;
    // ALREADY BEING READ IN THE BACKGROUND (2026-09-29): join that read
    // rather than fetching and drawing the same file a second time. If
    // it fails, fall through to the ordinary load below, which is the
    // path that shows the error and offers "Try again".
    const joined = inFlightRef.current.get(documentId);
    if (joined) {
      joined.then(
        (state) => {
          if (cancelled) return;
          // The reader leaves the map alone for the active document (see
          // the reader's own comment); it lands here instead.
          parkedRef.current.set(documentId, state);
          restore(state);
        },
        () => {
          if (!cancelled) void loadFresh();
        },
      );
      return () => {
        cancelled = true;
      };
    }
    void loadFresh();
    async function loadFresh() {
      try {
        // "Couldn't FETCH the file" and "couldn't DRAW the file" are
        // different faults with different fixes, and a fetch with no
        // clock of its own can't tell them apart — a stalled document
        // store would surface as a render timeout and send everyone
        // hunting the wrong thing (staging round 5). This clock is
        // generous: SharePoint occasionally takes its time, and only a
        // genuine stall should trip it.
        const fetchAbort = new AbortController();
        const fetchTimer = setTimeout(() => fetchAbort.abort(), 30_000);
        let res: Response;
        try {
          const bytesUrl = mapping
            ? `/api/esign/templates/${encodeURIComponent(documentId)}/bytes`
            : `/api/esign/documents/${encodeURIComponent(documentId)}/bytes`;
          res = await fetch(bytesUrl, {
            credentials: "include",
            signal: fetchAbort.signal,
          });
        } catch (err) {
          if (fetchAbort.signal.aborted) {
            throw new Error(
              "Couldn't get the document file — the document store didn't answer. " +
                "This is usually momentary; try again.",
            );
          }
          throw err;
        } finally {
          clearTimeout(fetchTimer);
        }
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as { message?: string } | null;
          throw new Error(body?.message ?? "Couldn't load the document.");
        }
        const bytes = await res.arrayBuffer();
        const rendered = await renderPdfPages(bytes);
        if (cancelled) return;
        setPages(rendered.pages);
        renderedRef.current = rendered;
        // Kick the suggestion ladder the moment the document is
        // readable — it runs while the advisor is still glancing at the
        // signer list, so the fields are usually there by the time they
        // look at the page.
        void runSuggestions(rendered);
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : "Couldn't load the document.");
          // A timeout is one failure where retrying in place is known
          // NOT to help: the renderer is wedged, so the same wedged
          // renderer would take the retry. A stale-chunk error reaching
          // HERE (rather than renderPdfPages's own one-time reload
          // already having fixed it) means that reload already ran this
          // tab and the file genuinely still isn't there — retrying
          // asks for the same missing file again. Both get the same
          // lead-with-reload treatment.
          setLoadTimedOut(err instanceof PdfRenderTimeout || isChunkLoadError(err));
        }
      }
    }
    return () => {
      cancelled = true;
    };
  }, [open, documentId, loadAttempt]);

  // LAND ON THE BLANK TAB WAS HEADING FOR (2026-09-29). Set by the Tab
  // walk when the next blank is on another document; applied here once
  // that document's fields are on screen, and cleared so it fires once.
  useEffect(() => {
    if (!jumpKey || !pages) return;
    const target = fields.find((f) => f.key === jumpKey);
    if (!target) return;
    setJumpKey(null);
    setSelectedKey(jumpKey);
    pageRefs.current.get(target.pageNumber)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [jumpKey, fields, pages]);

  /** Park the document currently on screen, then show another one.
   *
   *  Parking is what makes the rail safe to use: fields, stamps and the
   *  rendered pages all belong to ONE document, and switching without
   *  saving them first would silently throw away work someone just did
   *  on a legal document. */
  const showDocument = useCallback(
    (index: number) => {
      if (index === activeIndex) return;
      parkedRef.current.set(documentId, {
        fields,
        stamps,
        pages,
        rendered: renderedRef.current,
        fingerprint: fingerprintRef.current,
        suggested: true,
        appliedLayout,
        appliedLayoutInfo,
        layoutPositionsUnchecked,
        formRecognition,
        suggestNote,
        writtenOnCount,
        detectedBlanks,
      });
      setPlacingType(null);
      setPlacingLocked(false);
      setPlacingStamp(false);
      setActiveIndex(index);
    },
    [
      activeIndex,
      documentId,
      fields,
      stamps,
      pages,
      appliedLayout,
      appliedLayoutInfo,
      layoutPositionsUnchecked,
      formRecognition,
      suggestNote,
      writtenOnCount,
      detectedBlanks,
    ],
  );

  /** Move a document to a new position in the envelope.
   *
   *  Deliberately NOT a variant of showDocument: reordering does not
   *  switch which document is on screen, so nothing is parked and the
   *  live fields keep belonging to the same file. What does change is
   *  every POSITION — activeIndex is an index, so it is re-pointed at
   *  the document the sender is still looking at. Getting that wrong
   *  would silently show them a different document's pages with the
   *  fields they placed on this one still in state.
   *
   *  Each document's placed fields travel with it either way: they are
   *  parked under its own id (parkedRef), never under its position. */
  const moveDocument = useCallback(
    (from: number, to: number) => {
      if (from === to) return;
      if (from < 0 || to < 0 || from >= packageDocs.length || to >= packageDocs.length) return;
      const ids = moveInList(
        packageDocs.map((d) => d.documentId),
        from,
        to,
      );
      setDocOrder(ids);
      const stillOnScreen = ids.indexOf(documentId);
      if (stillOnScreen >= 0) setActiveIndex(stillOnScreen);
    },
    [packageDocs, documentId],
  );
  /** Which rail row is being dragged, and which one it is hovering
   *  over. Both null when nothing is in flight. */
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  /** Every document's fields, with the one on screen taken from live
   *  state rather than from the parked copy (which is one switch out of
   *  date). This is what the readiness summary and the send both read,
   *  so neither can ever disagree with what the sender is looking at. */
  /** Every document's fields, GHOSTS INCLUDED, with the one on screen
   *  taken from live state rather than from its parked copy (which is
   *  one switch out of date). Every package-wide reading below — the
   *  real fields, the census, the walk, the send line — comes off this
   *  one map, so none of them can disagree about what the package holds.
   *  `parkedVersion` is in the deps because a background read writes
   *  the map with nothing else moving (2026-09-29). */
  const allDocFields = useMemo(() => {
    const out = new Map<string, PrepField[]>();
    for (const d of packageDocs) {
      out.set(
        d.documentId,
        d.documentId === documentId ? fields : (parkedRef.current.get(d.documentId)?.fields ?? []),
      );
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- parkedVersion stands in for parkedRef, a ref by design
  }, [packageDocs, documentId, fields, parkedVersion]);
  /** Every document's REAL fields. A ghost is never sent, so it is
   *  never a blocker, never a signer's field, and never proof that a
   *  document has something to sign (2026-09-29). */
  const packageFields = useMemo(() => {
    const out = new Map<string, PrepField[]>();
    // A person-printing box in a seat nobody holds is not part of this send
    // (Grant, 2026-11-04), so it is neither sent nor a blocker.
    for (const [id, all] of allDocFields) {
      out.set(id, all.filter((f) => !isGhost(f) && !isEmptySeatBox(f, seatSignerKeyByRole)));
    }
    return out;
  }, [allDocFields, seatSignerKeyByRole]);

  /** Every problem the per-document notes below check — nothing placed,
   *  a field off the page, an unconfigured field, an unfilled record
   *  binding — but summed across the WHOLE package rather than just the
   *  document on screen. This is what actually decides whether Send is
   *  allowed. See computePackageBlockers's header (prepare-helpers.ts)
   *  for the 2026-09-18 Baker/Joey bug this fixes: a document with
   *  nothing to sign, correctly, was the reason a 7-document package's
   *  Send button stayed gray. */
  /** How many lines a multi-line box's text runs past its last rule —
   *  zero for every single-line box and every box whose text fits. The
   *  server refuses such a send outright (text_overflow, rather than
   *  shrinking a contract clause unreadable or cutting its tail); this
   *  is that refusal said here, where the sender can shorten the text or
   *  drag the box over more lines. Measured with the same wrapper and
   *  the same face the stamper prints in, so the count is the count. */
  const overflowLinesOf = useCallback(
    (f: PrepField): number => {
      if (!isMultilineField(f)) return 0;
      const text =
        f.value ??
        (f.mergeToken ? boundPreview(f.mergeToken, f.signerKey, f.roleKey).value : null);
      if (!text || !text.trim()) return 0;
      return layoutStyledMultiline(f, text).overflow;
    },
    // boundPreview closes over the record's values, which is what changes,
    // and over the signers (a `signer.name` box prints their name).
    [mergeByToken, eligible],
  );

  const packageBlockers = useMemo(() => {
    const docs = packageDocs.map((d) => ({
      doc: d,
      fields: packageFields.get(d.documentId) ?? [],
      pages: d.documentId === documentId ? pages : (parkedRef.current.get(d.documentId)?.pages ?? null),
    }));
    return computePackageBlockers(
      docs,
      (f) => sendBlockReason(f) !== null || overflowLinesOf(f) > 0,
      (fields) => {
        // mergeOptions === null means "the record's values haven't
        // loaded yet" — same silence rule unfilledMergeFields below
        // follows, so this doesn't raise a blocker that clears itself a
        // second later.
        if (mergeOptions === null) return [];
        const holes = unfilledBindings(fields, (token, f) =>
          boundPreview(token, f.signerKey, f.roleKey).value,
        );
        return holes.map((h) => fields[h.index]).filter((f): f is PrepField => Boolean(f));
      },
    );
    // `mergeByToken` isn't read directly here — `boundPreview` closes
    // over it — but it's in the deps for the same reason it's in
    // unfilledMergeFields's deps below: it's what actually changes when
    // the record's values load.
    // `eligible` for the same reason: a `signer.name` box previews the
    // name of whoever is on the send, so adding a signer changes it.
  }, [packageDocs, packageFields, documentId, pages, mergeOptions, mergeByToken, eligible, overflowLinesOf]);

  // The suggestion ladder. Rungs 2 and 3 are free and run right here on
  // what pdf.js already parsed; rung 4 (Stew) is the only one that costs
  // anything, so it is asked ONLY about the spans the free rungs left
  // unclaimed — a document that declares its own form fields never
  // reaches the model at all.
  //
  // Everything it produces is a PROPOSAL: dashed on the page, labelled
  // with where it came from, and the advisor accepts, adjusts or deletes
  // it. Nothing here can send anything.
  /** The document on screen: the ladder writes to React state, as it
   *  always did. See SuggestSink. */
  const activeSink = useMemo<SuggestSink>(
    () => ({
      documentId,
      getFingerprint: () => fingerprintRef.current,
      setFingerprint: (v) => {
        fingerprintRef.current = v;
      },
      setSuggesting,
      setSuggestNote,
      setWrittenOnCount,
      setAppliedLayout,
      setAppliedLayoutInfo,
      setLayoutPositionsUnchecked,
      setFormRecognition,
      setDetectedBlanks,
      setStamps,
      setFields,
    }),
    [documentId],
  );
  const runSuggestions = useCallback(
    async (rendered: RenderedPdf, sink: SuggestSink = activeSink) => {
      // A DRAFT'S SAVED WORK (PR 8c) stands in for the whole pass: the
      // fields and stamps exactly as they were left. The fingerprint is
      // measured afresh, since it is what lets this send teach the map.
      const saved = draftWorkRef.current.get(sink.documentId);
      if (saved) {
        draftWorkRef.current.delete(sink.documentId);
        sink.setFingerprint(await documentFingerprint(rendered.spans));
        sink.setFields(() => saved.fields);
        sink.setStamps(() => saved.stamps);
        sink.setSuggesting(false);
        return;
      }
      // An ATTACHMENT is read, never signed: nothing is suggested on it,
      // and no fingerprint is kept, so its send teaches no map.
      if (attachmentIdsRef.current.has(sink.documentId)) {
        sink.setSuggestNote("An attachment goes out to be read. Nothing on it is signed.");
        return;
      }
      sink.setSuggesting(true);
      sink.setSuggestNote(null);
      sink.setWrittenOnCount(0);
      sink.setAppliedLayout(null);
      sink.setAppliedLayoutInfo(null);
      sink.setFormRecognition(null);
      try {
        // Computed before anything else asks for it — it is what decides
        // whether this document is RECOGNIZED, which short-circuits both
        // of the cheaper rungs and the model.
        sink.setFingerprint(await documentFingerprint(rendered.spans));

        // The label dictionary needs to know which tokens THIS record can
        // actually fill, so a bound proposal is one the send can resolve.
        // Awaited rather than read opportunistically: the fetch is quick
        // and the PDF render ahead of it is not, but "usually loaded by
        // now" is how auto-binding would silently do nothing on a slow
        // morning. On failure this resolves to an empty list and every
        // proposal simply arrives unbound — never a guess.
        const loadedOptions = firstSignerContactId
          ? await ensureMergeOptions()
          : (mergeOptionsRef.current ?? []);
        const offeredTokens = new Set(loadedOptions.map((o) => o.token));
        const binding = {
          companySlug: effectiveCompany,
          tokenIsOffered: (t: string) => offeredTokens.has(t),
          // What a person here has already taught this company a caption
          // means — consulted ahead of the built-in dictionary, so
          // mapping one AAR form really does teach the other 27.
          learned: learnedCaptionsRef.current,
        };

        // THE BLANKS THE FORM DRAWS. AAR rules its blanks as vector
        // lines rather than typing underscores, so this is the only rung
        // that can see most of a listing contract at all — see
        // shared/esign/vector-blanks.ts.
        const blanks = detectBlanks(
          rendered.vectorPaths,
          rendered.pages.map((p) => ({
            pageNumber: p.pageNumber,
            width: p.widthPt,
            height: p.heightPt,
          })),
          rendered.spans,
        );
        // A blank somebody already wrote on is not a blank. Left in,
        // every answer on a finished form would be outlined and counted
        // as "still unmapped", and the screen would send the advisor off
        // to map a hundred lines that need nothing.
        // FOR BROKER USE ONLY STAYS BLANK (Grant, 2026-10-13: "this
        // always needs to be left blank"). Found off the page itself, and
        // applied to everything below: no outline, no proposal, nothing
        // asked of Stew, nothing placed from a saved or reviewed map.
        // DECISION: enforced here, on every path, rather than by editing
        // each saved map, so a map nobody has re-saved obeys it too.
        const officeRegions = officeUseRegions(
          rendered.spans,
          rendered.vectorPaths,
          rendered.pages.map((p) => ({ pageNumber: p.pageNumber, width: p.widthPt })),
        );
        const outsideOffice = <T extends { pageNumber: number; x: number; y: number; width: number; height: number }>(
          list: readonly T[],
        ): T[] => list.filter((b) => !insideRegions(b, officeRegions));
        sink.setDetectedBlanks(
          outsideOffice(blanks).filter((b) => !isAlreadyWrittenOn(b, rendered.spans)),
        );

        const free = outsideOffice(
          mergeSuggestions(
            suggestFromWidgets(rendered.widgets),
            suggestFromBlanks(blanks, rendered.spans, { binding }),
            suggestFromText(rendered.spans, { binding }),
          ),
        );

        // WHICH REVIEWED FORM IS THIS? (2026-10-13.) Read off the
        // printed lines every copy keeps, so a filled, stamped or
        // re-printed copy is recognized as well as the library's own.
        // Not while teaching a blank: that screen IS the map.
        const recognized: RecognizedForm[] = mapping
          ? []
          : recognizeForms(rendered.spans, [...AAR_FORM_SIGNATURES], {
              side:
                rolesCtxRef.current?.side === "buy" || rolesCtxRef.current?.side === "sell"
                  ? rolesCtxRef.current.side
                  : null,
            });
        const applicable = recognized.filter((r) => r.revisionMatches);
        /** The document pages a reviewed map will own. Nothing else is
         *  asked about them, and no guess is mixed in on them. */
        const mappedPages = new Set(applicable.flatMap((r) => r.pages.map((pg) => pg.documentPage)));

        // Only pay for what the free rungs missed. A span already
        // covered by a proposal is not worth a model asking about.
        //
        // CLAIMED BY GEOMETRY (2026-10-13). This compared a box's TOP to
        // a caption's top in 12pt buckets; a 28pt box's top sits a bucket
        // or two above the caption beside it, so the caption looked
        // unclaimed and Stew proposed the same blank again — one of the
        // ways boxes ended up stacked on each other. A span is claimed
        // when it sits on, beside or just under a box already proposed.
        // Signing-service stamps and the broker's box are never asked.
        const claimedBy = (span: { pageNumber: number; x: number; y: number; width: number; height: number }) => {
          const cx = span.x + span.width / 2;
          const cy = span.y + span.height / 2;
          return free.some(
            (f) =>
              f.pageNumber === span.pageNumber &&
              cx >= f.x - 40 &&
              cx <= f.x + f.width + 40 &&
              cy >= f.y - 14 &&
              cy <= f.y + f.height + 16,
          );
        };
        const unclaimed = rendered.spans.filter(
          (span) =>
            !mappedPages.has(span.pageNumber) &&
            !isProviderStamp(span.text) &&
            !insideRegions(span, officeRegions) &&
            !claimedBy(span),
        );

        // The server answers this ONE call two different ways: with a
        // saved layout when it recognizes the document, or with Stew's
        // reading when it doesn't. A layout comes back with `layout` set.
        let fromServer: SuggestedLike[] = [];
        let fromSavedLayout = false;
        /** Set when the server applied the reviewed map of a form this
         *  copy was RECOGNIZED as (Rung 1c), rather than a map saved on
         *  this copy or a library copy. Its boxes then arrive in the
         *  blank's own space and are moved onto this copy below, and
         *  only the pages they own are spared the guessing. */
        let viaRecognition = false;
        /** Which recognized forms the server actually had a map for. */
        let appliedFormKeys = new Set<string>();
        /** The slot list a saved map needs, as the server widened it.
         *  Read after the try below, so it is declared out here with the
         *  other two answers that block produces. */
        let layoutSlots: MappingSlot[] | null = null;
        /** The sender's own signature lines, from a map that knows its
         *  seats. Declared out here with the others for the same reason:
         *  the block below is a try, and these are read after it. */
        let myLines: NonNullable<
          Awaited<ReturnType<typeof apiV11.suggestEsignFields>>["stamps"]
        > = [];
        // Asked even when the free rungs claimed everything, because the
        // layout lookup does not depend on spans — a recognized document
        // must not be missed just because its lines were readable.
        try {
          const spanBatch = selectSpansForSuggester(unclaimed, SUGGESTER_SPAN_BUDGET).map((sp) => ({
            id: sp.id,
            pageNumber: sp.pageNumber,
            text: sp.text,
            x: sp.x,
            y: sp.y,
            width: sp.width,
            height: sp.height,
          }));
          // Read once: a call result cannot be narrowed across two reads.
          const fingerprint = sink.getFingerprint();
          const res = mapping
            ? await apiV11.suggestTemplateFields(mapping.templateId, {
                signers: mappingSlots,
                spans: spanBatch,
                // Lets the OS learn what this form is on the FIRST open
                // rather than on the first save — see the route.
                ...(fingerprint ? { contentFingerprint: fingerprint } : {}),
              })
            : await apiV11.suggestEsignFields(recordKind, recordId, {
                documentId: sink.documentId,
                ...(effectiveCompany ? { companySlug: effectiveCompany } : {}),
                ...(realtyOfferId ? { realtyOfferId } : {}),
                packageDocumentIds: packageDocIdsRef.current,
                ...(fingerprint ? { contentFingerprint: fingerprint } : {}),
                // THROUGH THE REF, NOT THE STATE (2026-09-18). This
                // callback's own deps array does not list
                // selectedSignerKeys, so reading it directly here reads
                // whatever the selection was when the callback was last
                // built — usually mount, before casting has picked out
                // a borrower and co-borrower. A map taught in plain
                // numbered slots (no seats) addresses its fields by
                // THAT list's position, so an empty/stale list drops
                // every field on the floor and the request silently
                // falls through to Stew reading the document cold. This
                // is the other half of Baker's vanishing-boxes bug
                // (2026-09-14, see selectedSignerKeysRef's own comment)
                // — that fix covered the ordinal fallback a few dozen
                // lines below; this call site was missed.
                //
                // HELP-OK: bugfix restoring documented behavior — the
                // help article already describes a recognized document
                // applying its saved map; no label or flow changed.
                signerContactIds: selectedSignerKeysRef.current,
                // A map taught in seats places each field on whoever is
                // in that seat today. Read through the ref for the same
                // reason the signer list is: this pass runs off a
                // callback made before the casting arrived.
                ...(seatPayloadRef.current.length > 0
                  ? { seats: seatPayloadRef.current }
                  : {}),
                // Echoed back so the server can confirm a seat claiming
                // to be stamped is one the catalog stamps on THIS side:
                // the buyer's agent is us on a purchase and the other
                // firm on a listing.
                ...(rolesCtxRef.current ? { side: rolesCtxRef.current.side } : {}),
                // Which reviewed forms this copy was recognized as. The
                // server applies their maps when it has no closer match
                // (a map saved on this very copy, or a library copy).
                ...(applicable.length > 0
                  ? { recognizedForms: applicable.map((r) => ({ formKey: r.formKey })) }
                  : {}),
                // Selected here rather than server-side so the payload stays
                // small AND covers the whole document: sending the first 600
                // spans of an eight-page contract is what hid its back half
                // from Stew entirely (2026-09-01).
                spans: spanBatch,
              });
          if (res.message) sink.setSuggestNote(res.message);
          if ("stamps" in res && Array.isArray(res.stamps)) myLines = res.stamps;
          fromSavedLayout = Boolean(res.layout);
          if (res.layout) {
            sink.setAppliedLayout(res.layout.name);
            sink.setAppliedLayoutInfo({
              createdByName: res.layout.createdByName ?? null,
              createdAt: res.layout.createdAt ?? null,
              timesApplied: res.layout.timesApplied ?? 0,
            });
            sink.setLayoutPositionsUnchecked(res.layout.positionsReconstructed === true);
          }
          if (mapping && res.layout && "signers" in res && Array.isArray(res.signers)) {
            // The seats the stored map was taught with come back with
            // the slots, so re-opening a map shows "Buyer 2" rather than
            // "Signer 2" — see slotsForSavedLayout.
            layoutSlots = res.signers.map((sg) => ({
              id: sg.id,
              label: sg.label,
              roleKey: sg.roleKey ?? null,
            }));
          }
          fromServer = res.suggestions.map((sg) => ({
            fieldType: sg.fieldType as EsignFieldType,
            pageNumber: sg.pageNumber,
            x: sg.x,
            y: sg.y,
            width: sg.width,
            height: sg.height,
            source: sg.source as SuggestionSource,
            confidence: sg.confidence,
            reason: sg.reason,
            signerHint: sg.signerHint,
            signerKey: sg.signerKey,
            signerIndex: sg.signerIndex,
            value: sg.value ?? null,
            required: sg.isRequired ?? true,
            mergeToken: sg.mergeToken ?? null,
            options: sg.options ?? null,
            groupName: sg.groupName ?? null,
            // A box the map taught as spanning several lines comes back
            // as one, at the pitch it was taught (2026-09-29).
            ...(sg.multiline === true && typeof sg.linePitch === "number"
              ? {
                  multiline: true,
                  linePitch: sg.linePitch,
                  ...(typeof sg.firstLineIndent === "number" && sg.firstLineIndent > 0
                    ? { firstLineIndent: sg.firstLineIndent }
                    : {}),
                }
              : {}),
            ...(parseTextStyle(sg.textStyle) ? { textStyle: parseTextStyle(sg.textStyle) as EsignTextStyle } : {}),
            // HOW THE BLANK FILLS. Dropped here until 2026-09-29: the
            // response type never named the key, so this mapping never
            // copied it, and every mapped locked box reached a real
            // send as "no rule" — the 2026-09-18 blocker then refused
            // Grant's own purchase contract with "107 locked boxes have
            // nothing typed in them yet". The per-form sendability test
            // calls applyLayout directly and never saw it.
            fillRule: sg.fillRule ?? null,
            // The offer's own answer for a box no merge field fills (the
            // SCA percentage, an HOA "paid by" box), kept with the value
            // it would replace so typing over it still wins (2026-11-03).
            // (Only a record send carries one; the mapping screen's
            // response has no offer.)
            ...("offerTerm" in sg && sg.offerTerm
              ? {
                  term: {
                    value: sg.offerTerm.value,
                    label: sg.offerTerm.label,
                    replaces: sg.value ?? null,
                    ...(sg.offerTerm.source ? { source: sg.offerTerm.source } : {}),
                  },
                }
              : {}),
            // The seat a locked box reads its person from. Dropped here
            // until 2026-09-16, which is why 31 atlas-placed name boxes
            // arrived on this screen having forgotten whose name they
            // print.
            //
            // And a locked name box that arrives with NO seat (a map
            // saved before seats were carried) starts on the form's
            // first signing seat, the same default a box placed by hand
            // gets (Nate, 2026-09-25: the buyer line read "Nobody yet"
            // and printed nothing). Never overrides a seat it has.
            roleKey:
              sg.roleKey ??
              (sg.signerKey === PREPARER_SIGNER_KEY &&
              sg.mergeToken &&
              wantsDefaultSeat(sg.mergeToken, true, null)
                ? (firstSeatOf(rolesRef.current)?.key ?? null)
                : null),
          }));
          // MOVE A RECOGNIZED FORM'S MAP ONTO THIS COPY (2026-10-13).
          // Each box is tagged with the form it came from; its page and
          // position are in the reviewed blank's space, and this copy may
          // be missing a page, carry several forms, or be shifted and
          // scaled by whatever printed it. A box whose page this copy
          // does not have is dropped, never put on some other page.
          const appliedKeys = new Set(
            "recognizedForms" in res && Array.isArray(res.recognizedForms)
              ? res.recognizedForms.map((r) => r.formKey)
              : [],
          );
          if (appliedKeys.size > 0) {
            viaRecognition = true;
            appliedFormKeys = appliedKeys;
            const byKey = new Map(recognized.map((r) => [r.formKey, r]));
            const keysInOrder = res.suggestions.map((sg) => ("formKey" in sg ? (sg.formKey ?? null) : null));
            fromServer = fromServer.flatMap((field, i) => {
              const key = keysInOrder[i];
              if (!key) return [field];
              const form = byKey.get(key);
              const placed = form ? placeOnDocument(field, form) : null;
              if (!placed) return [];
              const scale = form?.pages.find((pg) => pg.documentPage === placed.pageNumber)?.transform.scale ?? 1;
              return [
                {
                  ...placed,
                  ...(typeof placed.linePitch === "number" ? { linePitch: placed.linePitch * scale } : {}),
                  ...(typeof placed.firstLineIndent === "number"
                    ? { firstLineIndent: placed.firstLineIndent * scale }
                    : {}),
                  fromRecognizedForm: true,
                },
              ];
            });
            myLines = myLines.flatMap((line) => {
              const key = "formKey" in line ? (line.formKey ?? null) : null;
              const form = key ? byKey.get(key) : null;
              if (!form) return [line];
              const placed = placeOnDocument(line, form);
              return placed ? [placed] : [];
            });
          }
        } catch (err) {
          // A failed lookup costs the advisor nothing but the
          // suggestions — the free rungs' proposals still stand and
          // manual placement is untouched.
          sink.setSuggestNote(
            err instanceof ApiError
              ? err.message
              : "Couldn't read this one — place any missing fields yourself.",
          );
        }

        // The broker's box stays blank on a map's answer too (see above).
        fromServer = outsideOffice(fromServer);
        myLines = outsideOffice(myLines);

        // SAY WHICH FORM THIS IS, OR THAT THE OS DOESN'T KNOW IT.
        if (viaRecognition) {
          const shown = applicable.filter((r) => appliedFormKeys.has(r.formKey));
          sink.setFormRecognition({
            kind: "applied",
            forms: shown.map((r) => formLabel(r, r.mapRevision)),
            unmeasuredPages: shown.reduce((n, r) => n + r.pages.filter((pg) => !pg.aligned).length, 0),
          });
          // The map's own "placed from the standard layout" warning is
          // about the blank; on a recognized copy the measured pages are
          // what matter, and the note above says which weren't.
          sink.setLayoutPositionsUnchecked(false);
        } else if (!fromSavedLayout && !mapping) {
          const otherRevision = recognized.filter((r) => !r.revisionMatches);
          if (otherRevision.length > 0) {
            const first = otherRevision[0]!;
            sink.setFormRecognition({
              kind: "other_revision",
              forms: otherRevision.map((r) => formLabel(r, r.documentRevision)),
              documentRevision: revisionForDisplay(first.documentRevision),
              mapRevision: revisionForDisplay(first.mapRevision),
            });
          } else if (effectiveCompany === "realty") {
            sink.setFormRecognition({ kind: "unknown", forms: [] });
          }
        }

        // ── The sender signs their own lines, here, before anything
        //    is sent (2026-09-16) ───────────────────────────────────
        //
        // A stamp seat has no signer by definition, so until now every
        // one of these came back unplaced and the note told the advisor
        // the seat was one "nobody is in on this send". They are in it.
        // Eighty such lines sit in the Realty atlas.
        //
        // Placed BEFORE the empty-merge return below, because a form
        // that is nothing but the advisor's own acknowledgement has no
        // fields at all and would otherwise drop its signature lines on
        // the floor.
        if (myLines.length > 0) {
          const signed: PrepStamp[] = [];
          const dated: PrepField[] = [];
          for (const line of myLines) {
            if (line.fieldType === "date_signed") {
              // NOT a date-signed control. That control is rendered when
              // a signer signs, and nobody is going to: this one is
              // already signed, by the person looking at the screen. So
              // it prints as a locked box carrying the date — in THEIR
              // clock, which is the one that matches the signature
              // beside it.
              dated.push({
                key: crypto.randomUUID(),
                fromSeat: true,
                signerKey: PREPARER_SIGNER_KEY,
                fieldType: "text",
                pageNumber: line.pageNumber,
                x: line.x,
                y: line.y,
                width: line.width,
                height: line.height,
                required: false,
                value: formatStampDate(new Date()),
                mergeToken: null,
                options: null,
                groupName: null,
              });
              continue;
            }
            signed.push({
              key: crypto.randomUUID(),
              fromSeat: true,
              pageNumber: line.pageNumber,
              x: line.x,
              y: line.y,
              width: line.width,
              height: line.height,
            });
          }
          // Replace rather than append: a re-run of this pass on the
          // same document must not stack a second signature on top of
          // the first. Stamps the advisor placed by hand are kept —
          // they are not ours to remove.
          if (signed.length > 0) {
            sink.setStamps((prev) => [...prev.filter((st) => !st.fromSeat), ...signed]);
          }
          if (dated.length > 0) {
            sink.setFields((prev) => [...prev.filter((f) => !f.fromSeat), ...dated]);
          }
        }

        // A recognized document uses its saved layout ALONE. It is what
        // the team actually did to this exact document last time, so
        // mixing in the rungs' guesses would add fields they had already
        // decided not to place — and the "everything is editable" rule
        // means anything missing is one click away regardless.
        //
        // A RECOGNIZED form (2026-10-13) is the same idea page by page:
        // its map owns the pages it was found on, and only pages no
        // reviewed form covers (a cover letter, an unmapped addendum in
        // the same export) get the rungs' and Stew's guesses.
        //
        // Stew's signatures and dates are seated on the page's own line
        // first (snapSigningMarksToRules, 2026-10-14), so one that found
        // the same line as a ruled-line proposal merges with it instead of
        // floating beside it.
        const seatStew = (list: SuggestedLike[]): SuggestedLike[] => {
          const snapped = snapSigningMarksToRules(list, blanks);
          return list.map((f, i) => (f.source === "stew" ? snapped[i]! : f));
        };
        const ladderUnfolded: SuggestedLike[] = viaRecognition
          ? [
              ...fromServer.filter((f) => f.fromRecognizedForm === true),
              ...mergeSuggestions(
                free.filter((f) => !mappedPages.has(f.pageNumber)),
                seatStew(fromServer.filter((f) => f.fromRecognizedForm !== true && !mappedPages.has(f.pageNumber))),
              ),
            ]
          : fromSavedLayout
            ? fromServer
            : mergeSuggestions(free, seatStew(fromServer));
        // ONE BOX PER STACKED RUN, WHATEVER TAUGHT THE MAP (Grant,
        // 2026-09-29). A map saved before the OS could read a run of
        // lines opens with one box per line on the purchase contract's
        // lines 12–16; this folds those onto the run the page itself
        // draws, so the sender sees one multi-line box and the next save
        // remembers it that way. Narrow by construction — see
        // foldStackedProposals — and never touches a box with a value.
        const ladder: SuggestedLike[] = foldStackedProposals(ladderUnfolded, blanks).fields;
        // ALREADY ANSWERED ON THE PAGE (Nate, 2026-09-25). A form the
        // advisor uploads already filled in — a lender's pre-approval, a
        // counter the other side drafted — has a ruled line under every
        // answer, and every rung above proposes a box on each one. Those
        // boxes default to required, so accepting them would make the
        // client type over answers that are already there before they
        // could sign. Signing-block boxes always survive. A saved layout
        // and a mapping session are left alone: both are a person's
        // deliberate map of a blank form, not a guess about this copy.
        //
        // A RECOGNIZED copy is not a deliberate map of a blank: it is
        // the reviewed map laid over a copy somebody may already have
        // filled, and printing the record's value over the other side's
        // typed answer is exactly the mess this guards against.
        let merged = ladder;
        let writtenOnDropped = 0;
        if ((!fromSavedLayout || viaRecognition) && !mapping) {
          const { kept, droppedCount } = dropProposalsOnWrittenBlanks(ladder, rendered.spans);
          merged = kept;
          writtenOnDropped = droppedCount;
          sink.setWrittenOnCount(droppedCount);
        }
        // A BLOCK SOMEBODY ALREADY SIGNED gets no signing mark, whatever
        // proposed it — the free rungs, Stew, or a map laid over a copy
        // the other side sent back signed (2026-10-13; see
        // dropSigningMarksOnSignedBlocks).
        if (!mapping) {
          const { kept, droppedCount } = dropSigningMarksOnSignedBlocks(merged, rendered.spans);
          merged = kept;
          if (droppedCount > 0) sink.setWrittenOnCount(writtenOnDropped + droppedCount);
        }
        if (merged.length === 0) return;

        // Mapping mode only: rename the generic "Signer 1" / "Signer 2"
        // slots to the roles the document itself uses (Owner, Agent,
        // Broker, ...) the moment a fresh read finds any — never once
        // the advisor has touched the slots themselves (slotsCustomizedRef),
        // and never when a saved layout answered instead (a layout
        // resolves signers by POSITION, not by role word, so there is
        // nothing here for a rename to improve).
        //
        // Computed into a LOCAL below rather than read back from state:
        // setMappingSlots this tick isn't visible to `eligible` until
        // the next render, and the assignment right below needs the
        // roles NOW so a field lands under "Owner" on the very first
        // pass — not under "Signer 1" with the correct label arriving
        // only after the (now-mismatched) assignment already happened.
        let mappingCandidatesThisPass = eligible;
        // The ordinals below index into THIS list. For a real send it is
        // the current selection; for a saved map it is the slot list
        // that map was taught with — see the block directly after.
        let ordinalKeysThisPass = selectedSignerKeysRef.current;
        // SIGNING ROLES (2026-09-15): a hint that names a SEAT ("Buyer",
        // "Co-borrower", "Owner" on a listing) lands on whoever is CAST
        // in that seat — the person, not a name match, which is why it
        // runs before the name matcher below and after a layout's own
        // explicit owner.
        const castingThisPass = mapping ? null : castingRef.current;
        //
        // THREE ANSWERS, NOT TWO (2026-10-13). "Matched a seat that does
        // not sign on this send" (the Buyer on a listing, the Broker) used
        // to come back as null, exactly like "matched nothing", and fell
        // through to the first signer: the Broker's initials and the
        // buyer's lines all landed on the seller.
        type RoleHintMatch =
          | { kind: "signer"; key: string; roleKey: string }
          | { kind: "seat_does_not_sign"; roleKey: string; label: string };
        const matchRoleHintThisPass = (hint: string | null): RoleHintMatch | null => {
          if (!castingThisPass || !hint) return null;
          const role = matchCaptionToRole(hint, castingThisPass.map((seat) => seat.role));
          if (!role) return null;
          const seat = castingThisPass.find((x) => x.role.key === role.key);
          if (!seat) return null;
          const key = seatSignerKeyRef.current(seat);
          return key
            ? { kind: "signer", key, roleKey: role.key }
            : { kind: "seat_does_not_sign", roleKey: role.key, label: role.label };
        };

        // A SAVED MAP BRINGS ITS OWN SIGNER COUNT (Baker, 2026-09-14).
        //
        // The screen starts with two numbered slots. A map taught with
        // four had its Signer 3 and Signer 4 fields dropped on the way
        // in, and then deleted for real by the next save, which wrote
        // the truncated map back. The server widens the slot list to fit
        // the map and hands it back here; adopting it is what makes
        // "close and reopen" show what was actually saved.
        //
        // Set into a LOCAL as well as state for the same reason the
        // role-rename below does: setMappingSlots is not visible to the
        // assignment a few lines down until the next render, and that
        // assignment needs the widened list NOW.
        if (layoutSlots && layoutSlots.length > 0) {
          const adopted: MappingSlot[] = layoutSlots;
          setMappingSlots(adopted);
          mappingCandidatesThisPass = candidatesFromSlots(adopted);
          ordinalKeysThisPass = adopted.map((sl) => sl.id);
        }

        if (mapping && !fromSavedLayout && !slotsCustomizedRef.current) {
          const roleSlots = slotsFromSignerHints(
            merged.map((sg) => sg.signerHint ?? null),
            MAX_MAPPING_SLOTS,
            mappingCatalogRef.current,
          );
          if (roleSlots.length > 0) {
            setMappingSlots(roleSlots);
            mappingCandidatesThisPass = candidatesFromSlots(roleSlots);
          }
        }

        // EVERY proposal is fitted to its page here, whatever rung made
        // it — the free rungs, Stew, or a saved layout recorded before
        // this existed. A human's placements are clamped on every path
        // they can take; this is the suggestion path's equivalent, and
        // skipping it is what made "Accept all → Send" fail on the AAR
        // purchase contract every time (staging round 3, 2026-08-23).
        const all = fitSuggestionsToPages(
          merged,
          rendered.pages.map((p) => ({
            pageNumber: p.pageNumber,
            width: p.widthPt,
            height: p.heightPt,
          })),
        ) as SuggestedLike[];

        // Every proposal needs an owner. The signer hint is matched
        // against the people actually on this record, by seat first and
        // then by name.
        //
        // WHAT NOBODY CLAIMED (2026-10-13). Until now anything unmatched
        // went to the active signer — the first person on the send — so
        // on a form whose captions name nobody, ONE person got every box
        // (Nate's Addendum: Daniel 30, Amelia none). Now:
        //   * a text box, checkbox or multi-line box nobody claimed is
        //     the SENDER's, a ghost until accepted, typed, or handed to
        //     a signer — the redesign's own rule (docs/design/
        //     esign-prepare-and-send-redesign.md), never a client's by
        //     default;
        //   * signature and date lines nobody claimed are dealt out, one
        //     block per signer in seat order (distributeSigningBlocks);
        //   * a signing mark whose caption names a seat that doesn't sign
        //     on this send is left off rather than given to someone else;
        //   * initials keep the old default (every signer initials, so
        //     there is no fair way to deal them).
        // Teaching a blank form (mapping) keeps the old rule: its slots
        // are placeholders, not people.
        const isSigningMark = (t: EsignFieldType) => t === "signature" || t === "date_signed";
        let skippedForSeats = 0;
        // Signers in seat order, all of them, and per FAMILY of seats
        // (seller_1 / seller_2 are the "seller" family).
        const signingSeats = (castingThisPass ?? [])
          .filter((seat) => seat.role.kind === "signs")
          .map((seat) => ({ family: seat.role.key.replace(/_\d+$/, ""), key: seatSignerKeyRef.current(seat) }))
          .filter((x): x is { family: string; key: string } => Boolean(x.key));
        const familyKeys = (family: string) => [...new Set(signingSeats.filter((x) => x.family === family).map((x) => x.key))];
        /** A hint that names a role with no number ("Seller", not
         *  "Seller 2" or "Seller (2)"). */
        const bareRoleHint = (hint: string | null | undefined) => !!hint && !/\d/.test(hint);
        type Owner = { key: string; roleKey?: string | null } | { deal: string } | null;
        const owners: Owner[] = all.map((sg) => {
          const byIndex =
            typeof sg.signerIndex === "number" && sg.signerIndex >= 0
              ? ordinalKeysThisPass[sg.signerIndex]
              : undefined;
          const explicit = sg.signerKey ?? byIndex;
          if (explicit) return { key: explicit };
          const seat = matchRoleHintThisPass(sg.signerHint);
          if (seat?.kind === "signer") {
            // SEVERAL "SELLER" BLOCKS ARE SELLER 1 AND SELLER 2 (Grant,
            // 2026-10-14). A bare role word on a signature or date names
            // the role, not which of its people: every block on Nate's
            // Addendum came back "Seller" (Stew read it off the tick
            // boxes) and all of them landed on Seller 1. They are dealt
            // across that role's seats in reading order instead.
            if (!mapping && isSigningMark(sg.fieldType) && bareRoleHint(sg.signerHint)) {
              const family = seat.roleKey.replace(/_\d+$/, "");
              if (familyKeys(family).length > 1) return { deal: family };
            }
            return { key: seat.key, roleKey: seat.roleKey };
          }
          if (!mapping && seat?.kind === "seat_does_not_sign") {
            if (sg.fieldType === "signature" || sg.fieldType === "initials" || sg.fieldType === "date_signed") {
              skippedForSeats += 1;
              return null;
            }
            return { key: PREPARER_SIGNER_KEY, roleKey: seat.roleKey };
          }
          const byName = matchSignerHint(sg.signerHint, mappingCandidatesThisPass);
          if (byName) return { key: byName };
          if (mapping) return activeSignerIdRef.current ? { key: activeSignerIdRef.current } : null;
          if (isSigningMark(sg.fieldType)) return { deal: "" };
          if (sg.fieldType === "initials") {
            return activeSignerIdRef.current ? { key: activeSignerIdRef.current } : null;
          }
          return {
            key: PREPARER_SIGNER_KEY,
            // A box that prints a PERSON (their legal name, say) still
            // needs to know whose: the form's first signing seat, the
            // same default a hand-placed box gets.
            roleKey:
              sg.mergeToken && wantsDefaultSeat(sg.mergeToken, true, null)
                ? (firstSeatOf(rolesRef.current)?.key ?? null)
                : null,
          };
        });
        // Everyone signing this send, in seat order; with no cast (a
        // household send), the selected signers.
        const signersInSeatOrder = (() => {
          const list = signingSeats.length > 0 ? signingSeats.map((x) => x.key) : selectedSignerKeysRef.current;
          return [...new Set(list)].filter((k) => k !== PREPARER_SIGNER_KEY);
        })();
        // Deal each pool: a role family's blocks across that family's
        // seats, and blocks naming nobody across everyone signing.
        const pools = new Map<string, number[]>();
        owners.forEach((o, i) => {
          if (o && "deal" in o) pools.set(o.deal, [...(pools.get(o.deal) ?? []), i]);
        });
        const blocksPerPage = new Map<number, Map<string, number>>();
        let extraBlocks = 0;
        for (const [pool, indexes] of pools) {
          const keys = pool ? familyKeys(pool) : signersInSeatOrder;
          const dealt = distributeSigningBlocks(
            indexes.map((i) => all[i]!),
            keys,
          );
          indexes.forEach((i, k) => {
            const sg = all[i]!;
            const key = dealt.get(k) ?? activeSignerIdRef.current;
            if (!key) {
              owners[i] = null;
              return;
            }
            // One block per signer per page: four blocks and two signers
            // leaves two empty rather than a second line nobody needs.
            if (sg.fieldType === "signature") {
              const page = blocksPerPage.get(sg.pageNumber) ?? new Map<string, number>();
              const n = (page.get(key) ?? 0) + 1;
              page.set(key, n);
              blocksPerPage.set(sg.pageNumber, page);
              if (keys.length > 0 && n > 1) {
                extraBlocks += 1;
                owners[i] = null;
                return;
              }
            }
            owners[i] = { key };
          });
        }
        // A DATE GOES WITH THE SIGNATURE BESIDE IT, whoever proposed
        // either: the nearest signature to its left on the same line
        // decides its owner, and a date whose signature was left off is
        // left off too.
        all.forEach((sg, i) => {
          if (sg.fieldType !== "date_signed") return;
          const own = owners[i];
          if (own && "key" in own && sg.signerKey) return; // a map said whose
          const partner = all
            .map((o, j) => ({ o, j }))
            .filter(
              ({ o }) =>
                o.fieldType === "signature" &&
                o.pageNumber === sg.pageNumber &&
                Math.abs(o.y + o.height - (sg.y + sg.height)) <= 10 &&
                o.x < sg.x &&
                sg.x - (o.x + o.width) < 80,
            )
            .sort((a, b) => b.o.x - a.o.x)[0];
          if (!partner) return;
          const po = owners[partner.j];
          owners[i] = po && "key" in po ? { key: po.key } : null;
        });
        if (extraBlocks > 0 || skippedForSeats > 0) {
          const parts: string[] = [];
          if (extraBlocks > 0) {
            parts.push(
              `${extraBlocks} more signature ${extraBlocks === 1 ? "line was" : "lines were"} left empty: everyone signing already has one. Add a box by hand if someone else signs there.`,
            );
          }
          if (skippedForSeats > 0) {
            parts.push(
              `${skippedForSeats} signing ${skippedForSeats === 1 ? "mark belongs" : "marks belong"} to someone who isn't signing this send, so ${skippedForSeats === 1 ? "it was" : "they were"} left off.`,
            );
          }
          sink.setSuggestNote(parts.join(" "));
        }

        sink.setFields((prev) => [
          ...prev,
          ...all.flatMap((sg, index) => {
            // A saved layout already knows WHICH signer, by position —
            // that is more reliable than matching a role word, so it
            // wins when present.
            // A saved layout now says outright whose box this is, so
            // nobody counts into a signer list to find out. That
            // counting is what broke: a locked box belongs to nobody and
            // is stored at position -1, and counting to -1 finds nothing
            // — so the box either vanished or fell through to whoever
            // happened to be selected (Baker, 2026-09-15). The ordinal
            // lookup stays underneath for proposals that carry no owner,
            // which is every rung of the ladder except a saved layout.
            const owner = owners[index];
            if (!owner || "deal" in owner) return [];
            const signerKey = owner.key;
            const roleKey = sg.roleKey ?? owner.roleKey ?? null;
            return [
              // A tall text box with no rules under it wraps (2026-10-27,
              // shared/esign/multiline-text.ts openBoxLinePitch).
              withOpenBoxWrap({
                key: crypto.randomUUID(),
                signerKey,
                fieldType: sg.fieldType,
                pageNumber: sg.pageNumber,
                x: sg.x,
                y: sg.y,
                width: sg.width,
                height: sg.height,
                required: sg.required ?? true,
                // A layout carries the text somebody typed last time —
                // offering it again is the point of the feature.
                value: sg.value ?? null,
                // …and, when the field was BOUND, the binding rather
                // than the text: this send resolves it against today's
                // record, not the one that taught the layout.
                mergeToken: sg.mergeToken ?? null,
                // The type size the rung read off the page beside this
                // blank, when it read one. Absent from a saved layout
                // (which stores no size) and from a rung with nothing to
                // measure; the send falls back to the page's own body
                // type there, so nothing is left to the provider.
                //
                // Spread rather than assigned: `exactOptionalPropertyTypes`
                // draws a real distinction between a key that is MISSING
                // and one explicitly set to undefined, and "nobody read a
                // size here" is the missing one.
                ...(sg.fontSize !== undefined ? { fontSize: sg.fontSize } : {}),
                // A box spanning several printed lines keeps its pitch, so
                // the page can draw its text line by line on the rules.
                ...(sg.multiline === true && typeof sg.linePitch === "number"
                  ? {
                      multiline: true,
                      linePitch: sg.linePitch,
                      ...(typeof sg.firstLineIndent === "number" && sg.firstLineIndent > 0
                        ? { firstLineIndent: sg.firstLineIndent }
                        : {}),
                    }
                  : {}),
                ...(parseTextStyle(sg.textStyle) ? { textStyle: parseTextStyle(sg.textStyle) as EsignTextStyle } : {}),
                // A remembered form brings its own wording back with it:
                // the choices it offered and how its buttons were grouped.
                options: sg.options ?? null,
                groupName: sg.groupName ?? null,
                // …and how the blank fills, plus the words beside it,
                // which is what the "only what you know" list reads.
                fillRule: sg.fillRule ?? null,
                // The seat a locked box prints its person from. Worked
                // out above (and by the server for a map) and then, until
                // 2026-10-13, dropped right here.
                ...(roleKey ? { roleKey } : {}),
                ...(sg.term ? { term: sg.term } : {}),
                caption: sg.signerHint ?? null,
                // A SIGNING MARK IS NEVER A GHOST (2026-09-29). The
                // signature, initials, date-signed and printed-name
                // boxes are what the document is being sent for, so
                // they are placed for real the moment the ladder finds
                // them. Every other suggestion keeps its marker and is
                // a ghost: drawn, offered, and not sent until the sender
                // accepts, types, or hands it to a signer. See
                // shared/esign/ghost-offers.ts.
                ...(isSigningBlockField(sg)
                  ? {}
                  : {
                      proposal: {
                        source: sg.source,
                        confidence: sg.confidence,
                        reason: sg.reason,
                      },
                    }),
              } satisfies PrepField),
            ];
          }),
        ]);
      } finally {
        sink.setSuggesting(false);
      }
    },
    [
      mapping,
      mappingSlots,
      recordKind,
      recordId,
      effectiveCompany,
      activeSink,
      eligible,
      candidatesFromSlots,
      activeSignerId,
      firstSignerContactId,
      ensureMergeOptions,
    ],
  );

  // The standalone lanes (household / contact) open with NO company —
  // so the first suggestion run above asks the server with none, and
  // the company-scoped saved-layout lookup can't match. Until
  // 2026-08-30, choosing the company afterwards never re-asked: the
  // advisor who picked "Realty" a second later got Stew's cold read
  // instead of the layout their company saved last time. This re-runs
  // the ladder ONCE, on the null → chosen transition — and only while
  // nothing has been placed or accepted yet, because re-proposing over
  // an advisor's own work would fight them (their fields stand; they
  // can still place anything by hand).
  const prevCompanyRef = useRef<string | null>(effectiveCompany);
  useEffect(() => {
    const prev = prevCompanyRef.current;
    if (prev !== null || effectiveCompany === null) {
      prevCompanyRef.current = effectiveCompany;
      return;
    }
    // Chosen mid-run (or before the document rendered): leave the
    // transition unconsumed and let the effect retry when `suggesting`
    // flips off / the render lands — both are dependencies.
    const rendered = renderedRef.current;
    if (!rendered || suggesting) return;
    prevCompanyRef.current = effectiveCompany;
    if (fields.some((f) => !f.proposal)) return;
    // Drop the company-less proposals so the re-run replaces rather
    // than duplicates them (runSuggestions appends).
    setFields((cur) => cur.filter((f) => !f.proposal));
    void runSuggestions(rendered);
    // The other documents were read company-less too. Any of them with
    // nothing accepted yet is forgotten here, and the background reader
    // reads it again with the company known.
    let dropped = false;
    for (const [id, st] of parkedRef.current) {
      if (id === documentId || st.fields.some((f) => !f.proposal)) continue;
      parkedRef.current.delete(id);
      dropped = true;
    }
    if (dropped) bumpParked();
  }, [effectiveCompany, fields, suggesting, runSuggestions, documentId, bumpParked]);

  // ── READ EVERY DOCUMENT IN THE PACKAGE (2026-09-29, build PR 3) ────
  //
  // The active document loads first, exactly as before. Once its ladder
  // has run, the rest are read one at a time in the background: fetch,
  // draw, run the same ladder through a draft sink, park the draft. A
  // saved layout costs nothing; Stew only reads a document it does not
  // recognize, and only one at a time, so the document in front of the
  // sender is never slowed by the ones behind it.
  //
  // Never in mapping mode (one template, nothing to read beside it) and
  // never for a single document (nothing to read either).
  const readDocumentInBackground = useCallback(
    async (docId: string): Promise<ParkedDocumentState> => {
      // The same two clocks the active load keeps, for the same reason:
      // a stalled document store must not read as a render fault.
      const fetchAbort = new AbortController();
      const fetchTimer = setTimeout(() => fetchAbort.abort(), 30_000);
      let res: Response;
      try {
        res = await fetch(`/api/esign/documents/${encodeURIComponent(docId)}/bytes`, {
          credentials: "include",
          signal: fetchAbort.signal,
        });
      } catch (err) {
        if (fetchAbort.signal.aborted) {
          throw new Error("Couldn't get the document file — the document store didn't answer.");
        }
        throw err;
      } finally {
        clearTimeout(fetchTimer);
      }
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { message?: string } | null;
        throw new Error(body?.message ?? "Couldn't load the document.");
      }
      const rendered = await renderPdfPages(await res.arrayBuffer());
      const draft = emptyParkedState(rendered);
      const sink: SuggestSink = {
        documentId: docId,
        getFingerprint: () => draft.fingerprint,
        setFingerprint: (v) => {
          draft.fingerprint = v;
        },
        // The panel's "Reading the document" is about the document on
        // screen; a background read says where it is through the rail.
        setSuggesting: () => {},
        setSuggestNote: (v) => {
          draft.suggestNote = v;
        },
        setWrittenOnCount: (n) => {
          draft.writtenOnCount = n;
        },
        setAppliedLayout: (v) => {
          draft.appliedLayout = v;
        },
        setAppliedLayoutInfo: (v) => {
          draft.appliedLayoutInfo = v;
        },
        setLayoutPositionsUnchecked: (v) => {
          draft.layoutPositionsUnchecked = v;
        },
        setFormRecognition: (v) => {
          draft.formRecognition = v;
        },
        setDetectedBlanks: (v) => {
          draft.detectedBlanks = v;
        },
        setStamps: (update) => {
          draft.stamps = update(draft.stamps);
        },
        setFields: (update) => {
          draft.fields = update(draft.fields);
        },
      };
      await runSuggestions(rendered, sink);
      return draft;
    },
    [runSuggestions],
  );
  useEffect(() => {
    if (!open || !isPackage || mapping) return;
    // The active document first: its pages drawn and its ladder done.
    // A first document that could not be loaded is not a reason to
    // leave the others unread; its own error stays on screen.
    if ((!pages && !loadError) || suggesting) return;
    let cancelled = false;
    const gen = openGenRef.current;
    void (async () => {
      for (const d of packageDocs) {
        if (cancelled) return;
        const id = d.documentId;
        if (id === activeDocIdRef.current || parkedRef.current.has(id)) continue;
        if (readFailuresRef.current[id]) continue;
        // A read this effect's previous run started is still going:
        // wait for it rather than starting a second one beside it.
        const running = inFlightRef.current.get(id);
        if (running) {
          await running.catch(() => undefined);
          continue;
        }
        setReadingDocId(id);
        const read = readDocumentInBackground(id);
        inFlightRef.current.set(id, read);
        try {
          const state = await read;
          if (openGenRef.current !== gen) return;
          // The load effect joins this promise for the document on
          // screen and lands it itself, so the map is left alone here
          // for that one; and a document parked in the meantime (opened,
          // worked on, switched away from) is never overwritten.
          if (id !== activeDocIdRef.current && !parkedRef.current.has(id)) {
            parkedRef.current.set(id, state);
            bumpParked();
          }
        } catch (err) {
          if (openGenRef.current !== gen) return;
          setReadFailures((prev) => ({
            ...prev,
            [id]: err instanceof Error ? err.message : "Couldn't load the document.",
          }));
        } finally {
          inFlightRef.current.delete(id);
        }
      }
      if (!cancelled) setReadingDocId(null);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- parkedVersion re-arms the queue after a parked document is forgotten
  }, [open, isPackage, mapping, pages, loadError, suggesting, packageDocs, readDocumentInBackground, parkedVersion, bumpParked]);


  // WHAT IS STILL UNMAPPED ON THIS FORM.
  //
  // Nate, 2026-09-08, on the AAR listing contract: "Fields are still not
  // mapped well." The screen had no way to say that, or to be wrong
  // about it visibly — a form with 68 blanks nobody had placed looked
  // exactly like a form with none. This is that answer, and it is
  // measured against EVERY field on the page whatever put it there, so
  // it keeps counting down as a person works.
  const unmapped = useMemo(
    () => (suggesting ? [] : unmappedBlanks(detectedBlanks, fields)),
    [suggesting, detectedBlanks, fields],
  );
  const unmappedByPage = useMemo(() => {
    const map = new Map<number, DetectedBlank[]>();
    for (const b of unmapped) {
      const list = map.get(b.pageNumber);
      if (list) list.push(b);
      else map.set(b.pageNumber, [b]);
    }
    return map;
  }, [unmapped]);

  /** Fill one detected blank with a field of this type, at the geometry
   *  the FORM drew — no dragging, no sizing, no eyeballing. This is the
   *  edge-case path: the blank Stew could not name, done in one click
   *  and one pick. */
  const fillBlank = useCallback(
    (blank: DetectedBlank, fieldType: EsignFieldType, mergeToken: string | null) => {
      if (!activeSignerId) return;
      setFields((prev) => [
        ...prev,
        {
          key: crypto.randomUUID(),
          signerKey: activeSignerId,
          fieldType,
          pageNumber: blank.pageNumber,
          x: blank.x,
          // Seated on the rule the same way a proposal is, so a blank
          // filled by hand and one the OS proposed sit identically. A RUN
          // of lines filled as text takes the whole run and its pitch
          // (Grant, 2026-09-29); filled as anything else (a signature
          // across two lines is not a thing) it sits on the first rule.
          y:
            blank.kind === "box"
              ? blank.y
              : isRun(blank) && fieldType === "text"
                ? blank.y
                : Math.max(0, blank.lineY - FILLED_HEIGHT[fieldType] + 2),
          width: blank.kind === "box" ? blank.width : blank.width,
          height:
            blank.kind === "box"
              ? blank.height
              : isRun(blank) && fieldType === "text"
                ? blank.height
                : FILLED_HEIGHT[fieldType],
          required: true,
          value: null,
          ...(isRun(blank) && fieldType === "text"
            ? {
                multiline: true,
                linePitch: blank.linePitch,
                ...(blank.firstLineIndent ? { firstLineIndent: blank.firstLineIndent } : {}),
              }
            : {}),
          ...(fieldType === "dropdown" ? { options: ["Yes", "No"] } : {}),
          ...(fieldType === "radio"
            ? { groupName: lastRadioGroupFor(fields, activeSignerId) }
            : {}),
          ...(mergeToken ? { mergeToken } : {}),
        } satisfies PrepField,
      ]);
      setFillingBlank(null);
    },
    [activeSignerId, fields],
  );

  /** Bring the next unmapped blank into view. Finishing a form should be
   *  a checklist, not a hunt through eight pages for the one line nobody
   *  placed. */
  const unmappedFirstPage = unmapped[0]?.pageNumber ?? null;

  const goToNextUnmapped = useCallback(() => {
    const next = unmapped[0];
    if (!next) return;
    const el = blankRefs.current.get(blankKey(next));
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
    setFillingBlank(next);
  }, [unmapped]);

  /** How big a point is on screen right now. "fit" measures the document
   *  area against the first page; a number is the sender's own choice.
   *  Applied as CSS `zoom` on each page, so the canvas, the overlay and
   *  every field scale together and nothing here is repositioned — the
   *  pointer handlers divide the scale back out (pxToPt) and that is the
   *  only arithmetic zoom adds. */
  const displayScale = useMemo(() => {
    if (zoom !== "fit") return zoom;
    const first = pages?.[0];
    return first ? fitToWidthScale(docAreaWidth, first.widthPt) : 1;
  }, [zoom, pages, docAreaWidth]);
  useEffect(() => {
    const el = docAreaRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const measure = () => setDocAreaWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
    // Re-measured when the panel or the rail changes size, which is what
    // makes "fit" mean "fit the room you have now".
  }, [panelCollapsed, railExpanded, open]);

  // Mount rendered canvases (imperatively — same pattern as the email
  // attachment preview: the canvases are created outside React).
  //
  // DECISION: each page frame pulls its own canvas in the moment it
  // mounts (the ref callback), not only when `pages` changes. The pages
  // can finish loading while the frames do not exist yet: an offer opened
  // from an opportunity starts on "Signers & order", the PDF renders in
  // the background, and pressing Next mounts brand-new, empty frames.
  // With an effect keyed on `pages` alone, nothing ever filled them: every
  // page collapsed to zero size and its fields piled up in one heap
  // (Grant, 2026-10-15). The effect stays for the opposite order (frames
  // first, pages after).
  // HELP-OK: rendering fix only; no label, button or step a help article names changed.
  const pagesRef = useRef<RenderedPdfPage[] | null>(pages);
  pagesRef.current = pages;
  const attachCanvas = useCallback((host: HTMLDivElement, page: RenderedPdfPage) => {
    if (page.canvas.parentElement === host) return;
    host.innerHTML = "";
    // CSS size = points. The page wrapper's `zoom` scales this and
    // the overlay together, so screen px == PDF pt × displayScale.
    page.canvas.style.width = `${page.widthPt}px`;
    page.canvas.style.height = `${page.heightPt}px`;
    host.appendChild(page.canvas);
  }, []);
  const mountCanvas = useCallback(
    (pageNumber: number, host: HTMLDivElement | null) => {
      if (!host) {
        pageRefs.current.delete(pageNumber);
        return;
      }
      pageRefs.current.set(pageNumber, host);
      const page = pagesRef.current?.find((p) => p.pageNumber === pageNumber);
      if (page) attachCanvas(host, page);
    },
    [attachCanvas],
  );
  useEffect(() => {
    if (!pages) return;
    for (const page of pages) {
      const host = pageRefs.current.get(page.pageNumber);
      if (host) attachCanvas(host, page);
    }
  }, [pages, attachCanvas]);

  // ── Field interactions ─────────────────────────────────────────
  function handleOverlayClick(page: RenderedPdfPage, e: React.MouseEvent<HTMLDivElement>) {
    if (placingStamp && mySignature) {
      const overlay = e.currentTarget.getBoundingClientRect();
      const clickX = pxToPt(e.clientX - overlay.left, displayScale);
      const clickY = pxToPt(e.clientY - overlay.top, displayScale);
      const aspect = mySignature.naturalHeight / mySignature.naturalWidth;
      const width = STAMP_DEFAULT_WIDTH_PT;
      const height = Math.min(STAMP_MAX_HEIGHT_PT, Math.max(STAMP_MIN_HEIGHT_PT, width * aspect));
      const rect = clampToPage(
        { x: clickX - width / 2, y: clickY - height / 2, width, height },
        page,
      );
      const stamp: PrepStamp = { key: crypto.randomUUID(), pageNumber: page.pageNumber, ...rect };
      setStamps((prev) => [...prev, stamp]);
      setSelectedStampKey(stamp.key);
      setSelectedKey(null);
      return;
    }
    if (!placingType) {
      setSelectedKey(null);
      setSelectedStampKey(null);
      return;
    }
    // A locked box needs no signer — that is the point of it. Nor does a
    // record value placed while mapping (isMappedRecordValue).
    if (
      !placingLocked &&
      !activeSignerId &&
      !isMappedRecordValue({
        mapping: Boolean(mapping),
        placingLocked,
        placingType,
        mergeToken: pendingMergeToken,
        isPersonScoped: isPersonScopedToken,
      })
    )
      return;
    const overlay = e.currentTarget.getBoundingClientRect();
    const rect = placeAtPoint(
      placingType,
      {
        x: pxToPt(e.clientX - overlay.left, displayScale),
        y: pxToPt(e.clientY - overlay.top, displayScale),
      },
      page,
    );
    // A RECORD VALUE PLACED WHILE MAPPING IS OURS TO FILL (Grant, 2026-10-27).
    // Nate mapped the Commission Disbursement Authorization with "Placing for"
    // on Buyer 1, so every "From record" box (escrow company, price,
    // commission) was saved as Buyer 1's; on a listing nobody is Buyer 1 and
    // all ten were dropped from the send. A record-level value is filled by
    // the OS, never typed by a signer, so while mapping it belongs to nobody
    // whoever "Placing for" names. A token about one PERSON (their printed
    // name, their email) still belongs to the signer it is placed for.
    const recordValueWhileMapping = isMappedRecordValue({
      mapping: Boolean(mapping),
      placingLocked,
      placingType,
      mergeToken: pendingMergeToken,
      isPersonScoped: isPersonScopedToken,
    });
    const owner = placingLocked || recordValueWhileMapping ? PREPARER_SIGNER_KEY : activeSignerId!;
    // FIXED TEXT (Nate, 2026-10-26: "permanently add our address and wire
    // instructions to the blank PDF"). While mapping, a locked text box is
    // placed as a roomy multi-line box, because what goes in one is an address
    // block, not a single figure. The multi-line path already prints, saves
    // and reapplies (multiline-text.ts, layout-apply.ts), so this adds no new
    // field type; it only starts the box in the shape that job needs.
    const fixedText = mapping && placingLocked && placingType === "text" && !placingStrike;
    const placedRect = fixedText
      ? placeAtPoint("text", { x: pxToPt(e.clientX - overlay.left, displayScale), y: pxToPt(e.clientY - overlay.top, displayScale) }, page, FIXED_TEXT_SIZE)
      : rect;
    const field: PrepField = {
      key: crypto.randomUUID(),
      signerKey: owner,
      fieldType: placingType,
      pageNumber: page.pageNumber,
      ...(fixedText ? { multiline: true, linePitch: FIXED_TEXT_LINE_PITCH_PT } : {}),
      ...(placingLocked && placingStrike ? strikeRect(rect, page) : placedRect),
      required: true,
      value: placingLocked && placingType === "checkbox" ? BOLDSIGN_CHECKBOX_ON : null,
      ...(placingLocked && placingStrike ? { strike: true } : {}),
      // A pick-one with no choices and a choose-one with no group are
      // both refused at send, so neither is allowed to exist in an
      // invalid state even for a moment — placing one gives it a working
      // default the advisor then edits. "Yes / No" is the overwhelmingly
      // common pair on the forms these replace.
      ...(placingType === "dropdown" ? { options: ["Yes", "No"] } : {}),
      // A new choose-one joins the group of the LAST one placed for this
      // signer. Buttons get placed in pairs ("I elect" / "I decline"),
      // and the pair is what a group is — so grouping them is right far
      // more often than not, and the inspector shows the group and lets
      // you start a new one in a click.
      ...(placingType === "radio" && activeSignerId
        ? { groupName: lastRadioGroupFor(fields, activeSignerId) }
        : {}),
      // Armed via the "Record field" choice — the field is placed
      // ALREADY bound, so merging OS data is one motion (choose the
      // field, click the page) rather than two (place a plain Text
      // box, then separately discover it can be bound).
      ...(placingType === "text" && pendingMergeToken ? { mergeToken: pendingMergeToken } : {}),
      // A LOCKED box bound to a person's field starts on the form's
      // first signing seat rather than "Nobody yet" (Baker, 2026-09-18:
      // he placed one expecting it to print signer 1 and it printed
      // nothing, then blocked the send). The seat is still just a
      // starting guess — the "Whose?" picker changes it to Co-borrower,
      // Buyer 2, or off entirely — but a box someone just placed by
      // hand should open on the answer that is right most of the time,
      // the same reasoning as the Yes/No default above. Atlas-placed
      // boxes already carry their own seat and never pass through here
      // (wantsDefaultSeat — a newly placed box always starts unseated).
      ...(placingType === "text" &&
      pendingMergeToken &&
      wantsDefaultSeat(pendingMergeToken, placingLocked, null)
        ? { roleKey: firstSeatOf(roles)?.key ?? null }
        : {}),
    };
    setFields((prev) => [...prev, field]);
    setSelectedKey(field.key);
    setSelectedStampKey(null);
  }

  /** Drag a box around a group of fields to select all of them at once
   *  (Joey, 2026-09-18). Only fires on the overlay's own background —
   *  pointerdown on a field or an unmapped blank already stops there
   *  (startDrag / the blank button both call stopPropagation), so this
   *  never fights either of those for the same gesture. A plain click
   *  with no real movement changes nothing here and falls through to
   *  the ordinary click handler above, which still clears the selection
   *  or places a field exactly as it always has. */
  function handleOverlayPointerDown(page: RenderedPdfPage, e: React.PointerEvent<HTMLDivElement>) {
    if (placingType || placingStamp) return; // placement flow owns the click here
    if (e.button !== 0) return; // left button only
    // Stops the browser's own text-selection/drag-ghost while marqueeing
    // — same as startDrag/startDragStamp already do on their own
    // pointerdown. Doesn't suppress the click that follows on a plain,
    // no-movement press (that's a mouse-pointer thing, not a Pointer
    // Events default), which is what lets the ordinary click handler
    // still run when nothing was actually dragged.
    e.preventDefault();
    const overlay = e.currentTarget.getBoundingClientRect();
    const originX = pxToPt(e.clientX - overlay.left, displayScale);
    const originY = pxToPt(e.clientY - overlay.top, displayScale);
    const additive = e.shiftKey || e.metaKey || e.ctrlKey;
    marqueeDragRef.current = { pageNumber: page.pageNumber, originX, originY, additive, rect: null };
    const MOVE_THRESHOLD = 3;
    const onMove = (ev: PointerEvent) => {
      const drag = marqueeDragRef.current;
      if (!drag) return;
      const curX = pxToPt(ev.clientX - overlay.left, displayScale);
      const curY = pxToPt(ev.clientY - overlay.top, displayScale);
      if (!drag.rect && Math.abs(curX - drag.originX) < MOVE_THRESHOLD && Math.abs(curY - drag.originY) < MOVE_THRESHOLD) {
        return;
      }
      const rect = {
        x: Math.min(drag.originX, curX),
        y: Math.min(drag.originY, curY),
        width: Math.abs(curX - drag.originX),
        height: Math.abs(curY - drag.originY),
      };
      drag.rect = rect;
      setMarqueeRect({ pageNumber: drag.pageNumber, ...rect });
    };
    const onUp = () => {
      const drag = marqueeDragRef.current;
      marqueeDragRef.current = null;
      setMarqueeRect(null);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      // No real drag happened — let the click event that follows this
      // pointerup run its ordinary course (clear selection / place a
      // field), same as before this gesture existed.
      if (!drag || !drag.rect) return;
      const hit = fieldsInRect(fields, { pageNumber: drag.pageNumber, ...drag.rect });
      setSelectedStampKey(null);
      setSelectedKeysState((prev) => {
        const next = drag.additive ? new Set(prev) : new Set<string>();
        for (const f of hit) next.add(f.key);
        return next;
      });
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }

  function startDragStamp(e: React.PointerEvent, stamp: PrepStamp, kind: DragState["kind"]) {
    e.stopPropagation();
    e.preventDefault();
    // A stamped signature is part of the file once sent (PR 10b).
    if (correction) return;
    setSelectedStampKey(stamp.key);
    setSelectedKey(null);
    dragRef.current =
      kind === "move"
        ? { kind, key: stamp.key, startX: e.clientX, startY: e.clientY, origX: stamp.x, origY: stamp.y }
        : {
            kind,
            key: stamp.key,
            startX: e.clientX,
            startY: e.clientY,
            origW: stamp.width,
            origH: stamp.height,
          };
    const onMove = (ev: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const dx = pxToPt(ev.clientX - drag.startX, displayScale);
      const dy = pxToPt(ev.clientY - drag.startY, displayScale);
      setStamps((prev) =>
        prev.map((s) => {
          if (s.key !== drag.key) return s;
          const page = pages?.find((p) => p.pageNumber === s.pageNumber);
          if (!page) return s;
          if (drag.kind === "move") {
            return { ...s, ...clampToPage({ ...s, x: drag.origX + dx, y: drag.origY + dy }, page) };
          }
          const width = Math.max(12, drag.origW + dx);
          const height = Math.max(12, drag.origH + dy);
          return { ...s, ...clampToPage({ x: s.x, y: s.y, width, height }, page) };
        }),
      );
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }

  /** Shift/Cmd/Ctrl-click a field to add or remove it from the
   *  selection without disturbing the rest — no drag follows a modifier
   *  click, it's a discrete toggle. A plain pointerdown falls through
   *  to startDrag, which decides there whether this grabs just the one
   *  field or the whole group it's already part of. */
  function handleFieldPointerDown(e: React.PointerEvent, field: PrepField) {
    if (isLockedInCorrection(field)) {
      e.stopPropagation();
      e.preventDefault();
      return;
    }
    if (e.shiftKey || e.metaKey || e.ctrlKey) {
      e.stopPropagation();
      e.preventDefault();
      setSelectedStampKey(null);
      toggleFieldSelection(field.key);
      return;
    }
    startDrag(e, field, "move");
  }

  function startDrag(e: React.PointerEvent, field: PrepField, kind: FieldDragState["kind"]) {
    e.stopPropagation();
    e.preventDefault();
    if (isLockedInCorrection(field)) return;
    // Grabbing a field that's already one of SEVERAL selected moves the
    // whole group together — the point of building a selection at all
    // (Joey, 2026-09-18). Grabbing one outside the current selection
    // (or resizing, which is single-field only — the handle only shows
    // up when exactly one is selected) replaces the selection with just
    // it, same as a plain click always has.
    const wasMultiSelected = kind === "move" && selectedKeys.size > 1 && selectedKeys.has(field.key);
    if (!wasMultiSelected) setSelectedKey(field.key);
    if (kind === "resize") {
      fieldDragRef.current = {
        kind: "resize",
        key: field.key,
        startX: e.clientX,
        startY: e.clientY,
        origW: field.width,
        origH: field.height,
      };
    } else {
      const keys = wasMultiSelected ? [...selectedKeys] : [field.key];
      const origins = new Map(
        fields.filter((f) => keys.includes(f.key)).map((f) => [f.key, { x: f.x, y: f.y }] as const),
      );
      fieldDragRef.current = { kind: "move", keys, startX: e.clientX, startY: e.clientY, origins };
    }
    // A click that never really moved, on a field that WAS part of a
    // multi-selection, narrows the selection down to just that field —
    // the same "click picks just this one" every other tool does.
    // Dragging the group, or a fresh modifier-click, are the two ways
    // to keep or build a multi-selection.
    let moved = false;
    const MOVE_THRESHOLD = 2;
    const onMove = (ev: PointerEvent) => {
      const drag = fieldDragRef.current;
      if (!drag) return;
      const dx = pxToPt(ev.clientX - drag.startX, displayScale);
      const dy = pxToPt(ev.clientY - drag.startY, displayScale);
      if (Math.abs(dx) > MOVE_THRESHOLD || Math.abs(dy) > MOVE_THRESHOLD) moved = true;
      // A CLICK IS NOT A DRAG (Grant, 2026-10-13). Without this, the
      // hand's own tremor during a plain click moved the box a point and
      // ran it through taken(), which quietly turned a dashed suggestion
      // into a real field: the popover then said "yours" and "Filled
      // from the record" about a box nobody had touched.
      if (!moved) return;
      if (drag.kind === "move") {
        setFields((prev) =>
          prev.map((f) => {
            const origin = drag.origins.get(f.key);
            if (!origin) return f;
            const page = pages?.find((p) => p.pageNumber === f.pageNumber);
            if (!page) return f;
            return taken({
              ...f,
              ...clampToPage({ ...f, x: origin.x + dx, y: origin.y + dy }, page),
            });
          }),
        );
        return;
      }
      setFields((prev) =>
        prev.map((f) => {
          if (f.key !== drag.key) return f;
          const page = pages?.find((p) => p.pageNumber === f.pageNumber);
          if (!page) return f;
          const width = Math.max(12, drag.origW + dx);
          // A multi-line box grows and shrinks a whole line at a time, so
          // its last rule is always a real rule on the page.
          const height = isMultilineField(f)
            ? snapHeightToLines(Math.max(f.linePitch, drag.origH + dy), f.linePitch)
            : Math.max(12, drag.origH + dy);
          return taken({ ...f, ...clampToPage({ x: f.x, y: f.y, width, height }, page) });
        }),
      );
    };
    const onUp = () => {
      if (!moved && wasMultiSelected) setSelectedKey(field.key);
      // A TEXT BOX OVER PRINTED LINES WRITES ON THEM (Nate, 2026-10-15).
      // One drawn with the Text tool and pulled tall, or dropped onto a
      // run of rules, takes that run's lines when the gesture ends, so
      // typing starts on the first line like every other big box. A plain
      // click on a suggestion leaves it a suggestion (the 2026-10-13
      // rule above): only a box someone has made their own is seated.
      if (!wasMultiSelected) {
        setFields((prev) =>
          prev.map((f) => {
            if (f.key !== field.key || (f.proposal && !moved)) return f;
            const seat = seatOnRuledRun(f, detectedBlanks);
            // No rules under it: a box pulled tall enough for two lines
            // wraps anyway (Grant, 2026-10-27: "they are just big boxes
            // and the text just runs").
            return seat ? { ...f, ...seat } : withOpenBoxWrap(f);
          }),
        );
      }
      fieldDragRef.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }

  // ── GHOSTS: what the OS offers for each, and the count strip ──────
  //
  // The rule (shared/esign/ghost-offers.ts): every suggestion is a ghost
  // until the sender accepts its offer, types into it, or hands it to a
  // signer. These memos are the screen's reading of that rule for the
  // document on screen: which ghosts carry an offer and from where,
  // which are the sender's own questions, and how the numbers add up.

  /** The ghosts the "only what you know" list counts as the sender's
   *  (ask + gap): the amber ones on the page. */
  const askedKeys = useMemo(() => {
    const out = new Set<string>();
    for (const g of shortForm.groups) {
      for (const q of g.questions) if (q.kind === "ask" || q.kind === "gap") out.add(q.key);
    }
    return out;
  }, [shortForm]);

  /** How many of the sender's own blanks sit on each page of the document
   *  on screen, off the same `askedKeys` the panel's list and the count strip
   *  read, so a page's label can never disagree with the panel. */
  const pageNeeds = useMemo(() => {
    const out = new Map<number, number>();
    for (const f of fields) {
      if (askedKeys.has(f.key)) out.set(f.pageNumber, (out.get(f.pageNumber) ?? 0) + 1);
    }
    return out;
  }, [fields, askedKeys]);

  /** What the OS offers for one ghost, whichever document it is on:
   *  the binding resolved against today's record, then the rule in
   *  ghost-offers.ts. Null for a real field and for a ghost with
   *  nothing offered. */
  const offerForField = useCallback(
    (f: PrepField): GhostOffer | null => {
      if (!isGhost(f)) return null;
      const resolved =
        f.mergeToken && mergeOptions !== null
          ? boundPreview(f.mergeToken, f.signerKey, f.roleKey).value
          : null;
      return ghostOffer(f, resolved);
    },
    // `mergeByToken` is what boundPreview reads; same reason it is in
    // unfilledMergeFields's deps below.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- boundPreview closes over mergeByToken
    [mergeOptions, mergeByToken, eligible],
  );
  /** What the OS offers for every ghost on the document on screen,
   *  keyed by field. A ghost with no entry has nothing offered: it is
   *  either a question for the sender or ignored unless touched. */
  const offers = useMemo(() => {
    const out = new Map<string, GhostOffer>();
    for (const f of fields) {
      const offer = offerForField(f);
      if (offer) out.set(f.key, offer);
    }
    return out;
  }, [fields, offerForField]);
  /** The only-you keys of every OTHER document, from their parked
   *  fields; the active document's are `askedKeys` above, off the same
   *  list the panel shows. */
  const askedCacheRef = useRef<Map<string, { fields: readonly PrepField[]; keys: Set<string> }>>(
    new Map(),
  );
  const askedByDoc = useMemo(() => {
    const out = new Map<string, Set<string>>();
    for (const d of packageDocs) {
      if (d.documentId === documentId) {
        out.set(d.documentId, askedKeys);
        continue;
      }
      // A parked document's field list only changes identity when the
      // parked state is rewritten, so the answer is kept per list: a
      // keystroke on the active document must not re-run the only-you
      // pass over five other documents.
      const list = allDocFields.get(d.documentId) ?? [];
      const cached = askedCacheRef.current.get(d.documentId);
      if (cached && cached.fields === list) {
        out.set(d.documentId, cached.keys);
        continue;
      }
      const keys = askedKeysIn(list, d);
      askedCacheRef.current.set(d.documentId, { fields: list, keys });
      out.set(d.documentId, keys);
    }
    return out;
  }, [packageDocs, documentId, askedKeys, allDocFields]);
  const isAsked = useCallback(
    (docId: string, key: string) => askedByDoc.get(docId)?.has(key) === true,
    [askedByDoc],
  );

  /** WHICH BLANK THE POPOVER IS OPEN ON. Set when a ghost is selected
   *  and kept while that same field stays selected, even once typing
   *  or accepting has made it real — otherwise the popover would vanish
   *  on the first keystroke of "type your own". Cleared by Esc (which
   *  clears the selection) or by selecting anything else. */
  const [popoverKey, setPopoverKey] = useState<string | null>(null);
  useEffect(() => {
    if (!selectedField) {
      setPopoverKey(null);
      return;
    }
    if (isGhost(selectedField)) setPopoverKey(selectedField.key);
    else if (selectedField.key !== popoverKey) setPopoverKey(null);
  }, [selectedField, popoverKey]);
  const popoverField = useMemo(
    () => (popoverKey ? (fields.find((f) => f.key === popoverKey) ?? null) : null),
    [fields, popoverKey],
  );
  const popoverOffer = popoverField ? (offers.get(popoverField.key) ?? null) : null;

  /** Does this ghost need a person: an offer to accept, or a question
   *  only the sender can answer? Answered the same way for every
   *  document in the package. */
  const docGhostNeedsAPerson = useCallback(
    (docId: string, f: PrepField): boolean => {
      if (!isGhost(f)) return false;
      return offerForField(f) !== null || isAsked(docId, f.key);
    },
    [offerForField, isAsked],
  );
  /** Per document: does anything on it still need a person? Read by the
   *  rail's dots and the package review list. */
  const docTodo = useMemo(() => {
    const out = new Map<string, number>();
    for (const [id, all] of allDocFields) {
      out.set(id, all.filter((f) => docGhostNeedsAPerson(id, f)).length);
    }
    return out;
  }, [allDocFields, docGhostNeedsAPerson]);

  /** Alt+1 to 4: the send's signers as the panel lists them. */
  const seatOrder = useMemo(
    () => eligible.filter((p) => selected.has(p.key) && !copyOnly.has(p.key)).map((p) => p.key),
    [eligible, selected, copyOnly],
  );

  /** What Tab walks: every blank that needs a person — the sender's own
   *  questions and Stew's readings waiting for a look — across EVERY
   *  document in the package, in the order the client reads them and
   *  the order each form reads (2026-09-29). Keys are package walk keys
   *  (document + field); see prep-workspace.ts. */
  const walkKeys = useMemo(
    () =>
      packageWalk(
        packageDocs.map((d) => ({
          documentId: d.documentId,
          fields: (allDocFields.get(d.documentId) ?? []).filter(
            (f) =>
              isGhost(f) && (isAsked(d.documentId, f.key) || offerForField(f)?.sure === false),
          ),
        })),
      ),
    [packageDocs, allDocFields, isAsked, offerForField],
  );
  /** Put a blank on screen and select it, switching documents when it
   *  is on another one. The Tab walk's landing, and the group "Review"
   *  button's. */
  const jumpToField = useCallback(
    (docId: string, key: string) => {
      if (docId === documentId) {
        setSelectedKey(key);
        const target = fields.find((f) => f.key === key);
        if (target) {
          pageRefs.current
            .get(target.pageNumber)
            ?.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        return;
      }
      const index = packageDocs.findIndex((d) => d.documentId === docId);
      if (index < 0) return;
      setJumpKey(key);
      showDocument(index);
    },
    [documentId, fields, packageDocs, showDocument],
  );

  /** The selected ghost's offer, for the editor. Null for a real field
   *  and for a ghost with nothing offered. */
  const selectedOffer = selectedField ? (offers.get(selectedField.key) ?? null) : null;

  /** The numbers the panel leads with — the WHOLE PACKAGE's, each
   *  document counted over its own full list and the parts added up
   *  (sumCensus), so "Accept all N ready" and the count strip describe
   *  the envelope that Accept all acts on. */
  const census = useMemo(
    () =>
      sumCensus(
        packageDocs.map((d) =>
          ghostCensus(
            allDocFields.get(d.documentId) ?? [],
            (f) => offerForField(f),
            (f) => isAsked(d.documentId, f.key),
          ),
        ),
      ),
    [packageDocs, allDocFields, offerForField, isAsked],
  );
  /** Ghosts anywhere in the package: what the count strip is about. */
  const packageGhosts = census.total - census.real - census.signing;
  /** Has this document been read — on screen with its pages drawn,
   *  or parked (opened before, or read in the background)? */
  const isDocumentRead = useCallback(
    (docId: string) => (docId === documentId ? pages !== null : parkedRef.current.has(docId)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- parkedVersion stands in for parkedRef
    [documentId, pages, parkedVersion],
  );
  const readCount = useMemo(
    () => packageDocs.filter((d) => isDocumentRead(d.documentId)).length,
    [packageDocs, isDocumentRead],
  );
  /** A document's dot, with its to-do count: the one reading the rail's
   *  strip, its list and the review list share. The document on screen
   *  counts as failed when its own load failed. */
  const dotFor = useCallback(
    (docId: string): { dot: ReturnType<typeof documentDotState>; todo: number } => {
      const todo = docTodo.get(docId) ?? 0;
      return {
        dot: documentDotState({
          read: isDocumentRead(docId),
          failed:
            Boolean(readFailures[docId]) || (docId === documentId && loadError !== null),
          todo: todo > 0,
        }),
        todo,
      };
    },
    [docTodo, isDocumentRead, readFailures, documentId, loadError],
  );
  /** The words beside a document's number for its dot state. */
  const documentStateWords = useCallback(
    (docId: string, dot: ReturnType<typeof documentDotState>, todo: number): string => {
      switch (dot) {
        case "todo":
          return `${todo} to decide`;
        case "done":
          return "nothing left to decide";
        case "failed":
          return docId === documentId
            ? "couldn't be loaded; see the message on the page"
            : "couldn't read it here; open it to try again";
        default:
          if (docId === documentId) return "loading";
          return readingDocId === docId ? "reading it now" : "waiting to be read";
      }
    },
    [readingDocId, documentId],
  );
  /** How many documents in the package still hold a ghost with a sure
   *  offer, for the Accept all label. */
  const docsWithSureOffers = useMemo(
    () =>
      packageDocs.filter((d) =>
        (allDocFields.get(d.documentId) ?? []).some((f) => offerForField(f)?.sure === true),
      ).length,
    [packageDocs, allDocFields, offerForField],
  );

  // ── STEW READS THE OTHER DOCUMENTS IN THE PACKAGE (2026-09-30) ─────
  //
  // The drawing's eighth source: the lender's pre-approval letter names
  // the loan amount, the lender and the loan type, and the contract's
  // financing blanks should be offered them, tagged with the letter's
  // name, for a look. Runs ONCE per open, only once every document has
  // been read (so the blanks and the text are both complete) and the
  // record's values have loaded (so a linked blank the record answers
  // is not asked about).
  //
  // What is asked about: a ghost with no offer at all, on a text blank,
  // that is either the sender's own question or a linked blank the
  // record could not answer. What is read: the package's other documents
  // with readable text that are NOT blank library forms (a form copied
  // from the library carries its fingerprint up front, or applied a
  // saved map; there is nothing on a blank form to read). A blank on a
  // source document is not asked about from that same document.
  useEffect(() => {
    if (!open || !isPackage || mapping) return;
    if (packageReadRef.current) return;
    // Every document read, or given up on: a document the background
    // reader could not get must not hold the rest of the package back.
    if (readingDocId !== null) return;
    if (packageDocs.some((d) => !isDocumentRead(d.documentId) && !readFailures[d.documentId])) {
      return;
    }
    // The record's values, so a linked blank the record answers is not
    // asked about — waited for only when there is a record to load.
    if (firstSignerContactId && mergeOptions === null) return;
    packageReadRef.current = true;

    const sourceIds = new Set<string>();
    const sources: { documentId: string; spans: RenderedPdf["spans"] }[] = [];
    for (const d of packageDocs) {
      if (d.contentFingerprint) continue;
      const parked = d.documentId === documentId ? null : parkedRef.current.get(d.documentId);
      const layout = d.documentId === documentId ? appliedLayout : (parked?.appliedLayout ?? null);
      if (layout) continue;
      const rendered = d.documentId === documentId ? renderedRef.current : (parked?.rendered ?? null);
      if (!rendered || rendered.spans.length === 0) continue;
      sourceIds.add(d.documentId);
      sources.push({ documentId: d.documentId, spans: rendered.spans.slice(0, 500) });
    }
    const blanks: {
      key: string;
      documentName: string;
      caption: string | null;
      fieldType: string;
      fillRule: string | null;
    }[] = [];
    for (const d of packageDocs) {
      if (sourceIds.has(d.documentId)) continue;
      for (const f of allDocFields.get(d.documentId) ?? []) {
        if (!isGhost(f) || f.reading || f.fieldType !== "text") continue;
        if (offerForField(f) !== null) continue;
        const bound = (f.mergeToken ?? "").trim().length > 0;
        if (!bound && !isAsked(d.documentId, f.key)) continue;
        blanks.push({
          key: f.key,
          documentName: d.filename,
          caption: f.caption ?? null,
          fieldType: f.fieldType,
          fillRule: f.fillRule ?? null,
        });
      }
    }
    if (blanks.length === 0 || sources.length === 0) return;

    const gen = openGenRef.current;
    void (async () => {
      try {
        const res = await apiV11.readEsignPackage(recordKind, recordId, {
          ...(effectiveCompany ? { companySlug: effectiveCompany } : {}),
          blanks: blanks.slice(0, 60),
          sources: sources.slice(0, 3).map((src) => ({
            documentId: src.documentId,
            spans: src.spans.map((sp) => ({
              id: sp.id,
              pageNumber: sp.pageNumber,
              text: sp.text,
              x: sp.x,
              y: sp.y,
              width: sp.width,
              height: sp.height,
            })),
          })),
        });
        if (openGenRef.current !== gen) return;
        const byKey = new Map(res.readings.map((r) => [r.key, r]));
        if (byKey.size > 0) {
          const attach = (f: PrepField): PrepField => {
            const r = byKey.get(f.key);
            if (!r || !isGhost(f)) return f;
            return {
              ...f,
              reading: {
                value: r.value,
                documentId: r.sourceDocumentId,
                documentName: r.sourceDocumentName,
                spanId: r.sourceSpanId,
              },
            };
          };
          setFields((prev) => prev.map(attach));
          let changed = false;
          for (const [id, st] of parkedRef.current) {
            if (id === activeDocIdRef.current) continue;
            if (!st.fields.some((f) => byKey.has(f.key))) continue;
            parkedRef.current.set(id, { ...st, fields: st.fields.map(attach) });
            changed = true;
          }
          if (changed) bumpParked();
        }
        const names = [...new Set(res.readings.map((r) => r.sourceDocumentName))];
        const n = res.readings.length;
        setPackageReadNote(
          n > 0
            ? `Stew read ${n} ${n === 1 ? "value" : "values"} off ${names
                .map((x) => `"${x}"`)
                .join(" and ")}; each is tagged with the document and waits for a look.${
                res.message ? ` ${res.message}` : ""
              }`
            : (res.message ?? null),
        );
      } catch (err) {
        if (openGenRef.current !== gen) return;
        // Never an error wall: the blanks were always the sender's to
        // type; this only says the shortcut did not happen.
        setPackageReadNote(
          err instanceof ApiError
            ? err.message
            : "Stew couldn't read the other documents just now — the blanks are still yours to type.",
        );
      }
    })();
  }, [
    open,
    isPackage,
    mapping,
    readingDocId,
    isDocumentRead,
    readFailures,
    firstSignerContactId,
    packageDocs,
    mergeOptions,
    documentId,
    appliedLayout,
    allDocFields,
    offerForField,
    isAsked,
    recordKind,
    recordId,
    effectiveCompany,
    bumpParked,
  ]);

  /** Ghosts with an offer, grouped by where the offer came from, in the
   *  panel's order, across every document. Each entry remembers its
   *  document so "Review" can land on it. Empty groups are left out. */
  const offersByKind = useMemo(() => {
    const groups = new Map<OfferKind, { documentId: string; field: PrepField }[]>();
    for (const d of packageDocs) {
      for (const f of allDocFields.get(d.documentId) ?? []) {
        const offer = offerForField(f);
        if (!offer) continue;
        const list = groups.get(offer.kind) ?? [];
        list.push({ documentId: d.documentId, field: f });
        groups.set(offer.kind, list);
      }
    }
    return OFFER_KIND_ORDER.filter((k) => groups.has(k)).map((k) => ({
      kind: k,
      fields: groups.get(k) ?? [],
    }));
  }, [packageDocs, allDocFields, offerForField]);

  /** Linked ghosts the record could not answer. Not offered, not sent,
   *  and said out loud so "96 values ready" is never read as "every
   *  linked blank is handled". */
  const unansweredGhostBindings = useMemo(
    () =>
      mergeOptions === null
        ? 0
        : fields.filter(
            (f) =>
              isGhost(f) &&
              (f.mergeToken ?? "").trim().length > 0 &&
              !(f.value ?? "").trim() &&
              !offers.has(f.key),
          ).length,
    [fields, mergeOptions, offers],
  );

  /** Accept every offer a person need not look at (Grant, 2026-09-29:
   *  Stew's readings stay offered until reviewed). */
  const acceptAllSure = useCallback(() => {
    acceptGhosts((f) => offerForField(f)?.sure === true);
  }, [acceptGhosts, offerForField]);

  const acceptKind = useCallback(
    (kind: OfferKind) => {
      acceptGhosts((f) => offerForField(f)?.kind === kind);
    },
    [acceptGhosts, offerForField],
  );

  /** Put a whole source group back to ghosts. Keyed on the SAVED offer
   *  kind, remembered at accept time, since an accepted field no longer
   *  reads as an offer. */
  const acceptedByKindRef = useRef<Map<OfferKind, Set<string>>>(new Map());
  const acceptGroup = useCallback(
    (kind: OfferKind) => {
      const keys = new Set(
        (offersByKind.find((g) => g.kind === kind)?.fields ?? []).map((e) => e.field.key),
      );
      acceptedByKindRef.current.set(kind, keys);
      acceptKind(kind);
    },
    [offersByKind, acceptKind],
  );
  const undoGroup = useCallback(
    (kind: OfferKind) => {
      const keys = acceptedByKindRef.current.get(kind);
      if (!keys || keys.size === 0) return;
      acceptedByKindRef.current.delete(kind);
      unacceptTo(keys, kind === "stew" ? "stew" : "saved_layout");
    },
    [unacceptTo],
  );
  /** Which groups were accepted from the panel and can be put back. */
  const acceptedKinds = useMemo(() => {
    const out = new Set<OfferKind>();
    const realKeys = new Set<string>();
    for (const list of packageFields.values()) for (const f of list) realKeys.add(f.key);
    for (const [kind, keys] of acceptedByKindRef.current) {
      if ([...keys].some((k) => realKeys.has(k))) out.add(kind);
    }
    return out;
  }, [packageFields]);

  /** The send line: what goes out and what stays behind, across every
   *  document, counted off the same arrays the payload is built from. */
  const sendCounts = useMemo(() => {
    let sent = 0;
    let ghosts = 0;
    const byDoc = new Map<string, { sent: number; ghosts: number }>();
    for (const [id, all] of allDocFields) {
      const split = partitionForSend(all);
      sent += split.sent.length;
      ghosts += split.ghosts.length;
      byDoc.set(id, { sent: split.sent.length, ghosts: split.ghosts.length });
    }
    return { sent, ghosts, byDoc };
  }, [allDocFields]);

  /** REMOVE MEANS REMOVE (Grant, 2026-10-13). The Delete key, the
   *  panel's "Remove field" button and the × on a selected box all come
   *  here, so the three can never disagree again. Until now Backspace on
   *  an accepted box turned it back into a dashed ghost drawn in the same
   *  place — Nate pressed it, saw nothing change, and concluded the box
   *  could not be removed. Undo is on the toast for a minute. */
  const removeFields = useCallback(
    (keys: ReadonlySet<string>) => {
      const removed = fields.filter((f) => keys.has(f.key) && !isLockedInCorrection(f));
      if (removed.length === 0) return;
      const gone = new Set(removed.map((f) => f.key));
      setFields((prev) => prev.filter((f) => !gone.has(f.key)));
      setSelectedKeysState(new Set());
      toast.info(`Removed ${removed.length} ${removed.length === 1 ? "box" : "boxes"}.`, {
        action: {
          label: "Undo",
          onClick: () =>
            setFields((prev) => {
              const present = new Set(prev.map((f) => f.key));
              return [...prev, ...removed.filter((f) => !present.has(f.key))];
            }),
        },
      });
    },
    // isLockedInCorrection reads the correction state through closure;
    // listed so a stale copy can't remove a box correction mode locks.
    [fields, isLockedInCorrection],
  );

  // Keyboard: nudge / delete / escape. Attached while open.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (isTypingTarget(e.target)) {
        // THE POPOVER'S OWN BOX (2026-10-13). Focus lands in its "Type
        // it…" input the moment a blank is clicked, and this guard used
        // to swallow every key there — so Esc, Tab and Enter, the three
        // keys its own footer advertises, did nothing. Those pass
        // through to the page's handling below; so does Delete on an
        // empty box. Everything else is typing.
        // The box's own in-place editor (2026-10-14) gets the same keys,
        // except Enter in a multi-line box, which is a new line.
        const inPopover =
          e.target instanceof Element &&
          e.target.closest(".esign-ghost-pop, .esign-prep-field-editor");
        const isTextarea = e.target instanceof HTMLTextAreaElement;
        const empty =
          (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) &&
          e.target.value === "";
        const passes =
          inPopover &&
          (e.key === "Escape" ||
            e.key === "Tab" ||
            (e.key === "Enter" && !isTextarea) ||
            (e.key === "Delete" && empty));
        if (!passes) return;
        if (e.key === "Escape" || e.key === "Enter") (e.target as HTMLElement).blur();
      }
      if (e.key === "Escape") {
        if (placingType) {
          setPlacingType(null);
          setPlacingLocked(false);
          setPendingMergeToken(null);
          setRecordFieldPickerOpen(false);
        } else if (placingStamp) setPlacingStamp(false);
        else {
          setSelectedKey(null);
          setSelectedStampKey(null);
        }
        return;
      }
      // ACCEPT FROM THE KEYBOARD (2026-09-29). Shift+Enter takes every
      // sure offer on the document; Enter takes the selected ghost(s).
      // Neither touches a real field, and neither sends anything.
      // Enter on a focused button, link or select is that control's
      // own click; only a bare Enter on the page is the accept shortcut.
      const onControl =
        e.target instanceof Element && e.target.closest("button, a, select, [role='button']");
      if (e.key === "Enter" && onControl) return;
      if (e.key === "Enter" && e.shiftKey) {
        acceptAllSure();
        e.preventDefault();
        return;
      }
      // HAND TO A SEAT: Alt+1 to Alt+4 (Option on a Mac). A bare digit
      // on a blank always types (a price starts with a digit), so the
      // modifier is what makes this a hand-off (Grant, 2026-09-29).
      if (e.altKey && !e.ctrlKey && !e.metaKey) {
        // By PHYSICAL key: Option+1 on a Mac reports "¡" as its key.
        const digit = /^Digit[1-4]$/.test(e.code) ? e.code.slice(5) : e.key;
        const seat = seatForKey(digit, seatOrder);
        if (seat && selectedKeys.size > 0) {
          setFields((prev) =>
            prev.map((f) => (selectedKeys.has(f.key) ? taken({ ...f, signerKey: seat }) : f)),
          );
          e.preventDefault();
          return;
        }
      }
      // TAB WALKS THE BLANKS THAT NEED A PERSON: the ones only the
      // sender can answer, and Stew's readings waiting for a look, in
      // the order the form reads. Shift+Tab walks back. Wraps.
      if (
        e.key === "Tab" &&
        walkKeys.length > 0 &&
        !onControl &&
        (selectedKeys.size > 0 || document.activeElement === document.body)
      ) {
        const next = neighbourKey(
          walkKeys,
          selectedField ? packageWalkKey(documentId, selectedField.key) : null,
          e.shiftKey ? -1 : 1,
        );
        if (next) {
          const { documentId: docId, fieldKey } = splitWalkKey(next);
          jumpToField(docId, fieldKey);
        }
        e.preventDefault();
        return;
      }
      if (e.key === "Enter" && selectedKeys.size > 0) {
        acceptGhosts((f) => selectedKeys.has(f.key));
        e.preventDefault();
        return;
      }
      if (selectedKeys.size === 0 && !selectedStampKey) return;
      const step = e.shiftKey ? NUDGE_PT * 3 : NUDGE_PT;
      const delta =
        e.key === "ArrowLeft"
          ? { x: -step, y: 0 }
          : e.key === "ArrowRight"
            ? { x: step, y: 0 }
            : e.key === "ArrowUp"
              ? { x: 0, y: -step }
              : e.key === "ArrowDown"
                ? { x: 0, y: step }
                : null;
      if (selectedStampKey) {
        if (e.key === "Delete" || e.key === "Backspace") {
          setStamps((prev) => prev.filter((s) => s.key !== selectedStampKey));
          setSelectedStampKey(null);
          e.preventDefault();
          return;
        }
        if (!delta) return;
        e.preventDefault();
        setStamps((prev) =>
          prev.map((s) => {
            if (s.key !== selectedStampKey) return s;
            const page = pages?.find((p) => p.pageNumber === s.pageNumber);
            if (!page) return s;
            return { ...s, ...clampToPage({ ...s, x: s.x + delta.x, y: s.y + delta.y }, page) };
          }),
        );
        return;
      }
      // Delete / nudge apply to EVERY selected field, not just one —
      // the other half of "select several at once" (Joey, 2026-09-18).
      //
      // REMOVED, NOT PUT BACK (2026-10-13; was "back to a ghost" since
      // 2026-09-29). Delete or Backspace removes every selected box,
      // with Undo on the toast — see removeFields.
      if (e.key === "Delete" || e.key === "Backspace") {
        removeFields(selectedKeys);
        e.preventDefault();
        return;
      }
      if (!delta) return;
      e.preventDefault();
      setFields((prev) =>
        prev.map((f) => {
          if (!selectedKeys.has(f.key)) return f;
          const page = pages?.find((p) => p.pageNumber === f.pageNumber);
          if (!page) return f;
          return taken({ ...f, ...clampToPage({ ...f, x: f.x + delta.x, y: f.y + delta.y }, page) });
        }),
      );
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    open,
    selectedKeys,
    selectedStampKey,
    placingType,
    placingStamp,
    pages,
    acceptAllSure,
    acceptGhosts,
    seatOrder,
    walkKeys,
    selectedField,
    removeFields,
    documentId,
    jumpToField,
  ]);

  function toggleSigner(signerKey: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(signerKey)) {
        next.delete(signerKey);
        // Dropping a signer drops their placed fields — visibly, not
        // silently at send time.
        setFields((fs) => fs.filter((f) => f.signerKey !== signerKey));
      } else {
        next.add(signerKey);
      }
      return next;
    });
  }

  // ── Adding and removing a signer who is not one of our contacts ────
  //
  // Nate's complaint about BrokerMint (Grant, 2026-09-03) is the TIME
  // AND CLICKS to add or remove a signer, so the budget here is two
  // clicks to add and one to remove, and the work is measured against
  // that rather than against how the form looks.
  //
  // `guests` is positional and the key is derived from the index, so
  // removing one RENUMBERS the ones after it — which means the fields
  // placed for those signers have to move with them, or a co-op agent's
  // signature line would silently become the title officer's. That
  // remap is the whole reason removal isn't a one-line filter.

  /** Add a guest. Returns false (and sets the inline error) when the
   *  entry can't be used, so the form knows whether to close. */
  function addGuestSigner(rawName: string, rawEmail: string): boolean {
    const name = rawName.trim();
    const email = rawEmail.trim();
    if (!name) {
      setGuestError("Give this signer a name.");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setGuestError("That doesn't look like an email address.");
      return false;
    }
    // The provider keys a recipient by email, so two signers sharing one
    // address collide there — caught here instead, where it can be a
    // sentence rather than a failed send.
    const clash = eligible.find((p) => p.email?.toLowerCase() === email.toLowerCase());
    if (clash) {
      setGuestError(`${clash.displayName} is already on this send with that email.`);
      return false;
    }
    setGuestError(null);
    const key = guestSignerKey(guests.length);
    setGuests((prev) => [...prev, { name, email }]);
    // Added means added: a signer you just typed in is on the send and
    // is the one you are about to place fields for. Anything less would
    // be a third click.
    setSelected((prev) => new Set(prev).add(key));
    setActiveSignerId(key);
    // "Someone else" was chosen FOR a seat: the person just typed fills
    // it. Cast with the guest key they already have, so nothing is
    // added twice.
    const forRole = guestForRoleRef.current;
    if (forRole && casting) {
      guestForRoleRef.current = null;
      fillSeat(forRole, { key, kind: "party", contactId: null, displayName: name, email });
    }
    return true;
  }

  /** Remove a guest, with a real undo — because removing a signer also
   *  throws away every field placed for them, which is exactly the kind
   *  of destructive-by-side-effect action the reversibility rules say
   *  must be undoable. Nothing has been sent yet, so the undo is a
   *  restore of this screen's own state rather than a rollback. */
  function removeGuestSigner(key: string) {
    const index = guestSignerIndex(key);
    const guest = index === null ? undefined : guests[index];
    if (index === null || !guest) return;
    const theirFields = fields.filter((f) => f.signerKey === key);
    const wasSelected = selected.has(key);
    const wasCopyOnly = copyOnly.has(key);

    // Renumber: every guest after this one shifts down by one, and so
    // does the key on each of their fields. See
    // remapAfterGuestRemoval's header for why that is not optional.
    const remap = (k: string) => remapAfterGuestRemoval(k, index);
    const withoutThem = (list: PrepField[]) =>
      list
        .filter((f) => f.signerKey !== key)
        .map((f) => ({ ...f, signerKey: remap(f.signerKey) }));
    setGuests((prev) => prev.filter((_, i) => i !== index));
    setFields((prev) => withoutThem(prev));
    // EVERY document in the package, not only the one on screen. The
    // other documents' fields are parked in a ref, and leaving them on
    // the old numbering is the exact misattribution the remap exists to
    // prevent: the guest who took this one's number would silently
    // inherit their signature line on the addenda. An offer is a
    // contract plus its addenda plus a prequal letter, so this is the
    // normal shape of a Realty send, not an edge case.
    //
    // Their parked fields are kept alongside the active document's so
    // Undo can put all of them back where they came from.
    const parkedTheirs: Array<{ documentId: string; fields: PrepField[] }> = [];
    for (const [docId, state] of parkedRef.current.entries()) {
      const theirs = state.fields.filter((f) => f.signerKey === key);
      if (theirs.length > 0) parkedTheirs.push({ documentId: docId, fields: theirs });
      parkedRef.current.set(docId, { ...state, fields: withoutThem(state.fields) });
    }
    setSelected((prev) => {
      const next = new Set<string>();
      for (const k of prev) if (k !== key) next.add(remap(k));
      return next;
    });
    setCopyOnly((prev) => {
      const next = new Set<string>();
      for (const k of prev) if (k !== key) next.add(remap(k));
      return next;
    });
    setActiveSignerId((prev) => (prev === key ? null : prev ? remap(prev) : prev));

    // Counted across the WHOLE package, because that is what was
    // actually discarded — reporting only the visible document's share
    // would understate it on exactly the sends where it matters most.
    const lostCount =
      theirFields.length + parkedTheirs.reduce((n, p) => n + p.fields.length, 0);
    toast.info(
      lostCount > 0
        ? `Removed ${guest.name} and ${lostCount} ${lostCount === 1 ? "field" : "fields"}.`
        : `Removed ${guest.name}.`,
      {
        action: {
          label: "Undo",
          onClick: () => {
            // Re-added at the END, so the restored key is
            // `guest:<new length>` — the fields go back onto whoever
            // they belonged to, under whatever key that person now has.
            setGuests((prev) => {
              const restoredKey = guestSignerKey(prev.length);
              setFields((fs) => [
                ...fs,
                ...theirFields.map((f) => ({ ...f, signerKey: restoredKey })),
              ]);
              for (const parked of parkedTheirs) {
                const state = parkedRef.current.get(parked.documentId);
                if (!state) continue;
                parkedRef.current.set(parked.documentId, {
                  ...state,
                  fields: [
                    ...state.fields,
                    ...parked.fields.map((f) => ({ ...f, signerKey: restoredKey })),
                  ],
                });
              }
              if (wasSelected) setSelected((sel) => new Set(sel).add(restoredKey));
              if (wasCopyOnly) setCopyOnly((cc) => new Set(cc).add(restoredKey));
              return [...prev, guest];
            });
          },
        },
      },
    );
  }

  // ── Send ───────────────────────────────────────────────────────
  const signersWithoutFields = useMemo(() => {
    // Across the WHOLE package, not just the document on screen: a
    // buyer who signs the addendum but not the contract page in front
    // of you has something to sign, and reporting them as idle would
    // train senders to ignore this warning.
    const withFields = new Set<string>();
    for (const list of packageFields.values()) {
      for (const f of list) withFields.add(f.signerKey);
    }
    // Copy-only recipients sign nothing, so "no fields" is their
    // correct state, not a problem to report.
    return eligible.filter(
      (p) => selected.has(p.key) && !copyOnly.has(p.key) && !withFields.has(p.key),
    );
  }, [eligible, selected, copyOnly, packageFields]);

  /** Documents in the package that nobody has been asked to sign.
   *
   *  Not automatically a mistake — a prequal letter really is an
   *  attachment for the buyer to read, and forcing a signature onto it
   *  would be wrong. So this READS OUT before sending rather than
   *  blocking: the whole point of a package is that four documents go
   *  as one, and the one failure it introduces is a document that
   *  quietly went out with nothing on it because the sender never
   *  opened that tab. */
  const documentsWithoutFields = useMemo(
    () =>
      packageDocs.filter((d) => (packageFields.get(d.documentId)?.length ?? 0) === 0),
    [packageDocs, packageFields],
  );

  /** The pages of documents THEY wrote that nobody has looked at yet.
   *
   *  Computed from the parked page list rather than from a page count
   *  the caller supplied, because a page that has not been rendered is
   *  not a page anyone could have confirmed. Reading a document in the
   *  background draws its pages and NEVER confirms them: confirmation
   *  is a person saying the boxes on this page are right, so every
   *  page of a received document stays outstanding until somebody
   *  opens it and says so. */
  const unconfirmedTheirPages = useMemo(() => {
    return unconfirmedReceivedPages(
      packageDocs.map((d) => {
        const parked = d.documentId === documentId ? null : parkedRef.current.get(d.documentId);
        const pageList = d.documentId === documentId ? pages : (parked?.pages ?? null);
        return {
          documentId: d.documentId,
          title: d.filename,
          origin: d.origin ?? null,
          // A received document the background reader has not reached
          // yet has no measured pages; the moment it is read (or opened)
          // every one of its pages appears here, unconfirmed.
          pageNumbers: (pageList ?? []).map((pg) => pg.pageNumber),
          confirmedPages: confirmedPagesByDoc[d.documentId] ?? [],
        };
      }),
    );
  }, [packageDocs, documentId, pages, confirmedPagesByDoc]);

  /** Whether the document on screen is one the other side wrote. Drives
   *  the per-page confirm bar and nothing else. */
  const activeIsTheirs = isReceivedDocument(activeDoc.origin);
  const activeConfirmed = useMemo(
    () => new Set(confirmedPagesByDoc[documentId] ?? []),
    [confirmedPagesByDoc, documentId],
  );
  const confirmPage = useCallback(
    (pageNumber: number) => {
      setConfirmedPagesByDoc((prev) => {
        const current = prev[documentId] ?? [];
        if (current.includes(pageNumber)) {
          return { ...prev, [documentId]: current.filter((p) => p !== pageNumber) };
        }
        return { ...prev, [documentId]: [...current, pageNumber] };
      });
    },
    [documentId],
  );

  /** Every send-blocking condition the PROVIDER would refuse anyway,
   *  said here first, with the person named — the round-5 rule that a
   *  rejection after Send must never be the first time the advisor
   *  hears about a fixable setup problem. Checked at send time rather
   *  than disabling the button, so the message can say exactly who and
   *  what. */
  const fieldOwnershipProblem = useMemo(() => {
    const nameOf = (id: string) =>
      eligible.find((p) => p.key === id)?.displayName ?? "someone not on this record";
    // Real fields only: a ghost is not sent, so a ghost belonging to
    // someone unticked is not in the way (2026-09-29; before that Send
    // was an implicit accept and this covered proposals too).
    //
    // A field owned by a person who isn't checked as a signer. The
    // accept-time auto-check handles the accepted case, so an ACCEPTED
    // orphan here means someone was deliberately unchecked afterwards.
    // isUnselectedSignerField exempts the preparer's own locked boxes
    // (name/date/NMLS lines stamped in at Suggest time), the same as
    // the send route always has — see its own comment for why this
    // check needed to learn that too (2026-09-22, Joey's report).
    const unselectedOwner = realFields.find((f) => isUnselectedSignerField(f, selected));
    if (unselectedOwner) {
      return (
        `A field on the document belongs to ${nameOf(unselectedOwner.signerKey)}, ` +
        "who isn't picked as a signer — check them, or remove their fields."
      );
    }
    // A field owned by a copy-only recipient — the provider would ask
    // nobody to fill it, so the envelope could never complete.
    const ccOwner = realFields.find((f) => copyOnly.has(f.signerKey));
    if (ccOwner) {
      return (
        `${nameOf(ccOwner.signerKey)} is set to receive a copy only but has fields on the ` +
        "document — switch them to signing, or move those fields to a signer."
      );
    }
    // A signer whose only fields are auto-filled signing dates. BoldSign
    // refuses the send outright ("the document should contain field(s)
    // other than dates, labels and hyper link") — round 5 hit this as a
    // post-send provider rejection.
    for (const id of selected) {
      if (copyOnly.has(id)) continue;
      const theirs = realFields.filter((f) => f.signerKey === id);
      if (theirs.length > 0 && theirs.every((f) => f.fieldType === "date_signed")) {
        return (
          `${nameOf(id)}'s only field is an auto-filled signing date — the signature service ` +
          "needs at least one field they act on themselves. Add a signature, initials, or " +
          "another field for them, or remove the date."
        );
      }
    }
    return null;
  }, [realFields, selected, copyOnly, eligible]);

  /** Fields that don't sit fully on their page. Should be empty — every
   *  path that creates or moves a field clamps — so this is the honest
   *  backstop rather than a routine state, and it names the pages so
   *  nobody hunts an 11-page contract for the one bad box (staging
   *  round 3, 2026-08-23: the send was refused with "one of the placed
   *  fields", which meant scrolling all eleven). */
  const offPageFields = useMemo(() => {
    if (!pages) return [];
    // A ghost off the page is not sent, so it is not a problem.
    return realFields.filter((f) => isFieldOffPage(f, pages));
  }, [realFields, pages]);

  const offPageKeys = useMemo(
    () => new Set(offPageFields.map((f) => f.key)),
    [offPageFields],
  );

  const offPageNote = useMemo(() => {
    if (offPageFields.length === 0) return null;
    const where = describePages(offPageFields);
    return offPageFields.length === 1
      ? `1 field is off the page (${where}). Drag it back on, or remove it.`
      : `${offPageFields.length} fields are off the page (${where}). Drag them back on, or remove them.`;
  }, [offPageFields]);

  /** The same problem, but on a document other than the one on screen —
   *  see packageBlockers and otherDocumentBlockerNote above. */
  const otherDocOffPageNote = useMemo(
    () =>
      otherDocumentBlockerNote(packageBlockers.offPageByDoc, documentId, (f) =>
        f.length === 1 ? "1 field is off the page" : `${f.length} fields are off the page`,
      ),
    [packageBlockers.offPageByDoc, documentId],
  );

  /** A pick-one with no choices, a choose-one with no group, or a
   *  multi-line box whose text runs past its last line. The first two
   *  live in shared/esign/send-readiness.ts — one statement of "can
   *  this be sent?", so this screen and the per-form test cannot drift
   *  apart (read that module's header for why an empty locked box no
   *  longer blocks at all, 2026-09-29). The third is overflowLinesOf,
   *  counted with the others so the same note, the same red outline and
   *  the same disabled Send cover it. Real fields only: a ghost is not
   *  sent. */
  const unconfiguredFields = useMemo(
    () => [
      ...fieldsBlockingSend(realFields),
      ...realFields.filter((f) => overflowLinesOf(f) > 0),
    ],
    [realFields, overflowLinesOf],
  );

  const unconfiguredKeys = useMemo(
    () => new Set(unconfiguredFields.map((f) => f.key)),
    [unconfiguredFields],
  );

  const unconfiguredNote = useMemo(() => {
    if (unconfiguredFields.length === 0) return null;
    const overflowing = unconfiguredFields.filter((f) => overflowLinesOf(f) > 0);
    const rest = unconfiguredFields.filter((f) => overflowLinesOf(f) === 0);
    const emptyLists = rest.filter((f) => f.fieldType === "dropdown").length;
    const ungrouped = rest.length - emptyLists;
    const parts: string[] = [];
    if (overflowing.length > 0) {
      const over = overflowing.reduce((n, f) => n + overflowLinesOf(f), 0);
      parts.push(
        overflowing.length === 1
          ? `1 box has ${over} ${over === 1 ? "line" : "lines"} more text than it has lines for`
          : `${overflowing.length} boxes have more text than they have lines for (${over} lines over)`,
      );
    }
    if (emptyLists > 0) {
      parts.push(
        emptyLists === 1
          ? "1 pick-one list has no choices in it"
          : `${emptyLists} pick-one lists have no choices in them`,
      );
    }
    if (ungrouped > 0) {
      parts.push(
        ungrouped === 1
          ? "1 choose-one button isn't in a group"
          : `${ungrouped} choose-one buttons aren't in a group`,
      );
    }
    return `${parts.join(" and ")}. Click ${
      unconfiguredFields.length === 1 ? "it" : "each one"
    } to finish setting ${unconfiguredFields.length === 1 ? "it" : "them"} up.`;
  }, [unconfiguredFields, overflowLinesOf]);

  /** The same problem, but on a document other than the one on screen —
   *  see packageBlockers and otherDocumentBlockerNote above. A plain
   *  count rather than unconfiguredNote's dropdown/locked/ungrouped
   *  breakdown, since the fix here is "go open that document," not
   *  "click this one right here." */
  const otherDocUnconfiguredNote = useMemo(
    () =>
      otherDocumentBlockerNote(packageBlockers.blockingByDoc, documentId, (f) =>
        f.length === 1 ? "1 field still needs setup" : `${f.length} fields still need setup`,
      ),
    [packageBlockers.blockingByDoc, documentId],
  );

  /** A linked field that will print nothing.
   *
   *  The server already refuses these (422 `missing_merge_values`,
   *  workers/api/src/routes/esign.ts) — but only AFTER the click, which
   *  means placing every field, reviewing the whole document, pressing
   *  Send, and only then being told to go fill something in. Same
   *  refusal, said here, on the screen where it can be fixed — exactly
   *  the reasoning `unconfiguredFields` above was written for.
   *
   *  The test matches the server's rule (shared/esign/field-merge-bindings.ts):
   *  a typed override wins, so a field carrying one is never a hole.
   *
   *  Deliberately silent while the record's values are still loading.
   *  `mergeOptions === null` means "we don't know yet", and calling
   *  every linked field empty in that moment would raise a blocker that
   *  clears itself a second later. */
  const unfilledMergeFields = useMemo(() => {
    if (mergeOptions === null) return [];
    // Asked per FIELD, not per token: a contact token is only filled for
    // the signer whose box it is, so a co-borrower with no record on
    // file must show up here rather than pass the pre-flight and be
    // refused by the send a click later. Real fields only: a ghost the
    // record cannot answer is simply never offered and never sent.
    const holes = unfilledBindings(realFields, (token, f) =>
      boundPreview(token, f.signerKey, f.roleKey).value,
    );
    return holes.map((h) => realFields[h.index]).filter((f): f is PrepField => Boolean(f));
    // `eligible`: a `signer.name` box reads the name on the send.
  }, [realFields, mergeOptions, mergeByToken, eligible]);

  const unfilledMergeKeys = useMemo(
    () => new Set(unfilledMergeFields.map((f) => f.key)),
    [unfilledMergeFields],
  );

  /** How many linked fields the OS answered on its own. This is the
   *  number the advisor is actually here for — the whole promise of
   *  linking a field is that the record fills it — so it gets said out
   *  loud rather than left to be inferred from the absence of a
   *  complaint. Counted separately from fields someone typed over, so
   *  the claim "filled from this record" stays literally true. */
  const autoFilledMergeCount = useMemo(() => {
    if (mergeOptions === null) return 0;
    // Every field the record is being ASKED to answer (a binding with no
    // override), less the ones it couldn't. Derived from the same rule
    // rather than re-tested, so the two numbers can never disagree.
    const awaitingRecord = realFields.filter(
      (f) => (f.mergeToken ?? "").trim().length > 0 && (f.value ?? "").trim().length === 0,
    ).length;
    return awaitingRecord - unfilledMergeFields.length;
  }, [realFields, mergeOptions, unfilledMergeFields]);

  // "Auto-filled from this record" (PR 9b, mockup v3 screen 6): where the
  // record's answers on this document come from, so the sender sees what
  // came from where without opening each box. Read only.
  const autoFilledBySource = useMemo(() => {
    if (mergeOptions === null) return [];
    const unfilled = new Set(unfilledMergeFields.map((f) => f.key));
    const counts = new Map<string, number>();
    for (const f of realFields) {
      const token = (f.mergeToken ?? "").trim();
      if (!token || (f.value ?? "").trim() || unfilled.has(f.key)) continue;
      const source = mergeSourceLabel(token);
      counts.set(source, (counts.get(source) ?? 0) + 1);
    }
    return [...counts.entries()].map(([source, count]) => ({ source, count }));
  }, [realFields, mergeOptions, unfilledMergeFields]);

  const unfilledMergeNote = useMemo(() => {
    if (unfilledMergeFields.length === 0) return null;
    const labels = [
      ...new Set(
        unfilledMergeFields.map(
          (f) =>
            mergeByToken.get(f.mergeToken ?? "")?.label ??
            mergeTokenLabel(f.mergeToken ?? "") ??
            f.mergeToken ??
            "a linked field",
        ),
      ),
    ];
    const named =
      labels.length <= 3
        ? labels.join(", ")
        : `${labels.slice(0, 3).join(", ")} and ${labels.length - 3} more`;
    const one = unfilledMergeFields.length === 1;
    const it = one ? "it" : "them";
    // MAPPING A BLANK FORM (Baker, 2026-10-03). With no record here, the
    // only field that can come up empty is one linked to something this
    // form can never fill (a client's contact field on a Productions
    // form, whose signers are team members). "Type a value" is the wrong
    // advice on this screen: a value typed while mapping is saved as the
    // default for EVERY future send, so it would print one person's name
    // on everyone's copy.
    if (mapping) {
      return `${
        one ? "1 linked field" : `${unfilledMergeFields.length} linked fields`
      } can't fill on this form (${named} — ${describePages(unfilledMergeFields)}). Click Change and link ${it} to something this form can fill, or remove ${it}.`;
    }
    return `${
      one ? "1 linked field has" : `${unfilledMergeFields.length} linked fields have`
    } nothing to print (${named} — ${describePages(unfilledMergeFields)}). Fill ${it} in on the record, type a value into the field, or remove ${it}.`;
  }, [unfilledMergeFields, mergeByToken, mapping]);

  /** The same problem, but on a document other than the one on screen —
   *  see packageBlockers and otherDocumentBlockerNote above. */
  const otherDocUnfilledMergeNote = useMemo(
    () =>
      otherDocumentBlockerNote(packageBlockers.unfilledMergeByDoc, documentId, (f) =>
        f.length === 1
          ? "1 linked field has nothing to print"
          : `${f.length} linked fields have nothing to print`,
      ),
    [packageBlockers.unfilledMergeByDoc, documentId],
  );

  const sendLabel = useMemo(() => {
    const names = eligible
      .filter((p) => selected.has(p.key))
      .map((p) => p.displayName.split(" ")[0] ?? p.displayName);
    if (correction) return "Send the correction";
    if (names.length === 0) return "Send";
    if (names.length === 1) return `Send to ${names[0]}`;
    if (names.length === 2) return `Send to ${names[0]} & ${names[1]}`;
    return `Send to ${names[0]} + ${names.length - 1} more`;
  }, [eligible, selected, correction]);

  /** What the folded delivery section is set to, in one line. A folded
   *  section must still answer "what will happen when I press Send" —
   *  otherwise folding it trades a cramped panel for a blind one. */
  const deliverySummary = useMemo(() => {
    const parts: string[] = [];
    parts.push(
      remindEveryMinutes === null
        ? "No follow-ups"
        : `Follow up ${describeCadence(remindEveryMinutes)}`,
    );
    if (expiryDays !== null) parts.push(`${expiryDays}-day deadline`);
    if (enforceOrder) parts.push("one at a time");
    if (message.trim()) parts.push("note added");
    return parts.join(" · ");
  }, [remindEveryMinutes, expiryDays, enforceOrder, message]);

  // ── Review & send (PR 8b) ─────────────────────────────────────────
  // The property the record is about, when it has one (a Realty file):
  // it is what makes the default subject read like a note from a person.
  const reviewAddress =
    mergeOptions?.find((o) => o.token === "service_record.property_address")?.value ?? null;
  const reviewDefaultSubject = useMemo(
    () =>
      defaultSigningSubject({
        documentNames: packageDocs.map((d) => d.filename),
        propertyAddress: reviewAddress,
      }),
    [packageDocs, reviewAddress],
  );
  const reviewSubject = subjectDraft ?? reviewDefaultSubject;
  // ── The email's wording and what the sender keeps (PR 8f) ──────────
  // "My default wording" is per kind of document: the package's first
  // form's type, or every send when it has none.
  const firstPackageDoc = packageDocs[0] ?? null;
  const wordingType =
    (firstPackageDoc
      ? (firstPackageDoc.documentType ?? chosenTypeByDoc[firstPackageDoc.documentId] ?? null)
      : null) || ESIGN_ANY_DOCUMENT_TYPE;
  const wordingTypeLabel =
    wordingType === ESIGN_ANY_DOCUMENT_TYPE
      ? "every"
      : (typeOptions?.find((t) => t.id === wordingType)?.name ?? wordingType);
  // A video is remembered on the file it was sent from; never in mapping.
  const rememberRecord = mapping ? null : `${recordKind}:${recordId}`;
  // The library's wording, fetched the first time the window opens.
  useEffect(() => {
    if (!reviewOpen || standardBody !== null) return;
    let cancelled = false;
    apiV11
      .getSigningEmailTemplate()
      .then((r) => {
        if (!cancelled) setStandardBody(r.body);
      })
      // The compiled copy of today's wording: the preview still shows
      // what an email without a library row would say.
      .catch(() => {
        if (!cancelled) setStandardBody(SIGNING_INVITATION_FALLBACK_BODY);
      });
    return () => {
      cancelled = true;
    };
  }, [reviewOpen, standardBody]);
  // A new package starts from the sender's own wording for its kind of
  // document, and from a video they asked to remember on this file. Once
  // per opening, never over a draft or anything already typed.
  useEffect(() => {
    if (!open || draft || prefilledWordingRef.current || !settingsLoaded || !firstPackageDoc) return;
    prefilledWordingRef.current = true;
    const mine = wordingDefaults[wordingType];
    if (mine) {
      setMessage((m) => (m.trim() ? m : mine.note));
      setEmailBody((b) => b ?? mine.body);
    }
    const kept = rememberRecord
      ? rememberedVideos.find((v) => v.record === rememberRecord && v.documentType === wordingType)
      : undefined;
    if (kept) setSigningVideo((v) => v ?? kept.video);
  }, [open, draft, settingsLoaded, firstPackageDoc, wordingDefaults, wordingType, rememberRecord, rememberedVideos]);
  /** The signers, in the order step 2's list shows them. */
  const signerKeysInListOrder = eligible
    .filter((p) => selected.has(p.key) && !copyOnly.has(p.key))
    .map((p) => p.key);
  /** Each signer's place in line, 1 to n, exactly as step 2 numbers them
   *  (Nate, 2026-11-03: drag them into the order). Derived from the
   *  stored numbers rather than read off them, so the send can never
   *  carry an order the screen did not show: two people left sharing a
   *  number by an older draft, or a signer added after the order was set
   *  (who goes last), still get the number the list gave them. */
  const signingRank: Record<string, number> = Object.fromEntries(
    signersInOrder(signerKeysInListOrder, orderByKey).map((key, i) => [key, i + 1]),
  );
  /** True when a signing order is set and anyone signs: then each
   *  signer's place in line goes on the send. Off, everyone is asked at
   *  once. */
  const orderIsNumbered = enforceOrder && signerKeysInListOrder.length > 0;
  // ── Step 2, "Signers & order" (PR 8d): what its table shows ─────────
  const seatLabelFor = (key: string) =>
    casting?.find((x) => seatSignerKey(x) === key)?.role.label ?? null;
  const seatKeyFor = (key: string) =>
    casting?.find((x) => seatSignerKey(x) === key)?.role.key ?? null;
  /** The seats step 2's Role dropdown can put someone in: every signing
   *  seat nobody holds yet (see seatSigner for why not held ones). */
  const openSeats = [
    ...(casting ?? [])
      .filter((seat) => seat.role.kind === "signs" && !seat.person)
      .map((seat) => ({ key: seat.role.key, label: seat.role.label })),
    // Buyer / Seller 3 and 4 on a record with fewer people (Nate,
    // 2026-11-03): offered once the seats below are filled, and joined to
    // the casting when picked (withExtraSeat).
    ...(casting ? extraSeatsOnOffer(roles, casting) : []).map((r) => ({ key: r.key, label: r.label })),
  ];
  /** Firm-standard seat holders (the managing broker) not yet on the
   *  send, offered as one click on step 2. */
  const firmSuggestions = (casting ?? [])
    .filter((seat) => seat.role.fillRule.kind === "firm_role" && seat.person && !seat.leftOff && !seatSignerKey(seat))
    .map((seat) => ({ roleKey: seat.role.key, label: seat.role.label, name: seat.person?.displayName ?? "" }));
  const stepSigners = eligible
    .filter((p) => selected.has(p.key) && !copyOnly.has(p.key))
    .map((p) => ({
      key: p.key,
      name: p.displayName,
      email: p.email ?? "",
      roleKey: seatKeyFor(p.key),
      roleLabel: seatLabelFor(p.key),
      fromHousehold: householdPeople.some((h) => h.contact_id === p.key),
      color: (colorByContact.get(p.key) ?? signerColor(0)).base,
    }));
  // "Preview as signer" (PR 9b): who it can show, in the order they sign,
  // so it opens on the first person due to sign.
  const [previewOpen, setPreviewOpen] = useState(false);
  const previewSigners = [...stepSigners]
    .map((sg, i) => ({ sg, i }))
    .sort((a, b) =>
      enforceOrder ? (signingRank[a.sg.key] ?? 1) - (signingRank[b.sg.key] ?? 1) || a.i - b.i : a.i - b.i,
    )
    .map(({ sg }) => ({ key: sg.key, name: sg.name, color: sg.color }));
  /** What a box prints when it goes out, or null when the signer fills it. */
  const previewPrinted = (f: PrepField): string | null => {
    if ((f.value ?? "").trim()) return f.value ?? "";
    if ((f.mergeToken ?? "").trim()) {
      return boundPreview(f.mergeToken ?? "", f.signerKey, f.roleKey ?? null).value ?? "";
    }
    // A box the sender fills before it goes (locked to the preparer) is
    // never the signer's to fill.
    return f.signerKey === PREPARER_SIGNER_KEY ? "" : null;
  };
  const stepCopies = eligible
    .filter((p) => selected.has(p.key) && copyOnly.has(p.key))
    .map((p) => ({
      key: p.key,
      name: p.displayName,
      email: p.email ?? "",
      roleLabel: seatLabelFor(p.key),
      color: (colorByContact.get(p.key) ?? signerColor(0)).base,
    }));
  const stampedSeat = (casting ?? []).find((seat) => myStampSeats.has(seat.role.key)) ?? null;
  // A seat the forms have and nobody fills is said out loud (mockup note
  // 4): it prints empty, which can be right (our copy of the seller's
  // response) or a person forgotten.
  const stepEmptySeats = (casting ?? [])
    .filter((seat) => !seat.person && seat.role.kind === "signs" && !seat.leftOff)
    .map((seat) => `${seat.role.label}: nobody is in this seat, so its lines print empty.`);
  /** "Set a signing order" from step 2: everyone is numbered 1 to n in
   *  the order they are listed, then dragged into the order they sign
   *  (Nate, 2026-11-03). */
  function setOrderFromStep(on: boolean) {
    setEnforceOrder(on);
    if (!on) return;
    if (rolesCompany === "realty") {
      // Buyers together, then the Managing Broker (Nate, offer program
      // session 6). Anyone already numbered by hand keeps their number
      // only when ordering is off and back on, never mid-edit.
      setOrderByKey((prev) => ({ ...prev, ...defaultOfferSigningOrder(stepSigners) }));
      return;
    }
    setOrderByKey((prev) => ({ ...prev, ...(sequentialSigningOrder(stepSigners.map((p) => p.key), prev) ?? {}) }));
  }
  const reviewSigners = useMemo(() => {
    const counts = new Map<string, number>();
    for (const list of packageFields.values()) {
      for (const f of list) counts.set(f.signerKey, (counts.get(f.signerKey) ?? 0) + 1);
    }
    return eligible
      .filter((p) => selected.has(p.key) && !copyOnly.has(p.key))
      .map((p) => {
        const seat = casting?.find((x) => seatSignerKey(x) === p.key);
        return {
          key: p.key,
          name: p.displayName,
          email: p.email ?? "",
          role: seat ? seat.role.label : null,
          fieldCount: counts.get(p.key) ?? 0,
          color: (colorByContact.get(p.key) ?? signerColor(0)).base,
        };
      });
  }, [eligible, selected, copyOnly, casting, seatSignerKey, packageFields, colorByContact]);
  // "When it completes" (PR 12): the file's checklist is read when the
  // window opens, and each package form's type is matched against the
  // items still to come, the same match the checklist itself makes.
  const [reviewChecklist, setReviewChecklist] = useState<RecordChecklistResponse | null>(null);
  const reviewHasTab = !mapping && (recordKind === "transaction" || recordKind === "opportunity");
  useEffect(() => {
    if (!reviewOpen || !reviewHasTab) return;
    let cancelled = false;
    Promise.resolve()
      .then(() => apiV11.getRecordChecklist(recordKind as RecordChecklistKind, recordId))
      .then((res) => {
        if (!cancelled) setReviewChecklist(res);
      })
      // A convenience line: without the checklist the box just omits it.
      .catch(() => {
        if (!cancelled) setReviewChecklist(null);
      });
    return () => {
      cancelled = true;
    };
  }, [reviewOpen, reviewHasTab, recordKind, recordId]);
  const reviewCompletion = useMemo(() => {
    if (!reviewHasTab) return null;
    const types = new Set<string>();
    for (const d of packageDocs) {
      if (d.attachment) continue;
      const t = d.documentType ?? chosenTypeByDoc[d.documentId] ?? null;
      if (t) types.add(t);
    }
    const checksOff = (reviewChecklist?.checklists ?? []).flatMap((cl) =>
      cl.items
        .filter(
          (i) =>
            i.item_type === "document" &&
            i.document_definition_id !== null &&
            types.has(i.document_definition_id) &&
            (i.state === "missing" || i.state === "awaiting_signature"),
        )
        .map((i) => `${i.title} on ${cl.name}`),
    );
    // Same rule the filing uses: a household contact signing puts the
    // copy where the client can open it; paperwork only the team or a
    // vendor signs stays internal.
    const clientSigns = eligible.some(
      (p) => selected.has(p.key) && !copyOnly.has(p.key) && p.kind === "person",
    );
    return { signedShelf: clientSigns ? ("client" as const) : ("internal" as const), checksOff };
  }, [reviewHasTab, packageDocs, chosenTypeByDoc, reviewChecklist, eligible, selected, copyOnly]);
  const reviewEmptySeats = (casting ?? [])
    .filter((seat) => !seat.person && seat.role.kind === "signs" && !seat.leftOff)
    .map((seat) => seat.role.label);
  const reviewCopies = useMemo(
    () => [
      ...eligible
        .filter((p) => selected.has(p.key) && copyOnly.has(p.key))
        .map((p) => ({ name: p.displayName, email: p.email ?? "" })),
      ...forwardTo.map((r) => ({ name: r.name ?? "", email: r.email })),
    ],
    [eligible, selected, copyOnly, forwardTo],
  );
  const reviewDocuments = useMemo(
    () =>
      packageDocs.map((d) => {
        const placed = packageFields.get(d.documentId)?.length ?? 0;
        return {
          name: documentDisplayName(d.filename),
          detail:
            placed > 0
              ? `${placed} ${placed === 1 ? "field" : "fields"}`
              : d.attachment
                ? "Attached, not signed"
                : "Nothing to sign, it goes out for them to read",
        };
      }),
    [packageDocs, packageFields],
  );
  const reviewFollowUps =
    remindEveryMinutes === null
      ? "No reminders."
      : `Remind ${describeCadence(remindEveryMinutes)}, ${
          remindMax === 1 ? "once" : `up to ${remindMax} times`
        }.`;
  const reviewDeadline =
    expiryDays === null
      ? null
      : new Date(Date.now() + expiryDays * 86_400_000).toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          timeZone: "America/Phoenix",
        });
  /** "Edit" in the window: back to the screen, with that part in view. */
  const editFromReview = useCallback((section?: ReviewSection) => {
    setReviewOpen(false);
    if (!section) return;
    // The sections live in the side panel, which may be collapsed to
    // nothing; opening it first is what makes Edit land somewhere
    // (Nate, 2026-10-23: Edit on Copies went nowhere).
    if (section !== "documents") {
      setPanelCollapsed(false);
      try {
        localStorage.setItem("esign-prep-panel", "open");
      } catch {
        /* see chooseZoom */
      }
    }
    // A frame later, so a just-opened panel is in the page to find.
    window.setTimeout(() => {
      const selector: Record<ReviewSection, string> = {
        documents: ".esign-prep-rail",
        signers: ".esign-roles, [data-review-target=\"signers\"]",
        copies: "[data-fold-id=\"afterwards\"]",
        followups: "[data-fold-id=\"delivery\"]",
      };
      const el = document.querySelector<HTMLElement>(selector[section]);
      if (!el) return;
      // A folded section is opened, so the thing to change is showing.
      const fold = el.closest<HTMLElement>("[data-fold-id]") ?? el;
      if (fold.dataset.foldId && !fold.dataset.open) {
        fold.querySelector<HTMLButtonElement>(".esign-prep-fold-head")?.click();
      }
      el.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 60);
  }, []);

  // Where the app's content column actually starts, measured rather
  // than assumed. The prep screen fills that column so the TopNav and
  // the left rail stay visible and clickable; hardcoding the shell's
  // dimensions broke the day the navigation rework changed the sidebar
  // from 224px to 288px, a conflict no merge would have surfaced.
  // Re-measures on resize and on sidebar collapse (ResizeObserver sees
  // the column change width either way).
  //
  // MUST live above the `if (!open) return null` early return with
  // every other hook. These two sat BELOW it until 2026-08-25, which
  // is React error #310 by construction: a parent that keeps this
  // screen mounted and flips `open` renders two more hooks on open
  // than it did closed, and the whole route crashes to the error
  // boundary. Parents that only mount it when open never hit it —
  // which is exactly why the crash looked haunted: per entry path,
  // not per document.
  const [chromeInset, setChromeInset] = useState<{ top: number; left: number } | null>(null);
  useEffect(() => {
    if (!open) return;
    const main = document.getElementById("main-content");
    if (!main) return; // Rendered outside the shell (a test, a future route) — CSS fallbacks stand.
    const measure = () => {
      const box = main.getBoundingClientRect();
      // `top` comes from the TOP NAV's own bottom edge, not #main-content's
      // top. The nav is `position: sticky; top: 0`, so once the underlying
      // page has been scrolled at all — which it has, by the time someone
      // reaches an e-sign action further down a record — #main-content's
      // top goes negative and `Math.max(0, …)` clamped it to 0. That opened
      // this screen flush with the viewport top, covering the sticky nav
      // and leaving only the 69px rail sliver poking out above it — which
      // is what sliced the brand mark in the top-left corner down to a
      // fraction of itself (Grant flagged this in the Realty offer flow,
      // 2026-09-01). The nav's bottom edge is pinned to the viewport top
      // once it's stuck, so it never goes negative the same way.
      const topNav = document.querySelector('[data-testid="top-nav"]');
      const top = topNav ? topNav.getBoundingClientRect().bottom : Math.max(0, box.top);
      setChromeInset({ top: Math.max(0, top), left: Math.max(0, box.left) });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(main);
    window.addEventListener("resize", measure);
    // Re-measure on scroll too: the nav's stuck position (and therefore
    // this screen's top inset) can change after the screen is already
    // open, not just at mount.
    window.addEventListener("scroll", measure, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure);
    };
  }, [open]);

  /**
   * The caption a field sits against, computed here because here is
   * where the document's text layer actually is.
   *
   * The server never sees the PDF's words on a save — only the boxes —
   * so if this is not attached now, the saved map has no way to be
   * carried onto next year's revision of the form, and every field on
   * an eight-page contract gets placed by hand again
   * (shared/esign/layout-carryover.ts).
   *
   * Returns undefined rather than null when there is nothing to say, so
   * the key is simply absent from the payload instead of shipping a null
   * for every field on every send.
   */
  const anchorFor = useCallback(
    (
      rendered: RenderedPdf | null,
      box: { pageNumber: number; x: number; y: number; width: number; height: number },
    ) => {
      if (!rendered || rendered.spans.length === 0) return undefined;
      const offered = new Set((mergeOptionsRef.current ?? []).map((o) => o.token));
      return (
        anchorForBox(box, rendered.spans, {
          companySlug: effectiveCompany,
          tokenIsOffered: (t) => offered.has(t),
        }) ?? undefined
      );
    },
    [effectiveCompany],
  );

  /**
   * Teach this document's map to the company, without sending it.
   *
   * Grant, 2026-09-01: "he needs to be able to create a contract without
   * having to do all this mapping and work." The map was only ever
   * learned at the END of a real send, so the first person to use one of
   * the 27 AAR forms did the whole thing live on a client's listing —
   * and lost it entirely if they stopped before sending. This records
   * the same map the send would have taught, for everyone, now.
   *
   * Saves the document currently on screen. Layouts are matched per
   * document by content, so that is also the only unit that means
   * anything: a package is five separate maps, taught one at a time,
   * exactly as the send path teaches them.
   */
  async function handleSaveLayout() {
    if (fields.length === 0) {
      setError("Place at least one field before saving the map.");
      return;
    }
    if (needsCompany && !effectiveCompany) {
      setError("Choose which company this document is for before saving the map.");
      return;
    }
    const fingerprint = fingerprintRef.current;
    if (!fingerprint) {
      setError(
        "This document has too little text to recognize it by next time (it may be a scan).",
      );
      return;
    }
    setSavingLayout(true);
    setError(null);
    try {
      const placedFields = fields.map((f) => ({
        signerKey: f.signerKey,
        fieldType: f.fieldType,
        pageNumber: f.pageNumber,
        x: f.x,
        y: f.y,
        width: f.width,
        height: f.height,
        required: f.required,
        value: f.value,
        mergeToken: f.mergeToken ?? null,
        options: f.options ?? null,
        groupName: f.groupName ?? null,
        anchor: anchorFor(renderedRef.current, f),
      }));
      const pageSizes = (pages ?? []).map((pg) => ({
        pageNumber: pg.pageNumber,
        width: pg.widthPt,
        height: pg.heightPt,
      }));
      const res = mapping
        ? await apiV11.saveTemplateLayout(mapping.templateId, {
            signers: mappingSlots,
            fields: placedFields,
            pageSizes,
            // The blank may have been stored without a fingerprint —
            // every one uploaded before 2026-09-02 was, which is why
            // mapping refused them all with "no readable text". We have
            // just read this PDF and computed the missing value, so send
            // it; the server fills a NULL only, never overwrites.
            ...(fingerprint ? { contentFingerprint: fingerprint } : {}),
          })
        : await apiV11.saveEsignLayout(recordKind, recordId, {
            documentId,
            contentFingerprint: fingerprint,
            signerContactIds: selectedSignerKeys,
            ...(seatPayload.length > 0 ? { seats: seatPayload } : {}),
            ...(effectiveCompany ? { companySlug: effectiveCompany } : {}),
            fields: placedFields,
            pageSizes,
          });
      // Names what actually happened. "Saved" reads the same whether it
      // recorded a first answer or replaced the team's previous one, and
      // those are different acts.
      toast.success(
        res.created
          ? `Saved — every future send of ${res.name} starts with these ${res.field_count} fields.`
          : `Updated the saved map for ${res.name} — ${res.field_count} fields.`,
      );
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : mapping
            ? "Couldn't save the map just now — your fields are still here, try again."
            : "Couldn't save the map just now — your fields are still here, and sending still works.",
      );
    } finally {
      setSavingLayout(false);
    }
  }

  /**
   * "Nothing to sign — it goes out to be read" (Jake, 2026-09-29: the
   * firm's ADV rides along with the CEA as proof of delivery, and the
   * library refused it because a map needed at least one field). Saves
   * an EMPTY map on purpose, so the form reads "Read only" in the
   * library instead of "Not mapped", and nothing is ever suggested or
   * placed on it. Forms library only: on a real send a document with no
   * fields already goes out to be read, no map needed.
   */
  async function handleSaveReadOnly() {
    if (!mapping) return;
    if (fields.length > 0) {
      setError("Remove the fields first — a form with nothing to sign has no fields on it.");
      return;
    }
    const fingerprint = fingerprintRef.current;
    if (!fingerprint) {
      setError(
        "This document has too little text to recognize it by next time (it may be a scan).",
      );
      return;
    }
    setSavingLayout(true);
    setError(null);
    try {
      const res = await apiV11.saveTemplateLayout(mapping.templateId, {
        signers: mappingSlots,
        fields: [],
        pageSizes: (pages ?? []).map((pg) => ({
          pageNumber: pg.pageNumber,
          width: pg.widthPt,
          height: pg.heightPt,
        })),
        contentFingerprint: fingerprint,
        readOnly: true,
      });
      toast.success(
        `Saved — ${res.name} has nothing to sign. Add it to a template and it goes out for them to read.`,
      );
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Couldn't save that just now — try again.",
      );
    } finally {
      setSavingLayout(false);
    }
  }

  // ── "Save as draft" (Documents tab PR 8c) ─────────────────────────
  // Drafts live on Opportunity and Processing files, where the Documents
  // tab lists them. Never in mapping mode: a map is saved as a map.
  // A correction is never a draft (PR 10b): a draft of one would send a
  // second package if someone later pressed Send on it.
  const canSaveDraft =
    !mapping && !correction && (recordKind === "opportunity" || recordKind === "transaction");
  /** The draft as it stands now: the documents in order and the screen's
   *  state. Read by "Save as draft" and by autosave (PR 8e). */
  function buildDraftSnapshot(): { documents: EsignDraftDocument[]; state: EsignDraftStateV1 } {
    // What each document has, the one on screen from live state and the
    // rest from where they are parked. A document still waiting to be
    // read back from the draft this screen opened keeps its saved work.
    const work: Record<string, DraftDocumentWork> = {};
    for (const d of packageDocs) {
      const parked = parkedRef.current.get(d.documentId);
      const live =
        d.documentId === documentId
          ? { fields, stamps, fingerprint: fingerprintRef.current }
          : parked
            ? { fields: parked.fields, stamps: parked.stamps, fingerprint: parked.fingerprint }
            : null;
      const pending = draftWorkRef.current.get(d.documentId) ?? null;
      const keep = live && worthKeeping(live) ? live : pending;
      if (keep) work[d.documentId] = keep;
    }
    const state: EsignDraftStateV1 = {
      v: 1,
      companySlug: chosenCompany || null,
      work,
      chosenTypeByDoc,
      confirmedPagesByDoc,
      guests,
      selected: [...selected],
      copyOnly: [...copyOnly],
      casting,
      enforceOrder,
      orderByKey,
      remind: { amount: remindAmount, unit: remindUnit, times: remindMaxText },
      expiryDays,
      message,
      subject: subjectDraft,
      video: signingVideo,
      emailBody,
      forwardTo,
    };
    const documents = packageDocs.map((d) => ({
      documentId: d.documentId,
      filename: d.filename,
      documentType: d.documentType ?? null,
      origin: d.origin ?? null,
      attachment: d.attachment === true,
    }));
    return { documents, state };
  }
  // Autosave fires from a timer, after the render that scheduled it, so
  // it reads the snapshot and the draft it saves over through refs.
  const buildDraftSnapshotRef = useRef(buildDraftSnapshot);
  buildDraftSnapshotRef.current = buildDraftSnapshot;

  /**
   * Write the draft: a new one, or over the one this screen has. Quiet is
   * autosave's form: no toast, and the top bar says "Autosaved" instead.
   * Resolves to whether it saved.
   */
  function persistDraft(quiet: boolean): Promise<boolean> {
    if (!canSaveDraft || draftSaveInFlightRef.current) return Promise.resolve(false);
    const p = writeDraft(quiet, buildDraftSnapshotRef.current(), savedDraftRef.current).finally(() => {
      draftSavePromiseRef.current = null;
    });
    draftSavePromiseRef.current = p;
    return p;
  }
  /** One write of a given snapshot, new or over `target`. */
  async function writeDraft(
    quiet: boolean,
    snapshot: { documents: EsignDraftDocument[]; state: EsignDraftStateV1 },
    target: { id: string; versionNumber: number } | null,
  ): Promise<boolean> {
    const signature = JSON.stringify(snapshot);
    draftSaveInFlightRef.current = true;
    userTouchedRef.current = false;
    // Only a save the sender asked for changes the button: an autosave
    // flipping "Save draft" to "Saving…" on every pause read as the
    // screen doing something to their work. The header carries autosave.
    if (!quiet) setSavingDraft(true);
    if (quiet) setAutosave({ kind: "saving" });
    try {
      const res = target
        ? await apiV11.saveEsignDraft(recordKind, recordId, target.id, {
            ...snapshot,
            expected_version_number: target.versionNumber,
          })
        : await apiV11.createEsignDraft(recordKind, recordId, {
            ...snapshot,
            ...(realtyOfferId ? { realty_offer_id: realtyOfferId } : {}),
          });
      const next = { id: res.id, versionNumber: res.version_number };
      savedDraftRef.current = next;
      lastWrittenDraftRef.current = next;
      setSavedDraft(next);
      lastSavedSignatureRef.current = signature;
      savedThisSessionRef.current = true;
      setAutosave({ kind: "saved", at: new Date() });
      onDraftSaved?.();
      return true;
    } catch (err) {
      // Someone saved this draft elsewhere since it was opened. Autosave
      // stops rather than write over their work; "Save draft" says the
      // same thing if pressed.
      const conflict = err instanceof ApiError && err.status === 409;
      setAutosave(conflict ? { kind: "blocked" } : { kind: "failed" });
      if (!quiet) {
        toast.error(err instanceof ApiError ? err.message : "Couldn't save the draft. Try again.");
      }
      return false;
    } finally {
      draftSaveInFlightRef.current = false;
      if (!quiet) setSavingDraft(false);
    }
  }

  async function handleSaveDraft() {
    if (!canSaveDraft || savingDraft) return;
    window.clearTimeout(autosaveTimerRef.current);
    if (await persistDraft(false)) {
      toast.success("Saved as a draft. It's on the Documents tab, under the packages.");
    }
  }

  /** Whether there is anything a person did worth a draft, before one
   *  exists: autosave never creates a draft just because a screen opened. */
  function snapshotHasWork(snapshot: { state: EsignDraftStateV1 }): boolean {
    const st = snapshot.state;
    return (
      Object.keys(st.work).length > 0 ||
      st.message.trim() !== "" ||
      st.subject !== null ||
      st.video !== null ||
      st.emailBody !== null ||
      st.guests.length > 0 ||
      st.forwardTo.length > 0 ||
      st.enforceOrder
    );
  }

  /** Autosave's one decision, run a moment after the person pauses. */
  function autosaveNow(): void {
    if (!canSaveDraft || result || sendingRef.current || !userTouchedRef.current) return;
    if (autosaveRef.current.kind === "blocked") return;
    if (draftSaveInFlightRef.current) {
      scheduleAutosave();
      return;
    }
    const snapshot = buildDraftSnapshotRef.current();
    if (JSON.stringify(snapshot) === lastSavedSignatureRef.current) {
      userTouchedRef.current = false;
      return;
    }
    if (!savedDraftRef.current && !snapshotHasWork(snapshot)) return;
    void persistDraft(true);
  }
  const autosaveNowRef = useRef(autosaveNow);
  autosaveNowRef.current = autosaveNow;
  function scheduleAutosave(): void {
    window.clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = window.setTimeout(() => autosaveNowRef.current(), AUTOSAVE_PAUSE_MS);
  }
  /** Any click or keystroke inside the screen is a person working: mark
   *  it, and save a moment after they stop. Capture phase, so a control
   *  that stops propagation still counts. */
  function noteUserWork(e: React.SyntheticEvent): void {
    if (!canSaveDraft || result) return;
    // Looking is not working: zoom, the panel toggle, moving between
    // steps and closing change nothing worth a save.
    const t = e.target;
    if (t instanceof Element && t.closest("[data-no-autosave]")) return;
    userTouchedRef.current = true;
    scheduleAutosave();
  }

  const lastDeliverRef = useRef<"email" | "in_person">("email");

  /** In correction mode: the first thing on this package that changed
   *  but may not, in words, or null. Compares every document's locked
   *  part (live for the one on screen, parked for the rest) with how the
   *  package went out. */
  function correctionLockProblem(): string | null {
    if (!correction || !correctionBaseline) return null;
    for (const d of packageDocs) {
      const live = d.documentId === documentId;
      const st = live ? { fields, stamps } : parkedRef.current.get(d.documentId);
      if (!st) continue;
      const now = lockedSignature(st.fields, st.stamps, correctionLockedKeys);
      const was = correctionBaseline.get(d.documentId) ?? lockedSignature([], [], correctionLockedKeys);
      if (now !== was) {
        return `On "${documentDisplayName(d.filename)}", something that can't change after sending was changed: a signed person's box, a box printed into the document, or a stamped signature. Put it back as it was, or close this and use "Change the document itself".`;
      }
    }
    return null;
  }

  async function handleSend(
    deliver: "email" | "in_person" = "email",
    /** The advisor's answer to "the record says something different —
     *  update it too?" Absent on the first attempt, which is how the
     *  question gets asked at all: the server answers 409 with the
     *  terms, and the same send runs again carrying the answer. */
    recordSync?: "update_record" | "document_only",
    /** True when the send comes from the Review & send window. An email
     *  send that hasn't been reviewed opens the window instead of
     *  sending; the record-sync retry above has already been reviewed. */
    reviewed = false,
  ) {
    if (selected.size === 0) {
      setError("Pick at least one signer.");
      return;
    }
    // A seat the OS could not fill on its own (two parties tagged the
    // same role) is a question, and a question is not a send.
    if (roleBlockers.length > 0) {
      setError(roleBlockers[0]!);
      return;
    }
    // Caught here as well as server-side, because the server's refusal
    // arrives after the send button has already been pressed on a
    // document someone spent a minute laying out. The server check is
    // the gate; this is the courtesy.
    if (needsCompany && !effectiveCompany) {
      setError("Choose which company this document is for before sending.");
      return;
    }
    // Package-wide, not just the open tab (2026-09-18: a document with
    // nothing to sign — correctly — was blocking Send because it
    // happened to be the one on screen; see packageBlockers above). A
    // single document is a package of one, so this covers that case
    // too without a separate branch.
    if (packageBlockers.totalFieldCount === 0) {
      setError("Place at least one field — click a field type, then click the page.");
      return;
    }
    if (offPageNote) {
      setError(offPageNote);
      return;
    }
    if (otherDocOffPageNote) {
      setError(otherDocOffPageNote);
      return;
    }
    if (signersWithoutFields.length > 0) {
      setError(
        `${signersWithoutFields.map((p) => p.displayName).join(", ")} ` +
          `${signersWithoutFields.length === 1 ? "has" : "have"} no fields on ` +
          `${isPackage ? "any of these documents" : "the document"} — ` +
          `accept or place their fields, or uncheck them.`,
      );
      return;
    }
    // THE "YOU HAVEN'T OPENED THIS DOCUMENT" STOP IS GONE (Grant's
    // decision 2, 2026-09-29). Every document in the package is read
    // when it opens, its ghosts offered and counted, and the review
    // list under Send says per document what goes and what stays — so
    // a document the sender never clicked onto is not a document nobody
    // looked at. What remains is the one stop that is not the sender's
    // to waive:
    //
    // A document the other side wrote does not go out until somebody has
    // looked at every page of it. This one does NOT relent on a second
    // press: "send it anyway" is a reasonable answer to "this attachment
    // has nothing to sign" and an unreasonable one to "we guessed where
    // your buyer's signature goes on their contract." The server refuses
    // it too (409 pages_not_confirmed); this is the courtesy that names
    // the pages before the button is pressed.
    if (unconfirmedTheirPages.length > 0) {
      setError(unconfirmedPagesMessage(unconfirmedTheirPages));
      return;
    }
    if (fieldOwnershipProblem) {
      setError(fieldOwnershipProblem);
      return;
    }
    // Every check above has passed, so what the window shows is what
    // can actually go. In person emails nobody and skips it.
    // A correction has no review window: what goes is the change, and the
    // window that asked for its reason has already said what happens. The
    // locked part is checked against how the package went out first.
    if (correction) {
      const problem = correctionLockProblem();
      if (problem) {
        setError(problem);
        return;
      }
    }
    if (deliver === "email" && !recordSync && !reviewed && !correction) {
      setError(null);
      setReviewOpen(true);
      return;
    }
    lastDeliverRef.current = deliver;
    setSending(true);
    setError(null);
    // No autosave from here on: a save still on its way finishes first,
    // so the send clears the draft it made rather than leaving one behind.
    window.clearTimeout(autosaveTimerRef.current);
    sendingRef.current = true;
    if (draftSavePromiseRef.current) await draftSavePromiseRef.current;
    try {
      // The active document's work is parked first so the payload is
      // built from ONE place for every document — including the one on
      // screen, whose live state would otherwise be a special case and
      // the obvious thing to get wrong.
      parkedRef.current.set(documentId, {
        fields,
        stamps,
        pages,
        rendered: renderedRef.current,
        fingerprint: fingerprintRef.current,
        suggested: true,
        appliedLayout,
        appliedLayoutInfo,
        layoutPositionsUnchecked,
        formRecognition,
        suggestNote,
        writtenOnCount,
        detectedBlanks,
      });
      const payloadDocuments = packageDocs.map((d) => {
        const st = parkedRef.current.get(d.documentId);
        // WHAT THIS DOCUMENT'S TYPE IS SET IN, per page, read off its own
        // text layer (Baker, 2026-09-18). A field mapped from a caption
        // already carries its own size; this is the answer for a box
        // placed by hand, and for every box coming back from a map saved
        // before sizes were carried at all — which is the case that
        // matters, because Baker's StewDocs map is one of those and it
        // gets the right answer here without being re-taught.
        const bodyTypeByPage = new Map<number, number | null>();
        const typeSizeFor = (pageNumber: number): number | undefined => {
          if (!bodyTypeByPage.has(pageNumber)) {
            const spans = st?.rendered?.spans ?? [];
            bodyTypeByPage.set(pageNumber, dominantTypeSize(spans, pageNumber));
          }
          return bodyTypeByPage.get(pageNumber) ?? undefined;
        };
        /** Either `{ fontSize }` or nothing at all — never the key set to
         *  undefined, which `exactOptionalPropertyTypes` rejects and which
         *  would in any case say "the size is undefined" rather than "no
         *  size was read". */
        const fontSizeEntry = (f: PrepField): { fontSize?: number } => {
          const size = f.fontSize ?? typeSizeFor(f.pageNumber);
          return size === undefined ? {} : { fontSize: size };
        };
        // ONLY WHAT THE SENDER TOUCHED GOES OUT (2026-09-29). The ghosts
        // travel beside the fields under their own key, for one reason:
        // the map this send re-teaches must keep remembering them. The
        // server reads `ghosts` for that and nothing else.
        const { sent, ghosts } = partitionForSend(st?.fields ?? []);
        const asPayload = (f: PrepField) => ({
          signerKey: f.signerKey,
          // The seat a LOCKED box reads its person from. Travels with
          // the field because the server resolves the name from it —
          // see bindingPersonSignerKey.
          ...(f.roleKey ? { roleKey: f.roleKey } : {}),
          fieldType: f.fieldType,
          // A page of THIS document, which is what the server stores
          // and what a saved layout is keyed in. The translation into
          // the merged envelope's numbering happens server-side.
          pageNumber: f.pageNumber,
          x: f.x,
          y: f.y,
          width: f.width,
          height: f.height,
          required: f.required,
          value: f.value,
          // A VALUE WE FILLED IN PRINTS AND CANNOT BE RETYPED — unless
          // the sender deliberately said otherwise on this one box
          // (Baker, 2026-09-18). The rule itself is the server's; this
          // only carries the exception.
          signerMayEdit: f.signerMayEdit === true,
          // Its own size where the document told us, this page's body
          // type where it did not. Omitted rather than sent as a guess
          // when the page has no readable text at all, so the server
          // falls back to the forms' own body size instead of to
          // something invented here.
          ...fontSizeEntry(f),
          // A box spanning several lines says so, with its pitch: the
          // server prints such a value itself, one line per rule.
          ...(isMultilineField(f)
            ? {
                multiline: true,
                linePitch: f.linePitch,
                ...(f.firstLineIndent ? { firstLineIndent: f.firstLineIndent } : {}),
              }
            : {}),
          ...(f.strike ? { strike: true } : {}),
          ...(f.textStyle ? { textStyle: f.textStyle } : {}),
          mergeToken: f.mergeToken ?? null,
          options: f.options ?? null,
          groupName: f.groupName ?? null,
          // Each document in a package carries its own text layer, so
          // the anchor is computed against THAT document's spans —
          // never the one currently on screen.
          anchor: anchorFor(st?.rendered ?? null, f),
          // Sent so the server can honour a rule that is about the
          // ENVELOPE rather than the record: the purchase contract's
          // twelve "Addenda Incorporated" boxes tick when their
          // addendum is in this package, which is a fact only the
          // send knows. Ignored for every other field.
          fillRule: f.fillRule ?? null,
        });
        return {
          documentId: d.documentId,
          fields: sent.map(asPayload),
          // A ghost for someone not on the send cannot be remembered
          // positionally and would fail the server's signer check, so
          // it stays behind; the preparer's own ghosts belong to nobody
          // and always travel.
          ghosts: ghosts
            .filter((f) => isPreparerField(f) || selected.has(f.signerKey))
            .map(asPayload),
          pageSizes: (st?.pages ?? []).map((pg) => ({
            pageNumber: pg.pageNumber,
            width: pg.widthPt,
            height: pg.heightPt,
          })),
          stamps: (st?.stamps ?? []).map((sm) => ({
            pageNumber: sm.pageNumber,
            x: sm.x,
            y: sm.y,
            width: sm.width,
            height: sm.height,
          })),
          // Per document, so each one teaches and matches its OWN saved
          // layout — a form learned inside a package still auto-places
          // when it turns up on its own next month.
          ...(st?.fingerprint ? { contentFingerprint: st.fingerprint } : {}),
          // Only ever sent when THIS document had no type of its own —
          // the server ignores it otherwise, and so do we. Per-document
          // (2026-09-09), not the single top-level field below: a
          // package can carry several untyped documents at once, each
          // needing its own answer.
          ...(!d.documentType && chosenTypeByDoc[d.documentId]
            ? { documentType: chosenTypeByDoc[d.documentId] }
            : {}),
          // The sender's assertion that they looked at each page of a
          // document the other side wrote. Sent for every document
          // because the server decides which ones it matters for — it
          // reads `origin` off the row rather than trusting the client
          // to say which documents need reviewing.
          confirmedPages: confirmedPagesByDoc[d.documentId] ?? [],
        };
      });
      const sendBody: Parameters<typeof apiV11.sendForSignature>[2] = {
        // A package goes as the ordered list; a single document keeps
        // the flat body it has always sent, so the common path is
        // byte-for-byte the request it was before packages existed.
        ...(isPackage ? { documents: payloadDocuments } : { documentId }),
        ...(effectiveCompany ? { companySlug: effectiveCompany } : {}),
        // In panel order, which is the order the sender sees and the
        // ordinal a saved layout is taught in. A guest travels as the
        // name and email that were typed; a person on the record
        // travels as their contact id ALONE, so the server reads their
        // name and address off the contact row rather than from this
        // request.
        signers: eligible
          .filter((p) => selected.has(p.key))
          .map((p) => {
            // The seat this person was cast into, recorded on their
            // signer row so the envelope can say who signed AS what.
            const seat = casting?.find((x) => seatSignerKey(x) === p.key);
            const seatRole =
              seat && !copyOnly.has(p.key)
                ? { roleKey: seat.role.key, roleKind: "signs" as const }
                : {};
            // Each signer's number, only when every signer has one
            // (PR 8d); otherwise the list order is the signing order.
            const orderEntry =
              orderIsNumbered && !copyOnly.has(p.key) ? { order: signingRank[p.key] ?? 1 } : {};
            return p.contactId
              ? {
                  contactId: p.contactId,
                  role: copyOnly.has(p.key) ? ("cc" as const) : ("signer" as const),
                  ...seatRole,
                  ...orderEntry,
                }
              : {
                  name: p.displayName,
                  email: p.email ?? "",
                  signerKey: p.key,
                  role: copyOnly.has(p.key) ? ("cc" as const) : ("signer" as const),
                  ...seatRole,
                  ...orderEntry,
                };
          }),
        // The whole casting as it goes out — every seat, who filled it
        // or that it was left off — including the name-only seats that
        // have no signer row anywhere else.
        ...(casting ? { casting: castingRecord(casting) } : {}),
        enforceSigningOrder: enforceOrder,
        message: message.trim() || undefined,
        // What the sender read in Review & send. Email only: an in-person
        // send has no email for a subject or a video to go in.
        ...(deliver === "email" ? { emailSubject: reviewSubject } : {}),
        ...(deliver === "email" && signingVideo ? { video: signingVideo.url } : {}),
        // The email's lines, when the sender changed them for this send.
        ...(deliver === "email" && emailBody ? { emailBody } : {}),
        deliver,
        // Clears the draft this package came from, once it has gone.
        ...(savedDraftRef.current ? { draftId: savedDraftRef.current.id } : {}),
        // Files the package under the offer it is for (PR 8e).
        ...(realtyOfferId ? { realtyOfferId } : {}),
        schedule: {
          expiryDays,
          reminderEveryMinutes: remindEveryMinutes,
          // Days too, for a Worker that predates minutes: only when the
          // choice is whole days (an older Worker then chases as before).
          reminderEveryDays:
            remindEveryMinutes !== null && remindEveryMinutes % 1440 === 0
              ? remindEveryMinutes / 1440
              : null,
          reminderMax: remindEveryMinutes === null ? 0 : remindMax,
        },
        ...(forwardTo.length > 0
          ? {
              forwardTo: forwardTo.map((r) => ({
                email: r.email,
                name: r.name,
                source: r.source,
                sourceDetail: r.sourceDetail,
              })),
            }
          : {}),
        ...(isPackage
          ? {}
          : {
              fields: payloadDocuments[0]!.fields,
              ghosts: payloadDocuments[0]!.ghosts,
              pageSizes: payloadDocuments[0]!.pageSizes,
              stamps: payloadDocuments[0]!.stamps,
              // The one-document form of the same assertion the package
              // entries carry. Read by the server only when this
              // document's own row says the other side wrote it.
              confirmedPages: payloadDocuments[0]!.confirmedPages,
              // Lets this send TEACH a saved layout, so the next send of
              // the same document starts with the fields already placed.
              ...(fingerprintRef.current
                ? { contentFingerprint: fingerprintRef.current }
                : {}),
            }),
        // The single-document form's type only — a package sends each
        // member's answer on its own entry above instead (2026-09-09),
        // since `chosenType` here is just whichever document happens to
        // be active on screen and would otherwise leak into the
        // envelope-wide field regardless of which one that was. Only
        // ever sent when the document had no type of its own — the
        // server ignores it otherwise, and so do we.
        ...(!isPackage && !realDocumentType(primaryDocumentType) && chosenType
          ? { documentType: chosenType }
          : {}),
        ...(recordSync ? { recordSync } : {}),
      };
      if (correction) {
        await apiV11.correctEsignEnvelope(correction.envelopeId, {
          ...sendBody,
          // The documents travel as the list in both forms, so the server
          // can check they are the package's own.
          documents: payloadDocuments,
          reason: correction.reason,
        });
        toast.success(
          "Correction sent to the signing service. The people whose part changed get a fresh link as soon as it confirms.",
        );
        onCorrected?.();
        return;
      }
      const res = await apiV11.sendForSignature(recordKind, recordId, sendBody);
      setResult(res);
      setReviewOpen(false);
      // What the sender asked to keep for next time (PR 8f). Only once the
      // package has really gone, so a failed send remembers nothing.
      if (deliver === "email" && saveWordingDefault) {
        setWordingDefaults({ ...wordingDefaults, [wordingType]: { note: message, body: emailBody } });
      }
      if (deliver === "email" && rememberVideoOn && signingVideo && rememberRecord) {
        setRememberedVideos(
          rememberVideo(
            rememberedVideos,
            { record: rememberRecord, documentType: wordingType, video: signingVideo },
            new Date().toISOString(),
          ),
        );
      }
      const what = isPackage
        ? `${packageDocs.length} documents`
        : `"${primaryFilename}"`;
      const left =
        sendCounts.ghosts > 0
          ? ` ${sendCounts.ghosts} ${sendCounts.ghosts === 1 ? "blank" : "blanks"} left empty were not sent.`
          : "";
      toast.success(
        deliver === "in_person"
          ? `${what} ${isPackage ? "are" : "is"} ready to sign — ${sendCounts.sent} ${
              sendCounts.sent === 1 ? "field" : "fields"
            }.${left}`
          : `Sent ${what} for signature — ${sendCounts.sent} ${
              sendCounts.sent === 1 ? "field" : "fields"
            }.${left}`,
      );
      onSent?.({ envelope_id: res.envelope_id, realtyOfferAttached: res.realty_offer_attached === true });
    } catch (err) {
      // Not a failure — a question the server is the only one able to
      // ask, because only it resolved the record's values. Rendered as
      // a decision rather than a red banner: nothing is wrong, two
      // things simply disagree and somebody has to say which is true.
      if (err instanceof ApiError && err.code === "record_terms_differ") {
        const terms = (err.data as { terms?: RecordSyncTerm[] } | null)?.terms;
        if (terms && terms.length > 0) {
          setSyncPrompt({ deliver: lastDeliverRef.current, terms });
          setSending(false);
          return;
        }
      }
      setError(err instanceof ApiError ? err.message : "Couldn't send this document — try again.");
    } finally {
      setSending(false);
      sendingRef.current = false;
      // Success or not, back to the screen: a failure's message and the
      // "does the record match?" question are both said there.
      setReviewOpen(false);
    }
  }

  function handleClose() {
    // ── What closing does to the draft (PR 8e) ──
    // Unsent work is kept: anything not yet saved is saved now, and the
    // toast says where it went, with Discard for a package nobody wants.
    // A screen that sent, mapped, or saved nothing says nothing.
    window.clearTimeout(autosaveTimerRef.current);
    if (canSaveDraft && !result && !sending) {
      // Everything is captured NOW: the reset below empties the screen
      // before a save on its way could read it.
      const snapshot = buildDraftSnapshot();
      const unsaved =
        userTouchedRef.current &&
        autosave.kind !== "blocked" &&
        JSON.stringify(snapshot) !== lastSavedSignatureRef.current &&
        (savedDraftRef.current !== null || snapshotHasWork(snapshot));
      const targetNow = savedDraftRef.current;
      const inFlight = draftSavePromiseRef.current;
      const hadSaved = savedThisSessionRef.current;
      const kind = recordKind;
      const id = recordId;
      void (async () => {
        let keptId: string | null = hadSaved ? (savedDraftRef.current?.id ?? null) : null;
        if (inFlight) {
          await inFlight;
          keptId = lastWrittenDraftRef.current?.id ?? keptId;
        }
        if (unsaved) {
          const target = inFlight ? (lastWrittenDraftRef.current ?? targetNow) : targetNow;
          if (await writeDraft(true, snapshot, target)) keptId = lastWrittenDraftRef.current?.id ?? keptId;
        }
        if (!keptId) return;
        const draftId = keptId;
        toast.success("Kept as a draft on the Documents tab.", {
          action: {
            label: "Discard",
            onClick: () => {
              void apiV11
                .discardEsignDraft(kind, id, draftId)
                .then(() => {
                  onDraftSaved?.();
                  toast.success("Discarded the draft.", {
                    action: {
                      label: "Undo",
                      onClick: () => {
                        void apiV11
                          .restoreEsignDraft(kind, id, draftId)
                          .then(() => {
                            onDraftSaved?.();
                            toast.success("Brought the draft back.");
                          })
                          .catch(() => toast.error("Couldn't bring the draft back."));
                      },
                    },
                  });
                })
                .catch(() => toast.error("Couldn't discard the draft. It's still on the Documents tab."));
            },
          },
        });
      })();
    }
    userTouchedRef.current = false;
    lastSavedSignatureRef.current = null;
    savedThisSessionRef.current = false;
    setAutosave({ kind: "idle" });
    setResult(null);
    setError(null);
    setMessage("");
    setReviewOpen(false);
    setSubjectDraft(null);
    setSigningVideo(null);
    setEmailBody(null);
    setSaveWordingDefault(false);
    setRememberVideoOn(false);
    prefilledWordingRef.current = false;
    setChosenType("");
    setFields([]);
    setStamps([]);
    setActiveIndex(0);
    setSyncPrompt(null);
    // A background read still running belongs to the package that just
    // closed: it must write nothing into the next one's map.
    openGenRef.current += 1;
    inFlightRef.current.clear();
    setReadingDocId(null);
    setReadFailures({});
    setJumpKey(null);
    packageReadRef.current = false;
    setPackageReadNote(null);
    // The sender's drag applied to THAT envelope. A screen kept mounted
    // and reopened on a different package must start from the order its
    // caller asked for, not from the last one's.
    setDocOrder(null);
    setDragIndex(null);
    setDragOverIndex(null);
    parkedRef.current.clear();
    setSelectedKey(null);
    setSelectedStampKey(null);
    setPlacingType(null);
    setPlacingLocked(false);
    setPlacingStamp(false);
    setOrderByKey({});
    setSignersStep(false);
    setPreviewOpen(false);
    // A draft is opened afresh each time (PR 8c).
    appliedDraftIdRef.current = null;
    restoredDraftIdRef.current = null;
    draftWorkRef.current = new Map();
    draftCastingRef.current = null;
    savedDraftRef.current = null;
    lastWrittenDraftRef.current = null;
    setSavedDraft(null);
    onClose();
  }

  if (!open) return null;

  // ── The toolbar and the one finish button (Grant, 2026-10-14) ──────────
  // Mockup: docs/design/esign-prepare-and-send-toolbar-mockup-v1.html.
  // Both are new HOMES for controls that already existed (the panel's field
  // palette and the footer's four buttons). Nothing here decides anything the
  // old buttons did not: the same state, the same handlers, the same
  // disabled rules, moved.
  const armedTool: ToolId | null = armedToolId({ placingType, placingLocked, placingStrike, pendingMergeToken });

  /** Pressing a tool ARMS it, same as the palette it replaces. Always arm:
   *  pressing the armed one again must never silently disarm (a tester lost
   *  a placement to exactly that, 2026-08-22). Esc is how you stop. */
  function armTool(requested: ToolId) {
    // In fill-now mode the plain tools are the sender's locked twins.
    const tool: ToolId =
      fillNow && requested === "text" ? "locked_text" : fillNow && requested === "checkbox" ? "locked_checkbox" : requested;
    setPlacingStamp(false);
    setSelectedKey(null);
    setRecordFieldPickerOpen(false);
    if (tool === "locked_text" || tool === "strike") {
      // YOU FILL IT, THEY CANNOT CHANGE IT (Baker, 2026-09-14). Belongs to
      // the sender, so it needs no active signer. A strike (PR 4a) is the
      // same kind of box, printing a line through the words under it.
      setPlacingType("text");
      setPlacingLocked(true);
      setPlacingStrike(tool === "strike");
      setPendingMergeToken(null);
      return;
    }
    if (tool === "locked_checkbox") {
      // CHECKED BY YOU (Nate, 2026-10-15). The sender's own checkbox:
      // needs no signer, and is placed already checked because the only
      // reason to place one is to tick it (the side panel un-ticks).
      setPlacingType("checkbox");
      setPlacingLocked(true);
      setPlacingStrike(false);
      setPendingMergeToken(null);
      return;
    }
    if (tool === "record") return; // opened by the From record button, not armed here
    setPlacingType(tool);
    setPendingMergeToken(null);
    setPlacingLocked(false);
    setPlacingStrike(false);
  }

  const toolbarSigners = eligible
    .filter((p) => selected.has(p.key))
    .map((p) => ({
      key: p.key,
      name: p.displayName,
      color: colorByContact.get(p.key) ?? signerColor(0),
      generic: mapping ? !mappingSlots.find((sl) => sl.id === p.key)?.roleKey : undefined,
    }));
  // Seats the form could still add: the ones a person signs or the sender
  // stamps, not already on a slot. Name-only seats (escrow, seller on a buy
  // form) print a name and are reached through the Panel's seat picker.
  const toolbarAddSeats = mapping && mappingSlots.length < MAX_MAPPING_SLOTS
    ? mappingCatalog
        .filter((r) => r.kind !== "name_only" && !mappingSlots.some((sl) => sl.roleKey === r.key))
        .map((r) => ({ key: r.key, label: r.label }))
    : undefined;
  const activeSignerName = toolbarSigners.find((s) => s.key === activeSignerId)?.name ?? null;
  const toolbarHint = armedHint(
    armedTool,
    activeSignerName,
    pendingMergeToken ? boundPreview(pendingMergeToken).label : null,
    !!mapping,
  );

  // One rule for "may this go out", shared by Send and "sign now". Said once
  // because two copies of it is how one button turns gray while the other
  // stays live. Package-wide (2026-09-18): a document with nothing to sign
  // must never be why Send is gray, and a problem on a document nobody is
  // viewing must still stop it. See packageBlockers.
  const sendBlocked =
    sending ||
    eligible.length === 0 ||
    !pages ||
    packageBlockers.totalFieldCount === 0 ||
    packageBlockers.offPageByDoc.length > 0 ||
    packageBlockers.blockingByDoc.length > 0 ||
    packageBlockers.unfilledMergeByDoc.length > 0;

  // Teach the form, send nothing (2026-09-01). The quietest way to finish:
  // mapping a blank form is a once-per-form job. It is offered on every send
  // anyway, because the map worth keeping is usually discovered doing real
  // work on a real contract. Unlike Send it does NOT require the merge
  // pre-flight to be clean: a blank THIS record can't fill is still the
  // right field to remember.
  const templateAction: FinishAction = {
    key: "template",
    label: savingLayout ? "Saving…" : templateButtonLabel(activeDoc.filename),
    hint: "Keep this layout for every future send of the form",
    title: `Save this field layout so every future send of ${templateNameFor(activeDoc.filename)} lands in place. It saves seats and positions, never this file's values.`,
    disabled:
      savingLayout ||
      sending ||
      !pages ||
      fields.length === 0 ||
      offPageFields.length > 0 ||
      unconfiguredFields.length > 0,
    onClick: () => void handleSaveLayout(),
  };
  const finishPrimary: FinishAction = mapping
    ? templateAction
    : {
        key: "send",
        label: sending ? "Sending…" : sendLabel,
        disabled: sendBlocked,
        onClick: () => void handleSend("email"),
      };
  const finishMore: FinishAction[] = mapping
    ? fields.length === 0
      ? [
          {
            key: "read-only",
            label: "Nothing to sign — it's for them to read",
            title: "For a form the client only has to receive, like an ADV or a privacy notice",
            disabled: savingLayout || !pages,
            onClick: () => void handleSaveReadOnly(),
          },
        ]
      : []
    : [
        // In-person signing (Grant, 2026-08-24): same envelope, same fields,
        // nobody is emailed and the links come back as QR codes for the
        // person across the desk. A correction is emailed, never signed
        // across the desk.
        ...(correction
          ? []
          : [
              {
                key: "in-person",
                label: "They're here — sign now",
                hint: "Hand over the device and sign in person",
                disabled: sendBlocked,
                onClick: () => void handleSend("in_person"),
              },
            ]),
        // Save as draft (PR 8c): keeps everything on this screen and sends
        // nothing. Never greyed out by the checks that gate Send: a draft is
        // for work that isn't finished.
        ...(canSaveDraft
          ? [
              {
                key: "draft",
                label: savingDraft ? "Saving…" : savedDraft ? "Save draft" : "Save as draft",
                hint: "Keep it here and finish later. Nothing is sent",
                title: "Keep this package as it is and finish it later. Nothing is sent.",
                disabled: savingDraft || sending || !pages,
                onClick: () => void handleSaveDraft(),
              },
            ]
          : []),
        templateAction,
      ];

  return (
    <div
      className="esign-prep-screen"
      role="dialog"
      aria-label={`Prepare ${documentFilename} for signature`}
      onPointerDownCapture={noteUserWork}
      onPointerUpCapture={noteUserWork}
      onKeyDownCapture={noteUserWork}
      onChangeCapture={noteUserWork}
      style={
        chromeInset
          ? ({
              "--esign-prep-top": `${chromeInset.top}px`,
              "--esign-prep-left": `${chromeInset.left}px`,
            } as React.CSSProperties)
          : undefined
      }
    >
      {/* ── Top bar ── */}
      <div className="esign-prep-topbar">
        <div className="esign-prep-titles">
          <span className="esign-prep-eyebrow">
            {mapping ? "Mapping this form" : correction ? "Correct & resend" : (eyebrow ?? "Prepare & send")}
            {canSaveDraft && !result ? (
              <span
                className="esign-prep-autosave"
                data-state={autosave.kind}
                role="status"
                aria-live="polite"
              >
                {autosaveLabel(autosave) ? ` · ${autosaveLabel(autosave)}` : ""}
              </span>
            ) : null}
          </span>
          <span className="esign-prep-title">{documentFilename}</span>
        </div>
        <div className="esign-prep-topbar-acts" data-no-autosave>
          {pages && !signersStep && !result && !mapping && previewSigners.length > 0 ? (
            <Button variant="sw-default" size="sm" onClick={() => setPreviewOpen(true)}>
              Preview as signer
            </Button>
          ) : null}
          {startOnSigners && !signersStep && !result && !mapping ? (
            <Button variant="sw-default" size="sm" onClick={() => setSignersStep(true)}>
              ← Signers &amp; order
            </Button>
          ) : null}
          <Button variant="ghost" onClick={handleClose} disabled={sending}>
            {result ? "Done" : "Cancel"}
          </Button>
          {!result && !signersStep ? (
            <FinishSplitButton primary={finishPrimary} more={finishMore} />
          ) : null}
        </div>
      </div>

      {/* ── Toolbar: zoom, who a placed field is for, one click per field
          type, the record picker, Panel (mockup 2026-10-14) ── */}
      {pages && !result && !signersStep ? (
        <PrepareToolbar
          zoom={{
            fit: zoom === "fit",
            percent: Math.round(displayScale * 100),
            onFit: () => chooseZoom("fit"),
            onOut: () => chooseZoom(stepZoom(displayScale, -1)),
            onIn: () => chooseZoom(stepZoom(displayScale, 1)),
          }}
          signers={toolbarSigners}
          addSeats={toolbarAddSeats}
          onAddSeat={addSeatSlot}
          activeSignerKey={activeSignerId}
          onPickSigner={(key) => {
            setFillNow(false);
            setActiveSignerId(key);
            if (placingLocked) {
              setPlacingLocked(false);
              setPlacingType(null);
            }
          }}
          fillNow={fillNow}
          onPickFillNow={() => {
            setFillNow(true);
            setPlacingType(null);
            setPlacingLocked(false);
            setPlacingStrike(false);
            setPendingMergeToken(null);
          }}
          armed={armedTool}
          onArm={armTool}
          canPlaceForSigner={!!activeSignerId}
          canStrike={!mapping}
          mapping={!!mapping}
          recordLabel={pendingMergeToken && !placingLocked ? boundPreview(pendingMergeToken).label : null}
          recordOpen={recordFieldPickerOpen}
          onToggleRecord={() => {
            setPlacingLocked(false);
            void ensureMergeOptions();
            setRecordFieldPickerOpen((v) => !v);
          }}
          onCloseRecord={() => setRecordFieldPickerOpen(false)}
          recordPicker={
            <MergeFieldPicker
              options={mergeOptions}
              loadError={mergeLoadError}
              selected={pendingMergeToken}
              onPick={(token) => {
                setPendingMergeToken(token);
                setPlacingType("text");
                setPlacingStamp(false);
                setSelectedKey(null);
                setRecordFieldPickerOpen(false);
              }}
              onUnbind={() => {
                setPendingMergeToken(null);
                setPlacingType(null);
                setPlacingLocked(false);
                setRecordFieldPickerOpen(false);
              }}
              onClose={() => setRecordFieldPickerOpen(false)}
            />
          }
          hint={toolbarHint}
          panel={{ collapsed: panelCollapsed, onToggle: togglePanel }}
        />
      ) : null}

      {mapping ? (
        <p className="esign-prep-mapping-banner">
          Nothing here will be sent to anyone. You're teaching Stewardship OS where this
          form's fields go and what they fill in, once, for every future send
          {mappingCompanyLabel ? ` in ${mappingCompanyLabel}` : ""}.
        </p>
      ) : null}

      {correction ? (
        <p className="esign-prep-mapping-banner esign-prep-correction-banner" data-testid="esign-correction-banner">
          <strong>Correcting {correction.title}.</strong>{" "}
          {correction.signedNames.length > 0
            ? `${joinCorrectionNames(correction.signedNames)}'s ${correction.signedNames.length === 1 ? "signature is" : "signatures are"} kept, so ${correction.signedNames.length === 1 ? "their boxes are" : "their boxes are"} locked. `
            : ""}
          The documents and anything printed into them stay as they went out. Add, move or remove boxes
          for the people still to sign, add a signer, or change the note.
        </p>
      ) : null}

      {result && result.delivery === "in_person" ? (
        <InPersonPanel result={result} onDone={handleClose} />
      ) : result ? (
        <div className="esign-prep-result">
          <h3>Sent for signature</h3>
          <ul className="send-for-signature-results">
            {result.signers.map((s) => (
              <li key={s.email}>
                <strong>{s.name}</strong> ({s.email}) —{" "}
                {s.emailed ? (
                  "emailed"
                ) : (
                  <>
                    couldn't email automatically — send them this link yourself:
                    {/* A COPY control, not a link. This was an <a
                        target="_blank"> labelled "copy this link", which
                        opened the CLIENT's signing ceremony in the
                        advisor's own browser instead of copying anything
                        (2026-08-30 audit). The URL stays visible so it
                        can be selected by hand if the copy fails. */}
                    <span className="esign-envelope-fallback-link">
                      <code>{s.signUrl}</code>
                      <Button
                        variant="sw-default"
                        size="sm"
                        onClick={() => {
                          void copyTextToClipboard(s.signUrl).then((ok) => {
                            if (ok) toast.success(`Link copied — send it to ${s.name} yourself.`);
                            else toast.error("Couldn't copy — select the link text and copy it.");
                          });
                        }}
                      >
                        Copy link
                      </Button>
                    </span>
                  </>
                )}
              </li>
            ))}
          </ul>
          <Button variant="primary" onClick={handleClose}>
            Done
          </Button>
        </div>
      ) : signersStep ? (
        <SignersAndOrderStep
          eyebrow={eyebrow ?? "E-sign package"}
          signers={stepSigners}
          stamped={
            stampedSeat
              ? {
                  name: user?.name ?? "You",
                  email: user?.email ?? null,
                  roleLabel: stampedSeat.role.label,
                  color: (colorByContact.get(PREPARER_SIGNER_KEY) ?? signerColor(3)).base,
                }
              : null
          }
          copyOnly={stepCopies}
          addable={eligible
            .filter((p) => !selected.has(p.key) && p.email)
            .map((p) => ({ key: p.key, name: p.displayName, email: p.email ?? "" }))}
          enforceOrder={enforceOrder}
          onEnforceOrder={setOrderFromStep}
          orderByKey={orderByKey}
          onReorder={(keys) =>
            setOrderByKey((prev) => ({ ...prev, ...Object.fromEntries(keys.map((key, i) => [key, i + 1])) }))
          }
          onRemove={toggleSigner}
          seatOptions={openSeats}
          firmSuggestions={firmSuggestions}
          onAddFirm={(roleKey) => {
            const held = casting?.find((seat) => seat.role.key === roleKey)?.person;
            if (held) fillSeat(roleKey, held);
          }}
          onSeat={seatSigner}
          onAdd={(key, roleKey) => {
            toggleSigner(key);
            // Someone added after the order is set signs last; drag to move.
            if (roleKey) seatSigner(key, roleKey);
          }}
          onMakeSigner={(key) =>
            setCopyOnly((prev) => {
              const next = new Set(prev);
              next.delete(key);
              return next;
            })
          }
          onAddSomeoneElse={(name, email, roleKey) => {
            // The guest form fills a seat the way the field screen's
            // "Someone else" link does: through guestForRoleRef.
            guestForRoleRef.current = roleKey;
            const ok = addGuestSigner(name, email);
            if (!ok) guestForRoleRef.current = null;
            return ok;
          }}
          someoneElseError={guestError}
          emptySeats={stepEmptySeats}
          copyPicker={
            <ForwardRecipientsPicker
              recordKind={recordKind}
              recordId={recordId}
              recipients={forwardTo}
              onChange={setForwardTo}
              hideHeading
            />
          }
          onNext={() => setSignersStep(false)}
          onSaveDraft={canSaveDraft ? () => void handleSaveDraft() : null}
          savingDraft={savingDraft}
          onCancel={handleClose}
        />
      ) : (
        <div className="esign-prep-body">
          {/* ── The package rail ──
              Only when there IS a package. One document renders exactly
              the screen it always did, with nothing extra above it. */}
          {isPackage ? (
            <div
              className="esign-prep-rail"
              data-folded={railExpanded ? undefined : "true"}
              aria-label="Documents in this package"
            >
              {/* FOLDED BY DEFAULT (2026-09-29): ten documents still need
                  a visible list, but not 200px of one while a page is
                  being worked. A number per document, a dot that says
                  whether it has anything to decide, and one click to the
                  full list with its reorder controls. */}
              <button
                type="button"
                className="esign-prep-rail-toggle"
                onClick={toggleRail}
                aria-expanded={railExpanded ? "true" : "false"}
                title={railExpanded ? "Fold the document list" : "Show document names and reorder"}
              >
                {railExpanded ? "«" : "»"}
              </button>
              {!railExpanded ? (
                <ul className="esign-prep-rail-strip">
                  {packageDocs.map((d, i) => {
                    const { dot, todo } = dotFor(d.documentId);
                    return (
                      <li key={d.documentId}>
                        <button
                          type="button"
                          className="esign-prep-rail-num"
                          data-active={i === activeIndex ? "true" : undefined}
                          aria-current={i === activeIndex ? "true" : undefined}
                          title={`${i + 1}. ${d.filename} — ${documentStateWords(d.documentId, dot, todo)}`}
                          onClick={() => showDocument(i)}
                        >
                          {i + 1}
                          <span
                            className="esign-prep-rail-dot"
                            data-state={dot}
                            aria-hidden="true"
                          />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
              {/* Where the background read is, said in the rail because
                  that is where the dots it explains are. */}
              {readingDocId ? (
                <p
                  className="esign-prep-rail-reading"
                  title={`Reading the documents in this package: ${readCount} of ${packageDocs.length} read so far`}
                  aria-live="polite"
                >
                  {railExpanded
                    ? `Reading ${Math.min(readCount + 1, packageDocs.length)} of ${packageDocs.length}…`
                    : `${readCount}/${packageDocs.length}`}
                </p>
              ) : null}
              {/* Says what the order MEANS before offering to change it
                  — the rail looks like a tab strip, and nobody drags a
                  tab strip unless told the order is load-bearing. */}
              {railExpanded ? (
              <p className="esign-prep-rail-lead">
                They sign in this order. Drag to change it.
              </p>
              ) : null}
              {railExpanded ? (
              <ul className="esign-prep-rail-list">
                {packageDocs.map((d, i) => {
                  const placed = packageFields.get(d.documentId)?.length ?? 0;
                  const { dot, todo } = dotFor(d.documentId);
                  return (
                    <li
                      key={d.documentId}
                      className="esign-prep-rail-row"
                      draggable
                      data-dragging={dragIndex === i ? "true" : undefined}
                      data-dragover={
                        dragOverIndex === i && dragIndex !== null && dragIndex !== i
                          ? "true"
                          : undefined
                      }
                      onDragStart={(e) => {
                        setDragIndex(i);
                        e.dataTransfer.effectAllowed = "move";
                        // Firefox refuses to start a drag at all unless
                        // the event carries data.
                        e.dataTransfer.setData("text/plain", d.documentId);
                      }}
                      onDragOver={(e) => {
                        if (dragIndex === null) return;
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                        setDragOverIndex(i);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (dragIndex !== null) moveDocument(dragIndex, i);
                        setDragIndex(null);
                        setDragOverIndex(null);
                      }}
                      onDragEnd={() => {
                        setDragIndex(null);
                        setDragOverIndex(null);
                      }}
                    >
                      <span className="esign-prep-rail-grip" aria-hidden="true">
                        <GripVertical size={14} />
                      </span>
                      <button
                        type="button"
                        className="esign-prep-rail-item"
                        data-active={i === activeIndex ? "true" : undefined}
                        aria-current={i === activeIndex ? "true" : undefined}
                        onClick={() => showDocument(i)}
                      >
                        <span className="esign-prep-rail-index">{i + 1}</span>
                        <span className="esign-prep-rail-name">{d.filename}</span>
                        {/* Says what is TRUE rather than what is wrong: a
                            document the reader has not reached yet is
                            "waiting to be read", not "0 fields" — those
                            are different situations and only one of them
                            is a decision someone made. */}
                        <span className="esign-prep-rail-state" data-state={dot}>
                          {d.attachment && placed === 0
                            ? "Attachment · read only · no fields"
                            : dot === "reading" || dot === "failed"
                            ? documentStateWords(d.documentId, dot, todo)
                            : railStatus(placed, todo, recordFilledCount(packageFields.get(d.documentId)))}
                        </span>
                      </button>
                      {/* Page thumbnails under the document on screen
                          (PR 9b): one click to a page of a long form. */}
                      {i === activeIndex && pages ? (
                        <PageThumbs pages={pages} current={thumbPage} needs={mapping ? undefined : pageNeeds} onJump={jumpToPage} />
                      ) : null}
                      {/* Dragging is a mouse gesture: it does not work on
                          a phone and cannot be done from a keyboard. These
                          two buttons are the same reorder by other means,
                          which is what keeps this surface usable on the
                          phone (mobile parity) and reachable by tab. */}
                      <span className="esign-prep-rail-move">
                        <button
                          type="button"
                          aria-label={`Move ${d.filename} earlier`}
                          title="Move earlier"
                          disabled={i === 0}
                          onClick={() => moveDocument(i, i - 1)}
                        >
                          <ChevronUp size={14} aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          aria-label={`Move ${d.filename} later`}
                          title="Move later"
                          disabled={i === packageDocs.length - 1}
                          onClick={() => moveDocument(i, i + 1)}
                        >
                          <ChevronDown size={14} aria-hidden="true" />
                        </button>
                      </span>
                    </li>
                  );
                })}
              </ul>
              ) : null}
            </div>
          ) : null}
          {/* ── Document canvas ── */}
          <div
            ref={docAreaRef}
            className="esign-prep-doc"
            data-placing={placingType || placingStamp ? "true" : undefined}
          >
            {loadError ? (
              <div className="esign-prep-loadfail">
                <ErrorBanner message={loadError} />
                {/* No dead ends: a failed load always offers the next
                    move. Before this, a document that wouldn't render
                    left "Loading…" on screen forever with nothing to
                    press (staging round 3, 2026-08-23).

                    On a TIMEOUT the offer leads with Reload, because
                    "Try again" reuses the very renderer that just hung
                    — round 5 watched a retry freeze the tab for 45
                    seconds and change nothing. Reloading is what
                    actually clears it, so it's the primary button and
                    Try again stays as the quieter second option. */}
                {loadTimedOut ? (
                  <div className="esign-prep-loadfail-actions">
                    <Button variant="primary" onClick={() => window.location.reload()}>
                      Reload this page
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setLoadError(null);
                        setLoadTimedOut(false);
                        setLoadAttempt((n) => n + 1);
                      }}
                    >
                      Try again without reloading
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="sw-default"
                    onClick={() => {
                      setLoadError(null);
                      setLoadTimedOut(false);
                      setLoadAttempt((n) => n + 1);
                    }}
                  >
                    Try again
                  </Button>
                )}
              </div>
            ) : !pages ? (
              <LoadingBlock />
            ) : (
              pages.map((page) => (
                <div
                  key={page.pageNumber}
                  className="esign-prep-page"
                  // CSS zoom scales the canvas, the overlay and every
                  // field in one go; see displayScale.
                  style={{ zoom: displayScale, "--esign-scale": displayScale } as React.CSSProperties}
                >
                  <div ref={(el) => mountCanvas(page.pageNumber, el)} className="esign-prep-canvas" />
                  <div
                    className="esign-prep-overlay"
                    style={{ width: page.widthPt, height: page.heightPt }}
                    onClick={(e) => handleOverlayClick(page, e)}
                    onPointerDown={(e) => handleOverlayPointerDown(page, e)}
                  >
                    {fields
                      .filter((f) => f.pageNumber === page.pageNumber && !isEmptySeatBox(f, seatSignerKeyByRole))
                      .map((f) => {
                        const color = colorByContact.get(f.signerKey) ?? signerColor(0);
                        const signer = eligible.find((p) => p.key === f.signerKey);
                        const isSelected = selectedKeys.has(f.key);
                        const isSoloSelected = isSelected && selectedKeys.size === 1;
                        // Two visual kinds (Grant, 2026-08-22): SIGNATURE
                        // blocks (signature/initials — tinted fill, pen
                        // icon, heavier border) vs DATA fields (text/date/
                        // checkbox — light input-like fill, thin border).
                        // Person = hue; kind = weight/shape.
                        const isSignatureKind =
                          f.fieldType === "signature" || f.fieldType === "initials";
                        const showLabel = f.width >= 40;
                        // A narrow box shows only WHAT it is, short ("Date",
                        // "Initials"): its colour already says whose (Grant,
                        // 2026-10-14; "Dat…" and "In…" said neither).
                        const compactLabel = f.width < 90;
                        const inPlaceEdit =
                          isSoloSelected &&
                          f.fieldType === "text" &&
                          !f.strike &&
                          // Mapping a blank form has no typing for a signer's
                          // box (nothing to teach), but the sender's own
                          // locked box IS how fixed text (an address, wire
                          // instructions) gets onto the template, so the page
                          // is where it is typed (Nate, 2026-10-26).
                          (!mapping || isPreparerField(f)) &&
                          !isLockedInCorrection(f);
                        const offer = f.proposal ? offers.get(f.key) : undefined;
                        return (
                          <div
                            key={f.key}
                            className="esign-prep-field"
                            data-kind={isSignatureKind ? "signature" : "data"}
                            // A line through printed text (PR 4a): drawn as a
                            // line across a see-through box, so the words it
                            // strikes stay readable under it.
                            data-strike={f.strike ? "true" : undefined}
                            data-locked={isLockedInCorrection(f) ? "true" : undefined}
                            data-selected={isSelected ? "true" : undefined}
                            // A checkbox or round button is the size of the
                            // printed box it covers, so its mark is centred
                            // in it rather than lined up like a label
                            // (Nate, 2026-10-15: the check sat too far right).
                            data-compact={
                              f.fieldType === "checkbox" || f.fieldType === "radio"
                                ? "true"
                                : undefined
                            }
                            // A sender's checkbox left unchecked prints
                            // nothing; shown faint so that reads as "off".
                            // A ghost the binder offers as ticked draws its
                            // tick (session 4, 2026-11-04), so the page
                            // reads like the contract that will go out.
                            data-unticked={
                              f.fieldType === "checkbox" &&
                              isPreparerField(f) &&
                              (f.value ?? (offer && offer.kind !== "handed" ? offer.value : null)) !==
                                BOLDSIGN_CHECKBOX_ON
                                ? "true"
                                : undefined
                            }
                            data-offpage={offPageKeys.has(f.key) ? "true" : undefined}
                            // Marked the same way an off-page field is:
                            // the blocker note below the Send button says
                            // how many, and this says WHICH — otherwise
                            // "1 pick-one has no choices" means hunting
                            // the document for it.
                            data-unconfigured={
                              unconfiguredKeys.has(f.key) ? "true" : undefined
                            }
                            // Same reasoning again for a linked field the
                            // record can't answer: the note says how many
                            // and names them, this says which box.
                            data-unfilled={
                              unfilledMergeKeys.has(f.key) ? "true" : undefined
                            }
                            // A proposal is visibly not yet a decision:
                            // dashed and faded until the advisor accepts
                            // it. "No guess is presented as fact."
                            data-proposed={f.proposal ? "true" : undefined}
                            // A box spanning several printed lines draws
                            // its text line by line on those rules — see
                            // MultilineFieldText — and goes red when the
                            // text has more lines than the box.
                            data-multiline={isMultilineField(f) ? "true" : undefined}
                            data-overflow={overflowLinesOf(f) > 0 ? "true" : undefined}
                            // What the OS offers for a ghost, and where
                            // it looked (2026-09-29): the source tag on
                            // the page, and the amber state for a blank
                            // only the sender can answer.
                            data-offer={offer ? offer.kind : undefined}
                            data-review={offer && !offer.sure ? "true" : undefined}
                            data-asked={
                              f.proposal && !offer && askedKeys.has(f.key) ? "true" : undefined
                            }
                            title={
                              isLockedInCorrection(f)
                                ? printsByStamp(f)
                                  ? "Printed into the document when it went out. Kept as is."
                                  : "Signed. Kept as is."
                                : f.proposal
                                  ? `${FIELD_TYPE_LABEL[f.fieldType]}${signer ? ` · ${signer.displayName}` : ""} · ${proposalTitle(f.proposal)}`
                                  : f.strike
                                    ? "Strike through · printed into the document as a line"
                                    : `${FIELD_TYPE_LABEL[f.fieldType]}${signer ? ` · ${signer.displayName}` : ""}`
                            }
                            style={{
                              left: f.x,
                              top: f.y,
                              width: f.width,
                              height: f.height,
                              // A multi-line box stays see-through enough
                              // for the form's own rules to show under the
                              // type: they are what the lines are aligned to.
                              background: f.strike
                                ? "transparent"
                                : isSignatureKind
                                ? color.fill
                                : isMultilineField(f)
                                  ? "rgba(255, 255, 255, 0.45)"
                                  : "rgba(255, 255, 255, 0.88)",
                              borderColor: color.base,
                              color: color.deep,
                              boxShadow: isSelected ? `0 0 0 3px ${color.fill}` : undefined,
                              // Its label starts at the top too, where
                              // the typing will (2026-10-15).
                              ...(isTallSingleLineBox(f) ? { alignItems: "flex-start", paddingTop: 3 } : {}),
                            }}
                            onPointerDown={(e) => handleFieldPointerDown(e, f)}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {isSignatureKind && showLabel ? (
                              <svg
                                className="esign-prep-field-icon"
                                width="11"
                                height="11"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M12 20h9"></path>
                                <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"></path>
                              </svg>
                            ) : null}
                            {offer && showLabel ? (
                              <span className="esign-ghost-tag">{offerTag(offer)}</span>
                            ) : null}
                            {inPlaceEdit ? (
                              <FieldTextEditor
                                field={f}
                                placeholder={
                                  f.mergeToken
                                    ? (boundPreview(f.mergeToken, f.signerKey, f.roleKey).value ??
                                      unresolvedBindingText(f.mergeToken, f.signerKey, f.roleKey))
                                    : offer && offer.kind !== "handed" && offer.value
                                      ? offer.value
                                      : "Type here…"
                                }
                                onChange={(v) =>
                                  setFields((prev) =>
                                    prev.map((x) =>
                                      x.key === f.key ? taken({ ...x, value: v === "" ? null : v }) : x,
                                    ),
                                  )
                                }
                              />
                            ) : f.strike ? null : isMultilineField(f) ? (
                              // A box spanning several lines draws what it
                              // will print — the typed text, else the
                              // ghost's offer, else the record's value —
                              // one line per rule (2026-09-29).
                              <MultilineFieldText
                                field={f}
                                text={
                                  f.value ??
                                  (offer && offer.kind !== "handed" ? offer.value : null) ??
                                  (f.mergeToken
                                    ? boundPreview(f.mergeToken, f.signerKey, f.roleKey).value
                                    : null)
                                }
                                placeholder={
                                  f.mergeToken
                                    ? unresolvedBindingText(f.mergeToken, f.signerKey, f.roleKey)
                                    : `${multilineLineCount(f.height, f.linePitch)} lines${signer ? ` · ${signer.displayName}` : ""}`
                                }
                              />
                            ) : showLabel ? (
                              <span className="esign-prep-field-label" style={textStyleCss(f.textStyle)}>
                                {/* A GHOST WITH AN OFFER SHOWS THE OFFER,
                                    in place, so the page reads like the
                                    finished document and accepting is a
                                    look rather than a hunt (2026-09-29). */}
                                {/* A bound field shows what it WILL PRINT,
                                    so the page reads like the finished
                                    document rather than like a template.
                                    Its own name is the fallback while the
                                    record's values are still loading. */}
                                {offer && offer.kind !== "handed" && offer.value
                                  ? offer.value
                                  : f.fieldType === "text" && f.mergeToken
                                  ? // An override wins on the page exactly
                                    // as it will at send time.
                                    (f.value ??
                                      boundPreview(f.mergeToken, f.signerKey, f.roleKey).value ??
                                      unresolvedBindingText(f.mergeToken, f.signerKey, f.roleKey))
                                  : f.fieldType === "text" && f.value
                                    ? f.value
                                    : // A pick-one reads better as its first
                                      // choice with a caret than as the words
                                      // "Pick one" — the page then looks like
                                      // the form it is about to become.
                                      f.fieldType === "dropdown"
                                      ? `${(f.options ?? []).find((o) => o.trim()) ?? "Pick one"} ▾`
                                      : f.fieldType === "radio"
                                        ? `${f.groupName?.trim() || "No group"}${signer ? ` · ${signer.displayName}` : ""}`
                                        : compactLabel
                                          ? FIELD_TYPE_SHORT_LABEL[f.fieldType]
                                          : `${FIELD_TYPE_LABEL[f.fieldType]}${signer ? ` · ${signer.displayName}` : ""}`}
                              </span>
                            ) : f.fieldType === "checkbox" ? (
                              <svg
                                className="esign-prep-field-icon"
                                width="11"
                                height="11"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <polyline points="20 6 9 17 4 12"></polyline>
                              </svg>
                            ) : f.fieldType === "radio" ? (
                              // Same size as a checkbox, so it gets an icon
                              // rather than a label — round, because that is
                              // the shape every form in the world uses to
                              // mean "pick exactly one".
                              <svg
                                className="esign-prep-field-icon"
                                width="11"
                                height="11"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                              >
                                <circle cx="12" cy="12" r="9"></circle>
                              </svg>
                            ) : null}
                            {isSoloSelected ? (
                              // Resize stays single-field: dragging a
                              // corner on a multi-selection has no
                              // obvious meaning (resize each in place?
                              // scale the group?), so the handle simply
                              // doesn't appear once more than one is
                              // selected — narrow it to one first.
                              <span
                                className="esign-prep-field-resize"
                                style={{ borderColor: color.base }}
                                onPointerDown={(e) => startDrag(e, f, "resize")}
                              />
                            ) : null}
                            {/* A WAY TO REMOVE THE BOX ON THE BOX (Nate,
                                2026-10-13: "I do not see a way to remove
                                the box"). Same path as the Delete key. */}
                            {isSoloSelected && !isLockedInCorrection(f) ? (
                              <button
                                type="button"
                                className="esign-prep-field-x"
                                aria-label="Remove this box"
                                title="Remove this box"
                                onPointerDown={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeFields(new Set([f.key]));
                                }}
                              >
                                ×
                              </button>
                            ) : null}
                          </div>
                        );
                      })}
                    {/* ── Blanks the form drew that nothing has claimed ──
                        Not proposals: outlines. The OS is saying "the
                        form left room here and I could not tell you what
                        for." One click says what it is, at the exact
                        geometry the form drew, so the edge cases Stew
                        cannot name never come down to dragging a box by
                        eye. */}
                    {(unmappedByPage.get(page.pageNumber) ?? []).map((b) => {
                      const bKey = blankKey(b);
                      const menuOpen = fillingBlank ? blankKey(fillingBlank) === bKey : false;
                      return (
                        <button
                          key={bKey}
                          type="button"
                          ref={(el) => {
                            if (el) blankRefs.current.set(bKey, el);
                            else blankRefs.current.delete(bKey);
                          }}
                          className="esign-prep-blank"
                          data-open={menuOpen ? "true" : undefined}
                          style={{ left: b.x, top: b.y, width: b.width, height: b.height }}
                          title="The form left a blank here — click to say what fills it"
                          aria-label={`Unmapped blank on page ${page.pageNumber} — choose what fills it`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setBlankPickerOpen(false);
                            setFillingBlank(menuOpen ? null : b);
                            setSelectedKey(null);
                          }}
                        >
                          <span className="esign-prep-blank-dot" aria-hidden="true" />
                        </button>
                      );
                    })}
                    {/* THE GHOST POPOVER (2026-09-29): one blank, three
                        ways to make it real, on the page beside the
                        blank. Real fields keep the panel's editor. */}
                    {popoverField && popoverField.pageNumber === page.pageNumber && !mapping ? (
                      (() => {
                        // ON THE PAGE, AT SCREEN SIZE (2026-10-13). The
                        // popover lives inside the zoomed page so it
                        // stays beside its blank, but its own contents
                        // cancel the zoom: at "Fit" on a desktop the page
                        // runs ~1.9×, and the popover ran with it — 27px
                        // type and 60px buttons over twenty lines of the
                        // form. Placement is worked out in page points
                        // from its real on-screen size.
                        const POP_W_PX = 300;
                        const popHeightPx =
                          (popoverOffer ? 90 : 30) + (seatOrder.length > 0 ? 44 : 0) + 34;
                        const pop = placePopover(
                          popoverField,
                          page,
                          { widthPt: POP_W_PX / displayScale, heightPt: popHeightPx / displayScale },
                          6 / displayScale,
                        );
                        const signerName = (key: string) =>
                          eligible.find((p) => p.key === key)?.displayName ?? "the signer";
                        const sel = popoverField;
                        const selectedOffer = popoverOffer;
                        const isReal = !isGhost(sel);
                        return (
                          <div
                            className="esign-ghost-pop-anchor"
                            style={{
                              left: pop.left,
                              top:
                                pop.side === "above"
                                  ? popoverField.y - 6 / displayScale
                                  : pop.top,
                            }}
                          >
                          <div
                            className="esign-ghost-pop"
                            data-side={pop.side}
                            style={{ width: POP_W_PX }}
                            onClick={(e) => e.stopPropagation()}
                            onPointerDown={(e) => e.stopPropagation()}
                          >
                            <div className="esign-ghost-pop-cap">
                              <span>{sel.caption?.trim() || `Page ${sel.pageNumber}`}</span>
                              <i>{isReal ? "yours · Delete removes it" : "ghost · not yet a field"}</i>
                            </div>
                            {isReal && sel.fieldType !== "text" ? (
                              <div className="esign-ghost-pop-offer" data-done="true">
                                <span className="esign-ghost-pop-value">
                                  {`${FIELD_TYPE_LABEL[sel.fieldType]} · ${signerName(sel.signerKey)}`}
                                  <small>going out</small>
                                </span>
                              </div>
                            ) : selectedOffer ? (
                              <div className="esign-ghost-pop-offer" data-kind={selectedOffer.kind}>
                                <span className="esign-ghost-pop-value">
                                  {selectedOffer.kind === "handed"
                                    ? `For ${signerName(sel.signerKey)} to fill in`
                                    : (selectedOffer.value ?? "Worked out at send")}
                                  <small>
                                    {offerTag(selectedOffer)}
                                    {selectedOffer.sure ? "" : " · a guess, check it"}
                                  </small>
                                </span>
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={() => acceptGhosts((f) => f.key === sel.key)}
                                >
                                  Accept ⏎
                                </Button>
                              </div>
                            ) : null}
                            {/* The words go in the box on the page now
                                (2026-10-14); the popover keeps only what
                                the page can't do: take an offer, or hand
                                the box to a signer. */}
                            {seatOrder.length > 0 ? (
                              <div className="esign-ghost-pop-hand">
                                <span>Ask a signer to fill it:</span>
                                {seatOrder.map((key, i) => (
                                  <button
                                    key={key}
                                    type="button"
                                    className="esign-ghost-pop-seat"
                                    style={{
                                      borderColor: (colorByContact.get(key) ?? signerColor(i)).base,
                                      color: (colorByContact.get(key) ?? signerColor(i)).deep,
                                      background: (colorByContact.get(key) ?? signerColor(i)).soft,
                                    }}
                                    onClick={() =>
                                      setFields((prev) =>
                                        prev.map((f) =>
                                          f.key === sel.key ? taken({ ...f, signerKey: key }) : f,
                                        ),
                                      )
                                    }
                                  >
                                    <b>{i + 1}</b> {signerName(key).split(" ")[0]}
                                  </button>
                                ))}
                              </div>
                            ) : null}
                            <div className="esign-ghost-pop-foot">
                              <span>
                                <kbd>Esc</kbd> leave it, it won't be sent
                              </span>
                              <span>
                                <kbd>Tab</kbd> next
                              </span>
                            </div>
                          </div>
                          </div>
                        );
                      })()
                    ) : null}
                    {fillingBlank && fillingBlank.pageNumber === page.pageNumber ? (
                      <div
                        className="esign-prep-blank-menu-anchor"
                        style={{
                          // Same rule as the ghost popover: placed in page
                          // points, drawn at screen size (240px wide).
                          left: Math.min(
                            fillingBlank.x,
                            Math.max(0, page.widthPt - 250 / displayScale),
                          ),
                          top: fillingBlank.y + fillingBlank.height + 4 / displayScale,
                        }}
                      >
                      <div
                        className="esign-prep-blank-menu"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span className="esign-prep-blank-menu-title">What fills this blank?</span>
                        {!activeSignerId ? (
                          <p className="esign-prep-hint">
                            Pick a signer above first — every field belongs to somebody.
                          </p>
                        ) : (
                          <>
                            <div className="esign-prep-blank-menu-row">
                              {(fillingBlank.kind === "box"
                                ? (["checkbox"] as const)
                                : FILL_CHOICE_TYPES
                              ).map((t) => (
                                <button
                                  key={t}
                                  type="button"
                                  className="esign-prep-palette-item"
                                  onClick={() => fillBlank(fillingBlank, t, null)}
                                >
                                  {FIELD_TYPE_LABEL[t]}
                                </button>
                              ))}
                              <button
                                type="button"
                                className="esign-prep-palette-item"
                                onClick={() => fillBlank(fillingBlank, "text", null)}
                              >
                                They type it
                              </button>
                            </div>
                            <button
                              type="button"
                              className="esign-prep-toggle-more"
                              onClick={() => {
                                void ensureMergeOptions();
                                setBlankPickerOpen((v) => !v);
                              }}
                            >
                              {blankPickerOpen
                                ? "Hide record fields"
                                : "Fill it from this record instead"}
                            </button>
                            {blankPickerOpen ? (
                              <MergeFieldPicker
                                options={mergeOptions}
                                loadError={mergeLoadError}
                                selected={null}
                                onPick={(token) => {
                                  setBlankPickerOpen(false);
                                  fillBlank(fillingBlank, "text", token);
                                }}
                                onUnbind={() => setBlankPickerOpen(false)}
                                onClose={() => setBlankPickerOpen(false)}
                              />
                            ) : null}
                          </>
                        )}
                      </div>
                      </div>
                    ) : null}
                    {mySignature &&
                      stamps
                        .filter((s) => s.pageNumber === page.pageNumber)
                        .map((s) => (
                          <div
                            key={s.key}
                            className="esign-prep-stamp"
                            data-selected={s.key === selectedStampKey ? "true" : undefined}
                            style={{ left: s.x, top: s.y, width: s.width, height: s.height }}
                            onPointerDown={(e) => startDragStamp(e, s, "move")}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span className="esign-prep-stamp-badge">You</span>
                            <img src={`data:image/png;base64,${mySignature.imagePngBase64}`} alt="Your signature" />
                            {s.key === selectedStampKey ? (
                              <span
                                className="esign-prep-field-resize"
                                style={{ borderColor: colors.navySurface }}
                                onPointerDown={(e) => startDragStamp(e, s, "resize")}
                              />
                            ) : null}
                          </div>
                        ))}
                    {marqueeRect && marqueeRect.pageNumber === page.pageNumber ? (
                      <div
                        className="esign-prep-marquee"
                        style={{
                          left: marqueeRect.x,
                          top: marqueeRect.y,
                          width: marqueeRect.width,
                          height: marqueeRect.height,
                        }}
                      />
                    ) : null}
                  </div>
                  {/* The review pass on a document THEY wrote (Nate
                      §6.3). Sits UNDER the page, not over it: it is the
                      thing you press once you have looked at what is
                      above it, and a banner across the top would be read
                      before the page rather than after. Only appears on
                      a received document — we drew the boxes on our own
                      forms and there is no question to ask about them. */}
                  {activeIsTheirs ? (
                    <div
                      className={`esign-prep-page-confirm${
                        activeConfirmed.has(page.pageNumber) ? " is-confirmed" : ""
                      }`}
                    >
                      <span title={RECEIVED_DOCUMENT_EXPLANATION}>
                        {activeConfirmed.has(page.pageNumber)
                          ? "You checked this page"
                          : "They wrote this page — check our boxes are in the right places"}
                      </span>
                      <Button
                        size="sm"
                        variant={activeConfirmed.has(page.pageNumber) ? "ghost" : "primary"}
                        onClick={() => confirmPage(page.pageNumber)}
                      >
                        {activeConfirmed.has(page.pageNumber) ? "Undo" : "Confirm page"}
                      </Button>
                    </div>
                  ) : null}
                  <div className="esign-prep-page-number">
                    Page {page.pageNumber} of {pages.length}
                    {pages.length > 1 && !mapping ? (
                      <span
                        className="esign-prep-page-need"
                        data-need={(pageNeeds.get(page.pageNumber) ?? 0) > 0 ? "true" : undefined}
                      >
                        {" · "}
                        {pageNeedsText(pageNeeds.get(page.pageNumber) ?? 0)}
                      </span>
                    ) : null}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* ── Right panel ── */}
          <div className="esign-prep-panel" data-collapsed={panelCollapsed ? "true" : undefined}>
            <div className="esign-prep-panel-scroll">
              {error ? <ErrorBanner message={error} /> : null}

              {/* Suggested fields (Grant, 2026-08-22). Recommendation-
                  first, override-honest: one click takes them all, every
                  one stays adjustable, and nothing here has sent
                  anything. */}
              {suggesting ? (
                <div className="esign-prep-section">
                  <span className="esign-prep-eyebrow">Reading the document</span>
                  <p className="esign-prep-hint">
                    Looking for the places people need to sign — you can start placing
                    fields yourself in the meantime.
                  </p>
                </div>
              ) : mapping && proposedCount > 0 ? (
                // TEACHING A BLANK FORM sends nothing, so the ghost rule's
                // "not sent unless you touch it" has no meaning here: the
                // map keeps every box on this screen, dashed or not.
                <div className="esign-prep-section esign-prep-selected">
                  <span className="esign-prep-eyebrow">
                    {proposedCount} suggested {proposedCount === 1 ? "field" : "fields"}
                  </span>
                  <p className="esign-prep-hint">
                    Shown dashed on the page. Move, resize, retype or delete any of them — saving
                    the map keeps them all.
                  </p>
                </div>
              ) : packageGhosts > 0 || census.signing > 0 ? (
                <div className="esign-prep-section esign-prep-selected esign-ghosts">
                  <span className="esign-prep-eyebrow">
                    {formRecognition?.kind === "applied"
                      ? "Recognized form"
                      : appliedLayout
                        ? "You've sent this one before"
                        : "What the OS sees"}
                  </span>
                  {/* WHICH FORM THIS IS, OR THAT THE OS DOESN'T KNOW IT
                      (Grant, 2026-10-13). Nate could never tell a
                      reviewed map from a pile of guesses; now the panel
                      says which one he is looking at. */}
                  {formRecognition?.kind === "applied" ? (
                    <p className="esign-prep-hint esign-prep-recognized">
                      This is the <strong>{formRecognition.forms.join(" and ")}</strong>. Its boxes
                      come from the team's reviewed map of that form, moved to fit this copy's pages.
                      {formRecognition.unmeasuredPages
                        ? ` ${formRecognition.unmeasuredPages} ${
                            formRecognition.unmeasuredPages === 1 ? "page" : "pages"
                          } could not be measured against this copy, so check the boxes there before you send.`
                        : ""}
                    </p>
                  ) : formRecognition?.kind === "other_revision" ? (
                    <p className="esign-prep-blocker">
                      This looks like the {formRecognition.forms.join(" and ")}, but the team's
                      reviewed map was drawn on the {formRecognition.mapRevision ?? "earlier"}{" "}
                      version, so it was not used. Every box here is a suggestion. Check each one
                      before you send.
                    </p>
                  ) : formRecognition?.kind === "unknown" ? (
                    <p className="esign-prep-blocker">
                      The OS doesn't have a reviewed map for this form, so every box here is a
                      suggestion. Check each one before you send.
                    </p>
                  ) : null}
                  {/* THE COUNT STRIP (2026-09-29). Every number here is
                      counted over the same list of fields, so they add
                      up to the first one by construction — the accuracy
                      rule, applied to the sentence the screen leads
                      with. */}
                  <p className="esign-ghost-counts">
                    <span className="esign-ghost-pill">
                      <strong>{census.total}</strong> {census.total === 1 ? "blank" : "blanks"} seen
                    </span>
                    {census.signing > 0 ? (
                      <span className="esign-ghost-pill" data-kind="signing">
                        <strong>{census.signing}</strong> signing{" "}
                        {census.signing === 1 ? "mark" : "marks"} placed
                      </span>
                    ) : null}
                    {census.offeredSure + census.offeredReview > 0 ? (
                      <span className="esign-ghost-pill" data-kind="offered">
                        <strong>{census.offeredSure + census.offeredReview}</strong>{" "}
                        {census.offeredSure + census.offeredReview === 1 ? "value" : "values"} ready
                      </span>
                    ) : null}
                    {census.asked > 0 ? (
                      <span className="esign-ghost-pill" data-kind="asked">
                        <strong>{census.asked}</strong> only you can answer
                      </span>
                    ) : null}
                    {census.real > 0 ? (
                      <span className="esign-ghost-pill" data-kind="real">
                        <strong>{census.real}</strong> {census.real === 1 ? "is" : "are"} yours
                      </span>
                    ) : null}
                    {census.ignored > 0 ? (
                      <span className="esign-ghost-pill" data-kind="ignored">
                        <strong>{census.ignored}</strong> ignored unless touched
                      </span>
                    ) : null}
                  </p>
                  {isPackage ? (
                    <p className="esign-prep-hint">
                      Counted across all {packageDocs.length} documents in this package
                      {readingDocId ? ", with some still being read" : ""}. Accept all takes every
                      sure value on every document.
                    </p>
                  ) : null}
                  {packageReadNote ? <p className="esign-prep-hint">{packageReadNote}</p> : null}
                  <p className="esign-prep-hint">
                    {appliedLayout ? (
                      <>
                        Drawn where your team put them last time, from{" "}
                        <strong>{appliedLayout}</strong>.{" "}
                      </>
                    ) : null}
                    A dashed blank is a ghost: it costs nothing and is <strong>not sent</strong>{" "}
                    unless you accept its value, type into it, or hand it to a signer.
                    {unansweredGhostBindings > 0
                      ? ` ${unansweredGhostBindings} linked ${
                          unansweredGhostBindings === 1 ? "blank has" : "blanks have"
                        } nothing on the record yet, so nothing is offered for ${
                          unansweredGhostBindings === 1 ? "it" : "them"
                        }.`
                      : ""}
                  </p>
                  {/* WHOSE MAP THIS IS (Joey Ellis, 2026-09-18) — the
                      banner above said "you've sent this before" with
                      no way to tell whose work it was applying. This is
                      the answer, in the same words the database already
                      held. */}
                  {appliedLayout && appliedLayoutInfo && layoutProvenanceLine(appliedLayoutInfo) ? (
                    <p className="esign-prep-hint esign-prep-layout-provenance">
                      {layoutProvenanceLine(appliedLayoutInfo)}
                    </p>
                  ) : null}
                  {/* A MAP THAT ARRIVED INCOMPLETE SAYS SO (Baker,
                      2026-09-14). This note is how applyLayout reports
                      fields it could not place; it used to be computed,
                      sent, stored — and then never rendered. */}
                  {appliedLayout && suggestNote ? (
                    <p className="esign-prep-blocker">{suggestNote}</p>
                  ) : null}
                  {/* A MAP NOBODY CHECKED AGAINST THE PAGE SAYS SO, HERE
                      (2026-09-18). Not a blocker; what is not acceptable
                      is silence. */}
                  {layoutPositionsUnchecked ? (
                    <p className="esign-prep-blocker">
                      This form's boxes were placed from the standard layout, not measured on this
                      PDF, so nobody has checked them against the page. Look down the document
                      before you send — a box in the wrong place still looks deliberate.
                    </p>
                  ) : null}
                  {/* VALUES READY, BY SOURCE (2026-09-29). One row per
                      place a value came from, one click to take the
                      whole group; Accept all takes every sure group at
                      once and leaves Stew's readings for a look. */}
                  {offersByKind.length > 0 ? (
                    <div className="esign-ghost-groups">
                      {offersByKind.map((g) => (
                        <div key={g.kind} className="esign-ghost-group" data-kind={g.kind}>
                          <span className="esign-ghost-group-ic" aria-hidden="true">
                            {OFFER_KIND_LABEL[g.kind].charAt(0).toUpperCase()}
                          </span>
                          <span className="esign-ghost-group-name">
                            {OFFER_KIND_HEADING[g.kind]}
                            <small>
                              {g.kind === "stew"
                                ? "check these before accepting"
                                : g.kind === "document"
                                  ? "read off another document here; check each"
                                  : g.kind === "handed"
                                    ? "they type these when they sign"
                                    : "offered on the page, faded"}
                            </small>
                          </span>
                          <span className="esign-ghost-group-n">{g.fields.length}</span>
                          <Button
                            variant="sw-default"
                            size="sm"
                            onClick={() => {
                              if (g.kind === "stew" || g.kind === "document") {
                                // Review, not accept: land on the first one,
                                // whichever document it is on.
                                const first = g.fields[0];
                                if (first) jumpToField(first.documentId, first.field.key);
                              } else acceptGroup(g.kind);
                            }}
                          >
                            {g.kind === "stew" || g.kind === "document" ? "Review" : "Accept"}
                          </Button>
                        </div>
                      ))}
                      {[...acceptedKinds].map((kind) => (
                        <div key={`done-${kind}`} className="esign-ghost-group" data-done="true">
                          <span className="esign-ghost-group-ic" aria-hidden="true">
                            ✓
                          </span>
                          <span className="esign-ghost-group-name">
                            {OFFER_KIND_HEADING[kind]}
                            <small>accepted</small>
                          </span>
                          <Button variant="sw-default" size="sm" onClick={() => undoGroup(kind)}>
                            Undo
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {census.offeredSure > 0 ? (
                    <div className="esign-prep-suggest-actions">
                      <Button variant="primary" size="sm" onClick={acceptAllSure}>
                        Accept all {census.offeredSure} ready
                        {isPackage && docsWithSureOffers > 1
                          ? ` across ${docsWithSureOffers} documents`
                          : ""}
                        {census.offeredReview > 0
                          ? ` · leave ${census.offeredReview} for a look`
                          : ""}
                      </Button>
                    </div>
                  ) : null}
                </div>
              ) : suggestNote ? (
                <div className="esign-prep-section">
                  <span className="esign-prep-eyebrow">Placing fields</span>
                  <p className="esign-prep-hint">{suggestNote}</p>
                </div>
              ) : null}

              {/* Places left alone because the page already answers
                  them — see writtenOnCount. */}
              {!suggesting && writtenOnCount > 0 ? (
                <div className="esign-prep-section">
                  <span className="esign-prep-eyebrow">Already filled in</span>
                  <p className="esign-prep-hint">
                    {writtenOnCount} {writtenOnCount === 1 ? "place" : "places"} on this form
                    already {writtenOnCount === 1 ? "has" : "have"} an answer written in, so{" "}
                    {writtenOnCount === 1 ? "it got" : "they got"} no box. Nobody will be asked to
                    fill {writtenOnCount === 1 ? "it" : "them"} in.
                  </p>
                </div>
              ) : null}

              {/* ── What is still unmapped on this form ──
                  Nate, 2026-09-08: "Fields are still not mapped well."
                  The screen could not say that before, which is the part
                  that made it untrustworthy — a form with 68 unplaced
                  blanks and a form with none looked identical, so the
                  only way to find out was to send one and look at it.
                  Counted from the blanks the FORM draws, against every
                  field on the page whatever placed it. */}
              {unmapped.length > 0 ? (
                <div className="esign-prep-section esign-prep-unmapped">
                  <span className="esign-prep-eyebrow">
                    {unmapped.length} {unmapped.length === 1 ? "blank" : "blanks"} still unmapped
                  </span>
                  <p className="esign-prep-hint">
                    The form draws {unmapped.length === 1 ? "one more blank" : "these"} that
                    nothing has been placed on yet, marked with a dotted outline on the page.
                    Click one to say what fills it — it takes the shape the form drew, so
                    there is nothing to drag.
                  </p>
                  <Button variant="sw-default" size="sm" onClick={goToNextUnmapped}>
                    Next unmapped blank{unmappedFirstPage ? ` — page ${unmappedFirstPage}` : ""}
                  </Button>
                </div>
              ) : detectedBlanks.length > 0 && !suggesting ? (
                <div className="esign-prep-section">
                  <span className="esign-prep-eyebrow">Every blank is mapped</span>
                  <p className="esign-prep-hint">
                    All {detectedBlanks.length} blanks this form draws have a field on them.
                  </p>
                </div>
              ) : null}

              {/* Which COMPANY this document is for — asked only on the
                  standalone lanes (a household or a person), because
                  every other record carries its own. Not decoration:
                  the company selects which saved layouts are offered
                  and whether Realty's commission-disclosure merge
                  fields exist, so guessing would either hide a layout
                  the team saved or withhold a disclosure Realty owes.
                  A one-company user never sees this — their single
                  option is selected silently. */}
              {/* ── Needs your answer (was "What only you know", 2026-09-15) ──
                  The short list. Everything the OS can answer is
                  already answered and stays off this screen; what is
                  left is the reason the sender is at the keyboard.
                  Moved to sit right under the count strip and renamed
                  for the toolbar pass (2026-10-14): it is the first
                  thing to act on, so it is the first thing after the
                  summary, ahead of who signs. */}
              {!mapping && shortForm.groups.length > 0 ? (
                <div className="esign-prep-section esign-owyk">
                  <span className="esign-prep-eyebrow esign-owyk-title">
                    Needs your answer
                    {shortForm.askedCount > 0 ? (
                      <span className="esign-owyk-count">{shortForm.askedCount}</span>
                    ) : null}
                  </span>
                  <p className="esign-prep-hint">
                    {shortForm.askedCount === 0
                      ? "Nothing left to answer on this document."
                      : `${shortForm.askedCount} ${
                          shortForm.askedCount === 1 ? "question" : "questions"
                        } on this document.`}
                    {shortForm.filledForYouCount > 0
                      ? ` The OS filled ${shortForm.filledForYouCount} ${
                          shortForm.filledForYouCount === 1 ? "blank" : "blanks"
                        } for you.`
                      : ""}
                    {/* Said out loud rather than folded into either number.
                        These are the client's answers with no form asking
                        for them yet — counting them as filled is what made
                        an H.O.A. addendum claim 34 of its 70 blanks were
                        handled (2026-09-18). */}
                    {shortForm.waitingOnClientCount > 0
                      ? ` ${shortForm.waitingOnClientCount} ${
                          shortForm.waitingOnClientCount === 1
                            ? "blank is the client’s"
                            : "blanks are the client’s"
                        } to answer, and no form asks them yet.`
                      : ""}
                    {isPackage ? ` This list covers “${activeDoc.filename}” — switch documents for the rest.` : ""}
                  </p>
                  {shortForm.groups.map((g) => (
                    <div key={g.documentId} className="esign-owyk-group">
                      {/* One group today, because the list covers the active
                          document only — and the sentence above already names
                          it. A heading here would say the filename twice. */}
                      {shortForm.groups.length > 1 ? (
                        <span className="esign-owyk-group-head">{g.documentName}</span>
                      ) : null}
                      {g.questions.map((q) => (
                        <label
                          key={q.key}
                          className="esign-owyk-row"
                          data-kind={q.kind}
                          data-state={
                            q.kind === "default" && !answeredByHand.has(q.key) ? "default" : "typed"
                          }
                        >
                          <span className="esign-owyk-label">
                            {q.label}
                            <span className="esign-owyk-where">
                              page {q.pageNumber}
                              {q.kind === "gap" ? " · the OS has no home for this yet" : ""}
                              {q.kind === "client"
                                ? " · the client’s answer, and no form asks it yet"
                                : ""}
                            </span>
                            <button
                              type="button"
                              className="esign-owyk-show"
                              aria-label={`Show ${q.label} on the page`}
                              onClick={() => jumpToField(g.documentId, q.key)}
                            >
                              Show on the page ›
                            </button>
                          </span>
                          <span className="esign-owyk-answer">
                            {multilineQuestionKeys.has(q.key) ? (
                              // The additional-terms box and its kin: a
                              // question whose answer runs several lines
                              // gets several lines to type it in (Grant,
                              // 2026-09-29). Same handler, same jump.
                              <textarea
                                className="esign-owyk-input"
                                value={q.value ?? ""}
                                placeholder={q.kind === "client" ? "The client’s answer" : "Type it"}
                                maxLength={2000}
                                rows={4}
                                onChange={(e) => answerQuestion(q.key, e.target.value)}
                                onFocus={() => {
                                  const el = pageRefs.current.get(q.pageNumber);
                                  el?.scrollIntoView({ behavior: "smooth", block: "center" });
                                }}
                              />
                            ) : (
                              <input
                                className="esign-owyk-input"
                                value={q.value ?? ""}
                                // A client row stays typeable on purpose: the
                                // sender often HAS the answer (the seller told
                                // them on the phone) and refusing the keystroke
                                // would help nobody. What changes is that the
                                // OS stops claiming it filled it, and stops
                                // counting it as the sender's question.
                                placeholder={q.kind === "client" ? "The client’s answer" : "Type it"}
                                maxLength={500}
                                onChange={(e) => answerQuestion(q.key, e.target.value)}
                                onFocus={() => {
                                  // Jump the page to the blank being answered,
                                  // so the sender can check the list against
                                  // the paper rather than trusting a caption.
                                  const el = pageRefs.current.get(q.pageNumber);
                                  el?.scrollIntoView({ behavior: "smooth", block: "center" });
                                }}
                              />
                            )}
                            {/* The mockup's word for it, on the box rather than
                                in the question: this is the firm's standard
                                answer, and it stops saying so the moment the
                                sender types their own. */}
                            {q.kind === "default" && !answeredByHand.has(q.key) ? (
                              <span className="esign-owyk-tag">firm default</span>
                            ) : null}
                          </span>
                        </label>
                      ))}
                    </div>
                  ))}
                  {shortForm.someDocumentsUnmapped ? (
                    <p className="esign-prep-hint">
                      Some blanks here have never been told how they fill, so they are not on this
                      list. Map the form once and they will be.
                    </p>
                  ) : null}
                </div>
              ) : null}

              <div className="esign-prep-act">Who and what for</div>
              {needsCompany && companyOptions && companyOptions.length > 1 ? (
                <div className="esign-prep-section">
                  <span className="esign-prep-eyebrow">Company</span>
                  <label className="send-for-signature-message">
                    Which company is this document for?
                    <select
                      value={chosenCompany}
                      onChange={(e) => setChosenCompany(e.target.value)}
                    >
                      <option value="">Choose a company…</option>
                      {companyOptions.map((co) => (
                        <option key={co.slug} value={co.slug}>
                          {co.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <p className="esign-prep-hint">
                    Decides which saved layouts you're offered and which fields this document can
                    pull from.
                  </p>
                </div>
              ) : null}

              {/* What KIND of document this is. Asked once, here, because
                  this is the moment a human is looking at the document
                  and knows the answer — and because a signed document
                  with no type can never match an automation's
                  document-type filter (Grant, 2026-08-23). Optional on
                  purpose: not every document has a seeded requirement
                  that fits, and a missing type must never block a send. */}
              {documentType || (typeOptions && typeOptions.length > 0) ? (
                <FoldingSection
                  id="doctype"
                  title="Document type"
                  summary={
                    documentType
                      ? `Filed as ${typeLabel}`
                      : chosenType
                        ? (typeOptions?.find((t) => t.id === chosenType)?.name ?? "Chosen")
                        : "No type"
                  }
                >
                  {documentType ? (
                    <p className="esign-prep-hint">
                      Filed as <strong>{typeLabel}</strong>. Change it on the record if that's
                      wrong — a send never re-files a document.
                    </p>
                  ) : (
                    <>
                      <label className="send-for-signature-message">
                        What kind of document is this?
                        <select value={chosenType} onChange={(e) => setChosenType(e.target.value)}>
                          <option value="">Not listed — send without a type</option>
                          {(typeOptions ?? []).map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}
                            </option>
                          ))}
                        </select>
                      </label>
                      <p className="esign-prep-hint">
                        Files it against this record's checklist, and lets automations act on it
                        when it comes back signed.
                      </p>
                    </>
                  )}
                </FoldingSection>
              ) : null}

              {/* ── Signing roles (2026-09-15) ──────────────────────
                  One row per seat the company's catalog names, in the
                  shape Nate approved on the workflow mockup: a colour
                  stripe, the seat, what it does (signs / stamped by you
                  / name only), who is in it and why, the field count,
                  and one link to change it. The plain signer list below
                  stays the truth of who is emailed; this is the truth
                  of who signs AS what. */}
              {!mapping && casting && casting.length > 0 ? (
                <div className="esign-prep-section esign-roles">
                  <div className="esign-roles-head">
                    <span className="esign-prep-eyebrow">Signing roles</span>
                    {canSwapSeats ? (
                      <button type="button" className="esign-roles-swap" onClick={swapSeats}>
                        Swap 1 and 2
                      </button>
                    ) : null}
                  </div>
                  <p className="esign-prep-hint">
                    Filled from this record. Change any seat before you send.
                  </p>
                  {casting.map((seat) => {
                    const key = seatSignerKey(seat);
                    const count = fieldCountByRole.get(seat.role.key) ?? 0;
                    const isActive = key !== null && activeSignerId === key;
                    const state = seat.leftOff ? "off" : seat.outcome;
                    const person = seat.person;
                    const noEmail = person?.kind === "person" && !person.email && seat.role.kind === "signs";
                    // "Signs now · You" only on the seat that IS the
                    // sender (Nate, 2026-10-13: the empty Broker seat said
                    // it too). Any other stamp seat is someone else's
                    // to sign.
                    const kindChip =
                      seat.role.kind === "stamp"
                        ? seat.person?.kind === "owner" && seat.person.ownerUserId === user?.id
                          ? "Signs now · You"
                          : "Signed by them"
                        : seat.role.kind === "name_only"
                          ? "Name only"
                          : seat.role.optional && !person
                            ? "Optional"
                            : "Signs";
                    const picking = pickingRole === seat.role.key;
                    const recordOwner = rolesCtx?.owner ?? null;
                    const seatPerson = (
                      p: (typeof castPeople)[number],
                    ): CastPerson => ({
                      key: p.contactId,
                      kind: "person",
                      contactId: p.contactId,
                      displayName: p.displayName,
                      email: p.email,
                    });
                    const partyPerson = (
                      pt: RolesContext["parties"][number],
                    ): CastPerson => ({
                      key: pt.key,
                      kind: "party",
                      contactId: pt.contactId,
                      displayName: pt.displayName,
                      email: pt.email,
                      partyRole: pt.partyRole,
                    });
                    const needsEmail = seat.role.kind === "signs";
                    return (
                      <div
                        key={seat.role.key}
                        className={`esign-role esign-role-${seat.role.tone}`}
                        data-state={state}
                        data-active={isActive ? "true" : undefined}
                        data-kind={seat.role.kind}
                        onClick={() => {
                          if (key && selected.has(key)) setActiveSignerId(key);
                        }}
                      >
                        <span className="esign-role-stripe" />
                        <div className="esign-role-head">
                          <span className="esign-role-name">{seat.role.label}</span>
                          <span className={`esign-role-kind esign-role-kind-${seat.role.kind}`}>
                            {kindChip}
                          </span>
                        </div>
                        {count > 0 ? (
                          <span className="esign-role-count">
                            {count} {count === 1 ? "field" : "fields"}
                          </span>
                        ) : null}
                        <div className="esign-role-who" data-empty={person && !seat.leftOff ? undefined : "true"}>
                          {seat.leftOff ? (
                            "Left off this time"
                          ) : person ? (
                            <>
                              {person.displayName}
                              {person.email ? (
                                <span className="esign-role-mail">{person.email}</span>
                              ) : noEmail ? (
                                <span className="esign-role-mail esign-role-mail-warn">
                                  no email on file
                                </span>
                              ) : null}
                            </>
                          ) : seat.outcome === "ask" ? (
                            `Pick one of ${seat.candidates.length}`
                          ) : (
                            "Nobody"
                          )}
                        </div>
                        <div className="esign-role-why">
                          {seat.reason}
                          {/* THE SEAT PROMISES A STAMP. SAY SO WHEN THERE
                              IS NOTHING TO STAMP (2026-09-16).

                              The sentence beside this is written by the
                              pure casting code, which cannot know whether
                              this person has ever saved a signature — so
                              it says "your saved signature is stamped
                              before send" either way. Once those lines
                              began filling themselves, a sender with no
                              saved signature became the one case where
                              that sentence is false AND the consequence
                              is invisible: their signature lines go out
                              blank and nothing on the screen said why. */}
                          {seat.role.kind === "stamp" &&
                          seat.person?.kind === "owner" &&
                          seat.person.ownerUserId === user?.id &&
                          mySignature === null ? (
                            <span className="esign-role-why-warn">
                              You have no saved signature yet, so these lines go out blank.
                              Add one below and they sign themselves.
                            </span>
                          ) : null}
                        </div>
                        <button
                          type="button"
                          className="esign-role-change"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPickingRole(picking ? null : seat.role.key);
                            setTypedName("");
                          }}
                        >
                          {picking ? "Close" : person && !seat.leftOff ? "Change" : seat.outcome === "ask" ? "Pick" : "Add"}
                        </button>
                        {picking ? (
                          <div className="esign-role-picker" onClick={(e) => e.stopPropagation()}>
                            {seat.outcome === "ask" && seat.candidates.length > 0 ? (
                              <>
                                <span className="esign-role-group">Pick one</span>
                                {seat.candidates.map((c) => (
                                  <button
                                    key={c.key}
                                    type="button"
                                    className="esign-role-opt"
                                    disabled={needsEmail && !c.email}
                                    onClick={() => fillSeat(seat.role.key, c)}
                                  >
                                    <span className="esign-role-opt-name">{c.displayName}</span>
                                    <span className="esign-role-opt-note">
                                      {c.email ?? (needsEmail ? "no email" : "")}
                                    </span>
                                  </button>
                                ))}
                              </>
                            ) : null}
                            {seat.role.kind !== "stamp" && castPeople.length > 0 ? (
                              <>
                                <span className="esign-role-group">On this record</span>
                                {castPeople.map((p) => (
                                  <button
                                    key={p.contactId}
                                    type="button"
                                    className="esign-role-opt"
                                    data-on={person?.contactId === p.contactId ? "true" : undefined}
                                    disabled={needsEmail && !p.email}
                                    onClick={() => fillSeat(seat.role.key, seatPerson(p))}
                                  >
                                    <span className="esign-role-opt-name">{p.displayName}</span>
                                    <span className="esign-role-opt-note">
                                      {p.email ?? (needsEmail ? "no email on file" : "")}
                                    </span>
                                  </button>
                                ))}
                              </>
                            ) : null}
                            {seat.role.kind === "stamp" && recordOwner ? (
                              <>
                                <span className="esign-role-group">Stamped before send</span>
                                <button
                                  type="button"
                                  className="esign-role-opt"
                                  data-on={person?.kind === "owner" ? "true" : undefined}
                                  onClick={() =>
                                    fillSeat(seat.role.key, {
                                      key: "owner",
                                      kind: "owner",
                                      contactId: null,
                                      displayName: recordOwner.displayName,
                                      email: null,
                                    })
                                  }
                                >
                                  <span className="esign-role-opt-name">{recordOwner.displayName}</span>
                                  <span className="esign-role-opt-note">owns this record</span>
                                </button>
                              </>
                            ) : null}
                            {seat.role.kind !== "stamp" && rolesCtx && rolesCtx.parties.length > 0 ? (
                              <>
                                <span className="esign-role-group">On the Parties list</span>
                                {rolesCtx.parties.map((pt) => (
                                  <button
                                    key={pt.key}
                                    type="button"
                                    className="esign-role-opt"
                                    data-on={person?.key === pt.key ? "true" : undefined}
                                    disabled={needsEmail && !pt.email}
                                    onClick={() => fillSeat(seat.role.key, partyPerson(pt))}
                                  >
                                    <span className="esign-role-opt-name">{pt.displayName}</span>
                                    <span className="esign-role-opt-note">
                                      {pt.partyRole.replace(/_/g, " ")}
                                      {needsEmail && !pt.email ? " · no email" : ""}
                                    </span>
                                  </button>
                                ))}
                              </>
                            ) : null}
                            {seat.role.kind === "name_only" ? (
                              <form
                                className="esign-role-type"
                                onSubmit={(e) => {
                                  e.preventDefault();
                                  const name = typedName.trim();
                                  if (!name) return;
                                  fillSeat(seat.role.key, {
                                    key: `name:${seat.role.key}`,
                                    kind: "name",
                                    contactId: null,
                                    displayName: name,
                                    email: null,
                                  });
                                }}
                              >
                                <input
                                  className="esign-prep-guest-input"
                                  value={typedName}
                                  onChange={(e) => setTypedName(e.target.value)}
                                  placeholder="Type the name to print"
                                  aria-label={`Name to print for ${seat.role.label}`}
                                  maxLength={120}
                                />
                                <Button type="submit" variant="sw-default" size="sm" disabled={!typedName.trim()}>
                                  Use this name
                                </Button>
                              </form>
                            ) : null}
                            <div className="esign-role-picker-foot">
                              {seat.role.kind === "signs" ? (
                                <button
                                  type="button"
                                  className="esign-role-link"
                                  onClick={() => {
                                    guestForRoleRef.current = seat.role.key;
                                    setPickingRole(null);
                                    setAddingGuest(true);
                                  }}
                                >
                                  Someone else (by name and email)
                                </button>
                              ) : null}
                              {person || seat.outcome === "ask" || !seat.leftOff ? (
                                <button
                                  type="button"
                                  className="esign-role-link esign-role-link-off"
                                  onClick={() => leaveOff(seat.role.key)}
                                >
                                  Nobody this time
                                </button>
                              ) : null}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                  {roleBlockers.map((b) => (
                    <p key={b} className="esign-role-blocker">
                      {b}
                    </p>
                  ))}
                </div>
              ) : null}

              <div className="esign-prep-section" data-review-target="signers">
                <span className="esign-prep-eyebrow">{mapping ? "Signer slots" : "Signers"}</span>
                {mapping ? (
                  <p className="esign-prep-hint">
                    Stand-ins, not real people — nobody here is ever emailed. Name each slot with
                    the SEAT the form means ("Buyer 2", "Co-borrower") and every future send places
                    those fields on whoever is in that seat, wherever they sit in the list. Pick
                    "Something else" to type a word the catalog doesn't have; that slot keeps the
                    old behaviour and goes by position.
                  </p>
                ) : null}
                {mapping ? (
                  mappingSlots.map((slot, i) => {
                    const color = colorByContact.get(slot.id) ?? signerColor(i);
                    const count = fields.filter((f) => f.signerKey === slot.id).length;
                    const isActive = activeSignerId === slot.id;
                    return (
                      <div
                        key={slot.id}
                        className="esign-prep-signer"
                        data-active={isActive ? "true" : undefined}
                        style={isActive ? { borderColor: color.base } : undefined}
                        onClick={() => setActiveSignerId(slot.id)}
                      >
                        <span className="esign-prep-signer-dot" style={{ background: color.base }} />
                        {/* THE SEAT, not a free word (2026-09-15). The
                            catalog's own seats are the choices, so a
                            form taught here is taught in the same
                            vocabulary the send casts in. "Something
                            else" keeps the free-text box for a seat the
                            catalog doesn't carry — that slot is taught
                            positionally, exactly as every slot was
                            before today. */}
                        {mappingCatalog.length > 0 ? (
                          <select
                            className="esign-prep-signer-role"
                            value={slot.roleKey ?? ""}
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Which seat slot ${i + 1} is`}
                            onChange={(e) => setSlotSeat(slot.id, e.target.value || null)}
                          >
                            {mappingCatalog.map((r) => (
                              <option key={r.key} value={r.key}>
                                {r.label}
                              </option>
                            ))}
                            <option value="">Something else…</option>
                          </select>
                        ) : null}
                        {mappingCatalog.length === 0 || !slot.roleKey ? (
                          <input
                            className="esign-prep-signer-name-input"
                            value={slot.label}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => renameMappingSlot(slot.id, e.target.value)}
                            maxLength={80}
                            aria-label={`Signer ${i + 1} label`}
                          />
                        ) : (
                          <span className="esign-prep-signer-name">{slot.label}</span>
                        )}
                        {count > 0 ? (
                          <span
                            className="esign-prep-signer-count"
                            style={{ background: color.soft, color: color.deep }}
                          >
                            {count} {count === 1 ? "field" : "fields"}
                          </span>
                        ) : null}
                        {mappingSlots.length > 1 ? (
                          <Button
                            variant="danger-ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeMappingSlot(slot.id);
                            }}
                          >
                            Remove
                          </Button>
                        ) : null}
                      </div>
                    );
                  })
                ) : eligible.length === 0 ? (
                  <p className="send-for-signature-empty">
                    Nobody on this record has an email on file yet. Add one, or add a signer by name
                    and email below.
                  </p>
                ) : (
                  eligible.map((p) => {
                    const color = colorByContact.get(p.key) ?? signerColor(0);
                    const count = realFields.filter((f) => f.signerKey === p.key).length;
                    const isActive = activeSignerId === p.key;
                    const isOn = selected.has(p.key);
                    return (
                      <div
                        key={p.key}
                        className="esign-prep-signer"
                        data-active={isActive ? "true" : undefined}
                        data-guest={p.kind === "guest" ? "true" : undefined}
                        style={isActive ? { borderColor: color.base } : undefined}
                        onClick={() => {
                          if (isOn) setActiveSignerId(p.key);
                        }}
                      >
                        {/* The <label> is the real tap target — a bare
                            13px checkbox is the whole way to add or
                            remove a signer (the row's own onClick only
                            fires once someone is already selected), so
                            on a phone this is the one control standing
                            between an advisor and sending the envelope. */}
                        <label
                          className="esign-prep-signer-check"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={isOn}
                            onChange={() => toggleSigner(p.key)}
                            aria-label={`${p.displayName} signs this`}
                          />
                        </label>
                        <span
                          className="esign-prep-signer-dot"
                          style={{ background: color.base }}
                        />
                        <span className="esign-prep-signer-name">
                          {p.displayName}
                          {/* A guest says so, every time they are shown.
                              The whole point of this kind of signer is
                              that they are NOT in our database, and a
                              row that looked identical to a client's
                              would hide exactly that. */}
                          {p.kind === "guest" ? (
                            <span className="esign-prep-signer-guest">not in our records</span>
                          ) : null}
                        </span>
                        {count > 0 && !copyOnly.has(p.key) ? (
                          <span
                            className="esign-prep-signer-count"
                            style={{ background: color.soft, color: color.deep }}
                          >
                            {count} {count === 1 ? "field" : "fields"}
                          </span>
                        ) : null}
                        {isOn ? (
                          /* "Signs" vs "gets a copy". A copy recipient is
                             never asked to sign and hears nothing until
                             the document is fully executed — which is why
                             switching them here also clears any fields
                             placed for them, rather than leaving fields
                             assigned to somebody who will never see a
                             signing screen. */
                          <select
                            className="esign-prep-signer-role"
                            value={copyOnly.has(p.key) ? "cc" : "signer"}
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`What ${p.displayName} does`}
                            onChange={(e) => {
                              const asCopy = e.target.value === "cc";
                              setCopyOnly((prev) => {
                                const next = new Set(prev);
                                if (asCopy) next.add(p.key);
                                else next.delete(p.key);
                                return next;
                              });
                              if (asCopy) {
                                setFields((prev) => prev.filter((f) => f.signerKey !== p.key));
                              }
                            }}
                          >
                            <option value="signer">signs</option>
                            <option value="cc">gets a copy</option>
                          </select>
                        ) : null}
                        {/* ONE CLICK TO REMOVE, with undo (Grant,
                            2026-09-03 — Nate's BrokerMint complaint is
                            the time and clicks). Only a guest gets it:
                            a person on the record is un-checked rather
                            than deleted, because they belong to the
                            record whether or not they sign this one
                            document. */}
                        {p.kind === "guest" ? (
                          <Button
                            variant="danger-ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeGuestSigner(p.key);
                            }}
                          >
                            Remove
                          </Button>
                        ) : null}
                      </div>
                    );
                  })
                )}
                {/* ── Adding a signer who isn't one of our people ────
                    Two clicks for someone already on the deal (they are
                    offered as chips, because their name and email are
                    already recorded), and one small form for anyone
                    else. Neither creates a contact or a household. */}
                {!mapping ? (
                  <>
                    {partySuggestions.length > 0 ? (
                      <div className="esign-prep-signer-suggest">
                        <span className="esign-prep-signer-suggest-label">Also on this record</span>
                        <div className="esign-prep-signer-chips">
                          {partySuggestions.map((sug) => (
                            <button
                              key={sug.email}
                              type="button"
                              className="esign-prep-signer-chip"
                              onClick={() => addGuestSigner(sug.name, sug.email)}
                            >
                              <span>+ {sug.name}</span>
                              <span className="esign-prep-signer-chip-role">{sug.roleLabel}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    {addingGuest ? (
                      <form
                        className="esign-prep-guest-form"
                        onSubmit={(e) => {
                          e.preventDefault();
                          if (addGuestSigner(guestName, guestEmail)) {
                            setGuestName("");
                            setGuestEmail("");
                            setAddingGuest(false);
                          }
                        }}
                      >
                        <input
                          className="esign-prep-guest-input"
                          value={guestName}
                          onChange={(e) => setGuestName(e.target.value)}
                          placeholder="Name"
                          aria-label="Signer's name"
                          maxLength={120}
                          // The sender clicked "add someone else" — put
                          // them in the first box rather than making
                          // them click again to start typing.
                          autoFocus
                        />
                        <input
                          className="esign-prep-guest-input"
                          value={guestEmail}
                          onChange={(e) => setGuestEmail(e.target.value)}
                          placeholder="Email"
                          type="email"
                          aria-label="Signer's email"
                          maxLength={254}
                        />
                        <div className="esign-prep-guest-actions">
                          <Button type="submit" variant="sw-primary" size="sm">
                            Add
                          </Button>
                          <Button
                            type="button"
                            variant="sw-ghost"
                            size="sm"
                            onClick={() => {
                              setAddingGuest(false);
                              setGuestName("");
                              setGuestEmail("");
                              setGuestError(null);
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                        {guestError ? <p className="esign-prep-guest-error">{guestError}</p> : null}
                      </form>
                    ) : (
                      <Button
                        variant="sw-default"
                        size="sm"
                        onClick={() => {
                          setGuestError(null);
                          setAddingGuest(true);
                        }}
                      >
                        + Add someone else
                      </Button>
                    )}
                    {guests.length > 0 ? (
                      <p className="send-for-signature-note">
                        A signer added by name and email is emailed this one document. They are not
                        added to your contacts and no household is created for them.
                      </p>
                    ) : null}
                  </>
                ) : null}
                {mapping && mappingSlots.length < MAX_MAPPING_SLOTS ? (
                  <Button variant="sw-default" size="sm" onClick={addMappingSlot}>
                    + Add a signer
                  </Button>
                ) : null}
                {!mapping && candidates.some((p) => !p.email) ? (
                  <p className="send-for-signature-note">
                    {candidates
                      .filter((p) => !p.email)
                      .map((p) => p.display_name)
                      .join(", ")}{" "}
                    {candidates.filter((p) => !p.email).length === 1 ? "has" : "have"} no email on
                    file, so they're not selectable here.
                  </p>
                ) : null}
              </div>

              {/* Advisor self-signing (Grant, 2026-08-22): stamp your OWN
                  saved signature straight into the document — no envelope
                  field, no round trip back to you. */}
              <div className="esign-prep-section">
                <span className="esign-prep-eyebrow">Your signature</span>
                {mySignature === undefined ? null : mySignature === null ? (
                  // A plain caption here reads as a dead end — a loan
                  // officer needing to sign a document he's preparing
                  // (Joey, 2026-08-28: "I need to sign StewDocs as well
                  // and there's not currently a place for me to add
                  // myself as a signer") skimmed right past a one-line
                  // hint and never found this. Same visual weight as
                  // "Place my signature" below, so it reads as the next
                  // action rather than a footnote — and it opens the
                  // dedicated E-sign settings page (signature + initials
                  // + defaults), not the older Preferences card.
                  <>
                    <a
                      className="esign-prep-palette-item esign-prep-save-signature-cta"
                      href="/esign/settings"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Save your signature
                    </a>
                    <p className="esign-prep-hint">
                      One-time setup — draw it or type it. Once it's saved, come back here
                      and stamp it directly onto this document yourself.
                    </p>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="esign-prep-palette-item"
                      data-kind="stamp"
                      data-active={placingStamp ? "true" : undefined}
                      onClick={() => {
                        setPlacingStamp(true);
                        setPlacingType(null);
                        setPlacingLocked(false);
                        setSelectedStampKey(null);
                      }}
                    >
                      Place my signature
                    </button>
                    <p className="esign-prep-hint">
                      {placingStamp
                        ? "Click the page to stamp it in — Esc when done."
                        : "Stamped into the document itself before it sends — no envelope ever goes to you."}
                    </p>
                  </>
                )}
              </div>

              {selectedField ? (
                <div ref={selectedEditorRef} className="esign-prep-section esign-prep-selected">
                  <span className="esign-prep-eyebrow">{fillSummaryLabel(selectedField)}</span>
                  {/* A GHOST SAYS WHAT IT OFFERS, AND ONE CLICK TAKES IT
                      (2026-09-29). Nothing below this block sends unless
                      the ghost is accepted, typed into, or handed to a
                      signer; the value inputs and the signer picker
                      below already accept on their own (`taken`). */}
                  {isGhost(selectedField) ? (
                    <div className="esign-ghost-offer" data-kind={selectedOffer?.kind}>
                      {selectedOffer ? (
                        <>
                          <span className="esign-ghost-offer-value">
                            {selectedOffer.kind === "handed"
                              ? `For ${
                                  eligible.find((p) => p.key === selectedField.signerKey)
                                    ?.displayName ?? "the signer"
                                } to fill in`
                              : (selectedOffer.value ?? "Worked out at send")}
                            <small>
                              {offerTag(selectedOffer)}
                              {selectedOffer.sure
                                ? ""
                                : selectedOffer.kind === "document"
                                  ? " · a guess, check it against that document"
                                  : " · a guess, check it against the page"}
                            </small>
                          </span>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => acceptGhosts((f) => f.key === selectedField.key)}
                          >
                            Accept
                          </Button>
                        </>
                      ) : (
                        <span className="esign-ghost-offer-value">
                          Nothing offered.
                          <small>
                            {askedKeys.has(selectedField.key)
                              ? "Only you can answer this one — type below, or leave it."
                              : "Not sent unless you type below, or hand it to a signer."}
                          </small>
                        </span>
                      )}
                    </div>
                  ) : null}
                  {isPreparerField(selectedField) ? selectedField.strike ? null : (
                    // No signer to choose: the whole point is that this
                    // box belongs to nobody who signs. Said plainly here
                    // rather than shown as a disabled dropdown, which
                    // would read as something that ought to work.
                    <p className="esign-prep-hint">
                      <strong>You fill this one.</strong> It is printed into the page
                      before the document is sent, so the signer sees it and cannot
                      change it. Nothing here is a signing field.
                    </p>
                  ) : (
                    <label className="esign-prep-selected-row">
                      Signer
                      <select
                        value={selectedField.signerKey}
                        onChange={(e) => {
                          const signerKey = e.target.value;
                          if (!selected.has(signerKey)) return;
                          setFields((prev) =>
                            prev.map((f) =>
                              f.key === selectedField.key ? taken({ ...f, signerKey }) : f,
                            ),
                          );
                        }}
                      >
                        {eligible
                          .filter((p) => selected.has(p.key))
                          .map((p) => (
                            <option key={p.key} value={p.key}>
                              {p.displayName}
                            </option>
                          ))}
                      </select>
                    </label>
                  )}
                  {selectedField.strike ? (
                    <p className="esign-prep-hint">
                      This draws a line through the printed words under it. Drag the box
                      or its corner to cover exactly what you mean to strike. It belongs
                      to this send only; a saved form never brings it back.
                    </p>
                  ) : null}
                  {selectedField.fieldType === "checkbox" && isPreparerField(selectedField) ? (
                    <label className="esign-prep-selected-row">
                      <span>
                        <input
                          type="checkbox"
                          checked={selectedField.value === BOLDSIGN_CHECKBOX_ON}
                          onChange={(e) =>
                            setFields((prev) =>
                              prev.map((f) =>
                                f.key === selectedField.key
                                  ? { ...f, value: e.target.checked ? BOLDSIGN_CHECKBOX_ON : null }
                                  : f,
                              ),
                            )
                          }
                        />{" "}
                        Checked
                      </span>
                      <span className="esign-prep-hint">
                        This prints an X before it is sent; the signer cannot change it.
                        Unchecked prints nothing.
                      </span>
                    </label>
                  ) : null}
                  {/* HOW THE SENDER'S OWN TEXT LOOKS (Nate, 2026-10-26: "change
                      the color, indentation, size, boldness, italicize, or
                      even underline"). Only a box the sender fills prints
                      this; a signer's box is the signing service's to style. */}
                  {isPreparerField(selectedField) &&
                  selectedField.fieldType === "text" &&
                  !selectedField.strike ? (
                    <TextStyleControls
                      field={selectedField}
                      onChange={(next) =>
                        setFields((prev) => prev.map((f) => (f.key === selectedField.key ? taken(next(f)) : f)))
                      }
                    />
                  ) : null}
                  {selectedField.fieldType === "text" && !selectedField.strike ? (
                    <div className="esign-prep-selected-row">
                      What should this say?
                      {selectedField.mergeToken ? (
                        // BOUND: the field pulls from the record, so the
                        // thing to show is WHICH field and what it holds
                        // right now — not a text box whose contents the
                        // send would overwrite anyway.
                        <>
                          <div className="esign-merge-chip">
                            <span className="esign-merge-chip__label">
                              {boundPreview(selectedField.mergeToken, selectedField.signerKey, selectedField.roleKey).label}
                            </span>
                            <span
                              className="esign-merge-chip__value"
                              data-empty={
                                boundPreview(selectedField.mergeToken, selectedField.signerKey, selectedField.roleKey).value ? undefined : "true"
                              }
                            >
                              {boundPreview(selectedField.mergeToken, selectedField.signerKey, selectedField.roleKey).value ??
                                (mergeOptions === null
                                  ? "reading this record…"
                                  : emptyBindingReason(selectedField))}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                ensureMergeOptions();
                                setPickerForKey(
                                  pickerForKey === selectedField.key ? null : selectedField.key,
                                );
                              }}
                            >
                              Change
                            </Button>
                          </div>
                          {/* WHOSE NAME A LOCKED BOX PRINTS (Baker,
                              2026-09-16). A locked box belongs to the
                              preparer, so it names no signer — which is
                              right for boilerplate and wrong for the
                              case Baker hit: a box carrying the buyer's
                              name, printed but not editable by them.
                              It names a SEAT instead, and the casting
                              decides who is in it on each send.

                              Shown only where it means something: a
                              locked box bound to a token that reads a
                              person. An atlas-placed box already knows
                              its seat; this is how one placed by hand
                              gets told. */}
                          {selectedField.signerKey === PREPARER_SIGNER_KEY &&
                          isPersonScopedToken(selectedField.mergeToken) ? (
                            <label className="esign-field-row">
                              <span>Whose?</span>
                              <select
                                value={selectedField.roleKey ?? ""}
                                onChange={(e) => {
                                  // "Everyone, on one line" is not a seat:
                                  // it swaps the box to the token that
                                  // prints every person on the record by
                                  // legal name (Nate, 2026-09-25, line 1a
                                  // "BUYER'S NAME(S)"). A seat can only
                                  // ever name one person.
                                  if (e.target.value === EVERYONE_ONE_LINE) {
                                    setFields((fs) =>
                                      fs.map((f) =>
                                        f.key === selectedField.key
                                          ? taken({
                                              ...f,
                                              mergeToken: "transaction.people_legal_names",
                                              roleKey: null,
                                              value: null,
                                            })
                                          : f,
                                      ),
                                    );
                                    return;
                                  }
                                  const roleKey = e.target.value || null;
                                  setFields((fs) =>
                                    fs.map((f) =>
                                      f.key === selectedField.key ? { ...f, roleKey } : f,
                                    ),
                                  );
                                }}
                              >
                                <option value="">Nobody yet</option>
                                {roles.map((r) => (
                                  <option key={r.key} value={r.key}>
                                    {r.label}
                                  </option>
                                ))}
                                {selectedField.mergeToken === "contact.legal_full_name" &&
                                mergeByToken.has("transaction.people_legal_names") ? (
                                  <option value={EVERYONE_ONE_LINE}>Everyone, on one line</option>
                                ) : null}
                              </select>
                            </label>
                          ) : null}
                          {/* The per-send override. Typing here wins for
                              THIS send only — the binding survives, so the
                              next send of the same document resolves from
                              the record again and this one-off can't
                              become a permanent wrong answer. */}
                          {isMultilineField(selectedField) ? (
                            <textarea
                              className="esign-prep-multiline-input"
                              value={selectedField.value ?? ""}
                              maxLength={2000}
                              rows={4}
                              placeholder="Use different text, just this once"
                              onChange={(e) =>
                                setFields((prev) =>
                                  prev.map((f) =>
                                    f.key === selectedField.key
                                      ? taken({ ...f, value: e.target.value || null })
                                      : f,
                                  ),
                                )
                              }
                            />
                          ) : (
                            <input
                              type="text"
                              value={selectedField.value ?? ""}
                              maxLength={2000}
                              placeholder="Use different text, just this once"
                              onChange={(e) =>
                                setFields((prev) =>
                                  prev.map((f) =>
                                    f.key === selectedField.key
                                      ? taken({ ...f, value: e.target.value || null })
                                      : f,
                                  ),
                                )
                              }
                            />
                          )}
                        </>
                      ) : (
                        <>
                          {/* A box spanning several lines gets a box to
                              type several lines into: paragraph breaks
                              are kept, and the page shows each line on
                              its rule as it is typed. */}
                          {isMultilineField(selectedField) ? (
                            <textarea
                              className="esign-prep-multiline-input"
                              value={selectedField.value ?? ""}
                              maxLength={2000}
                              rows={5}
                              placeholder={`Type it — this box has ${multilineLineCount(selectedField.height, selectedField.linePitch)} lines`}
                              onChange={(e) =>
                                setFields((prev) =>
                                  prev.map((f) =>
                                    f.key === selectedField.key
                                      ? taken({ ...f, value: e.target.value || null })
                                      : f,
                                  ),
                                )
                              }
                            />
                          ) : (
                            <input
                              type="text"
                              value={selectedField.value ?? ""}
                              maxLength={2000}
                              placeholder="Type it, or pull it from this record"
                              onChange={(e) =>
                                setFields((prev) =>
                                  prev.map((f) =>
                                    f.key === selectedField.key
                                      ? taken({ ...f, value: e.target.value || null })
                                      : f,
                                  ),
                                )
                              }
                            />
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              ensureMergeOptions();
                              setPickerForKey(
                                pickerForKey === selectedField.key ? null : selectedField.key,
                              );
                            }}
                          >
                            Pull from this record…
                          </Button>
                        </>
                      )}
                      {pickerForKey === selectedField.key ? (
                        <MergeFieldPicker
                          options={mergeOptions}
                          loadError={mergeLoadError}
                          selected={selectedField.mergeToken ?? null}
                          onPick={(token) => {
                            setFields((prev) =>
                              prev.map((f) => {
                                if (f.key !== selectedField.key) return f;
                                // The typed value is dropped with the
                                // binding taking over: two answers to
                                // "what does this say?" is how a stale
                                // number survives on a contract.
                                const next = { ...f, mergeToken: token, value: null };
                                // Same default-seat rule as placing a new
                                // locked box (Baker, 2026-09-18): binding
                                // an EXISTING locked box to a person's
                                // field, by this same picker, should not
                                // leave it on "Nobody yet" either.
                                // wantsDefaultSeat is false when a seat
                                // is already chosen, so a roleKey someone
                                // deliberately set (or the atlas placed)
                                // is never overwritten.
                                if (wantsDefaultSeat(token, isPreparerField(f), f.roleKey)) {
                                  next.roleKey = firstSeatOf(roles)?.key ?? null;
                                }
                                return taken(next);
                              }),
                            );
                            setPickerForKey(null);
                          }}
                          onUnbind={() => {
                            setFields((prev) =>
                              prev.map((f) =>
                                f.key === selectedField.key
                                  ? taken({ ...f, mergeToken: null })
                                  : f,
                              ),
                            );
                            setPickerForKey(null);
                          }}
                          onClose={() => setPickerForKey(null)}
                        />
                      ) : null}
                      {selectedField.mergeToken ? (
                        <p className="esign-prep-hint">
                          {selectedField.value
                            ? "Your text is used for this send. The link to the record stays, so the next send fills itself in again."
                            : "Filled in from this record when you send — so this document prints today's value, not the one it was set up with."}
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                  {selectedField.fieldType === "dropdown" ? (
                    <div className="esign-prep-selected-row">
                      What can they pick from?
                      {/* One choice per line. A list editor with add/remove
                          buttons was the first design; typing a list is
                          faster than clicking one into existence, and
                          pasting the options straight off the form is the
                          way these actually get built. */}
                      <textarea
                        rows={Math.min(8, Math.max(3, (selectedField.options?.length ?? 2) + 1))}
                        value={(selectedField.options ?? []).join("\n")}
                        placeholder={"Yes\nNo"}
                        onChange={(e) => {
                          const options = e.target.value.split("\n");
                          setFields((prev) =>
                            prev.map((f) =>
                              f.key === selectedField.key ? taken({ ...f, options }) : f,
                            ),
                          );
                        }}
                      />
                      <p className="esign-prep-hint">
                        One choice per line. Blank lines are ignored.
                        {(selectedField.options ?? []).filter((o) => o.trim()).length === 0
                          ? " This list is empty — add at least one choice before sending."
                          : null}
                      </p>
                    </div>
                  ) : null}
                  {selectedField.fieldType === "radio" ? (
                    <div className="esign-prep-selected-row">
                      Which group is this button in?
                      <input
                        type="text"
                        value={selectedField.groupName ?? ""}
                        maxLength={60}
                        placeholder="Choice 1"
                        onChange={(e) =>
                          setFields((prev) =>
                            prev.map((f) =>
                              f.key === selectedField.key
                                ? taken({ ...f, groupName: e.target.value })
                                : f,
                            ),
                          )
                        }
                      />
                      <p className="esign-prep-hint">
                        {(() => {
                          const g = (selectedField.groupName ?? "").trim();
                          if (!g) {
                            return "Give it a group name before sending — an ungrouped button doesn't rule anything out, so the signer could pick both sides of an either/or.";
                          }
                          const n = fields.filter(
                            (f) =>
                              f.fieldType === "radio" &&
                              f.signerKey === selectedField.signerKey &&
                              (f.groupName ?? "").trim() === g,
                          ).length;
                          return n > 1
                            ? `${n} buttons are in "${g}" — the signer can pick exactly one of them. Type a different name to split this one off.`
                            : `Only this button is in "${g}" so far. Place another with the same name and the two rule each other out.`;
                        })()}
                      </p>
                    </div>
                  ) : null}
                  {selectedField.fieldType === "attachment" ? (
                    <>
                      <label className="esign-prep-selected-row">
                        What are you asking them for?
                        <input
                          type="text"
                          value={selectedField.value ?? ""}
                          placeholder="e.g. Driver's license photo"
                          maxLength={100}
                          onChange={(e) =>
                            setFields((prev) =>
                              prev.map((f) =>
                                f.key === selectedField.key
                                  ? taken({ ...f, value: e.target.value || null })
                                  : f,
                              ),
                            )
                          }
                        />
                      </label>
                      <p className="esign-prep-hint">
                        The signer gets an upload button here, labeled with
                        what you typed (left blank it says "Requested
                        file"). Whatever they upload is filed on this
                        record automatically once everyone has signed,
                        next to the signed document. Leave "Required to
                        finish signing" off if it's optional — they can
                        then finish without it.
                      </p>
                    </>
                  ) : null}
                  {/* Hidden for a locked box: there is no signer to
                      require anything of, so the checkbox would be a
                      control that does nothing. */}
                  {isPreparerField(selectedField) ? null : (
                    <label className="esign-prep-selected-check">
                      <input
                        type="checkbox"
                        checked={selectedField.required}
                        onChange={(e) =>
                          setFields((prev) =>
                            prev.map((f) =>
                              f.key === selectedField.key
                                ? taken({ ...f, required: e.target.checked })
                                : f,
                            ),
                          )
                        }
                      />
                      Required to finish signing
                    </label>
                  )}
                  {/* A VALUE WE FILLED IN PRINTS (Baker, 2026-09-18).
                      Shown only where the question can arise: a TEXT
                      field that will actually go out carrying something,
                      either typed here or pulled from the record. Every
                      other field has nothing to protect, and a checkbox
                      offering to unlock an empty box would be a control
                      that does nothing — the same reason "Required" is
                      hidden on a locked box just above. */}
                  {!isPreparerField(selectedField) &&
                  selectedField.fieldType === "text" &&
                  ((selectedField.value ?? "").trim().length > 0 || selectedField.mergeToken) ? (
                    <>
                      <label className="esign-prep-selected-check">
                        <input
                          type="checkbox"
                          checked={selectedField.signerMayEdit === true}
                          onChange={(e) =>
                            setFields((prev) =>
                              prev.map((f) =>
                                f.key === selectedField.key
                                  ? taken({ ...f, signerMayEdit: e.target.checked })
                                  : f,
                              ),
                            )
                          }
                        />
                        Let the signer change this
                      </label>
                      <p className="esign-prep-hint">
                        {selectedField.signerMayEdit === true
                          ? "They'll get an editable box with this as a starting point, and whatever they type is what the document says. Use this for a blank you're only part-way answering."
                          : "This prints onto the page and the signer has nothing to click. That's the default for anything you fill in, so what they sign is what you sent."}
                      </p>
                    </>
                  ) : null}
                  <div className="esign-prep-selected-actions">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (!pages) return;
                        const clones = repeatOnEveryPage(selectedField, pages, () => crypto.randomUUID());
                        setFields((prev) => [...prev, ...clones]);
                        toast.success(
                          `Repeated on ${clones.length} other ${clones.length === 1 ? "page" : "pages"}.`,
                        );
                      }}
                      disabled={!pages || pages.length < 2}
                    >
                      Repeat on every page
                    </Button>
                    <Button
                      variant="danger-ghost"
                      size="sm"
                      onClick={() => removeFields(new Set([selectedField.key]))}
                    >
                      Remove field
                    </Button>
                  </div>
                  <p className="esign-prep-hint">Arrow keys nudge · Shift+arrows nudge more · Delete removes</p>
                </div>
              ) : null}

              {/* Multi-select (Joey, 2026-09-18): "Required" and delete
                  apply to every field in the group at once — the whole
                  point of building a selection of more than one. The
                  per-field editor above only ever shows for exactly one
                  selected field, so this and it never render together. */}
              {selectedFields.length > 1 ? (
                <div ref={selectedEditorRef} className="esign-prep-section esign-prep-selected">
                  <span className="esign-prep-eyebrow">{selectedFields.length} fields selected</span>
                  <p className="esign-prep-hint">{multiSelectSummaryLabel(selectedFields)}</p>
                  {(() => {
                    const triState = requiredTriState(selectedFields);
                    if (triState === "none-applicable") {
                      return (
                        <p className="esign-prep-hint">
                          Every box here is one you fill yourself before sending — none of them
                          have a "Required" setting to change.
                        </p>
                      );
                    }
                    return (
                      <label className="esign-prep-selected-check">
                        <input
                          type="checkbox"
                          checked={triState === "all"}
                          ref={(el) => {
                            if (el) el.indeterminate = triState === "mixed";
                          }}
                          onChange={(e) => {
                            const nextRequired = e.target.checked;
                            setFields((prev) =>
                              prev.map((f) =>
                                selectedKeys.has(f.key) && !isPreparerField(f)
                                  ? taken({ ...f, required: nextRequired })
                                  : f,
                              ),
                            );
                          }}
                        />
                        Required to finish signing
                      </label>
                    );
                  })()}
                  <div className="esign-prep-selected-actions">
                    <Button
                      variant="danger-ghost"
                      size="sm"
                      onClick={() => removeFields(selectedKeys)}
                    >
                      Remove {selectedFields.length} fields
                    </Button>
                  </div>
                  <p className="esign-prep-hint">
                    Drag any one of them to move the whole group · Arrow keys nudge all of them ·
                    Shift+arrows nudge more · Delete removes all of them · Shift-click a field to
                    add or drop it, or click one plainly to go back to editing it alone
                  </p>
                </div>
              ) : null}

              {selectedStampKey ? (
                <div className="esign-prep-section esign-prep-selected">
                  <span className="esign-prep-eyebrow">Your signature stamp</span>
                  <Button
                    variant="danger-ghost"
                    size="sm"
                    onClick={() => {
                      setStamps((prev) => prev.filter((s) => s.key !== selectedStampKey));
                      setSelectedStampKey(null);
                    }}
                  >
                    Remove stamp
                  </Button>
                  <p className="esign-prep-hint">Arrow keys nudge · Shift+arrows nudge more · Delete removes</p>
                </div>
              ) : null}
            </div>

            <div className="esign-prep-footer">
              {!mapping && (
                <>
                  {/* Folded by default (2026-09-02): these three have
                  working defaults, are changed on a minority of sends,
                  and were pinned open taking the space the signers and
                  the field buttons needed. The summary line says what
                  they are set to without opening anything, so folding
                  hides the CONTROLS, never the state. */}
                  <FoldingSection
                    id="delivery"
                    title="Reminders, deadline and note"
                    summary={deliverySummary}
                  >
                    {/* Written as one sentence rather than a settings panel:
                  the advisor is reading "what will happen after I click
                  send", and a labelled form of three fields makes a
                  routine send feel like configuration. */}
                    <p className="esign-prep-schedule">
                      <span className="doc-remrow">
                        Remind every
                        <input
                          className="doc-remrow-num"
                          type="number"
                          inputMode="numeric"
                          min={1}
                          aria-label="Remind every (number)"
                          value={remindAmount}
                          onChange={(e) => setRemindAmount(e.target.value)}
                        />
                        <select
                          className="doc-remrow-unit"
                          aria-label="Remind every (unit)"
                          value={remindUnit}
                          onChange={(e) => setRemindUnit(e.target.value as ReminderUnit)}
                        >
                          <option value="hours">hours</option>
                          <option value="days">days</option>
                          <option value="weeks">weeks</option>
                        </select>
                        · up to
                        <input
                          className="doc-remrow-num"
                          type="number"
                          inputMode="numeric"
                          min={0}
                          max={ESIGN_UI_MAX_REMINDERS}
                          aria-label="How many reminders (0 means never)"
                          value={remindMaxText}
                          onChange={(e) => setRemindMaxText(e.target.value)}
                        />
                        times
                      </span>
                      <label>
                        <span className="esign-prep-schedule-label">Deadline</span>
                        <select
                          value={expiryDays ?? "none"}
                          onChange={(e) =>
                            setExpiryDays(e.target.value === "none" ? null : Number(e.target.value))
                          }
                        >
                          <option value="none">no deadline</option>
                          <option value={7}>1 week</option>
                          <option value={14}>2 weeks</option>
                          <option value={30}>30 days</option>
                          <option value={90}>90 days</option>
                        </select>
                      </label>
                    </p>
                    {[...selected].filter((id) => !copyOnly.has(id)).length > 1 ? (
                      <label className="esign-prep-order">
                        <input
                          type="checkbox"
                          checked={enforceOrder}
                          onChange={(e) => setEnforceOrder(e.target.checked)}
                        />
                        <span>
                          Ask them one at a time, in this order — the next person is emailed
                          automatically once the previous one signs
                        </span>
                      </label>
                    ) : null}
                    <label className="send-for-signature-message">
                      Note to include (optional)
                      <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        maxLength={2000}
                        rows={2}
                      />
                    </label>
                  </FoldingSection>
                  <FoldingSection
                    id="afterwards"
                    title="Afterwards"
                    summary={
                      forwardTo.length > 0
                        ? `${forwardTo.length} ${forwardTo.length === 1 ? "person" : "people"} get the signed copy`
                        : "Nobody else gets a copy"
                    }
                  >
                    <ForwardRecipientsPicker
                      recordKind={recordKind}
                      recordId={recordId}
                      recipients={forwardTo}
                      onChange={setForwardTo}
                      hideHeading
                    />
                  </FoldingSection>
                  {/* ── The package, before it goes ──
                  What is about to be sent, document by document, so the
                  last thing the sender reads is the whole envelope
                  rather than the one page they happen to be looking at.
                  A document with nothing on it is stated plainly — it
                  may be perfectly correct (a prequal letter is there to
                  be read), and it may be the tab someone forgot. */}
                  {isPackage ? (
                    <div className="esign-prep-package-review">
                      <p className="esign-prep-review">
                        Sending {packageDocs.length} documents as one request — they sign once, in
                        this order.
                      </p>
                      <ol className="esign-prep-package-list">
                        {packageDocs.map((d) => {
                          const placed = packageFields.get(d.documentId)?.length ?? 0;
                          const left = sendCounts.byDoc.get(d.documentId)?.ghosts ?? 0;
                          const failed = readFailures[d.documentId];
                          return (
                            <li key={d.documentId}>
                              {d.filename}
                              <span className="esign-workspace-muted">
                                {" — "}
                                {placed > 0
                                  ? `${placed} ${placed === 1 ? "field" : "fields"}`
                                  : "nothing to sign"}
                                {left > 0
                                  ? ` · ${left} ${left === 1 ? "suggestion" : "suggestions"} left, not sent`
                                  : ""}
                                {failed && !isDocumentRead(d.documentId)
                                  ? " · couldn't be read here; open it to check before sending"
                                  : ""}
                              </span>
                            </li>
                          );
                        })}
                      </ol>
                      {documentsWithoutFields.length > 0 ? (
                        <p className="esign-prep-review">
                          {documentsWithoutFields.length === 1
                            ? "One document has nothing to sign — it goes out for them to read."
                            : `${documentsWithoutFields.length} documents have nothing to sign — they go out for them to read.`}
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                  {/* Said BEFORE Send, where it can still change what
                      happens (Jake, 2026-09-29): his CEA went out with
                      Outlook never connected, and the only warning was
                      a clause in the small print, so two invitations
                      silently never left. Send still works — it falls
                      back to copyable links — so this warns, it does
                      not block. */}
                  {outlookConnected === false ? (
                    <NotConnectedNotice context="esign" compact />
                  ) : null}
                </>
              )}
              {/* ── Review & send (PR 8b) ── portalled over this screen. */}
              {previewOpen && pages ? (
                <PreviewAsSigner
                  documentName={documentDisplayName(activeDoc.filename)}
                  pages={pages}
                  fields={realFields}
                  stamps={stamps}
                  signers={previewSigners}
                  printed={previewPrinted}
                  senderName={user?.name ?? "You"}
                  onClose={() => setPreviewOpen(false)}
                />
              ) : null}
              <ReviewAndSend
                open={reviewOpen}
                onBack={editFromReview}
                onSend={() => void handleSend("email", undefined, true)}
                sending={sending}
                documents={reviewDocuments}
                signers={reviewSigners}
                emptySeats={reviewEmptySeats}
                completion={reviewCompletion}
                enforceOrder={enforceOrder && reviewSigners.length > 1}
                orderLine={
                  orderIsNumbered
                    ? describeSigningOrder(
                        reviewSigners.map((r) => ({
                          name: r.name.trim().split(/\s+/)[0] ?? r.name,
                          order: signingRank[r.key] ?? 1,
                        })),
                      )
                    : null
                }
                copies={reviewCopies}
                followUps={reviewFollowUps}
                deadline={reviewDeadline}
                from={{
                  name: senderIdentity.companySignOffName ?? user?.name ?? user?.email ?? "You",
                  email: senderIdentity.mailboxUpn ?? user?.email ?? null,
                  note: senderIdentity.mailboxUpn
                    ? `the ${senderIdentity.companySignOffName ?? "company"} inbox`
                    : "your Outlook",
                }}
                companySignOff={senderIdentity.useSenderSignature ? null : senderIdentity.companySignOffName}
                willEmail={outlookConnected !== false}
                subject={reviewSubject}
                defaultSubject={reviewDefaultSubject}
                onSubjectChange={(v) => setSubjectDraft(v === reviewDefaultSubject ? null : v)}
                note={message}
                onNoteChange={setMessage}
                video={signingVideo}
                onVideoChange={setSigningVideo}
                firstDocumentName={documentDisplayName(packageDocs[0]?.filename ?? primaryFilename)}
                standardBody={standardBody}
                emailBody={emailBody}
                onEmailBodyChange={setEmailBody}
                wordingDefaultLabel={wordingTypeLabel}
                saveWordingDefault={saveWordingDefault}
                onSaveWordingDefault={setSaveWordingDefault}
                rememberVideoLabel={rememberRecord ? wordingTypeLabel : null}
                rememberVideo={rememberVideoOn}
                onRememberVideo={setRememberVideoOn}
              />
              {/* ── The record says something different (Nate §6.4) ──
                  Not an error. Two things disagree and somebody has to
                  say which is true, so it reads as a decision and both
                  answers are real. Asked every time, with no way to
                  turn it off: the whole value is being asked at the
                  moment the record would otherwise be left behind. */}
              {syncPrompt ? (
                <div className="esign-prep-sync">
                  <p className="esign-prep-sync-head">
                    <strong>
                      {syncPrompt.terms.length === 1
                        ? "One term on this document does not match the record."
                        : `${syncPrompt.terms.length} terms on this document do not match the record.`}
                    </strong>
                  </p>
                  <ul className="esign-prep-sync-list">
                    {syncPrompt.terms.map((t) => (
                      <li key={t.field}>
                        <span className="esign-prep-sync-label">{t.label}</span>
                        <span className="esign-prep-sync-change">
                          {t.recordValue || "nothing"} → {t.documentValue}
                        </span>
                        {!t.writable ? (
                          <em className="esign-prep-sync-unreadable">
                            can't be read as a {t.label.toLowerCase()} — stays on the document only
                          </em>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                  <div className="esign-prep-sync-actions">
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={sending}
                      onClick={() => {
                        const p = syncPrompt;
                        setSyncPrompt(null);
                        void handleSend(p.deliver, "update_record");
                      }}
                    >
                      Update the record too
                    </Button>
                    <Button
                      variant="sw-ghost"
                      size="sm"
                      disabled={sending}
                      onClick={() => {
                        const p = syncPrompt;
                        setSyncPrompt(null);
                        void handleSend(p.deliver, "document_only");
                      }}
                    >
                      Only this document
                    </Button>
                  </div>
                </div>
              ) : null}
              {offPageNote ? <p className="esign-prep-blocker">{offPageNote}</p> : null}
              {/* The same three problems, but on a document other than
                  the one on screen (2026-09-18) — a package with an
                  issue on a document nobody is currently viewing used
                  to stop the send silently, with nothing here to say
                  why. See packageBlockers and otherDocumentBlockerNote. */}
              {otherDocOffPageNote ? (
                <p className="esign-prep-blocker">{otherDocOffPageNote}</p>
              ) : null}
              {/* Standing where the sender is looking when they reach
                  for Send, rather than only after they press it. Names
                  the pages, because "confirm every page" with one page
                  left on an eleven-page counter is a scavenger hunt. */}
              {unconfirmedTheirPages.length > 0 ? (
                <p className="esign-prep-blocker">
                  {unconfirmedPagesMessage(unconfirmedTheirPages)}
                </p>
              ) : null}
              {unconfiguredNote ? <p className="esign-prep-blocker">{unconfiguredNote}</p> : null}
              {otherDocUnconfiguredNote ? (
                <p className="esign-prep-blocker">{otherDocUnconfiguredNote}</p>
              ) : null}
              {unfilledMergeNote ? <p className="esign-prep-blocker">{unfilledMergeNote}</p> : null}
              {otherDocUnfilledMergeNote ? (
                <p className="esign-prep-blocker">{otherDocUnfilledMergeNote}</p>
              ) : null}
              {/* The review line. An advisor's question before sending a
                  contract is "did it really fill all that in, or am I
                  about to send a document full of blanks?" — so the
                  answer is stated, not implied. Only shown when the
                  record actually answered something; a document with no
                  linked fields says nothing rather than "0". */}
              {/* WHAT GOES AND WHAT STAYS (2026-09-29). Counted off the
                  same arrays the payload is built from, across every
                  document, so this sentence and the send can never
                  disagree. A blank left alone is a choice, and the
                  number is how the sender knows it was theirs. */}
              {!mapping && sendCounts.sent + sendCounts.ghosts > 0 ? (
                <p className="esign-prep-review esign-ghost-sendline">
                  Sending <strong>{sendCounts.sent}</strong>{" "}
                  {sendCounts.sent === 1 ? "field" : "fields"}
                  {sendCounts.ghosts > 0 ? (
                    <>
                      {" · "}
                      <strong>{sendCounts.ghosts}</strong>{" "}
                      {sendCounts.ghosts === 1 ? "blank" : "blanks"} left empty, not sent
                    </>
                  ) : null}
                </p>
              ) : null}
              {autoFilledMergeCount > 0 ? (
                <p className="esign-prep-review">
                  <strong>{autoFilledMergeCount}</strong>{" "}
                  {autoFilledMergeCount === 1 ? "field fills" : "fields fill"} in from this record
                  {unfilledMergeFields.length > 0 ? (
                    <>
                      {" · "}
                      <strong>{unfilledMergeFields.length}</strong> still{" "}
                      {unfilledMergeFields.length === 1 ? "needs" : "need"} you
                    </>
                  ) : (
                    " — nothing to retype"
                  )}
                </p>
              ) : null}
              {autoFilledBySource.length > 0 ? (
                <div className="esign-prep-autofill">
                  <div className="esign-prep-autofill-h">Auto-filled from this record</div>
                  <ul>
                    {autoFilledBySource.map((s) => (
                      <li key={s.source}>
                        <span>{s.source}</span>
                        <span className="esign-prep-autofill-n">
                          {s.count} {s.count === 1 ? "field" : "fields"}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <p className="esign-prep-footnote">
                {mapping ? (
                  fields.length === 0 ? (
                    "No fields yet — place at least one (click a field type, then the page)"
                  ) : (
                    <>
                      {fields.length} {fields.length === 1 ? "field" : "fields"} ·{" "}
                      {mappingSlots.length} {mappingSlots.length === 1 ? "signer" : "signers"}
                    </>
                  )
                ) : (
                  <>
                    {realFields.length === 0
                      ? "Nothing to send yet — accept a value, type into a blank, or place a field"
                      : `${realFields.length} ${realFields.length === 1 ? "field" : "fields"}`}
                    {stamps.length > 0 ? ` · ${stamps.length} of yours signed` : ""} ·{" "}
                    {selected.size} {selected.size === 1 ? "signer" : "signers"} ·{" "}
                    {/* Three states, three claims. `null` means the status
                        check failed or hasn't answered — claiming "sent
                        from your Outlook" then is exactly the false promise
                        the 2026-08-22 fix removed for the `false` case
                        (2026-08-30 audit). Unknown stays neutral. The one
                        named exception (senderIdentity) is checked FIRST:
                        for her this is never "your Outlook" even when
                        Outlook is connected, because the send is routed
                        to the Insurance company inbox instead. */}
                    {outlookConnected === false
                      ? "Outlook isn't connected — you'll get links to share yourself"
                      : senderIdentity.mailboxUpn
                        ? outlookConnected === true
                          ? `sent from the ${senderIdentity.companySignOffName ?? "company"} inbox, not your own address`
                          : `emailed from the ${senderIdentity.companySignOffName ?? "company"} inbox if Outlook is connected — otherwise you'll get links to share yourself`
                        : outlookConnected === true
                          ? "sent from your Outlook"
                          : "emailed from your Outlook if it's connected — otherwise you'll get links to share yourself"}
                    {remindEveryMinutes !== null && outlookConnected !== false
                      ? ` · we'll follow up ${describeCadence(remindEveryMinutes)} until they sign, ${
                          remindMax === 1 ? "once" : `up to ${remindMax} times`
                        }`
                      : ""}
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** "Daniel" / "Daniel and Priya" / "Daniel, Priya and Ann". */
function joinCorrectionNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}
